import store from '@/store'
import { consumeToken } from '@/js/tokenService'
import { reverseGeocode } from '@/js/locationInfo'
import codeShizen from '@/js/codeShizen'
import codeShizenOriginal from '@/js/codeShizenOriginal'
import maplibregl from 'maplibre-gl'
import axios from 'axios'
import DOMPurify from 'dompurify'
import muni from '@/js/muni'
import pyramid, { lavelUpdate, generateStartEndPointsFromGeoJSON, generateSegmentLabelGeoJSON } from '@/js/pyramid'
import * as turf from '@turf/turf'
import { forseMoveLayer, getNextZIndex, queryFGBWithPolygon, wsg84ToJgd, markerAddAndRemove, toggleThumbnailMarker } from '@/js/downLoad'
// mapillary-js (1MB) は使用時に動的ロード
// import { Viewer, CameraControls, RenderMode, TransitionMode } from 'mapillary-js'
import { ref, watch, createApp } from 'vue'
import {
  showFeatureInfo,
  clearFeatureInfo,
  clearSelectedFeature,
  highlightSelectedFeature,
  getSourceData,
  updatePolygonEdgeLabels,
} from '@/js/draw'
import DrawPopup from '@/components/Draw-popup.vue'
import vuetify from '@/plugins/vuetify'
import { routeWithORS } from '@/js/utils/context-menu'
import mw5 from '@/js/mw5'
import { getUsageTracker } from '@/js/utils/usageTracker'

// ポップアップルーティング用のORS APIキー
const ORS_API_KEY = process.env.VUE_APP_ORS_API_KEY

// 幕末期近世の村 AI解説キャッシュ（キー: 村名_領分_石高_国_郡）
const bakumatsuAiCache = new Map()

// 最寄り筆界点表示用レイヤーID
const NV_SOURCE = 'nearest-vertex-source'
const NV_LINE = 'nearest-vertex-line'
const NV_POINT = 'nearest-vertex-point'
const NV_LABEL = 'nearest-vertex-label'

// 平面直角座標→WGS84変換（Gauss-Krüger逆変換）
const _JGD = [null,[33,129.5],[33,131],[36,132+10/60],[33,133.5],[36,134+20/60],[36,136],[36,137+10/60],[36,138.5],[36,139+50/60],[40,140+50/60],[44,140.25],[44,142.25],[44,144.25],[26,142],[26,127.5],[26,124],[26,131],[20,136],[26,154]]
function _planeToLngLat(sys, X, Y) {
  const o = _JGD[sys]; if (!o) return null
  const p0=o[0]*Math.PI/180, l0=o[1]*Math.PI/180, a=6378137, m0=.9999
  const f=1/298.257222101, e2=2*f-f*f, e=Math.sqrt(e2), n=f/(2-f)
  const n2=n*n, n3=n2*n, n4=n3*n
  const A_=a/(1+n)*(1+n2/4+n4/64)
  const a1=n/2-2*n2/3+5*n3/16+41*n4/180, a2=13*n2/48-3*n3/5+557*n4/1440, a3=61*n3/240-103*n4/140, a4=49561*n4/161280
  const b1=n/2-2*n2/3+37*n3/96-n4/360, b2=n2/48+n3/15-437*n4/1440, b3=17*n3/480-37*n4/840, b4=4397*n4/161280
  const d1=2*n-2*n2/3-2*n3+116*n4/45, d2=7*n2/3-8*n3/5-227*n4/45, d3=56*n3/15-136*n4/35, d4=4279*n4/630
  const sp=Math.sin(p0), tp=Math.tan(p0)
  const sg=Math.sinh(e*Math.atanh(e*sp)), tc=tp*Math.sqrt(1+sg*sg)-sg*Math.sqrt(1+tp*tp)
  const chi0=Math.atan(tc)
  const S0=A_*(chi0+a1*Math.sin(2*chi0)+a2*Math.sin(4*chi0)+a3*Math.sin(6*chi0)+a4*Math.sin(8*chi0))
  const xi=(X/m0+S0)/A_, eta=Y/(A_*m0)
  let xi_=xi, eta_=eta; const bs=[0,b1,b2,b3,b4]
  for(let j=1;j<=4;j++){xi_-=bs[j]*Math.sin(2*j*xi)*Math.cosh(2*j*eta);eta_-=bs[j]*Math.cos(2*j*xi)*Math.sinh(2*j*eta)}
  const chi=Math.asin(Math.sin(xi_)/Math.cosh(eta_)); const ds=[0,d1,d2,d3,d4]
  let phi=chi; for(let j=1;j<=4;j++) phi+=ds[j]*Math.sin(2*j*chi)
  return [Math.atan2(Math.sinh(eta_),Math.cos(xi_))*180/Math.PI+l0*180/Math.PI, phi*180/Math.PI]
}

/**
 * 基準点から最寄りの筆界点を検索し、結線表示する
 * 境界点座標プロパティ（実測座標）を使用してタイルクリップの人工頂点を回避
 */
function showNearestVertices(map, baseLng, baseLat, layerId) {
  // 基準点がどの年度のレイヤーかで対応する筆fill layerを決定
  let fillLayer = 'oh-homusyo-2025-fill'
  if (layerId.includes('2026')) fillLayer = 'oh-homusyo-2026-fill'

  // 筆fill layerが存在しなければ終了
  if (!map.getLayer(fillLayer)) return

  const basePoint = turf.point([baseLng, baseLat])

  // ビューポート内の筆ポリゴンを取得
  const parcels = map.queryRenderedFeatures({ layers: [fillLayer] })
  if (parcels.length === 0) return

  // 境界点座標プロパティから実測座標を取得
  const seen = new Set()
  const vertices = []
  for (const f of parcels) {
    const raw = f.properties['境界点座標']
    const coordSys = f.properties['座標系'] || ''
    if (!raw) continue
    const sysMatch = coordSys.match(/(\d+)/)
    const sysNum = sysMatch ? parseInt(sysMatch[1]) : null
    if (!sysNum) continue

    const pairs = raw.split(';').filter(s => s.trim())
    for (const pair of pairs) {
      const [x, y] = pair.trim().split(',')
      if (!x || !y) continue
      const key = `${parseFloat(x).toFixed(3)}_${parseFloat(y).toFixed(3)}`
      if (seen.has(key)) continue
      seen.add(key)
      const lngLat = _planeToLngLat(sysNum, parseFloat(x), parseFloat(y))
      if (!lngLat) continue
      const dist = turf.distance(basePoint, turf.point(lngLat), { units: 'meters' })
      vertices.push({ coord: lngLat, dist, chiban: f.properties['地番'] || '' })
    }
  }

  if (vertices.length === 0) return

  // 最短1件を抽出
  vertices.sort((a, b) => a.dist - b.dist)
  const top = vertices.slice(0, 1)

  // GeoJSONを生成
  const features = []
  const colors = ['#1976D2', '#F57C00', '#7B1FA2'] // 青・橙・紫
  top.forEach((v, i) => {
    const bearing = turf.bearing(basePoint, turf.point(v.coord))
    const bearingNorm = ((bearing % 360) + 360) % 360

    // 結線
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[baseLng, baseLat], v.coord] },
      properties: { color: colors[i], label: `${v.dist.toFixed(1)}m / ${bearingNorm.toFixed(0)}°`, rank: i },
    })
    // 筆界点
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: v.coord },
      properties: { color: colors[i], rank: i },
    })
  })
  // 基準点自体もポイントに
  features.push({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [baseLng, baseLat] },
    properties: { color: '#D32F2F', rank: -1 },
  })

  const geojson = { type: 'FeatureCollection', features }

  // overlay追加/更新
  if (map.getSource(NV_SOURCE)) {
    map.getSource(NV_SOURCE).setData(geojson)
  } else {
    map.addSource(NV_SOURCE, { type: 'geojson', data: geojson })
    map.addLayer({
      id: NV_LINE,
      type: 'line',
      source: NV_SOURCE,
      filter: ['==', '$type', 'LineString'],
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2.5,
        'line-dasharray': [4, 3],
      },
    })
    map.addLayer({
      id: NV_POINT,
      type: 'circle',
      source: NV_SOURCE,
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': 6,
        'circle-color': ['get', 'color'],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    })
    map.addLayer({
      id: NV_LABEL,
      type: 'symbol',
      source: NV_SOURCE,
      filter: ['==', '$type', 'LineString'],
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 13,
        'text-font': ['Open Sans Bold'],
        'symbol-placement': 'line-center',
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': '#fff',
        'text-halo-width': 2,
      },
    })
  }
}

/**
 * 最寄り筆界点表示をクリア
 */
function clearNearestVertices(map) {
  if (map.getLayer(NV_LABEL)) map.removeLayer(NV_LABEL)
  if (map.getLayer(NV_POINT)) map.removeLayer(NV_POINT)
  if (map.getLayer(NV_LINE)) map.removeLayer(NV_LINE)
  if (map.getSource(NV_SOURCE)) map.removeSource(NV_SOURCE)
}

// ポップアップ同期（複数画面で同時ポップアップ）
function isPopupSyncMode() {
  return store.state.isFullScreenPopup && (store.state.map2Flg || store.state.is4ScreenMode)
}

function getActiveMaps() {
  const maps = ['map01']
  if (store.state.map2Flg || store.state.is4ScreenMode) maps.push('map02')
  if (store.state.is4ScreenMode) maps.push('map03', 'map04')
  return maps
}

function queryFeaturesWithHitRadius(map, point, layers, hitRadius = 12) {
  const bbox = [
    [point.x - hitRadius, point.y - hitRadius],
    [point.x + hitRadius, point.y + hitRadius],
  ]
  return map.queryRenderedFeatures(bbox, { layers })
}

// PHP API ベースURL（開発環境ではプロキシ経由）
const PHP_API_BASE = process.env.VUE_APP_PHP_API_BASE || '/oh3/php'

// メッシュ人口ポップアップ共通HTML生成（125m/250m/500m/1km共通）
function buildMeshPopupHtml(jinko, ronen, agingRate) {
  return '<div style="padding:12px 14px;font-size:13px;line-height:1.7;font-family:-apple-system,BlinkMacSystemFont,sans-serif;">' +
    '<div style="font-weight:700;font-size:15px;color:#333;margin-bottom:6px;padding-bottom:6px;border-bottom:2px solid #1976D2;">' +
    jinko.toLocaleString() + '<span style="font-size:12px;color:#888;margin-left:2px;">人</span></div>' +
    (jinko > 0 ? '<table style="width:100%;border-collapse:collapse;">' +
    '<tr><td style="padding:2px 0;color:#666;">高齢者(65+)</td>' +
    '<td style="padding:2px 0;text-align:right;font-weight:600;font-variant-numeric:tabular-nums;">' +
    ronen.toLocaleString() + '<span style="font-size:11px;color:#888;margin-left:2px;">人</span>' +
    '<span style="font-size:11px;color:#999;margin-left:4px;">(' + agingRate + '%)</span></td></tr>' +
    '</table>' : '') +
    '</div>'
}

// グローバル画像プレビュー関数（popup内のonclickから呼び出し用）
window.openGlobalMediaPreview = (src, kind = 'image', filename = '') => {
  store.commit('openGlobalMediaPreview', { src, kind, filename })
}

// 旧版地形図プレビュー関数（popup内のonclickから呼び出し用）
// mw5.jsのextentデータをIDで引いてFWを開く
const mw5Map = Object.fromEntries(mw5.map(m => [m.id, m]))
window.openMw5Preview = (id) => {
  const entry = mw5Map[id]
  if (!entry) { console.warn('[mw5-preview] entry not found for id', id); return }
  const name = entry.title.match(/『(.+?)』/)?.[1] || entry.title
  store.commit('addMw5Preview', { id, title: name, extent: entry.extent })
}

// ポップアップから3Dジオラマを開く（指定座標に移動してからジオラマ表示）
// 避難施設ポップアップから到達圏を表示
window.openIsochroneFromFacility = (lng, lat, name) => {
  store.commit('setIsochronePreset', { lng, lat, name, autoRun: true })
  store.dispatch('showFloatingWindow', 'isochrone-window')
}

// 道路専用到達圏: FWを開いてプリセット座標をセット
window.openRoadIsochrone = (lng, lat) => {
  store.commit('setRoadIsochronePreset', { lng, lat })
  store.dispatch('showFloatingWindow', 'road-isochrone-window')
}

window.openDioramaAt = (lng, lat, zoom = 14) => {
  const map = store.state.map01
  if (!map) return
  // UI要素を閉じる
  const st = store.state
  Object.keys(st.floatingWindows).forEach(id => { st.floatingWindows[id] = false })
  st.showDrawDrawer = false
  st.showDrawListDrawer = false
  st.showAboutDrawer = false
  st.showPointInfoDrawer = false
  st.showRightDrawer = false
  st.showChibanzuDrawer = false
  st.popupDialog = false
  closeAllPopups()
  // 座標に移動してジオラマ表示
  map.flyTo({ center: [lng, lat], zoom, pitch: 0, bearing: 0, duration: 600 })
  map.once('idle', () => { st.isDiorama3D = true })
}

// 国土数値情報プロパティ名 → 日本語ラベル変換マップ
const kkPropLabel = {
  // W01 ダム
  W01_001: 'ダム名', W01_002: '所在地', W01_003: 'ダム形式', W01_004: '河川名', W01_005: '水系名',
  W01_006: '目的', W01_007: '堤高(m)', W01_008: '堤頂長(m)', W01_009: '総貯水量(千m³)',
  W01_010: '竣工年', W01_011: '事業者', W01_012: '管理者', W01_013: 'URL',
  // W09 湖沼
  W09_001: '湖沼名', W09_002: '最大水深(m)', W09_003: '面積(km²)', W09_004: '水面標高(m)',
  W09_005: '周囲長(km)', W09_006: '透明度(m)', W09_007: '所在地',
  // W05 河川
  W05_001: '河川コード', W05_002: '水系名', W05_003: '河川名', W05_004: '区間名',
  // C23 海岸線
  C23_001: '海岸名', C23_002: '海岸種別', C23_003: '海岸管理者', C23_004: '所在地',
  // P23 海岸保全施設
  P23_001: '施設名', P23_002: '施設種別', P23_003: '管理者', P23_004: '所在地',
  // G08 低位地帯
  G08_001: 'メッシュコード', G08_002: '標高(m)', G08_003: '地区名', G08_004: '面積(km²)',
  // L01 地価公示
  L01_001: '標準地番号', L01_006: '住居表示', L01_008: '価格(円/m²)', L01_009: '地積(m²)',
  L01_024: '用途', L01_025: '建ぺい率(%)', L01_028: '容積率(%)',
  // L02 都道府県地価調査
  L02_001: '基準地番号', L02_004: '住居表示', L02_006: '価格(円/m²)', L02_007: '地積(m²)',
  L02_021: '用途', L02_022: '建ぺい率(%)', L02_025: '容積率(%)',
  // A54 盛土
  fillSlopeClassification: '盛土分類', prefectureName: '都道府県', localGovernmentName: '市区町村',
  fillSlopeNumber: '盛土番号',
  // A12 農業地域
  A12_001: '都道府県コード', A12_002: '支庁コード', A12_003: '地区名', LAYER_NO: 'レイヤー区分',
  // A13 森林地域
  A13_001: '都道府県コード', A13_002: '支庁コード', A13_003: '地区名',
  // A45 国有林野
  A45_002: '管理局名', A45_003: '森林名', A45_004: '面積(ha)',
  // N03 行政区域
  N03_001: '都道府県名', N03_002: '支庁名', N03_003: '郡・政令市名', N03_004: '市区町村名',
  N03_005: '行政区域コード', N03_007: '設置年月日',
  // A16 DID
  A16_003: '都道府県名', A16_005: '人口', A16_006: '面積(km²)',
  // A27 小学校区
  A27_002: '設置者コード', A27_004: '学校名', A27_005: '所在地',
  // A32 中学校区
  A32_002: '設置者コード', A32_004: '学校名', A32_005: '所在地',
  // A38 医療圏
  P23a_003: '三次医療圏名', P23b_002: '二次医療圏コード', P23b_003: '二次医療圏名',
  // A35 景観
  A35a_003: '都道府県名', A35a_004: '計画名', A35a_005: '策定日', A35a_006: '変更日',
  A35d_003: '都道府県名', A35d_005: '地区名', A35d_006: '告示日', A35d_007: '変更日', A35d_008: '面積(ha)',
  A35f_003: '都道府県名', A35f_005: '地区名', A35f_006: '告示日',
  A35g_003: '都道府県名', A35g_005: '名称', A35g_007: '種別', A35g_008: '指定日', A35g_009: '所在地',
  A35h_003: '都道府県名', A35h_005: '名称', A35h_007: '種別', A35h_008: '指定日', A35h_009: '所在地',
  // A43 伝統的建造物群
  A43_001: '都道府県コード', A43_002: '地区コード', A43_004: '地区名', A43_006: '選定年月日',
  A43_007: '種別', A43_008: '面積(ha)', A43_009: '都道府県名', A43_010: '市区町村名',
  // A44 歴史的風致
  A44_001: '都道府県コード', A44_002: '市区町村コード', A44_004: '地区名', A44_005: '認定日', A44_008: '面積(ha)',
  // A09 都市地域
  prefec_cd: '都道府県コード', area_cd: '区域コード',
  layer_no: 'レイヤー区分',
  // A29 用途地域
  A29_002: '都道府県コード', A29_003: '市区町村コード', A29_004: '用途地域コード', A29_005: '用途地域名',
  A29_006: '建ぺい率(%)', A29_007: '容積率(%)',
  // A50 立地適正化
  A50_002: '都道府県コード', A50_004: '市区町村名', A50_005: '計画名',
  A50_006: '区域区分', A50_007: '区域名',
  // A42 歴史的風土
  A42_001: '都道府県コード', A42_002: '区域コード', A42_004: '区域名', A42_007: '面積(ha)',
  // P13 都市公園
  P13_003: '公園名', P13_005: '種別', P13_006: '面積(m²)', P13_007: '供用開始年', P13_008: '管理者',
  // A55 都市計画決定情報
  YoutoName: '用途名', YoutoCode: '用途コード', AreaCode: '区域コード', AreaType: '区域種別',
  FAR: '容積率(%)', BCR: '建ぺい率(%)', Pref: '都道府県', Cityname: '市区町村',
  TokeiType: '都市計画区域種別', TokeiName: '都市計画区域名',
  DistType: '地区種別', DistName: '地区名', FNDate: '決定年月日',
  // A03 三大都市圏
  A03_001: '計画区域名', A03_002: '都道府県名', A03_003: '市区町村名',
  // A17 過疎地域
  A17_003: '市区町村名', A17_006: '都道府県名', A17_008: '過疎種別', A17_009: '過疎区分',
  // A24 振興山村
  A24_003: '地域名', A24_006: '都道府県名', A24_007: '市区町村名',
  // A25 特定農山村
  A25_003: '地域名', A25_006: '都道府県名',
  // A19 離島振興
  A19_003: '地域名', A19_006: '都道府県名', A19_007: '市区町村名',
  // A18 半島振興
  A18_003: '地域名', A18_006: '都道府県名', A18_007: '市区町村名',
  // A22 豪雪地帯
  A22_003: '市区町村名', A22_006: '都道府県名', A22_008: '豪雪種別名', A22_009: '豪雪区分',
  // A23 特殊土壌
  A23_003: '地域名', A23_004: '土壌区分', A23_006: '都道府県名', A23_007: '市区町村名',
  // A37 半島循環道路
  A37_003: '路線名', A37_006: '都道府県名', A37_007: '半島名',
  // A39 密集市街地
  A39_003: '地区名', A39_006: '都道府県名', A39_007: '市区町村名',
  // P20 避難施設
  P20_002: '施設名', P20_003: '住所', P20_004: '施設種別', P20_005: '収容人数',
  P20_006: '施設規模', P20_007: '対象災害',
  // A30b 竜巻
  A30b_002: '現象種別', A30b_003: '発生日時', A30b_005: '発生場所', A30b_013: '藤田スケール', A30b_014: '被害概要',
  // A46 地すべり
  'A46-a_003': '都道府県名', 'A46-a_004': '区域名', 'A46-a_005': '指定年月日', 'A46-a_006': '所管省庁',
  A46_003: '都道府県名', A46_004: '区域名', A46_005: '指定年月日', A46_006: '所管省庁',
  // A47 急傾斜地
  'A47-a_003': '都道府県名', 'A47-a_004': '区域名', 'A47-a_005': '指定年月日', 'A47-a_006': '面積(m²)',
  A47_003: '都道府県名', A47_004: '区域名', A47_005: '指定年月日', A47_006: '面積(m²)',
  // A48 災害危険区域
  A48_001: '都道府県コード', A48_002: '市区町村コード', A48_005: '区域名', A48_007: '指定理由',
  A48_008: '条例名',
  // A52 砂防指定地
  'A52-a_003': '都道府県名', 'A52-a_004': '区域名', 'A52-a_005': '指定年月日',
  A52_003: '都道府県名', A52_004: '区域名', A52_005: '指定年月日',
  // A40 津波浸水
  A40_003: '浸水深区分', A40_004: '都道府県名', A40_005: '想定名称',
  // A49 高潮浸水
  A49_003: '浸水深区分', A49_004: '都道府県名',
  // A51 雨水出水
  A51_003: '都道府県名', A51_004: '浸水深区分', A51_005: '浸水深区分',
  // A33 土砂災害
  A33_001: '現象種類', A33_002: '区域区分', A33_003: '都道府県名', A33_004: '市区町村名',
  A33_005: '区域名', A33_006: '所在地',
  // A31a 洪水浸水
  A31a_003: '河川名', A31a_004: '想定規模', A31a_005: '浸水深ランク',
  A31_003: '河川名', A31_004: '想定規模', A31_005: '浸水深ランク',
  A31_105: '計画規模浸水深', A31_205: '想定最大浸水深', A31_305: '浸水継続時間',
  // P28 国・都道府県の機関
  P28_001: '行政区域コード', P28_002: '機関分類コード', P28_003: '名称', P28_004: '所在地', P28_005: '管理者コード',
  // P05 市町村役場等
  P05_001: '行政区域コード', P05_002: '施設分類コード', P05_003: '名称', P05_004: '所在地',
  // P34 市区町村役場
  P34_001: '行政区域コード', P34_002: '分類コード', P34_003: '名称', P34_004: '所在地',
  // P02 公共施設
  P02_001: '行政区域コード', P02_002: '大分類コード', P02_003: '小分類コード', P02_004: '名称', P02_005: '所在地', P02_006: '管理者',
  // P22 下水道
  P22a_001: '行政区域コード', P22a_002: '施設名称', P22a_003: '所在地',
  P22b_001: '行政区域コード', P22b_002: '施設名称', P22b_003: '所在地',
  // P15 廃棄物処理施設
  P15_001: '施設名', P15_002: '市区町村名', P15_003: '施設種別コード',
  // P03 発電施設
  P03_0001: '事業者名', P03_0002: '発電所名', P03_0003: '所在地', P03_0004: '発電区分',
  // P26 ニュータウン
  P26_001: '都道府県名', P26_002: '市区町村名', P26_003: '行政区域コード', P26_004: '地区名',
  // P16 研究機関
  P16_001: '名称', P16_002: '設置主体コード', P16_003: '研究分野コード', P16_004: '所在地',
  // P24 地場産業
  P24_001: 'ID', P24_002: '行政区域コード', P24_003: '都道府県名', P24_004: '市区町村名', P24_005: '施設名', P24_006: '所在地',
  // P31 物流拠点
  P31_001: '名称', P31_002: '施設種別コード', P31_003: '所在地', P31_004: '供用開始年', P31_005: '所在地2',
  // P33 集客施設
  P33_001: 'ID', P33_002: '行政区域コード', P33_003: '都道府県名', P33_004: '施設区分コード', P33_005: '施設名称', P33_006: '種別', P33_007: '所在地',
  // P14 福祉施設
  P14_001: 'ID', P14_002: '行政区域コード', P14_003: '都道府県名', P14_004: '所在地', P14_005: '大分類コード', P14_006: '中分類コード', P14_007: '小分類コード', P14_008: '名称',
  // P27 文化施設
  P27_001: '行政区域コード', P27_002: '大分類コード', P27_003: '都道府県名', P27_004: '分類コード', P27_005: '施設名', P27_006: '所在地',
  // P32 文化財
  P32_001: 'ID', P32_002: '行政区域コード', P32_003: '都道府県名', P32_004: '大分類コード', P32_005: '小分類コード', P32_006: '名称', P32_007: '所在地',
  // A34 世界文化遺産
  A34_001: '名称', A34_002: '構成資産名', A34_003: '所在地', A34_004: '登録年',
  // A28 世界自然遺産
  A28_001: '名称', A28_002: '所在地', A28_003: '登録年',
  // P19 地域資源
  P19_001: 'ID', P19_002: '行政区域コード', P19_003: '都道府県名', P19_004: '類型コード',
  P19_005: '市区町村名', P19_006: '名称（ふりがな）', P19_007: '所在地', P19_008: '名称',
  // A10 自然公園
  A10_001: '公園名', A10_002: '都道府県', A10_003: '地域種別',
  // A11 自然保全
  A11_001: '名称', A11_002: '都道府県', A11_003: '地域種別',
  // A15 鳥獣保護区
  A15_001: '名称', A15_002: '都道府県', A15_003: '種別', A15_004: '保護区分',
  // N10 緊急輸送道路
  N10_001: '路線名', N10_002: '区分',
  // N12 重要物流道路
  N12_001: '路線名', N12_002: '区分',
  // P36 高速バス停留所
  name: '停留所名', operator: '運行事業者',
  // C28 空港
  C28_001: '空港名', C28_002: '種別', C28_003: '設置管理者', C28_004: '所在地',
  // C02 港湾
  C02_001: '種別コード', C02_002: '港湾法種別コード', C02_003: '行政区域コード', C02_004: '都道府県名',
  C02_005: '港湾名', C02_006: '所在地', C02_007: '管理者',
  // N09 定期旅客航路
  N09_001: '航路名', N09_002: '発着港', N09_003: '事業者', N09_004: '航路距離(km)',
  // S10a 港湾間流通量
  S10a_001: '発港', S10a_002: '着港', S10a_003: '品目', S10a_004: '数量(トン)',
  // S10b 空港間流通量
  S10b_001: '発空港', S10b_002: '着空港', S10b_003: '旅客数',
  // S05-d 貨物旅客流動量
  S05d_001: '発地域', S05d_002: '着地域', S05d_003: '品目', S05d_004: '数量',
  // A19s 離島振興統計
  A19s_001: '都道府県コード', A19s_002: '支庁コード', A19s_003: '島コード', A19s_004: '島名',
  A19s_005: '市区町村コード', A19s_006: '市区町村名', A19s_007: '人口（総数）', A19s_008: '人口（男）',
  A19s_009: '人口（女）', A19s_010: '世帯数', A19s_011: '面積(km2)',
  // A22s 豪雪地帯統計
  A22s_001: '都道府県コード', A22s_002: '市区町村コード', A22s_003: '市区町村名',
  A22s_004: '豪雪地帯区分', A22s_005: '人口（総数）', A22s_006: '世帯数', A22s_007: '面積(km2)',
  // A18s-a 半島振興統計
  A18sa_001: '半島名', A18sa_002: '都道府県コード', A18sa_003: '市区町村コード',
  A18sa_004: '市区町村名', A18sa_005: '人口（総数）', A18sa_006: '世帯数', A18sa_007: '面積(km2)',
  // A20s 奄美群島統計
  A20s_001: '都道府県コード', A20s_002: '市区町村コード', A20s_003: '市区町村名',
  A20s_004: '人口（総数）', A20s_005: '世帯数', A20s_006: '面積(km2)',
  // A21s 小笠原諸島統計
  A21s_001: '都道府県コード', A21s_002: '市区町村コード', A21s_003: '市区町村名',
  A21s_004: '人口（総数）', A21s_005: '世帯数', A21s_006: '面積(km2)',
  // S05-c 駅別乗降客数（パーソントリップ）
  S05c_001: '調査年', S05c_002: '駅コード', S05c_003: '駅名', S05c_004: '事業者名',
  S05c_005: 'バス乗車トリップ', S05c_006: '自動車乗車トリップ', S05c_007: '二輪車乗車トリップ',
  S05c_008: '徒歩乗車トリップ', S05c_009: 'バス降車トリップ', S05c_010: '自動車降車トリップ',
  // G04-a 標高・傾斜度3次メッシュ
  G04a_001: 'メッシュコード', G04a_002: '平均標高(m)', G04a_003: '最高標高(m)', G04a_004: '最低標高(m)',
  G04a_005: '中央標高(m)', G04a_006: '平均傾斜度(度)', G04a_007: '最大傾斜度(度)',
  // G04-c 標高・傾斜度4次メッシュ
  G04c_001: 'メッシュコード', G04c_002: '平均標高(m)', G04c_003: '最高標高(m)', G04c_004: '最低標高(m)',
  G04c_005: '中央標高(m)', G04c_006: '平均傾斜度(度)', G04c_007: '最大傾斜度(度)',
  // G04-d 標高・傾斜度5次メッシュ
  G04d_001: 'メッシュコード', G04d_002: '平均標高(m)', G04d_003: '最高標高(m)', G04d_004: '最低標高(m)',
  G04d_005: '中央標高(m)', G04d_006: '平均傾斜度(度)', G04d_007: '最大傾斜度(度)',
  // G02 平年値メッシュ（v3.0）
  G02_001: 'メッシュコード',
  // 降水量 (0.1mm)
  G02_002: '1月降水量', G02_003: '2月降水量', G02_004: '3月降水量', G02_005: '4月降水量',
  G02_006: '5月降水量', G02_007: '6月降水量', G02_008: '7月降水量', G02_009: '8月降水量',
  G02_010: '9月降水量', G02_011: '10月降水量', G02_012: '11月降水量', G02_013: '12月降水量',
  G02_014: '年降水量',
  // 気温 (0.1℃) 月別: 最高/最低/平均 × 12ヶ月
  G02_015: '1月最高気温', G02_016: '1月最低気温', G02_017: '1月平均気温',
  G02_018: '2月最高気温', G02_019: '2月最低気温', G02_020: '2月平均気温',
  G02_021: '3月最高気温', G02_022: '3月最低気温', G02_023: '3月平均気温',
  G02_024: '4月最高気温', G02_025: '4月最低気温', G02_026: '4月平均気温',
  G02_027: '5月最高気温', G02_028: '5月最低気温', G02_029: '5月平均気温',
  G02_030: '6月最高気温', G02_031: '6月最低気温', G02_032: '6月平均気温',
  G02_033: '7月最高気温', G02_034: '7月最低気温', G02_035: '7月平均気温',
  G02_036: '8月最高気温', G02_037: '8月最低気温', G02_038: '8月平均気温',
  G02_039: '9月最高気温', G02_040: '9月最低気温', G02_041: '9月平均気温',
  G02_042: '10月最高気温', G02_043: '10月最低気温', G02_044: '10月平均気温',
  G02_045: '11月最高気温', G02_046: '11月最低気温', G02_047: '11月平均気温',
  G02_048: '12月最高気温', G02_049: '12月最低気温', G02_050: '12月平均気温',
  G02_051: '年平均最高気温', G02_052: '年平均最低気温', G02_053: '年平均気温',
  // 最深積雪 (cm)
  G02_054: '1月最深積雪', G02_055: '2月最深積雪', G02_056: '3月最深積雪', G02_057: '4月最深積雪', G02_058: '年最深積雪',
  // 日照時間 (0.1h)
  G02_059: '1月日照時間', G02_060: '2月日照時間', G02_061: '3月日照時間', G02_062: '4月日照時間',
  G02_063: '5月日照時間', G02_064: '6月日照時間', G02_065: '7月日照時間', G02_066: '8月日照時間',
  G02_067: '9月日照時間', G02_068: '10月日照時間', G02_069: '11月日照時間', G02_070: '12月日照時間',
  G02_071: '年日照時間',
  // 全天日射量 (0.1MJ/m²)
  G02_072: '1月日射量', G02_073: '2月日射量', G02_074: '3月日射量', G02_075: '4月日射量',
  G02_076: '5月日射量', G02_077: '6月日射量', G02_078: '7月日射量', G02_079: '8月日射量',
  G02_080: '9月日射量', G02_081: '10月日射量', G02_082: '11月日射量', G02_083: '12月日射量',
  G02_084: '年日射量',
  // W07 流域メッシュ
  W07_001: 'メッシュコード', W07_002: '水系域コード', W07_003: '河川コード', W07_004: '流域名',
  // L03-a 土地利用3次メッシュ（PMTilesフィールド名は日本語）
  // L03-b 土地利用細分メッシュ
  L03b_001: 'メッシュコード', L03b_002: '土地利用種別',
  // L03-b-u 都市地域土地利用細分メッシュ
  L03bu_001: 'メッシュコード', L03bu_002: '土地利用種別',
  // L03-b-c 土地利用詳細メッシュ
  L03bc_001: 'メッシュコード', L03bc_002: '土地利用種別',
  // A30a5 土砂災害・雪崩メッシュ
  A30a5_001: 'メッシュコード', A30a5_002: '地すべり件数', A30a5_003: '崖崩れ件数',
  A30a5_004: '土石流件数', A30a5_005: '雪崩件数',
  // A31b 洪水浸水想定区域メッシュ
  rank: 'ランク',
  // P09 宿泊容量メッシュ
  P09_001: 'メッシュコード', P09_002: 'ホテル施設数', P09_003: 'ホテル客室数',
  P09_004: 'ホテル収容人数', P09_005: '旅館施設数', P09_006: '旅館客室数',
  P09_007: '旅館収容人数', P09_008: '簡易宿所数', P09_009: '簡易宿所客室数',
  P09_010: '簡易宿所収容人数', P09_011: '会社保養所数', P09_012: '会社保養所客室数',
  P09_013: '総収容人数', P09_014: '総施設数',
  // N04 道路密度・道路延長メッシュ（各6フィールド: 左辺/上辺/右辺/下辺交差, 実延長, 1km²換算）
  N04_001: 'メッシュコード', N04_002: 'メッシュ面積(km²)',
  N04_008: '25m以上 密度(m/km²)', N04_007: '25m以上 実延長(m)',
  N04_014: '19.5-25m 密度(m/km²)', N04_013: '19.5-25m 実延長(m)',
  N04_020: '13-19.5m 密度(m/km²)', N04_019: '13-19.5m 実延長(m)',
  N04_026: '13m以上不明 密度(m/km²)', N04_025: '13m以上不明 実延長(m)',
  N04_032: '5.5-13m 密度(m/km²)', N04_031: '5.5-13m 実延長(m)',
  N04_038: '3-5.5m 密度(m/km²)', N04_037: '3-5.5m 実延長(m)',
  N04_044: '3m未満 密度(m/km²)', N04_043: '3m未満 実延長(m)',
  N04_050: '未調査 密度(m/km²)', N04_049: '未調査 実延長(m)',
  N04_056: '合計 道路密度(m/km²)', N04_055: '合計 道路実延長(m)',
}
// KSJポップアップ用: ラベル付きフィールドを優先表示するヘルパー
const kkSkipFields = new Set(['IDO', 'KEIDO', 'ORigna', 'Origna', 'PosStat', 'TreeCode', 'gml_id', 'fid'])
function kkPropsHtml(props) {
  const entries = Object.entries(props).filter(([k]) => !k.startsWith('_') && !kkSkipFields.has(k))
  const labeled = entries.filter(([k]) => kkPropLabel[k])
  if (labeled.length > 0) {
    return labeled.filter(([, v]) => v !== null && v !== '' && v !== undefined).map(([k, v]) => kkPropLabel[k] + ': ' + v).join('<br>')
  }
  return entries.filter(([, v]) => v !== null && v !== '' && v !== undefined).slice(0, 8).map(([k, v]) => k + ': ' + v).join('<br>')
}

// 緑のオープンデータ カテゴリトグル用のstate
let greenCategoryFilterState = null // null = 全表示, string = 単一カテゴリ

// 緑のオープンデータ カテゴリフィルター（popup内のonclickから呼び出し用）
window.toggleGreenCategory = (categoryId) => {
  // 全マップに適用（マルチスクリーン対応）
  const maps = [store.state.map01, store.state.map02, store.state.map03, store.state.map04].filter(m => m)
  if (maps.length === 0) return

  const layerId = 'oh-tokyoGreenPmtiles'

  if (greenCategoryFilterState === categoryId) {
    // 同じカテゴリをクリック → 全表示に戻す
    maps.forEach(map => {
      if (map.getLayer(layerId)) {
        map.setFilter(layerId, null)
      }
    })
    greenCategoryFilterState = null
    // FW連動: 全カテゴリ選択
    store.commit('setGreenSelectedCategories', null)
  } else {
    // 新しいカテゴリをクリック → そのカテゴリのみ表示
    maps.forEach(map => {
      if (map.getLayer(layerId)) {
        map.setFilter(layerId, ['==', ['get', '_category'], categoryId])
      }
    })
    greenCategoryFilterState = categoryId
    // FW連動: 単一カテゴリ選択
    store.commit('setGreenSelectedCategories', categoryId)
  }
}

// ポップアップルーティングのレイヤー管理（複数ポップアップで共有）
const popupRouteState = {
  routes: [], // [{sourceId, lineId, arrowId, markerSourceId, markerCircleId, markerTextId}]
}

// 指定座標へのルーティング実行（汎用）
export async function routeToPoint(map, destLng, destLat) {
  const center = map.getCenter()
  const origin = [center.lng, center.lat]
  const dest = [destLng, destLat]

  try {
    const geojson = await routeWithORS(map, [origin, dest], {
      profile: 'foot-hiking',
      apiKey: ORS_API_KEY,
      fit: false,
    })

    const properties = geojson.features?.[0]?.properties || {}
    const durationMin = Math.round((properties.summary?.duration || 0) / 60)
    const distanceM = Math.round(properties.summary?.distance || 0)

    // ユニークなID生成
    const routeId = `popup-route-${Date.now()}`
    const ids = {
      sourceId: `${routeId}-source`,
      lineId: `${routeId}-line`,
      arrowId: `${routeId}-arrow`,
      markerSourceId: `${routeId}-marker-source`,
      markerCircleId: `${routeId}-marker-circle`,
      markerTextId: `${routeId}-marker-text`,
    }

    // ルートライン描画
    map.addSource(ids.sourceId, { type: 'geojson', data: geojson })
    map.addLayer({
      id: ids.lineId,
      type: 'line',
      source: ids.sourceId,
      paint: {
        'line-color': '#1976D2',
        'line-width': 5,
        'line-opacity': 0.8,
      },
    })
    map.addLayer({
      id: ids.arrowId,
      type: 'symbol',
      source: ids.sourceId,
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 100,
        'text-field': '▶',
        'text-size': 16,
        'text-rotate': 0,
        'text-keep-upright': false,
      },
      paint: {
        'text-color': '#0D47A1',
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 1,
      },
    })

    // 到着地点マーカー描画
    map.addSource(ids.markerSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: { type: 'Point', coordinates: dest },
          properties: { duration: durationMin, distance: distanceM },
        }],
      },
    })
    map.addLayer({
      id: ids.markerCircleId,
      type: 'circle',
      source: ids.markerSourceId,
      paint: {
        'circle-radius': 20,
        'circle-color': '#1976D2',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
      },
    })
    map.addLayer({
      id: ids.markerTextId,
      type: 'symbol',
      source: ids.markerSourceId,
      layout: {
        'text-field': ['concat', ['get', 'duration'], '分'],
        'text-size': 11,
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      },
      paint: {
        'text-color': '#FFFFFF',
      },
    })

    // 状態に追加
    popupRouteState.routes.push(ids)

    return { durationMin, distanceM }
  } catch (e) {
    console.error('ポップアップルーティングエラー:', e)
    throw e
  }
}

// ポップアップルーティングを全てクリア（汎用）
export function clearAllPopupRoutes(map) {
  for (const ids of popupRouteState.routes) {
    // レイヤー削除
    const layers = [ids.markerTextId, ids.markerCircleId, ids.arrowId, ids.lineId]
    for (const layerId of layers) {
      if (map.getLayer(layerId)) map.removeLayer(layerId)
    }
    // ソース削除
    const sources = [ids.markerSourceId, ids.sourceId]
    for (const sourceId of sources) {
      if (map.getSource(sourceId)) map.removeSource(sourceId)
    }
  }
  popupRouteState.routes = []
}

// 県別森林計画図ポップアップ用プロパティ設定
const prefForestPopupConfig = {
  hokkaido: { muni: 'region', rinpan: '林班', shohan: '小班' },
  iwate: { muni: '市町村', rinpan: '林班', shohan: '小班' },
  niigata: { muni: '新市町村名', rinpan: '林班', shohan: '小班' },
  gunma: { muni: 'muni', rinpan: null, shohan: null, nameField: 'NAME', codeField: 'FIELD001' },
  tochigi: { muni: '計画区', rinpan: null, shohan: '小班', extra: [['林種', '林種'], ['中樹種', '樹種'], ['林齢', '林齢'], ['小班面積', '面積(ha)'], ['所有形態', '所有形態']] },
  nagano: { muni: 'municipality', rinpan: 'RIN', shohan: 'SHO' },
  shizuoka: { muni: '市町村名', rinpan: '林班', shohan: '小班_親番', extra: [['樹種名_1', '樹種'], ['林齢_1', '林齢'], ['森林簿面積', '面積(ha)']] },
  wakayama: { muni: 'muni', rinpan: 'frh_rinpan', shohan: null, shohanExpr: ['frh_syoha0', 'frh_syoha1'], extra: [['frj_tree_0', '樹種'], ['frj_frst_1', '林齢'], ['frh_syoha3', '面積(ha)'], ['frh_own_fo', '所有形態']] },
  ehime: { muni: 'muni', rinpan: '林班番号', shohan: null },
  yamaguchi: { muni: null, rinpan: '林班', shohan: '小班', extra: [['林齢1', '林齢'], ['面積', '面積(ha)']] },
  kumamoto: { muni: null, rinpan: '林班', shohan: '表示用小班', extra: [['第１林齢', '林齢'], ['ポリゴン面', '面積(ha)']] },
  nagasaki: { muni: '市町村名', rinpan: '林班', shohan: '小班', extra: [['樹種', '樹種'], ['林齢', '林齢'], ['森林面積', '面積(ha)'], ['林種', '林種']] },
}
const prefForestNames = {
  hokkaido: '北海道', iwate: '岩手県', niigata: '新潟県', gunma: '群馬県', tochigi: '栃木県',
  nagano: '長野県', shizuoka: '静岡県', wakayama: '和歌山県', ehime: '愛媛県',
  yamaguchi: '山口県', kumamoto: '熊本県', nagasaki: '長崎県',
}

// 各マップごとにポップアップを管理（4画面モード対応）
export const popups = {
  map01: [],
  map02: [],
  map03: [],
  map04: []
}

/**
 * 全画面ポップアップ: クリックされたマップ以外のマップにも同じ座標でpopup()を発火する
 */
export function triggerFullScreenPopup(e, clickedMapName) {
  if (!isPopupSyncMode()) return
  const lngLat = e.lngLat
  for (const mapName of getActiveMaps()) {
    if (mapName === clickedMapName) continue
    const mapInstance = store.state[mapName]
    if (!mapInstance) continue
    popup({ lngLat, point: mapInstance.project(lngLat) }, mapInstance, mapName, store.state.map2Flg)
  }
}

/**
 * FloatingWindowを使ってドロー地物のポップアップを作成（新方式）
 */
export function openDrawFloatingWindow(feature, map) {
  if (!feature) return

  // currentDrawFeatureをstoreに保存
  store.commit('setCurrentDrawFeature', feature)

  // FloatingWindowを開く
  store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: true })

  // 既存のMapLibreポップアップを全て閉じる
  closeAllPopups()
}
const isSmall = window.innerWidth < 500

// Turf.jsを使用して面積、周長、頂点数を計算する関数
export function calculatePolygonMetrics(polygon) {
  try {
    // 面積の計算（㎡単位で取得）
    const areaRaw = turf.area(polygon) // m2
    // 面積のフォーマット
    let area
    if (areaRaw >= 1_000_000) {
      // 100万m²以上はkm²で
      area = '約' + (areaRaw / 1_000_000).toFixed(2) + 'km²'
    } else if (areaRaw >= 10_000) {
      // 1万m²以上は1k m²表記
      area = '約' + (areaRaw / 1_000).toFixed(1) + 'k m²'
    } else {
      // それ未満はそのままm²
      area = '約' + areaRaw.toFixed(1) + 'm²'
    }
    // 周長の計算（m単位で取得）
    const perimRaw = turf.length(polygon, { units: 'meters' })
    // 周長のフォーマット
    let perimeter
    if (perimRaw >= 1000) {
      perimeter = '約' + (perimRaw / 1000).toFixed(2) + 'km'
    } else {
      perimeter = '約' + perimRaw.toFixed(1) + 'm'
    }
    // 頂点数の計算
    const coordinates = polygon.geometry.coordinates[0]
    let vertexCount = coordinates.length
    if (
      coordinates[0][0] === coordinates[vertexCount - 1][0] &&
      coordinates[0][1] === coordinates[vertexCount - 1][1]
    ) {
      vertexCount -= 1
    }
    return {
      area: area,
      perimeter: perimeter,
      vertexCount: vertexCount,
    }
  } catch (error) {
    console.error('面積・周長・頂点数の計算中にエラーが発生しました:', error)
  }
}

const legend_shinsuishin = [
  { r: 247, g: 245, b: 169, title: '0.5m未満' },
  { r: 255, g: 216, b: 192, title: '0.5～3.0m' },
  { r: 255, g: 183, b: 183, title: '3.0～5.0m' },
  { r: 255, g: 145, b: 145, title: '5.0～10.0m' },
  { r: 242, g: 133, b: 201, title: '10.0～20.0m' },
  { r: 220, g: 122, b: 220, title: '20.0m以上' },
]
const legend_shitchi = [
  {
    r: 254,
    g: 227,
    b: 200,
    title: [
      '砂礫地',
      '土地の表面が砂と小石のところ。砂や礫でできた荒地、風の運搬作用によって砂が堆積してできた砂丘も含む。',
    ],
  },
  { r: 254, g: 200, b: 200, title: ['泥地', '常にぬかるんでいて植物が存在せず、通過が困難な土地。'] },
  { r: 228, g: 172, b: 123, title: ['泥炭地', '｢泥炭地｣と記された範囲。'] },
  { r: 200, g: 200, b: 228, title: ['湿地', '概ね湿潤で葦(あし)などの植物が生えるような土地のこと。'] },
  { r: 209, g: 234, b: 255, title: ['干潟・砂浜', '満潮時には、海面に没する地形。'] },
  {
    r: 147,
    g: 200,
    b: 254,
    title: [
      '河川、湖沼、海面',
      '河川や水路、湖沼と記された範囲及び、河口部から海上の範囲。養魚場や貯木場、小規模な農業用の池なども含む。',
    ],
  },
  {
    r: 251,
    g: 247,
    b: 176,
    title: [
      '田（水田、陸田）',
      '水田は稲や蓮などを栽培する田で四季を通じて水がある土地のこと。陸田は稲を栽培する田で冬季に水が涸れ、歩けるような土地のこと。乾田とも言う。',
    ],
  },
  {
    r: 225,
    g: 227,
    b: 118,
    title: ['深田', '膝ぐらいまでぬかる泥深い田もしくは小舟を用いて耕作するような田のこと。沼田とも言う。'],
  },
  { r: 227, g: 227, b: 200, title: ['塩田', '海水から食塩を取るために設けた砂浜の設備。'] },
  {
    r: 162,
    g: 222,
    b: 162,
    title: ['草地', '牧草を栽培する土地や｢草｣と記された範囲。ただし、山地や台地上のものは取得しない。'],
  },
  {
    r: 173,
    g: 200,
    b: 147,
    title: [
      '荒地',
      '開墾されたことがないまたは、かつては開墾されていたが長期間荒れ果てたところ。ただし、山地や台地上のものは取得しない。',
    ],
  },
  { r: 119, g: 227, b: 201, title: ['ヨシ（芦葦）', '蘆｣、｢芦｣、｢葦｣、｢葮｣、｢蓮｣と記された範囲。または芦葦記号。'] },
  { r: 173, g: 255, b: 173, title: ['茅', '｢茅｣、｢萱｣と記された範囲。'] },
  { r: 144, g: 73, b: 11, title: ['堤防', '河川の氾濫や海水の浸入を防ぐため、河岸･海岸に沿って設けた土石の構築物。'] },
]
// 高潮浸水想定区域、津波浸水想定
const legend_hightide_tsunami = [
  { r: 255, g: 255, b: 179, title: '0.3m未満' },
  { r: 247, g: 245, b: 169, title: '0.3～0.5m' },
  { r: 248, g: 225, b: 166, title: '0.5～1.0m' },
  { r: 255, g: 216, b: 192, title: '1.0～3.0m' },
  { r: 255, g: 183, b: 183, title: '3.0～5.0m' },
  { r: 255, g: 145, b: 145, title: '5.0～10.0m' },
  { r: 242, g: 133, b: 201, title: '10.0～20.0m' },
  { r: 220, g: 122, b: 188, title: '20.0m以上' },
]
// 土砂災害警戒区域
const legend_dosyasaigai = [
  {
    r: 229,
    g: 200,
    b: 49,
    title: '土石流警戒区域(指定前)<br><br>山腹が崩壊して生じた土石等又は渓流の土石等が水と一体となって流下する自然現象',
  },
  {
    r: 230,
    g: 201,
    b: 49,
    title: '土石流警戒区域(指定前)<br><br>山腹が崩壊して生じた土石等又は渓流の土石等が水と一体となって流下する自然現象',
  },
  {
    r: 229,
    g: 200,
    b: 50,
    title: '土石流警戒区域(指定前)<br><br>山腹が崩壊して生じた土石等又は渓流の土石等が水と一体となって流下する自然現象',
  },
  {
    r: 230,
    g: 200,
    b: 49,
    title: '土石流警戒区域(指定前)<br><br>山腹が崩壊して生じた土石等又は渓流の土石等が水と一体となって流下する自然現象',
  },
  {
    r: 230,
    g: 201,
    b: 49,
    title: '土石流警戒区域(指定前)<br><br>山腹が崩壊して生じた土石等又は渓流の土石等が水と一体となって流下する自然現象',
  },
  {
    r: 230,
    g: 200,
    b: 50,
    title: '土石流警戒区域(指定済)<br><br>山腹が崩壊して生じた土石等又は渓流の土石等が水と一体となって流下する自然現象',
  },
  {
    r: 165,
    g: 0,
    b: 33,
    title:
      '土石流<span style="color: red">特別</span>警戒区域(指定済)<br><br>山腹が崩壊して生じた土石等又は渓流の土石等が水と一体となって流下する自然現象',
  },
  {
    r: 169,
    g: 10,
    b: 34,
    title:
      '土石流<span style="color: red">特別</span>警戒区域(指定前)<br><br>山腹が崩壊して生じた土石等又は渓流の土石等が水と一体となって流下する自然現象',
  },
  {
    r: 169,
    g: 10,
    b: 33,
    title:
      '土石流<span style="color: red">特別</span>警戒区域(指定前)<br><br>山腹が崩壊して生じた土石等又は渓流の土石等が水と一体となって流下する自然現象',
  },
]
// 土石流危険渓流
const legend_dosekiryu = [
  {
    r: 245,
    g: 153,
    b: 101,
    title: '土石流危険渓流<br><br>土石流の発生の危険性があり、人家等に被害を与えるおそれがある渓流',
  },
]
// 急傾斜地崩壊危険箇所
const legend_kyukeisya = [
  {
    r: 224,
    g: 224,
    b: 254,
    title: '急傾斜地崩壊危険箇所<br><br>傾斜度30°かつ高さ5m以上の急傾斜地で人家等に被害を与えるおそれのある箇所',
  },
]
// 地すべり危険箇所
const legend_jisuberi = [
  {
    r: 255,
    g: 235,
    b: 223,
    title:
      '地すべり危険箇所<br><br>地すべりが発生している又は地すべりが発生するおそれがある区域のうち、人家等に被害を与えるおそれのある箇所',
  },
]
// ため池結界
const legend_tameike = [{ r: 0, g: 0, b: 255, title: 'ため池決壊による危険性' }]
// 浸水継続時間
const legend_keizoku = [
  { r: 160, g: 210, b: 255, title: '12時間未満' },
  { r: 0, g: 65, b: 255, title: '12時間～1日未満' },
  { r: 250, g: 245, b: 0, title: '1日～3日未満' },
  { r: 44, g: 54, b: 0, title: '3日～1週間未満' },
  { r: 255, g: 153, b: 0, title: '1週間～2週間未満' },
  { r: 255, g: 40, b: 0, title: '2週間以上～' },
  { r: 180, g: 0, b: 104, title: '4週間以上～' },
]
// 家屋倒壊等氾濫想定区域（氾濫流）
const legend_kaokutoukai_hanran = [
  { r: 255, g: 0, b: 0, title: '家屋倒壊等氾濫想定区域（氾濫流）' },
]
// 雪崩危険箇所
const legend_nadare = [
  { r: 255, g: 255, b: 0, title: '雪崩危険箇所' },
  { r: 254, g: 254, b: 0, title: '雪崩危険箇所' },
  { r: 254, g: 254, b: 101, title: '雪崩危険箇所' },
  { r: 255, g: 255, b: 102, title: '雪崩危険箇所' },
]
// --- 宮崎県防災ハイブリッドレイヤー凡例 ---
const legend_hyuganada_totatsu = [
  { r: 192, g: 21, b: 27, title: '～15分' },
  { r: 241, g: 74, b: 55, title: '15分～20分' },
  { r: 251, g: 124, b: 92, title: '20分～25分' },
  { r: 252, g: 179, b: 152, title: '25分～30分' },
  { r: 254, g: 227, b: 214, title: '30分～' },
]
// 震度凡例: ラスタータイル実測色に合わせたRGB（MVTスタイルと同じ色）
const legend_hyuganada_shindo = [
  { r: 211, g: 235, b: 249, title: '震度3' },
  { r: 117, g: 251, b: 253, title: '震度4' },
  { r: 0,   g: 0,   b: 245, title: '震度5弱' },
  { r: 117, g: 251, b: 76,  title: '震度5強' },
  { r: 255, g: 255, b: 84,  title: '震度6弱' },
  { r: 239, g: 135, b: 51,  title: '震度6強' },
  { r: 188, g: 39,  b: 27,  title: '震度7' },
]
const legend_nantora_shindo = [
  { r: 211, g: 235, b: 249, title: '震度3' },
  { r: 117, g: 251, b: 253, title: '震度4' },
  { r: 0,   g: 0,   b: 245, title: '震度5弱' },
  { r: 117, g: 251, b: 76,  title: '震度5強' },
  { r: 255, g: 255, b: 84,  title: '震度6弱' },
  { r: 239, g: 135, b: 51,  title: '震度6強' },
  { r: 188, g: 39,  b: 27,  title: '震度7' },
]
const legend_nantora_ekijyoka = [
  { r: 192, g: 192, b: 192, title: '液状化可能性 なし（PL=0）' },
  { r: 116, g: 249, b: 75,  title: '液状化可能性 小（PL≦5）' },
  { r: 255, g: 255, b: 84,  title: '液状化可能性 中（5＜PL≦15）' },
  { r: 234, g: 51,  b: 35,  title: '液状化可能性 大（PL＞15）' },
]
const legend_hyuganada_tsunami = [
  { r: 255, g: 255, b: 179, title: '0.3m未満' },
  { r: 247, g: 245, b: 169, title: '0.3～0.5m' },
  { r: 248, g: 225, b: 166, title: '0.5～1.0m' },
  { r: 255, g: 216, b: 192, title: '1.0～3.0m' },
  { r: 255, g: 183, b: 183, title: '3.0～5.0m' },
  { r: 255, g: 145, b: 145, title: '5.0～10.0m' },
  { r: 242, g: 133, b: 201, title: '10.0～20.0m' },
  { r: 220, g: 122, b: 220, title: '20.0m以上' },
]
// シームレス地質図
const legend_seamless = [{ r: 0, g: 0, b: 0, title: '' }]
// 土地利用細分メッシュ（ラスタ版）
const legend_tochiriyo = [
  { r: 255, g: 255, b: 0, title: '田' },
  { r: 255, g: 204, b: 153, title: 'その他の農用地' },
  { r: 0, g: 170, b: 0, title: '森林' },
  { r: 255, g: 153, b: 0, title: '荒地' },
  { r: 255, g: 0, b: 0, title: '建物用地' },
  { r: 140, g: 140, b: 140, title: '道路' },
  { r: 180, g: 180, b: 180, title: '鉄道' },
  { r: 200, g: 70, b: 15, title: 'その他の用地' },
  { r: 0, g: 0, b: 255, title: '河川地及び湖沼' },
  { r: 255, g: 255, b: 153, title: '海浜' },
  { r: 0, g: 204, b: 255, title: '海水域' },
  { r: 0, g: 255, b: 0, title: 'ゴルフ場' },
]
const legend_dojo = [
  { r: 255, g: 255, b: 253, soilID: 'A1', title: '人工物質土', explain: '人工物質土' },
  { r: 255, g: 255, b: 252, soilID: 'A1j1', title: '硬盤型人工物質土', explain: '硬盤型人工物質土' },
  { r: 255, g: 255, b: 251, soilID: 'A1j2', title: '無機質人工物質土', explain: '無機質人工物質土' },
  { r: 255, g: 255, b: 250, soilID: 'A1j3', title: '有機質人工物質土', explain: '有機質人工物質土' },
  { r: 255, g: 255, b: 249, soilID: 'A2', title: '盛土造成土', explain: '盛土造成土' },
  { r: 255, g: 255, b: 248, soilID: 'A2i1', title: '台地盛土造成土', explain: '台地盛土造成土' },
  { r: 255, g: 255, b: 247, soilID: 'A2i3', title: '低地盛土造成土', explain: '低地盛土造成土' },
  { r: 0, g: 0, b: 1, soilID: 'B1', title: '泥炭土', explain: '湿生植物の遺体が、過湿のため分解を免れ厚く堆積した土壌。' },
  { r: 2, g: 0, b: 0, soilID: 'B1e1', title: '高位泥炭土', explain: 'ミズゴケ類、ホロムイスゲ、ツルコケモモ、ミカズキグサ類、ホロムイソウを合わせた割合（面積）が最も多い泥炭物質からなる泥炭土。' },
  { r: 2, g: 2, b: 0, soilID: 'B1e1w1', title: '表層無機質高位泥炭土', explain: '表層に厚さ10cm以上の「無機質層」がある高位泥炭土。' },
  { r: 2, g: 4, b: 0, soilID: 'B1e1w2', title: '下層無機質高位泥炭土', explain: '土壌表面から75cm以内に積算して25cm以上の「無機質層」がある高位泥炭土。' },
  { r: 2, g: 6, b: 0, soilID: 'B1e1y1', title: '典型高位泥炭土', explain: '典型的な高位泥炭土。' },
  { r: 4, g: 0, b: 0, soilID: 'B1e2', title: '中間泥炭土', explain: 'ヌマガヤ、ワタスゲ、ヤチヤナギ、アカエゾマツを合わせた割合（面積）が最も多い泥炭物質からなる泥炭土。' },
  { r: 4, g: 2, b: 0, soilID: 'B1e2w1', title: '表層無機質中間泥炭土', explain: '表層に厚さ10cm以上の「無機質層」がある中間泥炭土。' },
  { r: 4, g: 4, b: 0, soilID: 'B1e2w2', title: '下層無機質中間泥炭土', explain: '土壌表面から75cm以内に積算して25cm以上の「無機質層」がある中間泥炭土。' },
  { r: 4, g: 6, b: 0, soilID: 'B1e2x2', title: '繊維質中間泥炭土', explain: '土壌表面から50cmまでの「泥炭物質」のうち、「繊維質泥炭物質」の割合が最も多い中間泥炭土。' },
  { r: 4, g: 8, b: 0, soilID: 'B1e2y1', title: '典型中間泥炭土', explain: '典型的な中間泥炭土。' },
  { r: 6, g: 0, b: 0, soilID: 'B1e3', title: '低位泥炭土', explain: '高位泥炭物質、中間泥炭物質以外の泥炭物質からなる泥炭土。' },
  { r: 6, g: 2, b: 0, soilID: 'B1e3w1', title: '表層無機質低位泥炭土', explain: '表層に厚さ10cm以上の「無機質層」がある低位泥炭土。' },
  { r: 6, g: 4, b: 0, soilID: 'B1e3w2', title: '下層無機質低位泥炭土', explain: '土壌表面から75cm以内に積算して25cm以上の「無機質層」がある低位泥炭土。' },
  { r: 6, g: 6, b: 0, soilID: 'B1e3x2', title: '繊維質低位泥炭土', explain: '土壌表面から50cmまでの「泥炭物質」のうち、「繊維質泥炭物質」の割合が最も多い低位泥炭土。' },
  { r: 6, g: 8, b: 0, soilID: 'B1e3y1', title: '典型低位泥炭土', explain: '典型的な低位泥炭土。' },
  { r: 8, g: 0, b: 0, soilID: 'B1e4', title: '腐朽質泥炭土', explain: '土壌表面から50cmまでの「泥炭物質」からなる層のうち、「腐朽質泥炭物質」の割合が最も多い泥炭土。' },
  { r: 8, g: 2, b: 0, soilID: 'B1e4w1', title: '表層無機質腐朽質泥炭土', explain: '表層に厚さ10cm以上の「無機質層」がある腐朽質泥炭土。' },
  { r: 8, g: 4, b: 0, soilID: 'B1e4w2', title: '下層無機質腐朽質泥炭土', explain: '土壌表面から75cm以内に積算して25cm以上の「無機質層」がある腐朽質泥炭土。' },
  { r: 8, g: 6, b: 0, soilID: 'B1e4x2', title: '不明', explain: '不明' },
  { r: 8, g: 8, b: 0, soilID: 'B1e4y1', title: '典型腐朽質泥炭土', explain: '典型的な腐朽質泥炭土。' },
  { r: 76, g: 0, b: 115, soilID: 'C1', title: 'ポドゾル', explain: '漂白した層と腐植または鉄・アルミニウムが集積した層の層序をもつ土壌。' },
  { r: 78, g: 0, b: 115, soilID: 'C1a3', title: '湿性ポドゾル', explain: '土壌表面から75cm以内に「地下水湿性特徴」を示す層または「グライ層」の上端が現れるポドゾル。' },
  { r: 78, g: 2, b: 115, soilID: 'C1a3p1', title: '岩盤質湿性ポドゾル', explain: '土壌表面から60cm以内に岩盤が現れる湿性ポドゾル。' },
  { r: 78, g: 4, b: 115, soilID: 'C1a3p2', title: '礫質湿性ポドゾル', explain: '土壌表面から60cm以内に礫層が現れる湿性ポドゾル。' },
  { r: 78, g: 6, b: 115, soilID: 'C1a3y1', title: '典型湿性ポドゾル', explain: '典型的な湿性ポドゾル。' },
  { r: 80, g: 0, b: 115, soilID: 'C1b1', title: '疑似グライ化ポドゾル', explain: '土壌表面から50-75cmに「疑似グライ層」の上端が現れるポドゾル。' },
  { r: 80, g: 2, b: 115, soilID: 'C1b1p1', title: '岩盤質疑似グライ化ポドゾル', explain: '土壌表面から60cm以内に岩盤が現れる疑似グライ化ポドゾル。' },
  { r: 80, g: 4, b: 115, soilID: 'C1b1p2', title: '礫質疑似グライ化ポドゾル', explain: '土壌表面から60cm以内に礫層が現れる疑似グライ化ポドゾル。' },
  { r: 80, g: 6, b: 115, soilID: 'C1b1y1', title: '典型疑似グライ化ポドゾル', explain: '典型的な疑似グライ化ポドゾル。' },
  { r: 82, g: 0, b: 115, soilID: 'C1b2', title: '表層疑似グライ化ポドゾル', explain: '土壌表面から50cm以内に「疑似グライ層」の上端が現れるポドゾル。' },
  { r: 82, g: 2, b: 115, soilID: 'C1b2p1', title: '岩盤質表層疑似グライ化ポドゾル', explain: '土壌表面から60cm以内に岩盤が現れる表層疑似グライ化ポドゾル。' },
  { r: 82, g: 4, b: 115, soilID: 'C1b2p2', title: '礫質表層疑似グライ化ポドゾル', explain: '土壌表面から60cm以内に礫層が現れる表層疑似グライ化ポドゾル。' },
  { r: 82, g: 6, b: 115, soilID: 'C1b2y1', title: '典型表層疑似グライ化ポドゾル', explain: '典型的な表層疑似グライ化ポドゾル。' },
  { r: 84, g: 0, b: 115, soilID: 'C1e5', title: '表層泥炭質ポドゾル', explain: '土壌表面から25cm以内に、積算して厚さ10cm以上の「泥炭物質」からなる層をもつポドゾル。' },
  { r: 84, g: 2, b: 115, soilID: 'C1e5p1', title: '岩盤質表層泥炭質ポドゾル', explain: '土壌表面から60cm以内に岩盤が現れる表層泥炭質ポドゾル。' },
  { r: 84, g: 4, b: 115, soilID: 'C1e5p2', title: '礫質表層泥炭質ポドゾル', explain: '土壌表面から60cm以内に礫層が現れる表層泥炭質ポドゾル。' },
  { r: 84, g: 6, b: 115, soilID: 'C1e5y1', title: '典型表層泥炭質ポドゾル', explain: '典型的な表層泥炭質ポドゾル。' },
  { r: 86, g: 0, b: 115, soilID: 'C1z1', title: '普通ポドゾル', explain: '典型的なポドゾル。' },
  { r: 86, g: 2, b: 115, soilID: 'C1z1p1', title: '岩盤質普通ポドゾル', explain: '土壌表面から60cm以内に岩盤が現れる普通ポドゾル。' },
  { r: 86, g: 4, b: 115, soilID: 'C1z1p2', title: '礫質普通ポドゾル', explain: '土壌表面から60cm以内に礫層が現れる普通ポドゾル。' },
  { r: 86, g: 6, b: 115, soilID: 'C1z1y1', title: '典型普通ポドゾル', explain: '典型的な普通ポドゾル。' },
  { r: 255, g: 163, b: 77, soilID: 'D1', title: '未熟黒ボク土', explain: '堆積した火山放出物が、ある程度の土壌化作用を受け、リン酸を固定する性質や有機物の集積（全炭素で3％以上）を示しはじめた段階の土壌。' },
  { r: 253, g: 163, b: 77, soilID: 'D1a3', title: '湿性未熟黒ボク土', explain: '土壌表面から50cm以内に、「地下水湿性特徴」を示す層または「地下水グライ層」の上端が現れる未熟黒ボク土。' },
  { r: 253, g: 165, b: 77, soilID: 'D1a3w3', title: '下層泥炭湿性未熟黒ボク土', explain: '土壌表面から75cm以内に積算して厚さ25cm以上の「泥炭物質」からなる層をもつ湿性未熟黒ボク土。' },
  { r: 253, g: 167, b: 77, soilID: 'D1a3w4', title: '下層低地湿性未熟黒ボク土', explain: '土壌表面から75cm以内に積算して厚さ25cm以上の「沖積堆積物」からなる層をもつ湿性未熟黒ボク土。' },
  { r: 253, g: 169, b: 77, soilID: 'D1a3y1', title: '典型湿性未熟黒ボク土', explain: '典型的な湿性未熟黒ボク土。' },
  { r: 251, g: 163, b: 77, soilID: 'D1h3', title: '腐植質未熟黒ボク土', explain: '表層に有機物を多く含む「腐植質表層」または「多腐植質表層」をもつ未熟黒ボク土 。' },
  { r: 251, g: 165, b: 77, soilID: 'D1h3p3', title: '盤層型腐植質未熟黒ボク土', explain: '土壌表面から60cm以内に盤層が現れる腐植質未熟黒ボク土。' },
  { r: 251, g: 167, b: 77, soilID: 'D1h3w4', title: '下層低地腐植質未熟黒ボク土', explain: '土壌表面から75cm以内に積算して厚さ25cm以上の「沖積堆積物」からなる層のをもつ腐植質未熟黒ボク土。' },
  { r: 251, g: 169, b: 77, soilID: 'D1h3w5', title: '下層黒ボク腐植質未熟黒ボク土', explain: '土壌表面から100cm以内に積算で25cm以上の「黒ボク特徴」を示す層をもつ腐植質未熟黒ボク土。' },
  { r: 251, g: 171, b: 77, soilID: 'D1h3y1', title: '典型腐植質未熟黒ボク土', explain: '典型的な腐植質未熟黒ボク土。' },
  { r: 249, g: 163, b: 77, soilID: 'D1h4', title: '埋没腐植質未熟黒ボク土', explain: '過去に表層であった層で、かつ、有機物が蓄積した「埋没腐植層」の上端が土壌表面から50cm以内に現れる未熟黒ボク土。' },
  { r: 249, g: 165, b: 77, soilID: 'D1h4p3', title: '盤層型埋没腐植質未熟黒ボク土', explain: '土壌表面から60cm以内に盤層が現れる埋没腐植質未熟黒ボク土。' },
  { r: 249, g: 167, b: 77, soilID: 'D1h4y1', title: '典型埋没腐植質未熟黒ボク土', explain: '典型的な埋没腐植質未熟黒ボク土。' },
  { r: 247, g: 163, b: 77, soilID: 'D1z1', title: '普通未熟黒ボク土', explain: '典型的な未熟黒ボク土。' },
  { r: 247, g: 165, b: 77, soilID: 'D1z1p3', title: '盤層型普通未熟黒ボク土', explain: '土壌表面から60cm以内に盤層が現れる普通未熟黒ボク土。' },
  { r: 247, g: 167, b: 77, soilID: 'D1z1w4', title: '下層低地普通未熟黒ボク土', explain: '土壌表面から75cm以内に積算して厚さ25cm以上の「沖積堆積物」からなる層のをもつ普通未熟黒ボク土。' },
  { r: 247, g: 169, b: 77, soilID: 'D1z1w5', title: '下層黒ボク普通未熟黒ボク土', explain: '土壌表面から100cm以内に積算で25cm以上の「黒ボク特徴」を示す層をもつ普通未熟黒ボク土。' },
  { r: 247, g: 171, b: 77, soilID: 'D1z1y1', title: '典型普通未熟黒ボク土', explain: '典型的な普通未熟黒ボク土。' },
  { r: 255, g: 160, b: 122, soilID: 'D2', title: 'グライ黒ボク土', explain: '地下水位の高い場所にある黒ボク土で、一年を通じて水で飽和されている層が50cm以内に出てくる土壌。' },
  { r: 253, g: 160, b: 122, soilID: 'D2e6', title: '泥炭質グライ黒ボク土　', explain: '土壌表面から100cm以内に積算して厚さ 25cm以上の湿生植物の遺体からなる層をもつグライ黒ボク土。' },
  { r: 253, g: 158, b: 122, soilID: 'D2e6v3', title: '多腐植質泥炭質グライ黒ボク土', explain: '「多腐植質表層」をもつ泥炭質グライ黒ボク土。' },
  { r: 253, g: 156, b: 122, soilID: 'D2e6y1', title: '典型泥炭質グライ黒ボク土', explain: '典型的な泥炭質グライ黒ボク土。' },
  { r: 251, g: 160, b: 122, soilID: 'D2h1', title: '厚層グライ黒ボク土　', explain: '表層に有機物を多く含み、かつ、層の厚さが50cmを超える「腐植質表層」または「多腐植質表層」をもつグライ黒ボク土。' },
  { r: 251, g: 158, b: 122, soilID: 'D2h1x4', title: '非アロフェン質厚層グライ黒ボク土', explain: '土壌表面から50cm以内に積算して厚さ25cm以上の「非アロフェン質黒ボク特徴」を示す層をもつ厚層グライ黒ボク土。' },
  { r: 251, g: 156, b: 122, soilID: 'D2h1v3', title: '多腐植質厚層グライ黒ボク土', explain: '「多腐植質表層」をもつ厚層グライ黒ボク土。' },
  { r: 251, g: 154, b: 122, soilID: 'D2h1v4', title: '腐植質厚層グライ黒ボク土', explain: '「腐植質表層」をもつ厚層グライ黒ボク土。' },
  { r: 249, g: 160, b: 122, soilID: 'D2z1', title: '普通グライ黒ボク土　', explain: '典型的なグライ黒ボク土。' },
  { r: 249, g: 158, b: 122, soilID: 'D2z1p1', title: '岩盤質普通グライ黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる普通グライ黒ボク土。' },
  { r: 249, g: 156, b: 122, soilID: 'D2z1p2', title: '礫質普通グライ黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる普通グライ黒ボク土。' },
  { r: 249, g: 154, b: 122, soilID: 'D2z1v3', title: '多腐植質普通グライ黒ボク土', explain: '「多腐植質表層」をもつ普通グライ黒ボク土。' },
  { r: 249, g: 152, b: 122, soilID: 'D2z1y1', title: '典型普通グライ黒ボク土', explain: '典型的な普通グライ黒ボク土。' },
  { r: 205, g: 78, b: 55, soilID: 'D3', title: '多湿黒ボク土', explain: '地下水の影響で湿っている黒ボク土。' },
  { r: 207, g: 78, b: 55, soilID: 'D3d1', title: '下層台地多湿黒ボク土　', explain: '土壌表面から50cm以内に黒ボク土とは異なる台地土壌の上端が現れる多湿黒ボク土。' },
  { r: 207, g: 80, b: 55, soilID: 'D3d1v3', title: '多腐植質下層台地多湿黒ボク土', explain: '「多腐植質表層」をもつ下層台地多湿黒ボク土。' },
  { r: 207, g: 82, b: 55, soilID: 'D3d1y1', title: '典型下層台地多湿黒ボク土', explain: '典型的な下層台地多湿黒ボク土。' },
  { r: 209, g: 78, b: 55, soilID: 'D3d2', title: '下層低地多湿黒ボク土　', explain: '土壌表面から75cm以内に積算して25cm以上の河川氾濫堆積物（沖積堆積物）からなる層をもつ多湿黒ボク土。' },
  { r: 209, g: 80, b: 55, soilID: 'D3d2p2', title: '礫質下層低地多湿黒ボク土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる下層低地多湿黒ボク土。' },
  { r: 209, g: 82, b: 55, soilID: 'D3d2v3', title: '多腐植質下層低地多湿黒ボク土', explain: '「多腐植質表層」をもつ下層低地多湿黒ボク土。' },
  { r: 209, g: 84, b: 55, soilID: 'D3d2y1', title: '典型下層低地多湿黒ボク土', explain: '典型的な下層低地多湿黒ボク土。' },
  { r: 211, g: 78, b: 55, soilID: 'D3e6', title: '泥炭質多湿黒ボク土　', explain: '土壌表面から100cm以内に積算して厚さ 25cm以上の湿生植物の遺体からなる層をもつ多湿黒ボク土。' },
  { r: 211, g: 80, b: 55, soilID: 'D3e6v3', title: '多腐植質泥炭質多湿黒ボク土', explain: '「多腐植質表層」をもつ泥炭質多湿黒ボク土。' },
  { r: 211, g: 82, b: 55, soilID: 'D3e6y1', title: '典型泥炭質多湿黒ボク土', explain: '典型的な泥炭質多湿黒ボク土。' },
  { r: 213, g: 78, b: 55, soilID: 'D3h1', title: '厚層多湿黒ボク土　', explain: '表層に有機物を多く含み、かつ、層の厚さが50cmを超える「腐植質表層」または「多腐植質表層」をもつ多湿黒ボク土。' },
  { r: 213, g: 80, b: 55, soilID: 'D3h1p1', title: '岩盤質厚層多湿黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる厚層多湿黒ボク土。' },
  { r: 213, g: 82, b: 55, soilID: 'D3h1p2', title: '礫質厚層多湿黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる厚層多湿黒ボク土。' },
  { r: 213, g: 84, b: 55, soilID: 'D3h1x4', title: '非アロフェン質厚層多湿黒ボク土', explain: '土壌表面から50cm以内に積算して厚さ25cm以上の「非アロフェン質黒ボク特徴」を示す層をもつ厚層多湿黒ボク土。' },
  { r: 213, g: 86, b: 55, soilID: 'D3h1v3', title: '多腐植質厚層多湿黒ボク土', explain: '「多腐植質表層」をもつ厚層多湿黒ボク土。' },
  { r: 213, g: 88, b: 55, soilID: 'D3h1v4', title: '腐植質厚層多湿黒ボク土', explain: '「腐植質表層」をもつ厚層多湿黒ボク土。' },
  { r: 215, g: 78, b: 55, soilID: 'D3z1', title: '普通多湿黒ボク土　', explain: '典型的な多湿黒ボク土。' },
  { r: 215, g: 80, b: 55, soilID: 'D3z1p1', title: '岩盤質普通多湿黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる普通多湿黒ボク土。' },
  { r: 215, g: 82, b: 55, soilID: 'D3z1p2', title: '礫質普通多湿黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる普通多湿黒ボク土。' },
  { r: 215, g: 84, b: 55, soilID: 'D3z1v3', title: '多腐植質普通多湿黒ボク土', explain: '「多腐植質表層」をもつ普通多湿黒ボク土。' },
  { r: 215, g: 86, b: 55, soilID: 'D3z1x4', title: '非アロフェン質普通多湿黒ボク土', explain: '土壌表面から50cm以内に積算して厚さ25cm以上の「非アロフェン質黒ボク特徴」を示す層をもつ普通多湿黒ボク土。' },
  { r: 215, g: 88, b: 55, soilID: 'D3z1y1', title: '典型普通多湿黒ボク土', explain: '典型的な普通多湿黒ボク土。' },
  { r: 206, g: 135, b: 64, soilID: 'D4', title: '褐色黒ボク土', explain: '森林下に出来る黒ボク土で、有機物含量は高いが、黒色味が弱く褐色の表層を持つ。' },
  { r: 208, g: 135, b: 64, soilID: 'D4h1', title: '厚層褐色黒ボク土', explain: '表層60cm以内に50cm以上の褐色で有機物含量が高い「褐色多腐植質黒ボク表層」をもつ褐色黒ボク土。' },
  { r: 208, g: 137, b: 64, soilID: 'D4h1p1', title: '岩盤質厚層褐色黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる厚層褐色黒ボク土。' },
  { r: 208, g: 139, b: 64, soilID: 'D4h1p2', title: '礫質厚層褐色黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる厚層褐色黒ボク土。' },
  { r: 208, g: 141, b: 64, soilID: 'D4h1x4', title: '非アロフェン質厚層褐色黒ボク土', explain: '土壌表面から50cm以内に積算して厚さ25cm以上の「非アロフェン質黒ボク特徴」を示す層をもつ厚層褐色黒ボク土。' },
  { r: 208, g: 143, b: 64, soilID: 'D4h1y1', title: '典型厚層褐色黒ボク土', explain: '典型的な厚層褐色黒ボク土。' },
  { r: 210, g: 135, b: 64, soilID: 'D4h4', title: '埋没腐植質褐色黒ボク土', explain: '過去に表層であった層で、かつ、有機物が蓄積した「埋没腐植層」の上端が土壌表面から50cm以内に現れる褐色黒ボク土。' },
  { r: 210, g: 137, b: 64, soilID: 'D4h4p1', title: '岩盤質埋没腐植質褐色黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる埋没腐植質褐色黒ボク土。' },
  { r: 210, g: 139, b: 64, soilID: 'D4h4p2', title: '礫質埋没腐植質褐色黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる埋没腐植質褐色黒ボク土。' },
  { r: 210, g: 141, b: 64, soilID: 'D4h4x4', title: '非アロフェン質埋没腐植質褐色黒ボク土', explain: '土壌表面から50cm以内に積算して厚さ25cm以上の「非アロフェン質黒ボク特徴」を示す層をもつ埋没腐植質褐色黒ボク土。' },
  { r: 210, g: 143, b: 64, soilID: 'D4h4y1', title: '典型埋没腐植質褐色黒ボク土', explain: '典型的な埋没腐植質褐色黒ボク土。' },
  { r: 212, g: 135, b: 64, soilID: 'D4z1', title: '普通褐色黒ボク土', explain: '典型的な褐色黒ボク土。' },
  { r: 212, g: 137, b: 64, soilID: 'D4z1p1', title: '岩盤質普通褐色黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる普通褐色黒ボク土。' },
  { r: 212, g: 139, b: 64, soilID: 'D4z1p2', title: '礫質普通褐色黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる普通褐色黒ボク土。' },
  { r: 212, g: 141, b: 64, soilID: 'D4z1x4', title: '非アロフェン質普通褐色黒ボク土', explain: '土壌表面から50cm以内に積算して厚さ25cm以上の「非アロフェン質黒ボク特徴」を示す層をもつ普通褐色黒ボク土。' },
  { r: 212, g: 143, b: 64, soilID: 'D4z1y1', title: '典型普通褐色黒ボク土', explain: '典型的な普通褐色黒ボク土。' },
  { r: 137, g: 90, b: 68, soilID: 'D5', title: '非アロフェン質黒ボク土', explain: '結晶性粘土を含む黒ボク土。' },
  { r: 139, g: 90, b: 68, soilID: 'D5c1', title: '水田化非アロフェン質黒ボク土', explain: '水田利用による湛水期間中に代かき層から還元溶脱した鉄が下部の酸化的な層で酸化沈殿して形成された「水田鉄集積層」をもつ非アロフェン質黒ボク土。' },
  { r: 139, g: 92, b: 68, soilID: 'D5c1p1', title: '岩盤質水田化非アロフェン質黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる水田化非アロフェン質黒ボク土。' },
  { r: 139, g: 94, b: 68, soilID: 'D5c1p2', title: '礫質水田化非アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる水田化非アロフェン質黒ボク土。' },
  { r: 139, g: 96, b: 68, soilID: 'D5c1w4', title: '下層低地水田化非アロフェン質黒ボク土', explain: '土壌表面から75cm以内に積算して厚さ25cm以上の「沖積堆積物」からなる層をもつ水田化非アロフェン質黒ボク土。' },
  { r: 139, g: 98, b: 68, soilID: 'D5c1v2', title: '厚層水田化非アロフェン質黒ボク土', explain: '厚さ50cm以上の「腐植質表層」または「多腐植質表層」をもつ水田化非アロフェン質黒ボク土。' },
  { r: 139, g: 100, b: 68, soilID: 'D5c1y1', title: '典型水田化非アロフェン質黒ボク土', explain: '典型的な水田化非アロフェン質黒ボク土。' },
  { r: 141, g: 90, b: 68, soilID: 'D5h1', title: '厚層非アロフェン質黒ボク土', explain: '表層に有機物を多く含み、かつ、層の厚さが50cmを超える「腐植質表層」または「多腐植質表層」をもつ非アロフェン質黒ボク土。' },
  { r: 141, g: 92, b: 68, soilID: 'D5h1v3', title: '多腐植質厚層非アロフェン質黒ボク土', explain: '「多腐植質表層」をもつ厚層非アロフェン質黒ボク土。' },
  { r: 141, g: 94, b: 68, soilID: 'D5h1v4', title: '腐植質厚層非アロフェン質黒ボク土', explain: '「腐植質表層」をもつ厚層非アロフェン質黒ボク土。' },
  { r: 143, g: 90, b: 68, soilID: 'D5h4', title: '埋没腐植質非アロフェン質黒ボク土', explain: '過去に表層であった層で、かつ、有機物が蓄積した「埋没腐植層」の上端が土壌表面から50cm以内に現れる非アロフェン質黒ボク土。' },
  { r: 143, g: 92, b: 68, soilID: 'D5h4p1', title: '岩盤質埋没腐植質非アロフェン質黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる埋没腐植質非アロフェン質黒ボク土。' },
  { r: 143, g: 94, b: 68, soilID: 'D5h4p2', title: '礫質埋没腐植質非アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる埋没腐植質非アロフェン質黒ボク土。' },
  { r: 143, g: 96, b: 68, soilID: 'D5h4y1', title: '典型埋没腐植質非アロフェン質黒ボク土', explain: '典型的な埋没腐植質非アロフェン質黒ボク土。' },
  { r: 145, g: 90, b: 68, soilID: 'D5z1', title: '普通非アロフェン質黒ボク土', explain: '典型的な非アロフェン質黒ボク土。' },
  { r: 145, g: 92, b: 68, soilID: 'D5z1p1', title: '岩盤質普通非アロフェン質黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる普通非アロフェン質黒ボク土。' },
  { r: 145, g: 94, b: 68, soilID: 'D5z1p2', title: '礫質普通非アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる普通非アロフェン質黒ボク土。' },
  { r: 145, g: 96, b: 68, soilID: 'D5z1w4', title: '下層低地普通非アロフェン質黒ボク土', explain: '土壌表面から75cm以内に積算して厚さ25cm以上の「沖積堆積物」からなる層をもつ普通非アロフェン質黒ボク土。' },
  { r: 145, g: 98, b: 68, soilID: 'D5z1v1', title: '淡色普通非アロフェン質黒ボク土', explain: '「腐植質表層」または「多腐植質表層」をもたない普通非アロフェン質黒ボク土。' },
  { r: 145, g: 100, b: 68, soilID: 'D5z1v3', title: '多腐植質普通非アロフェン質黒ボク土', explain: '「多腐植質表層」をもつ普通非アロフェン質黒ボク土。' },
  { r: 145, g: 102, b: 68, soilID: 'D5z1v4', title: '腐植質普通非アロフェン質黒ボク土', explain: '「腐植質表層」をもつ普通非アロフェン質黒ボク土。' },
  { r: 138, g: 69, b: 0, soilID: 'D6', title: 'アロフェン質黒ボク土', explain: '非晶質粘土鉱物（アロフェン）主体の黒ボク土。' },
  { r: 136, g: 69, b: 0, soilID: 'D6c1', title: '水田化アロフェン質黒ボク土', explain: '水田利用による湛水期間中に代かき層から還元溶脱した鉄が下部の酸化的な層で酸化沈殿して形成された「水田鉄集積層」をもつアロフェン質黒ボク土。' },
  { r: 136, g: 71, b: 0, soilID: 'D6c1p1', title: '岩盤質水田化アロフェン質黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる水田化アロフェン質黒ボク土。' },
  { r: 136, g: 73, b: 0, soilID: 'D6c1p2', title: '礫質水田化アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる水田化アロフェン質黒ボク土。' },
  { r: 136, g: 75, b: 0, soilID: 'D6c1w4', title: '下層低地水田化アロフェン質黒ボク土', explain: '土壌表面から75cm以内に積算して厚さ25cm以上の「沖積堆積物」からなる層をもつ水田化アロフェン質黒ボク土。' },
  { r: 136, g: 77, b: 0, soilID: 'D6c1v1', title: '淡色水田化アロフェン質黒ボク土', explain: '「腐植質表層」または「多腐植質表層」をもたない水田化アロフェン質黒ボク土。' },
  { r: 136, g: 79, b: 0, soilID: 'D6c1v2', title: '厚層水田化アロフェン質黒ボク土', explain: '厚さ50cm以上の「腐植質表層」または「多腐植質表層」をもつ水田化アロフェン質黒ボク土。' },
  { r: 136, g: 81, b: 0, soilID: 'D6c1v3', title: '多腐植質水田化アロフェン質黒ボク土', explain: '「多腐植質表層」をもつ水田化アロフェン質黒ボク土。' },
  { r: 136, g: 83, b: 0, soilID: 'D6c1v4', title: '腐植質水田化アロフェン質黒ボク土', explain: '「腐植質表層」をもつ水田化アロフェン質黒ボク土。' },
  { r: 134, g: 69, b: 0, soilID: 'D6d1', title: '下層台地アロフェン質黒ボク土', explain: '土壌表面から50cm以内に黒ボク土とは異なる台地土壌の上端が現れるアロフェン質黒ボク土。' },
  { r: 134, g: 71, b: 0, soilID: 'D6d1p1', title: '岩盤質下層台地アロフェン質黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる下層台地アロフェン質黒ボク土。' },
  { r: 134, g: 73, b: 0, soilID: 'D6d1p2', title: '礫質下層台地アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる下層台地アロフェン質黒ボク土。' },
  { r: 134, g: 75, b: 0, soilID: 'D6d1v1', title: '淡色下層台地アロフェン質黒ボク土', explain: '「腐植質表層」または「多腐植質表層」をもたない下層台地アロフェン質黒ボク土。' },
  { r: 134, g: 77, b: 0, soilID: 'D6d1v3', title: '多腐植質下層台地アロフェン質黒ボク土', explain: '「多腐植質表層」をもつ下層台地アロフェン質黒ボク土。' },
  { r: 134, g: 79, b: 0, soilID: 'D6d1v4', title: '腐植質下層台地アロフェン質黒ボク土', explain: '「腐植質表層」をもつ下層台地アロフェン質黒ボク土。' },
  { r: 132, g: 69, b: 0, soilID: 'D6d2', title: '下層低地アロフェン質黒ボク土', explain: '土壌表面から75cm以内に積算して25cm以上の河川氾濫堆積物（沖積堆積物）からなる層をもつアロフェン質黒ボク土。' },
  { r: 132, g: 71, b: 0, soilID: 'D6d2p2', title: '礫質下層低地アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる下層低地アロフェン質黒ボク土。' },
  { r: 132, g: 73, b: 0, soilID: 'D6d2v1', title: '淡色下層低地アロフェン質黒ボク土', explain: '「腐植質表層」または「多腐植質表層」をもたない下層低地アロフェン質黒ボク土。' },
  { r: 132, g: 75, b: 0, soilID: 'D6d2v3', title: '多腐植質下層低地アロフェン質黒ボク土', explain: '「多腐植質表層」をもつ下層低地アロフェン質黒ボク土。' },
  { r: 132, g: 77, b: 0, soilID: 'D6d2v4', title: '腐植質下層低地アロフェン質黒ボク土', explain: '「腐植質表層」をもつ下層低地アロフェン質黒ボク土。' },
  { r: 130, g: 69, b: 0, soilID: 'D6h1', title: '厚層アロフェン質黒ボク土', explain: '表層に有機物を多く含み、かつ、層の厚さが50cmを超える「腐植質表層」または「多腐植質表層」をもつアロフェン質黒ボク土 。' },
  { r: 130, g: 71, b: 0, soilID: 'D6h1p1', title: '岩盤質厚層アロフェン質黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる厚層アロフェン質黒ボク土。' },
  { r: 130, g: 73, b: 0, soilID: 'D6h1p2', title: '礫質厚層アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる厚層アロフェン質黒ボク土。' },
  { r: 130, g: 75, b: 0, soilID: 'D6h1p3', title: '盤層型厚層アロフェン質黒ボク土', explain: '土壌表面から60cm以内に盤層が現れる厚層アロフェン質黒ボク土。' },
  { r: 130, g: 77, b: 0, soilID: 'D6h1v3', title: '多腐植質厚層アロフェン質黒ボク土', explain: '「多腐植質表層」をもつ厚層アロフェン質黒ボク土。' },
  { r: 130, g: 79, b: 0, soilID: 'D6h1v4', title: '腐植質厚層アロフェン質黒ボク土', explain: '「腐植質表層」をもつ厚層アロフェン質黒ボク土。' },
  { r: 128, g: 69, b: 0, soilID: 'D6h2', title: '淡色アロフェン質黒ボク土', explain: '表層の有機物含量は低く、土色は淡いアロフェン質黒ボク土。' },
  { r: 128, g: 71, b: 0, soilID: 'D6h2p1', title: '岩盤質淡色アロフェン質黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる淡色アロフェン質黒ボク土。' },
  { r: 128, g: 73, b: 0, soilID: 'D6h2p2', title: '礫質淡色アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる淡色アロフェン質黒ボク土。' },
  { r: 128, g: 75, b: 0, soilID: 'D6h2p3', title: '盤層型淡色アロフェン質黒ボク土', explain: '土壌表面から60cm以内に盤層が現れる淡色アロフェン質黒ボク土。' },
  { r: 128, g: 77, b: 0, soilID: 'D6h2v5', title: '埋没腐植質淡色アロフェン質黒ボク土', explain: '土壌表面から50cm以内に「埋没腐植層」の上端が現れる淡色アロフェン質黒ボク土。' },
  { r: 128, g: 79, b: 0, soilID: 'D6h2y1', title: '典型淡色アロフェン質黒ボク土', explain: '典型的な淡色アロフェン質黒ボク土。' },
  { r: 126, g: 69, b: 0, soilID: 'D6h4', title: '埋没腐植質アロフェン質黒ボク土', explain: '過去に表層であった層で、かつ、有機物が蓄積した「埋没腐植層」の上端が土壌表面から50cm以内に現れるアロフェン質黒ボク土。' },
  { r: 126, g: 71, b: 0, soilID: 'D6h4p1', title: '岩盤質埋没腐植質アロフェン質黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる埋没腐植質アロフェン質黒ボク土。' },
  { r: 126, g: 73, b: 0, soilID: 'D6h4p2', title: '礫質埋没腐植質アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる埋没腐植質アロフェン質黒ボク土。' },
  { r: 126, g: 75, b: 0, soilID: 'D6h4p3', title: '盤層型埋没腐植質アロフェン質黒ボク土', explain: '土壌表面から60cm以内に盤層が現れる埋没腐植質アロフェン質黒ボク土。' },
  { r: 126, g: 77, b: 0, soilID: 'D6h4y1', title: '典型埋没腐植質アロフェン質黒ボク土', explain: '典型的な埋没腐植質アロフェン質黒ボク土。' },
  { r: 124, g: 69, b: 0, soilID: 'D6z1', title: '普通アロフェン質黒ボク土', explain: '典型的なアロフェン質黒ボク土。' },
  { r: 124, g: 71, b: 0, soilID: 'D6z1p1', title: '岩盤質普通アロフェン質黒ボク土', explain: '土壌表面から60cm以内に岩盤が現れる普通アロフェン質黒ボク土。' },
  { r: 124, g: 73, b: 0, soilID: 'D6z1p2', title: '礫質普通アロフェン質黒ボク土', explain: '土壌表面から60cm以内に礫層が現れる普通アロフェン質黒ボク土。' },
  { r: 124, g: 75, b: 0, soilID: 'D6z1p3', title: '盤層型普通アロフェン質黒ボク土', explain: '土壌表面から60cm以内に盤層が現れる普通アロフェン質黒ボク土。' },
  { r: 124, g: 77, b: 0, soilID: 'D6z1v3', title: '多腐植質普通アロフェン質黒ボク土', explain: '「多腐植質表層」をもつ普通アロフェン質黒ボク土。' },
  { r: 124, g: 79, b: 0, soilID: 'D6z1v4', title: '腐植質普通アロフェン質黒ボク土', explain: '「腐植質表層」をもつ普通アロフェン質黒ボク土。' },
  { r: 115, g: 0, b: 76, soilID: 'E1', title: '石灰性暗赤色土', explain: '石灰岩など石灰質の母材から出来た土壌。' },
  { r: 117, g: 0, b: 76, soilID: 'E1j4', title: '粘土集積石灰性暗赤色土', explain: '「粘土集積層」をもつ石灰性暗赤色土。' },
  { r: 117, g: 2, b: 76, soilID: 'E1j4p1', title: '岩盤質粘土集積石灰性暗赤色土', explain: '土壌表面から60cm以内に岩盤が現れる粘土集積石灰性暗赤色土。' },
  { r: 117, g: 4, b: 76, soilID: 'E1j4p2', title: '礫質粘土集積石灰性暗赤色土', explain: '土壌表面から60cm以内に礫層が現れる粘土集積石灰性暗赤色土。' },
  { r: 117, g: 6, b: 76, soilID: 'E1j4y1', title: '典型粘土集積石灰性暗赤色土', explain: '典型的な粘土集積石灰性暗赤色土。' },
  { r: 119, g: 0, b: 76, soilID: 'E1z1', title: '普通石灰性暗赤色土', explain: '典型的な石灰性暗赤色土。' },
  { r: 119, g: 2, b: 76, soilID: 'E1z1p1', title: '岩盤質普通石灰性暗赤色土', explain: '土壌表面から60cm以内に岩盤が現れる普通石灰性暗赤色土。' },
  { r: 119, g: 4, b: 76, soilID: 'E1z1p2', title: '礫質普通石灰性暗赤色土', explain: '土壌表面から60cm以内に礫層が現れる普通石灰性暗赤色土。' },
  { r: 119, g: 6, b: 76, soilID: 'E1z1y1', title: '典型普通石灰性暗赤色土', explain: '典型的な普通石灰性暗赤色土。' },
  { r: 168, g: 0, b: 132, soilID: 'E2', title: '酸性暗赤色土', explain: '暗赤色の次表層の一部で酸性（pHが5.5未満）を示す土壌。' },
  { r: 170, g: 0, b: 132, soilID: 'E2j4', title: '粘土集積酸性暗赤色土', explain: '「粘土集積層」をもつ酸性暗赤色土。' },
  { r: 170, g: 2, b: 132, soilID: 'E2j4p1', title: '岩盤質粘土集積酸性暗赤色土', explain: '土壌表面から60cm以内に岩盤が現れる粘土集積酸性暗赤色土。' },
  { r: 170, g: 4, b: 132, soilID: 'E2j4p2', title: '礫質粘土集積酸性暗赤色土', explain: '土壌表面から60cm以内に礫層が現れる粘土集積酸性暗赤色土。' },
  { r: 170, g: 6, b: 132, soilID: 'E2j4y1', title: '典型粘土集積酸性暗赤色土', explain: '典型的な粘土集積酸性暗赤色土。' },
  { r: 172, g: 0, b: 132, soilID: 'E2z1', title: '普通酸性暗赤色土', explain: '典型的な酸性暗赤色土。' },
  { r: 172, g: 2, b: 132, soilID: 'E2z1p1', title: '岩盤質普通酸性暗赤色土', explain: '土壌表面から60cm以内に岩盤が現れる普通酸性暗赤色土。' },
  { r: 172, g: 4, b: 132, soilID: 'E2z1p2', title: '礫質普通酸性暗赤色土', explain: '土壌表面から60cm以内に礫層が現れる普通酸性暗赤色土。' },
  { r: 172, g: 6, b: 132, soilID: 'E2z1y1', title: '典型普通酸性暗赤色土', explain: '典型的な普通酸性暗赤色土。' },
  { r: 230, g: 0, b: 169, soilID: 'E3', title: '塩基性暗赤色土', explain: '主に超塩基性岩（かんらん岩、蛇紋岩）を母材とし、pHが高い土壌。' },
  { r: 232, g: 0, b: 169, soilID: 'E3j4', title: '粘土集積塩基性暗赤色土', explain: '「粘土集積層」をもつ塩基性暗赤色土。' },
  { r: 232, g: 2, b: 169, soilID: 'E3j4p1', title: '岩盤質粘土集積塩基性暗赤色土', explain: '土壌表面から60cm以内に岩盤が現れる粘土集積塩基性暗赤色土。' },
  { r: 232, g: 4, b: 169, soilID: 'E3j4p2', title: '礫質粘土集積塩基性暗赤色土', explain: '土壌表面から60cm以内に礫層が現れる粘土集積塩基性暗赤色土。' },
  { r: 232, g: 6, b: 169, soilID: 'E3j4y1', title: '典型粘土集積塩基性暗赤色土', explain: '典型的な粘土集積塩基性暗赤色土。' },
  { r: 234, g: 0, b: 169, soilID: 'E3z1', title: '普通塩基性暗赤色土', explain: '典型的な塩基性暗赤色土。' },
  { r: 234, g: 2, b: 169, soilID: 'E3z1p1', title: '岩盤質普通塩基性暗赤色土', explain: '土壌表面から60cm以内に岩盤が現れる普通塩基性暗赤色土。' },
  { r: 234, g: 4, b: 169, soilID: 'E3z1p2', title: '礫質普通塩基性暗赤色土', explain: '土壌表面から60cm以内に礫層が現れる普通塩基性暗赤色土。' },
  { r: 234, g: 6, b: 169, soilID: 'E3z1y1', title: '典型普通塩基性暗赤色土', explain: '典型的な普通塩基性暗赤色土。' },
  { r: 0, g: 112, b: 255, soilID: 'F1', title: '低地水田土', explain: '元々地下水の影響が無いか弱いところに水田を作ったため、鉄集積層が出来たり、灰色化した特徴的な断面をもつようになった低地の土壌。' },
  { r: 2, g: 112, b: 255, soilID: 'F1a3', title: '湿性低地水田土', explain: '土壌表面から75cm以内にグライ層よりも酸化的な「地下水湿性特徴」が現れる低地水田土。' },
  { r: 2, g: 114, b: 255, soilID: 'F1a3p2', title: '礫質湿性低地水田土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる湿性低地水田土。' },
  { r: 2, g: 116, b: 255, soilID: 'F1a3t1', title: '細粒質湿性低地水田土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である湿性低地水田土。' },
  { r: 2, g: 118, b: 255, soilID: 'F1a3t2', title: '中粒質湿性低地水田土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である湿性低地水田土。' },
  { r: 2, g: 120, b: 255, soilID: 'F1a3t4', title: '粗粒質湿性低地水田土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である湿性低地水田土。' },
  { r: 4, g: 112, b: 255, soilID: 'F1a5', title: '表層グライ化低地水田土', explain: '水稲耕作下で灌漑水によって生成し、落水後も維持され作土から下方へ発達しているグライ層がある低地水田土。 ' },
  { r: 4, g: 114, b: 255, soilID: 'F1a5p2', title: '礫質表層グライ化低地水田土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる表層グライ化低地水田土。' },
  { r: 4, g: 116, b: 255, soilID: 'F1a5t1', title: '細粒質表層グライ化低地水田土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である表層グライ化低地水田土。' },
  { r: 4, g: 118, b: 255, soilID: 'F1a5t3', title: '中粗粒質表層グライ化低地水田土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である表層グライ化低地水田土。' },
  { r: 6, g: 112, b: 255, soilID: 'F1b3', title: '下層褐色低地水田土', explain: '土壌表面から75cm以内に厚さ15cm以上の黄褐色の層（乾いた条件下で生成）の上端が現れる低地水田土。' },
  { r: 6, g: 114, b: 255, soilID: 'F1b3p2', title: '礫質下層褐色低地水田土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる下層褐色低地水田土。' },
  { r: 6, g: 116, b: 255, soilID: 'F1b3t1', title: '細粒質下層褐色低地水田土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である下層褐色低地水田土。' },
  { r: 6, g: 118, b: 255, soilID: 'F1b3t2', title: '中粒質下層褐色低地水田土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である下層褐色低地水田土。' },
  { r: 6, g: 120, b: 255, soilID: 'F1b3t4', title: '粗粒質下層褐色低地水田土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である下層褐色低地水田土。' },
  { r: 8, g: 112, b: 255, soilID: 'F1j6', title: '漂白化低地水田土', explain: '水田の作土層のうち、粘土、鉄の移動により漂白化を受けた低地水田土。' },
  { r: 8, g: 114, b: 255, soilID: 'F1j6p2', title: '礫質漂白化低地水田土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる漂白化低地水田土。' },
  { r: 8, g: 116, b: 255, soilID: 'F1j6y1', title: '典型漂白化低地水田土', explain: '典型的な漂白化低地水田土。' },
  { r: 10, g: 112, b: 255, soilID: 'F1z1', title: '普通低地水田土', explain: '典型的な低地水田土。' },
  { r: 10, g: 114, b: 255, soilID: 'F1z1p2', title: '礫質普通低地水田土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる普通低地水田土。' },
  { r: 10, g: 116, b: 255, soilID: 'F1z1t1', title: '細粒質普通低地水田土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である普通低地水田土。' },
  { r: 10, g: 118, b: 255, soilID: 'F1z1t2', title: '中粒質普通低地水田土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である普通低地水田土。' },
  { r: 10, g: 120, b: 255, soilID: 'F1z1t4', title: '粗粒質普通低地水田土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である普通低地水田土。' },
  { r: 3, g: 15, b: 255, soilID: 'F2', title: 'グライ低地土', explain: '低地土大群の中で、最も地下水位が高く、年間を通じてほとんど水に飽和されたグライ層が50cm以内に出てくる土壌。' },
  { r: 5, g: 15, b: 255, soilID: 'F2a1', title: '還元型グライ低地土', explain: '土壌表面から25cm以深のグライ層に「含む」以上の斑鉄がないグライ低地土。' },
  { r: 5, g: 17, b: 255, soilID: 'F2a1p2', title: '礫質還元型グライ低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる還元型グライ低地土。' },
  { r: 5, g: 19, b: 255, soilID: 'F2a1t1', title: '細粒質還元型グライ低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である還元型グライ低地土。' },
  { r: 5, g: 21, b: 255, soilID: 'F2a1t2', title: '中粒質還元型グライ低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である還元型グライ低地土。' },
  { r: 5, g: 23, b: 255, soilID: 'F2a1t4', title: '粗粒質還元型グライ低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である還元型グライ低地土。' },
  { r: 7, g: 15, b: 255, soilID: 'F2a2', title: '斑鉄型グライ低地土', explain: '土壌表面から25cm以深のグライ層に「含む」以上の斑鉄があるグライ低地土。還元型グライ低地土よりもやや酸化的である。' },
  { r: 7, g: 17, b: 255, soilID: 'F2a2p2', title: '礫質斑鉄型グライ低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる斑鉄型グライ低地土。' },
  { r: 7, g: 19, b: 255, soilID: 'F2a2t1', title: '細粒質斑鉄型グライ低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である斑鉄型グライ低地土。' },
  { r: 7, g: 21, b: 255, soilID: 'F2a2t2', title: '中粒質斑鉄型グライ低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である斑鉄型グライ低地土。' },
  { r: 7, g: 23, b: 255, soilID: 'F2a2t4', title: '粗粒質斑鉄型グライ低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である斑鉄型グライ低地土。' },
  { r: 9, g: 15, b: 255, soilID: 'F2a6', title: '表層灰色グライ低地土', explain: '土壌表面から25-50cmの間にグライ層よりも酸化的な「地下水湿性特徴」が現れるグライ低地土。' },
  { r: 9, g: 17, b: 255, soilID: 'F2a6p2', title: '礫質表層灰色グライ低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる表層灰色グライ低地土。' },
  { r: 9, g: 19, b: 255, soilID: 'F2a6t1', title: '細粒質表層灰色グライ低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である表層灰色グライ低地土。' },
  { r: 9, g: 21, b: 255, soilID: 'F2a6t2', title: '中粒質表層灰色グライ低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である表層灰色グライ低地土。' },
  { r: 9, g: 23, b: 255, soilID: 'F2a6t4', title: '粗粒質表層灰色グライ低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である表層灰色グライ低地土。' },
  { r: 11, g: 15, b: 255, soilID: 'F2e6', title: '泥炭質グライ低地土', explain: '表層100cm以内に積算して25cm以上の「泥炭物質」からなる層があるグライ低地土。 ' },
  { r: 11, g: 17, b: 255, soilID: 'F2e6t1', title: '細粒質泥炭質グライ低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である泥炭質グライ低地土。' },
  { r: 11, g: 19, b: 255, soilID: 'F2e6t2', title: '中粒質泥炭質グライ低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である泥炭質グライ低地土。' },
  { r: 11, g: 21, b: 255, soilID: 'F2e6t4', title: '粗粒質泥炭質グライ低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である泥炭質グライ低地土。' },
  { r: 13, g: 15, b: 255, soilID: 'F2h3', title: '腐植質グライ低地土', explain: '表層に有機物を多く含む「腐植質表層」または「多腐植質表層」をもつ グライ低地土。' },
  { r: 13, g: 17, b: 255, soilID: 'F2h3p2', title: '礫質腐植質グライ低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる腐植質グライ低地土。' },
  { r: 13, g: 19, b: 255, soilID: 'F2h3t1', title: '細粒質腐植質グライ低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である腐植質グライ低地土。' },
  { r: 13, g: 21, b: 255, soilID: 'F2h3t3', title: '中粗粒質腐植質グライ低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である腐植質グライ低地土。' },
  { r: 15, g: 15, b: 255, soilID: 'F2j7', title: '硫酸酸性質グライ低地土', explain: '硫化物を含む湖沼成・海成堆積物からなり、強い酸性を示すグライ低地土。' },
  { r: 15, g: 17, b: 255, soilID: 'F2j7t1', title: '細粒質硫酸酸性質グライ低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である硫酸酸性質グライ低地土。' },
  { r: 15, g: 19, b: 255, soilID: 'F2j7y1', title: '典型硫酸酸性質グライ低地土', explain: '典型的な硫酸酸性質グライ低地土。' },
  { r: 0, g: 3, b: 168, soilID: 'F3', title: '灰色低地土', explain: '中間的な湿性状態の沖積地の土壌。' },
  { r: 2, g: 3, b: 168, soilID: 'F3a5', title: '表層グライ化灰色低地土', explain: '水稲耕作下で灌漑水によって生成し、落水後も維持され作土から下方へ発 達しているグライ層をもつ灰色低地土。 ' },
  { r: 2, g: 5, b: 168, soilID: 'F3a5p2', title: '礫質表層グライ化灰色低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる表層グライ化灰色低地土。' },
  { r: 2, g: 7, b: 168, soilID: 'F3a5t1', title: '細粒質表層グライ化灰色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である表層グライ化灰色低地土。' },
  { r: 2, g: 9, b: 168, soilID: 'F3a5t3', title: '中粗粒質表層グライ化灰色低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である表層グライ化灰色低地土。' },
  { r: 4, g: 3, b: 168, soilID: 'F3a7', title: 'グライ化灰色低地土', explain: '土壌表面から50-75cmに地下水によって生成するグライ層の上端が現れる灰色低地土。' },
  { r: 4, g: 5, b: 168, soilID: 'F3a7p2', title: '礫質グライ化灰色低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れるグライ化灰色低地土。' },
  { r: 4, g: 7, b: 168, soilID: 'F3a7t1', title: '細粒質グライ化灰色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土であるグライ化灰色低地土。' },
  { r: 4, g: 9, b: 168, soilID: 'F3a7t2', title: '中粒質グライ化灰色低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土であるグライ化灰色低地土。' },
  { r: 4, g: 11, b: 168, soilID: 'F3a7t4', title: '粗粒質グライ化灰色低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土であるグライ化灰色低地土。' },
  { r: 6, g: 3, b: 168, soilID: 'F3d3', title: '下層黒ボク灰色低地土', explain: '土壌表面から100cm以内に積算して厚さ25cm以上の「黒ボク特徴」を示す層をもつ灰色低地土。' },
  { r: 6, g: 5, b: 168, soilID: 'F3d3t1', title: '細粒質下層黒ボク灰色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である下層黒ボク灰色低地土。' },
  { r: 6, g: 7, b: 168, soilID: 'F3d3y1', title: '典型下層黒ボク灰色低地土', explain: '典型的な下層黒ボク灰色低地土。' },
  { r: 8, g: 3, b: 168, soilID: 'F3e6', title: '泥炭質灰色低地土', explain: '表層100cm以内に積算して25cm以上の「泥炭物質」からなる層がある灰色低地土。' },
  { r: 8, g: 5, b: 168, soilID: 'F3e6p2', title: '礫質泥炭質灰色低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる泥炭質灰色低地土。' },
  { r: 8, g: 7, b: 168, soilID: 'F3e6t1', title: '細粒質泥炭質灰色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である泥炭質灰色低地土。' },
  { r: 8, g: 9, b: 168, soilID: 'F3e6t3', title: '中粗粒質泥炭質灰色低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である泥炭質灰色低地土。' },
  { r: 10, g: 3, b: 168, soilID: 'F3h3', title: '腐植質灰色低地土', explain: '表層に有機物を多く含む「腐植質表層」または「多腐植質表層」をもつ 灰色低地土。' },
  { r: 10, g: 5, b: 168, soilID: 'F3h3p2', title: '礫質腐植質灰色低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる腐植質灰色低地土。' },
  { r: 10, g: 7, b: 168, soilID: 'F3h3t1', title: '細粒質腐植質灰色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である腐植質灰色低地土。' },
  { r: 10, g: 9, b: 168, soilID: 'F3h3t3', title: '中粗粒質腐植質灰色低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である腐植質灰色低地土。' },
  { r: 12, g: 3, b: 168, soilID: 'F3j7', title: '硫酸酸性質灰色低地土', explain: '硫化物を含む湖沼成・海成堆積物からなり、強い酸性を示す灰色低地土。' },
  { r: 12, g: 5, b: 168, soilID: 'F3j7t1', title: '細粒質硫酸酸性質灰色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である硫酸酸性質灰色低地土。' },
  { r: 12, g: 7, b: 168, soilID: 'F3j7y1', title: '典型硫酸酸性質灰色低地土', explain: '典型的な硫酸酸性質灰色低地土。' },
  { r: 14, g: 3, b: 168, soilID: 'F3z1', title: '普通灰色低地土', explain: '典型的な灰色低地土。' },
  { r: 14, g: 5, b: 168, soilID: 'F3z1p2', title: '礫質普通灰色低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる普通灰色低地土。' },
  { r: 14, g: 7, b: 168, soilID: 'F3z1t1', title: '細粒質普通灰色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である普通灰色低地土。' },
  { r: 14, g: 9, b: 168, soilID: 'F3z1t2', title: '中粒質普通灰色低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である普通灰色低地土。' },
  { r: 14, g: 11, b: 168, soilID: 'F3z1t4', title: '粗粒質普通灰色低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である普通灰色低地土。' },
  { r: 0, g: 132, b: 168, soilID: 'F4', title: '褐色低地土', explain: '沖積低地の中では最も乾いた土地にある、黄褐色の次表層を持つ土壌。' },
  { r: 2, g: 132, b: 168, soilID: 'F4a3', title: '湿性褐色低地土', explain: '土壌表面から75cm以内にグライ層よりも酸化的な「地下水湿性特徴」が現れる褐色低地土。' },
  { r: 2, g: 134, b: 168, soilID: 'F4a3p2', title: '礫質湿性褐色低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる湿性褐色低地土。' },
  { r: 2, g: 136, b: 168, soilID: 'F4a3t1', title: '細粒質湿性褐色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である湿性褐色低地土。' },
  { r: 2, g: 138, b: 168, soilID: 'F4a3t2', title: '中粒質湿性褐色低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である湿性褐色低地土。' },
  { r: 2, g: 140, b: 168, soilID: 'F4a3t4', title: '粗粒質湿性褐色低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である湿性褐色低地土。' },
  { r: 4, g: 132, b: 168, soilID: 'F4c1', title: '水田化褐色低地土', explain: '作土下方まで灰色化し斑鉄をもつ褐色低地土。' },
  { r: 4, g: 134, b: 168, soilID: 'F4c1p2', title: '礫質水田化褐色低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる水田化褐色低地土。' },
  { r: 4, g: 136, b: 168, soilID: 'F4c1t1', title: '細粒質水田化褐色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である水田化褐色低地土。' },
  { r: 4, g: 138, b: 168, soilID: 'F4c1t2', title: '中粒質水田化褐色低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である水田化褐色低地土。' },
  { r: 4, g: 140, b: 168, soilID: 'F4c1t4', title: '粗粒質水田化褐色低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である水田化褐色低地土。' },
  { r: 6, g: 132, b: 168, soilID: 'F4h3', title: '腐植質褐色低地土', explain: '表層に有機物を多く含む「腐植質表層」または「多腐植質表層」をもつ 褐色低地土。' },
  { r: 6, g: 134, b: 168, soilID: 'F4h3p2', title: '礫質腐植質褐色低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる腐植質褐色低地土。' },
  { r: 6, g: 136, b: 168, soilID: 'F4h3t1', title: '細粒質腐植質褐色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である腐植質褐色低地土。' },
  { r: 6, g: 138, b: 168, soilID: 'F4h3t2', title: '中粒質腐植質褐色低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である腐植質褐色低地土。' },
  { r: 6, g: 140, b: 168, soilID: 'F4h3t4', title: '粗粒質腐植質褐色低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である腐植質褐色低地土。' },
  { r: 8, g: 132, b: 168, soilID: 'F4z1', title: '普通褐色低地土', explain: '典型的な褐色低地土。' },
  { r: 8, g: 134, b: 168, soilID: 'F4z1p2', title: '礫質普通褐色低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる普通褐色低地土。' },
  { r: 8, g: 136, b: 168, soilID: 'F4z1t1', title: '細粒質普通褐色低地土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である普通褐色低地土。' },
  { r: 8, g: 138, b: 168, soilID: 'F4z1t2', title: '中粒質普通褐色低地土', explain: '次表層の土性がシルト質壌土、壌土、細砂壌土である普通褐色低地土。' },
  { r: 8, g: 140, b: 168, soilID: 'F4z1t4', title: '粗粒質普通褐色低地土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土である普通褐色低地土。' },
  { r: 0, g: 169, b: 230, soilID: 'F5', title: '未熟低地土', explain: '未風化の土砂が堆積したままの土壌。' },
  { r: 2, g: 169, b: 230, soilID: 'F5a3', title: '湿性未熟低地土', explain: '土壌表面から75cm以内にグライ層よりも酸化的な「地下水湿性特徴」が現れる未熟低地土。' },
  { r: 2, g: 171, b: 230, soilID: 'F5a3p2', title: '礫質湿性未熟低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる湿性未熟低地土。' },
  { r: 2, g: 173, b: 230, soilID: 'F5a3y1', title: '典型湿性未熟低地土', explain: '典型的な湿性未熟低地土。' },
  { r: 4, g: 169, b: 230, soilID: 'F5z1', title: '普通未熟低地土', explain: '典型的な未熟低地土。' },
  { r: 4, g: 171, b: 230, soilID: 'F5z1p2', title: '礫質普通未熟低地土', explain: '土壌表面から60cm以内に礫層または岩盤が現れる普通未熟低地土。' },
  { r: 4, g: 173, b: 230, soilID: 'F5z1y1', title: '典型普通未熟低地土', explain: '典型的な普通未熟低地土。' },
  { r: 255, g: 0, b: 0, soilID: 'G1', title: '粘土集積赤黄色土', explain: '「粘土集積層」をもつ赤黄色土。' },
  { r: 253, g: 0, b: 0, soilID: 'G1a3', title: '湿性粘土集積赤黄色土', explain: '土壌表面から50-75cmに「地下水湿性特徴」を示す層の上端が現れる粘土集積赤黄色土。' },
  { r: 253, g: 2, b: 0, soilID: 'G1a3p1', title: '岩盤質湿性粘土集積赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる湿性粘土集積赤黄色土。' },
  { r: 253, g: 4, b: 0, soilID: 'G1a3p2', title: '礫質湿性粘土集積赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる湿性粘土集積赤黄色土。' },
  { r: 253, g: 6, b: 0, soilID: 'G1a3t1', title: '細粒質湿性粘土集積赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である湿性粘土集積赤黄色土。' },
  { r: 253, g: 8, b: 0, soilID: 'G1a3t3', title: '中粗粒質湿性粘土集積赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である湿性粘土集積赤黄色土。' },
  { r: 251, g: 0, b: 0, soilID: 'G1b1', title: '疑似グライ化粘土集積赤黄色土', explain: '土壌表面から75cm以内に「疑似グライ層」の上端が現れる粘土集積赤黄色土。' },
  { r: 251, g: 2, b: 0, soilID: 'G1b1p1', title: '岩盤質疑似グライ化粘土集積赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる疑似グライ化粘土集積赤黄色土。' },
  { r: 251, g: 4, b: 0, soilID: 'G1b1p2', title: '礫質疑似グライ化粘土集積赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる疑似グライ化粘土集積赤黄色土。' },
  { r: 251, g: 6, b: 0, soilID: 'G1b1t1', title: '細粒質疑似グライ化粘土集積赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である疑似グライ化粘土集積赤黄色土。' },
  { r: 251, g: 8, b: 0, soilID: 'G1b1t3', title: '中粗粒質疑似グライ化粘土集積赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である疑似グライ化粘土集積赤黄色土。' },
  { r: 249, g: 0, b: 0, soilID: 'G1c1', title: '水田化粘土集積赤黄色土', explain: '「水田鉄集積層」をもつ粘土集積赤黄色土。' },
  { r: 249, g: 2, b: 0, soilID: 'G1c1p1', title: '岩盤質水田化粘土集積赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる水田化粘土集積赤黄色土。' },
  { r: 249, g: 4, b: 0, soilID: 'G1c1p2', title: '礫質水田化粘土集積赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる水田化粘土集積赤黄色土。' },
  { r: 249, g: 6, b: 0, soilID: 'G1c1t1', title: '細粒質水田化粘土集積赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である水田化粘土集積赤黄色土。' },
  { r: 249, g: 8, b: 0, soilID: 'G1c1t3', title: '中粗粒質水田化粘土集積赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である水田化粘土集積赤黄色土。' },
  { r: 247, g: 0, b: 0, soilID: 'G1h3', title: '腐植質粘土集積赤黄色土', explain: '「腐植質表層」または「多腐植質表層」をもつ粘土集積赤黄色土。' },
  { r: 247, g: 2, b: 0, soilID: 'G1h3p1', title: '岩盤質腐植質粘土集積赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる腐植質粘土集積赤黄色土。' },
  { r: 247, g: 4, b: 0, soilID: 'G1h3p2', title: '礫質腐植質粘土集積赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる腐植質粘土集積赤黄色土。' },
  { r: 247, g: 6, b: 0, soilID: 'G1h3t1', title: '細粒質腐植質粘土集積赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である腐植質粘土集積赤黄色土。' },
  { r: 247, g: 8, b: 0, soilID: 'G1h3t3', title: '中粗粒質腐植質粘土集積赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である腐植質粘土集積赤黄色土。' },
  { r: 245, g: 0, b: 0, soilID: 'G1j5', title: '灰白化粘土集積赤黄色土', explain: '「漂白層」をもつ粘土集積赤黄色土。' },
  { r: 245, g: 2, b: 0, soilID: 'G1j5p1', title: '岩盤質灰白化粘土集積赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる灰白化粘土集積赤黄色土。' },
  { r: 245, g: 4, b: 0, soilID: 'G1j5p2', title: '礫質灰白化粘土集積赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる灰白化粘土集積赤黄色土。' },
  { r: 245, g: 6, b: 0, soilID: 'G1j5t1', title: '細粒質灰白化粘土集積赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である灰白化粘土集積赤黄色土。' },
  { r: 245, g: 8, b: 0, soilID: 'G1j5t3', title: '中粗粒質灰白化粘土集積赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である灰白化粘土集積赤黄色土。' },
  { r: 243, g: 0, b: 0, soilID: 'G1k1', title: '赤色粘土集積赤黄色土', explain: '湿状態の土色が赤色の「粘土集積層」をもつ粘土集積赤黄色土。' },
  { r: 243, g: 2, b: 0, soilID: 'G1k1p1', title: '岩盤質赤色粘土集積赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる赤色粘土集積赤黄色土。' },
  { r: 243, g: 4, b: 0, soilID: 'G1k1p2', title: '礫質赤色粘土集積赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる赤色粘土集積赤黄色土。' },
  { r: 243, g: 6, b: 0, soilID: 'G1k1t1', title: '細粒質赤色粘土集積赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である赤色粘土集積赤黄色土。' },
  { r: 243, g: 8, b: 0, soilID: 'G1k1t3', title: '中粗粒質赤色粘土集積赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である赤色粘土集積赤黄色土。' },
  { r: 241, g: 0, b: 0, soilID: 'G1z1', title: '普通粘土集積赤黄色土', explain: '典型的な粘土集積赤黄色土。' },
  { r: 241, g: 2, b: 0, soilID: 'G1z1p1', title: '岩盤質普通粘土集積赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる普通粘土集積赤黄色土。' },
  { r: 241, g: 4, b: 0, soilID: 'G1z1p2', title: '礫質普通粘土集積赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる普通粘土集積赤黄色土。' },
  { r: 241, g: 6, b: 0, soilID: 'G1z1t1', title: '細粒質普通粘土集積赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である普通粘土集積赤黄色土。' },
  { r: 241, g: 8, b: 0, soilID: 'G1z1t3', title: '中粗粒質普通粘土集積赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である普通粘土集積赤黄色土。' },
  { r: 255, g: 115, b: 223, soilID: 'G2', title: '風化変質赤黄色土', explain: '「粘土集積層」をもたず、「風化変質層」をもつ赤黄色土。' },
  { r: 253, g: 115, b: 223, soilID: 'G2a3', title: '湿性風化変質赤黄色土', explain: '土壌表面から50-75cmに「地下水湿性特徴」を示す層の上端が現れる風化変質赤黄色土。' },
  { r: 253, g: 117, b: 223, soilID: 'G2a3p1', title: '岩盤質湿性風化変質赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる湿性風化変質赤黄色土。' },
  { r: 253, g: 119, b: 223, soilID: 'G2a3p2', title: '礫質湿性風化変質赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる湿性風化変質赤黄色土。' },
  { r: 253, g: 121, b: 223, soilID: 'G2a3t1', title: '細粒質湿性風化変質赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である湿性風化変質赤黄色土。' },
  { r: 253, g: 123, b: 223, soilID: 'G2a3t3', title: '中粗粒質湿性風化変質赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である湿性風化変質赤黄色土。' },
  { r: 251, g: 115, b: 223, soilID: 'G2b1', title: '疑似グライ化風化変質赤黄色土', explain: '土壌表面から75㎝以内に「疑似グライ層」の上端が現れる風化変質赤黄色土。' },
  { r: 251, g: 117, b: 223, soilID: 'G2b1p1', title: '岩盤質疑似グライ化風化変質赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる疑似グライ化風化変質赤黄色土。' },
  { r: 251, g: 119, b: 223, soilID: 'G2b1p2', title: '礫質疑似グライ化風化変質赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる疑似グライ化風化変質赤黄色土。' },
  { r: 251, g: 121, b: 223, soilID: 'G2b1t1', title: '細粒質疑似グライ化風化変質赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である疑似グライ化風化変質赤黄色土。' },
  { r: 251, g: 123, b: 223, soilID: 'G2b1t3', title: '中粗粒質疑似グライ化風化変質赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である疑似グライ化風化変質赤黄色土。' },
  { r: 249, g: 115, b: 223, soilID: 'G2c1', title: '水田化風化変質赤黄色土', explain: '「水田鉄集積層」をもつ風化変質赤黄色土。' },
  { r: 249, g: 117, b: 223, soilID: 'G2c1p1', title: '岩盤質水田化風化変質赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる水田化風化変質赤黄色土。' },
  { r: 249, g: 119, b: 223, soilID: 'G2c1p2', title: '礫質水田化風化変質赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる水田化風化変質赤黄色土。' },
  { r: 249, g: 121, b: 223, soilID: 'G2c1t1', title: '細粒質水田化風化変質赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である水田化風化変質赤黄色土。' },
  { r: 249, g: 123, b: 223, soilID: 'G2c1t3', title: '中粗粒質水田化風化変質赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である水田化風化変質赤黄色土。' },
  { r: 247, g: 115, b: 223, soilID: 'G2h3', title: '腐植質風化変質赤黄色土', explain: '「腐植質表層」または「多腐植質表層」をもつ風化変質赤黄色土。' },
  { r: 247, g: 117, b: 223, soilID: 'G2h3p1', title: '岩盤質腐植質風化変質赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる腐植質風化変質赤黄色土。' },
  { r: 247, g: 119, b: 223, soilID: 'G2h3p2', title: '礫質腐植質風化変質赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる腐植質風化変質赤黄色土。' },
  { r: 247, g: 121, b: 223, soilID: 'G2h3t1', title: '細粒質腐植質風化変質赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である腐植質風化変質赤黄色土。' },
  { r: 247, g: 123, b: 223, soilID: 'G2h3t3', title: '中粗粒質腐植質風化変質赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である腐植質風化変質赤黄色土。' },
  { r: 245, g: 115, b: 223, soilID: 'G2j5', title: '灰白化風化変質赤黄色土', explain: '「漂白層」をもつ風化変質赤黄色土。' },
  { r: 245, g: 117, b: 223, soilID: 'G2j5p1', title: '岩盤質灰白化風化変質赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる灰白化風化変質赤黄色土。' },
  { r: 245, g: 119, b: 223, soilID: 'G2j5p2', title: '礫質灰白化風化変質赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる灰白化風化変質赤黄色土。' },
  { r: 245, g: 121, b: 223, soilID: 'G2j5t1', title: '細粒質灰白化風化変質赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である灰白化風化変質赤黄色土。' },
  { r: 245, g: 123, b: 223, soilID: 'G2j5t3', title: '中粗粒質灰白化風化変質赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である灰白化風化変質赤黄色土。' },
  { r: 243, g: 115, b: 223, soilID: 'G2k1', title: '赤色風化変質赤黄色土', explain: '湿状態の土色が赤色の「粘土集積層」をもつ風化変質赤黄色土。' },
  { r: 243, g: 117, b: 223, soilID: 'G2k1p1', title: '岩盤質赤色風化変質赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる赤色風化変質赤黄色土。' },
  { r: 243, g: 119, b: 223, soilID: 'G2k1p2', title: '礫質赤色風化変質赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる赤色風化変質赤黄色土。' },
  { r: 243, g: 121, b: 223, soilID: 'G2k1t1', title: '細粒質赤色風化変質赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である赤色風化変質赤黄色土。' },
  { r: 243, g: 123, b: 223, soilID: 'G2k1t3', title: '中粗粒質赤色風化変質赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である赤色風化変質赤黄色土。' },
  { r: 241, g: 115, b: 223, soilID: 'G2k2', title: 'ばん土質風化変質赤黄色土', explain: '土壌表面から50cm以内にリン酸吸収係数1000mg P2O5/100g以上（リン酸保持量60％以上）の層が25cm以上ある風化変質赤黄色土。' },
  { r: 241, g: 117, b: 223, soilID: 'G2k2p1', title: '岩盤質ばん土質風化変質赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れるばんど質風化変質赤黄色土。' },
  { r: 241, g: 119, b: 223, soilID: 'G2k2p2', title: '礫質ばん土質風化変質赤黄色土', explain: '土壌表面から60cm以内に礫層が現れるばんど質風化変質赤黄色土。' },
  { r: 241, g: 121, b: 223, soilID: 'G2k2t1', title: '細粒質ばん土質風化変質赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土であるばんど質風化変質赤黄色土。' },
  { r: 241, g: 123, b: 223, soilID: 'G2k2t3', title: '中粗粒質ばん土質風化変質赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土であるばんど質風化変質赤黄色土。' },
  { r: 239, g: 115, b: 223, soilID: 'G2z1', title: '普通風化変質赤黄色土', explain: '典型的な風化変質赤黄色土。' },
  { r: 239, g: 117, b: 223, soilID: 'G2z1p1', title: '岩盤質普通風化変質赤黄色土', explain: '土壌表面から60cm以内に岩盤が現れる普通風化変質赤黄色土。' },
  { r: 239, g: 119, b: 223, soilID: 'G2z1p2', title: '礫質普通風化変質赤黄色土', explain: '土壌表面から60cm以内に礫層が現れる普通風化変質赤黄色土。' },
  { r: 239, g: 121, b: 223, soilID: 'G2z1t1', title: '細粒質普通風化変質赤黄色土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である普通風化変質赤黄色土。' },
  { r: 239, g: 123, b: 223, soilID: 'G2z1t3', title: '中粗粒質普通風化変質赤黄色土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である普通風化変質赤黄色土。' },
  { r: 115, g: 255, b: 223, soilID: 'H1', title: '停滞水グライ土', explain: '土壌表面から50cm以内に「グライ層」の上端が現れ、かつその層の下端が土壌表面から50cm以深まで及んでいるか、または岩盤まで及んでいる土壌。' },
  { r: 117, g: 255, b: 223, soilID: 'H1c3', title: '水田型停滞水グライ土', explain: '「水田逆グライ層」をもつ停滞水グライ土。' },
  { r: 117, g: 253, b: 223, soilID: 'H1c3p1', title: '岩盤質水田型停滞水グライ土', explain: '土壌表面から60cm以内に岩盤が現れる水田型停滞水グライ土。' },
  { r: 117, g: 251, b: 223, soilID: 'H1c3p2', title: '礫質水田型停滞水グライ土', explain: '土壌表面から60cm以内に礫層が現れる水田型停滞水グライ土。' },
  { r: 117, g: 249, b: 223, soilID: 'H1c3t1', title: '細粒質水田型停滞水グライ土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である水田型停滞水グライ土。' },
  { r: 117, g: 247, b: 223, soilID: 'H1c3t3', title: '中粗粒質水田型停滞水グライ土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である水田型停滞水グライ土。' },
  { r: 119, g: 255, b: 223, soilID: 'H1e5', title: '表層泥炭質停滞水グライ土', explain: '土壌表面から25cm以内に、積算して10cm以上の「泥炭物質」からなる層をもつ停滞水グライ土。' },
  { r: 119, g: 253, b: 223, soilID: 'H1e5p1', title: '岩盤質表層泥炭質停滞水グライ土', explain: '土壌表面から60cm以内に岩盤が現れる表層泥炭質停滞水グライ土。' },
  { r: 119, g: 251, b: 223, soilID: 'H1e5p2', title: '礫質表層泥炭質停滞水グライ土', explain: '土壌表面から60cm以内に礫層が現れる表層泥炭質停滞水グライ土。' },
  { r: 119, g: 249, b: 223, soilID: 'H1e5t1', title: '細粒質表層泥炭質停滞水グライ土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である表層泥炭質停滞水グライ土。' },
  { r: 119, g: 247, b: 223, soilID: 'H1e5t3', title: '中粗粒質表層泥炭質停滞水グライ土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である表層泥炭質停滞水グライ土。' },
  { r: 121, g: 255, b: 223, soilID: 'H1h3', title: '腐植質停滞水グライ土', explain: '「腐植質表層」または「多腐植質表層」をもつ停滞水グライ土。' },
  { r: 121, g: 253, b: 223, soilID: 'H1h3p1', title: '岩盤質腐植質停滞水グライ土', explain: '土壌表面から60cm以内に岩盤が現れる腐植質停滞水グライ土。' },
  { r: 121, g: 251, b: 223, soilID: 'H1h3p2', title: '礫質腐植質停滞水グライ土', explain: '土壌表面から60cm以内に礫層が現れる腐植質停滞水グライ土。' },
  { r: 121, g: 249, b: 223, soilID: 'H1h3t1', title: '細粒質腐植質停滞水グライ土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である腐植質停滞水グライ土。' },
  { r: 121, g: 247, b: 223, soilID: 'H1h3t3', title: '中粗粒質腐植質停滞水グライ土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である腐植質停滞水グライ土。' },
  { r: 123, g: 255, b: 223, soilID: 'H1z1', title: '普通停滞水グライ土', explain: '典型的な停滞水グライ土。' },
  { r: 123, g: 253, b: 223, soilID: 'H1z1p1', title: '岩盤質普通停滞水グライ土', explain: '土壌表面から60cm以内に岩盤が現れる普通停滞水グライ土。' },
  { r: 123, g: 251, b: 223, soilID: 'H1z1p2', title: '礫質普通停滞水グライ土', explain: '土壌表面から60cm以内に礫層が現れる普通停滞水グライ土。' },
  { r: 123, g: 249, b: 223, soilID: 'H1z1t1', title: '細粒質普通停滞水グライ土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である普通停滞水グライ土。' },
  { r: 123, g: 247, b: 223, soilID: 'H1z1t3', title: '中粗粒質普通停滞水グライ土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である普通停滞水グライ土。' },
  { r: 163, g: 255, b: 115, soilID: 'H2', title: '疑似グライ土', explain: '台地に分布し、緻密な下層をもつ排水の良くない土壌。' },
  { r: 165, g: 255, b: 115, soilID: 'H2a4', title: '地下水型疑似グライ土', explain: '土壌表面から50-75cmに、厚さ10cm以上の「地下水湿性特徴」または「グライ特徴」を示す層の上端が現れる疑似グライ土。' },
  { r: 165, g: 253, b: 115, soilID: 'H2a4p1', title: '岩盤質地下水型疑似グライ土', explain: '土壌表面から60cm以内に岩盤が現れる地下水型疑似グライ土。' },
  { r: 165, g: 251, b: 115, soilID: 'H2a4p2', title: '礫質地下水型疑似グライ土', explain: '土壌表面から60cm以内に礫層が現れる地下水型疑似グライ土。' },
  { r: 165, g: 249, b: 115, soilID: 'H2a4t1', title: '細粒質地下水型疑似グライ土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である地下水型疑似グライ土。' },
  { r: 165, g: 247, b: 115, soilID: 'H2a4t3', title: '中粗粒質地下水型疑似グライ土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である地下水型疑似グライ土。' },
  { r: 167, g: 255, b: 115, soilID: 'H2b4', title: '褐色疑似グライ土', explain: '土壌表面から30cm以内に「疑似グライ層」または厚さ10cm以上の「地下水湿性特徴」を示す層がみられない疑似グライ土。' },
  { r: 167, g: 253, b: 115, soilID: 'H2b4p1', title: '岩盤質褐色疑似グライ土', explain: '土壌表面から60cm以内に岩盤が現れる褐色疑似グライ土。' },
  { r: 167, g: 251, b: 115, soilID: 'H2b4p2', title: '礫質褐色疑似グライ土', explain: '土壌表面から60cm以内に礫層が現れる褐色疑似グライ土。' },
  { r: 167, g: 249, b: 115, soilID: 'H2b4t1', title: '細粒質褐色疑似グライ土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である褐色疑似グライ土。' },
  { r: 167, g: 247, b: 115, soilID: 'H2b4t3', title: '中粗粒質褐色疑似グライ土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である褐色疑似グライ土。' },
  { r: 169, g: 255, b: 115, soilID: 'H2c1', title: '水田化疑似グライ土', explain: '「水田鉄集積層」をもつ疑似グライ土。' },
  { r: 169, g: 253, b: 115, soilID: 'H2c1p1', title: '岩盤質水田化疑似グライ土', explain: '土壌表面から60cm以内に岩盤が現れる水田化疑似グライ土。' },
  { r: 169, g: 251, b: 115, soilID: 'H2c1p2', title: '礫質水田化疑似グライ土', explain: '土壌表面から60cm以内に礫層が現れる水田化疑似グライ土。' },
  { r: 169, g: 249, b: 115, soilID: 'H2c1t1', title: '細粒質水田化疑似グライ土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である水田化疑似グライ土。' },
  { r: 169, g: 247, b: 115, soilID: 'H2c1t3', title: '中粗粒質水田化疑似グライ土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である水田化疑似グライ土。' },
  { r: 171, g: 255, b: 115, soilID: 'H2h3', title: '腐植質疑似グライ土', explain: '「腐植質表層」または「多腐植質表層」をもつ疑似グライ土。' },
  { r: 171, g: 253, b: 115, soilID: 'H2h3p1', title: '岩盤質腐植質疑似グライ土', explain: '土壌表面から60cm以内に岩盤が現れる腐植質疑似グライ土。' },
  { r: 171, g: 251, b: 115, soilID: 'H2h3p2', title: '礫質腐植質疑似グライ土', explain: '土壌表面から60cm以内に礫層が現れる腐植質疑似グライ土。' },
  { r: 171, g: 249, b: 115, soilID: 'H2h3t1', title: '細粒質腐植質疑似グライ土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である腐植質疑似グライ土。' },
  { r: 171, g: 247, b: 115, soilID: 'H2h3t3', title: '中粗粒質腐植質疑似グライ土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である腐植質疑似グライ土。' },
  { r: 173, g: 255, b: 115, soilID: 'H2z1', title: '普通疑似グライ土', explain: '典型的な疑似グライ土。' },
  { r: 173, g: 253, b: 115, soilID: 'H2z1p1', title: '岩盤質普通疑似グライ土', explain: '土壌表面から60cm以内に岩盤が現れる普通疑似グライ土。' },
  { r: 173, g: 251, b: 115, soilID: 'H2z1p2', title: '礫質普通疑似グライ土', explain: '土壌表面から60cm以内に礫層が現れる普通疑似グライ土。' },
  { r: 173, g: 249, b: 115, soilID: 'H2z1t1', title: '細粒質普通疑似グライ土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である普通疑似グライ土。' },
  { r: 173, g: 247, b: 115, soilID: 'H2z1t3', title: '中粗粒質普通疑似グライ土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である普通疑似グライ土。' },
  { r: 34, g: 140, b: 34, soilID: 'I1', title: '褐色森林土', explain: '火山灰の影響の少ない山地・丘陵地に分布する褐色あるいは黄褐色の「風化変質層」をもつ土壌。' },
  { r: 36, g: 140, b: 34, soilID: 'I1a3', title: '湿性褐色森林土', explain: '土壌表面から75cm以内に「表面水湿性特徴」または「地下水湿性特徴」を示す層の上端が現れる褐色森林土。' },
  { r: 36, g: 142, b: 34, soilID: 'I1a3p1', title: '岩盤質湿性褐色森林土', explain: '土壌表面から60cm以内に岩盤が現れる湿性褐色森林土。' },
  { r: 36, g: 144, b: 34, soilID: 'I1a3p2', title: '礫質湿性褐色森林土', explain: '土壌表面から60cm以内に礫層が現れる湿性褐色森林土。' },
  { r: 36, g: 146, b: 34, soilID: 'I1a3t1', title: '細粒質湿性褐色森林土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である湿性褐色森林土。' },
  { r: 36, g: 148, b: 34, soilID: 'I1a3t3', title: '中粗粒質湿性褐色森林土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である湿性褐色森林土。' },
  { r: 38, g: 140, b: 34, soilID: 'I1c1', title: '水田化褐色森林土', explain: '「水田鉄集積層」をもつ褐色森林土。' },
  { r: 38, g: 142, b: 34, soilID: 'I1c1p1', title: '岩盤質水田化褐色森林土', explain: '土壌表面から60cm以内に岩盤が現れる水田化褐色森林土。' },
  { r: 38, g: 144, b: 34, soilID: 'I1c1p2', title: '礫質水田化褐色森林土', explain: '土壌表面から60cm以内に礫層が現れる水田化褐色森林土。' },
  { r: 38, g: 146, b: 34, soilID: 'I1c1t1', title: '細粒質水田化褐色森林土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である水田化褐色森林土。' },
  { r: 38, g: 148, b: 34, soilID: 'I1c1t3', title: '中粗粒質水田化褐色森林土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である水田化褐色森林土。' },
  { r: 40, g: 140, b: 34, soilID: 'I1d4', title: '下層赤黄色褐色森林土', explain: '土壌表面から75cm以内に「赤黄色特徴」を示す「風化変質層」または「粘土集積層」をもつ褐色森林土。' },
  { r: 40, g: 142, b: 34, soilID: 'I1d4p1', title: '岩盤質下層赤黄色褐色森林土', explain: '土壌表面から60cm以内に岩盤が現れる下層赤黄色褐色森林土。' },
  { r: 40, g: 144, b: 34, soilID: 'I1d4p2', title: '礫質下層赤黄色褐色森林土', explain: '土壌表面から60cm以内に礫層が現れる下層赤黄色褐色森林土。' },
  { r: 40, g: 146, b: 34, soilID: 'I1d4t1', title: '細粒質下層赤黄色褐色森林土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である下層赤黄色褐色森林土。' },
  { r: 40, g: 148, b: 34, soilID: 'I1d4t3', title: '中粗粒質下層赤黄色褐色森林土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である下層赤黄色褐色森林土。' },
  { r: 42, g: 140, b: 34, soilID: 'I1h3', title: '腐植質褐色森林土', explain: '「腐植質表層」または「多腐植質表層」をもつ褐色森林土。' },
  { r: 42, g: 142, b: 34, soilID: 'I1h3p1', title: '岩盤質腐植質褐色森林土', explain: '土壌表面から60cm以内に岩盤が現れる腐植質褐色森林土。' },
  { r: 42, g: 144, b: 34, soilID: 'I1h3p2', title: '礫質腐植質褐色森林土', explain: '土壌表面から60cm以内に礫層が現れる腐植質褐色森林土。' },
  { r: 42, g: 146, b: 34, soilID: 'I1h3t1', title: '細粒質腐植質褐色森林土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である腐植質褐色森林土。' },
  { r: 42, g: 148, b: 34, soilID: 'I1h3t3', title: '中粗粒質腐植質褐色森林土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である腐植質褐色森林土。' },
  { r: 44, g: 140, b: 34, soilID: 'I1i1', title: '台地褐色森林土', explain: '洪積世堆積物（更新世の河成、海成、湖沼成の古い未固結堆積物） を母材とする褐色森林土。' },
  { r: 44, g: 142, b: 34, soilID: 'I1i1p1', title: '岩盤質台地褐色森林土', explain: '土壌表面から60cm以内に岩盤が現れる台地褐色森林土。' },
  { r: 44, g: 144, b: 34, soilID: 'I1i1p2', title: '礫質台地褐色森林土', explain: '土壌表面から60cm以内に礫層が現れる台地褐色森林土。' },
  { r: 44, g: 146, b: 34, soilID: 'I1i1t1', title: '細粒質台地褐色森林土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である台地褐色森林土。' },
  { r: 44, g: 148, b: 34, soilID: 'I1i1t3', title: '中粗粒質台地褐色森林土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である台地褐色森林土。' },
  { r: 46, g: 140, b: 34, soilID: 'I1j8', title: '塩基型褐色森林土', explain: '次表層の全ての亜層位でpH(H2O)が6.5以上である褐色森林土。' },
  { r: 46, g: 142, b: 34, soilID: 'I1j8p1', title: '岩盤質塩基型褐色森林土', explain: '土壌表面から60cm以内に岩盤が現れる塩基型褐色森林土。' },
  { r: 46, g: 144, b: 34, soilID: 'I1j8p2', title: '礫質塩基型褐色森林土', explain: '土壌表面から60cm以内に礫層が現れる塩基型褐色森林土。' },
  { r: 46, g: 146, b: 34, soilID: 'I1j8t1', title: '細粒質塩基型褐色森林土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である塩基型褐色森林土。' },
  { r: 46, g: 148, b: 34, soilID: 'I1j8t3', title: '中粗粒質塩基型褐色森林土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である塩基型褐色森林土。' },
  { r: 48, g: 140, b: 34, soilID: 'I1k2', title: 'ばん土質褐色森林土', explain: '土壌表面から50cm以内にリン酸吸収係数1000mg P2O5/100g以上（リン酸保持量60％以上）の層が25cm以上ある褐色森林土。' },
  { r: 48, g: 142, b: 34, soilID: 'I1k2p1', title: '岩盤質ばん土質褐色森林土', explain: '土壌表面から60cm以内に岩盤が現れるばん土質褐色森林土。' },
  { r: 48, g: 144, b: 34, soilID: 'I1k2p2', title: '礫質ばん土質褐色森林土', explain: '土壌表面から60cm以内に礫層が現れるばん土質褐色森林土。' },
  { r: 48, g: 146, b: 34, soilID: 'I1k2t1', title: '細粒質ばん土質褐色森林土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土であるばん土質褐色森林土。' },
  { r: 48, g: 148, b: 34, soilID: 'I1k2t3', title: '中粗粒質ばん土質褐色森林土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土であるばん土質褐色森林土。' },
  { r: 50, g: 140, b: 34, soilID: 'I1k3', title: 'ポドゾル化褐色森林土', explain: '「ポドゾル性集積層（断面形態）」または「ポドゾル性集積層（分析値）」をもつ褐色森林土。' },
  { r: 50, g: 142, b: 34, soilID: 'I1k3p1', title: '岩盤質ポドゾル化褐色森林土', explain: '土壌表面から60cm以内に岩盤が現れるポドゾル化褐色森林土。' },
  { r: 50, g: 144, b: 34, soilID: 'I1k3p2', title: '礫質ポドゾル化褐色森林土', explain: '土壌表面から60cm以内に礫層が現れるポドゾル化褐色森林土。' },
  { r: 50, g: 146, b: 34, soilID: 'I1k3t1', title: '細粒質ポドゾル化褐色森林土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土であるポドゾル化褐色森林土。' },
  { r: 50, g: 148, b: 34, soilID: 'I1k3t3', title: '中粗粒質ポドゾル化褐色森林土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土であるポドゾル化褐色森林土。' },
  { r: 52, g: 140, b: 34, soilID: 'I1z1', title: '普通褐色森林土', explain: '典型的な褐色森林土。' },
  { r: 52, g: 142, b: 34, soilID: 'I1z1p1', title: '岩盤質普通褐色森林土', explain: '土壌表面から60cm以内に岩盤が現れる普通褐色森林土。' },
  { r: 52, g: 144, b: 34, soilID: 'I1z1p2', title: '礫質普通褐色森林土', explain: '土壌表面から60cm以内に礫層が現れる普通褐色森林土。' },
  { r: 52, g: 146, b: 34, soilID: 'I1z1t1', title: '細粒質普通褐色森林土', explain: '次表層の土性が重埴土、シルト質埴土、軽埴土、砂質埴土、シルト質埴壌土、埴壌土、砂質埴壌土である普通褐色森林土。' },
  { r: 52, g: 148, b: 34, soilID: 'I1z1t3', title: '中粗粒質普通褐色森林土', explain: '次表層の土性が粗砂壌土、壌質砂土、砂土、シルト質壌土、壌土、細砂壌土である普通褐色森林土。' },
  { r: 211, g: 181, b: 141, soilID: 'J1', title: '火山放出物未熟土', explain: '層位分化が未発達で、土壌表面から50cm以内に積算で厚さ25cm以上の「火山放出物」からなる層をもつ土壌。' },
  { r: 213, g: 181, b: 141, soilID: 'J1a3', title: '湿性火山放出物未熟土', explain: '土壌表面から50cm以内に「グライ特徴」または「地下水湿性特徴」を示す層の上端が現れる火山放出物未熟土。' },
  { r: 213, g: 183, b: 141, soilID: 'J1a3w3', title: '下層泥炭湿性火山放出物未熟土', explain: '土壌表面から75cm以内に積算して25cm以上の「泥炭物質」からなる層をもつ湿性火山放出物未熟土。' },
  { r: 213, g: 185, b: 141, soilID: 'J1a3w4', title: '下層低地湿性火山放出物未熟土', explain: '土壌表面から75cm以内に積算して25cm以上の「沖積堆積物」をもつ層をもつ湿性火山放出物未熟土。' },
  { r: 213, g: 187, b: 141, soilID: 'J1a3y1', title: '典型湿性火山放出物未熟土', explain: '典型的な湿性火山放出物未熟土。' },
  { r: 215, g: 181, b: 141, soilID: 'J1z1', title: '普通火山放出物未熟土', explain: '典型的な火山放出物未熟土。' },
  { r: 215, g: 183, b: 141, soilID: 'J1z1p3', title: '盤層型普通火山放出物未熟土', explain: '土壌表面から60cm以内に盤層が現れる普通火山放出物未熟土。' },
  { r: 215, g: 185, b: 141, soilID: 'J1z1w4', title: '下層低地普通火山放出物未熟土', explain: '土壌表面から75cm以内に積算して25cm以上の「沖積堆積物」からなる層をもつ普通火山放出物未熟土。' },
  { r: 215, g: 187, b: 141, soilID: 'J1z1w5', title: '下層黒ボク普通火山放出物未熟土', explain: '土壌表面から100cm以内に積算して25cm以上の「黒ボク特徴」をもつ層をもつ普通火山放出物未熟土。' },
  { r: 215, g: 189, b: 141, soilID: 'J1z1y1', title: '典型普通火山放出物未熟土', explain: '典型的な普通火山放出物未熟土。' },
  { r: 238, g: 216, b: 175, soilID: 'J2', title: '砂質未熟土', explain: '土壌表面から100cmまで、石礫含量が断面割合で35％未満で、かつ、土性は壌質砂土、またはそれより粗い。ただし、石灰質、サンゴ質の砂も含む。' },
  { r: 240, g: 216, b: 175, soilID: 'J2a3', title: '湿性砂質未熟土', explain: '土壌表面から75cm以内に「グライ特徴」または「地下水湿性特徴」を示す層の上端が現れる砂質未熟土。' },
  { r: 240, g: 218, b: 175, soilID: 'J2a3y1', title: '典型湿性砂質未熟土', explain: 'すべての湿性砂質未熟土。' },
  { r: 242, g: 216, b: 175, soilID: 'J2f2', title: '石灰質砂質未熟土', explain: '土壌表面から20-50㎝の間で、炭酸カルシウム換算で2.0％以上の炭酸塩を含む砂質未熟土。' },
  { r: 242, g: 218, b: 175, soilID: 'J2f2y1', title: '典型石灰質砂質未熟土', explain: 'すべての石灰質砂質未熟土。' },
  { r: 242, g: 220, b: 175, soilID: 'J2z1', title: '普通砂質未熟土', explain: '典型的な砂質未熟土。' },
  { r: 242, g: 222, b: 175, soilID: 'J2z1y1', title: '典型普通砂質未熟土', explain: 'すべての普通砂質未熟土。' },
  { r: 204, g: 204, b: 0, soilID: 'J3', title: '固結岩屑土', explain: '土壌表面から30cm以内に固結した岩盤が現れる土壌。' },
  { r: 206, g: 204, b: 0, soilID: 'J3a3', title: '湿性固結岩屑土', explain: '土壌表面から岩盤までの間に「グライ特徴」または「地下水湿性特徴」を示す層の上端が現れる固結岩屑土。' },
  { r: 206, g: 206, b: 0, soilID: 'J3a3y1', title: '典型湿性固結岩屑土', explain: 'すべての湿性固結岩屑土。' },
  { r: 208, g: 204, b: 0, soilID: 'J3f2', title: '石灰質固結岩屑土', explain: '岩盤上の層が炭酸カルシウム換算で2.0％以上の炭酸塩を含む固結岩屑土。' },
  { r: 208, g: 206, b: 0, soilID: 'J3f2x3', title: '暗色表層石灰質固結岩屑土', explain: '「富塩基暗色表層」をもち、かつ、岩盤上の層が炭酸カルシウム換算で2.0％以上の炭酸塩を含む固結岩屑土。' },
  { r: 208, g: 208, b: 0, soilID: 'J3f2y1', title: '典型石灰質固結岩屑土', explain: '典型的な石灰質固結岩屑土。' },
  { r: 210, g: 204, b: 0, soilID: 'J3z1', title: '普通固結岩屑土', explain: '典型的な固結岩屑土。' },
  { r: 210, g: 206, b: 0, soilID: 'J3z1y1', title: '典型普通固結岩屑土', explain: 'すべての普通固結岩屑土。' },
  { r: 255, g: 255, b: 0, soilID: 'J4', title: '陸成未熟土', explain: '層位分化が進んでおらず、母材そのものの物理的・化学的性質の影響を強く受けている土壌。' },
  { r: 253, g: 255, b: 0, soilID: 'J4f1', title: '花崗岩型陸成未熟土', explain: '花崗岩に由来する陸成未熟土。' },
  { r: 253, g: 253, b: 0, soilID: 'J4f1y1', title: '典型花崗岩型陸成未熟土', explain: 'すべての花崗岩型陸成未熟土。' },
  { r: 251, g: 255, b: 0, soilID: 'J4f2', title: '石灰質陸成未熟土', explain: '土壌表面から20-50cmの間で炭酸カルシウム換算で2.0％以上の炭酸塩を含む陸成未熟土。' },
  { r: 251, g: 253, b: 0, soilID: 'J4f2x3', title: '暗色表層石灰質陸成未熟土', explain: '「富塩基暗色表層」をもち、かつ、土壌表面から20-50cmの間で炭酸カルシウム換算で2.0％以上の炭酸塩を含む陸成未熟土。' },
  { r: 251, g: 251, b: 0, soilID: 'J4f2y1', title: '典型石灰質陸成未熟土', explain: '典型的な石灰質陸成未熟土。' },
  { r: 249, g: 255, b: 0, soilID: 'J4f3', title: '泥灰岩質陸成未熟土', explain: '泥灰岩に由来し、土壌表面から100cm、または基岩までの全ての層位で粘土含量が30％以上である陸成未熟土。' },
  { r: 249, g: 253, b: 0, soilID: 'J4f3x1', title: '湿性泥灰岩質陸成未熟土', explain: '土壌表面から75㎝以内に「グライ特徴」または「地下水湿性特徴」を示す層の上端が現れる泥灰岩質陸成未熟土。 ' },
  { r: 249, g: 251, b: 0, soilID: 'J4f3y1', title: '典型泥灰岩質陸成未熟土', explain: '典型的な泥灰岩質陸成未熟土。' },
  { r: 247, g: 255, b: 0, soilID: 'J4f4', title: '軟岩型陸成未熟土', explain: '礫または岩盤がコテ、シャベルで削れる陸成未熟土。' },
  { r: 247, g: 253, b: 0, soilID: 'J4f4y1', title: '典型軟岩型陸成未熟土', explain: 'すべての軟岩型陸成未熟土。' },
  { r: 245, g: 255, b: 0, soilID: 'J4z1', title: '普通陸成未熟土', explain: '典型的な陸成未熟土。 ' },
  { r: 245, g: 253, b: 0, soilID: 'J4z1y1', title: '典型普通陸成未熟土', explain: 'すべての普通陸成未熟土。' },
  { r: 255, g: 255, b: 254, soilID: 'Z1', title: '岩石地', explain: '岩石地。' },
  { r: 255, g: 255, b: 255, soilID: 'Z4', title: 'その他', explain: '不明。' },
]
/**
 * ポップアップを閉じる
 * @param {string} mapName - 指定した場合はそのマップのポップアップのみ閉じる。省略時は全マップのポップアップを閉じる
 */
export function closeAllPopups(mapName) {
  if (mapName && popups[mapName]) {
    // 指定マップのポップアップのみ閉じる
    popups[mapName].forEach((popup) => popup.remove())
    popups[mapName].length = 0
    // 地籍調査v2ハイライトをクリア
    const map = store.state[mapName]
    if (map && map.getLayer('oh-chiseki-v2-highlight')) {
      map.setFilter('oh-chiseki-v2-highlight', ['==', ['get', 'code'], ''])
    }
  } else {
    // 全マップのポップアップを閉じる（後方互換性）
    Object.keys(popups).forEach((key) => {
      popups[key].forEach((popup) => popup.remove())
      popups[key].length = 0
      // 地籍調査v2ハイライトをクリア
      const map = store.state[key]
      if (map && map.getLayer && map.getLayer('oh-chiseki-v2-highlight')) {
        map.setFilter('oh-chiseki-v2-highlight', ['==', ['get', 'code'], ''])
      }
    })
  }
}
// IDから対応するlabelを取得する関数
function getLabelByLayerId(layerId, data) {
  for (const mapKey in data) {
    const mapLayers = data[mapKey]
    for (const item of mapLayers) {
      if (item.layers.some((layer) => layer.id === layerId)) {
        return item.label
      }
    }
  }
  return null
}
//------------------------------------------------------------------------------------------------------------
function latLngToTile(lat, lng, z) {
  const w = Math.pow(2, z === undefined ? 0 : z) / 2, // 世界全体のピクセル幅
    yrad = Math.log(Math.tan((Math.PI * (90 + lat)) / 360))
  return { x: (lng / 180 + 1) * w, y: (1 - yrad / Math.PI) * w }
}
function getLegendItem(legend, url, lat, lng, z) {
  // map.getCanvas().style.cursor = 'progress'
  return new Promise(function (resolve) {
    const p = latLngToTile(lat, lng, z),
      x = Math.floor(p.x), // タイルX座標
      y = Math.floor(p.y), // タイルY座標
      i = (p.x - x) * 256, // タイル内i座標
      j = (p.y - y) * 256, // タイル内j座標
      img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = function () {
      const canvas = document.createElement('canvas'),
        context = canvas.getContext('2d')
      let v, d
      canvas.width = 1
      canvas.height = 1
      context.drawImage(img, i, j, 1, 1, 0, 0, 1, 1)
      d = context.getImageData(0, 0, 1, 1).data
      if (legend[0].r + legend[0].g + legend[0].b === 0) {
        if (d[3] === 255) {
          v = 'dummy'
        } else {
          v = null
        }
      } else {
        // まず完全一致を試す
        v = legend.find((o) => o.r == d[0] && o.g == d[1] && o.b == d[2])
        // 完全一致しない場合、最近傍色でマッチ（PNG圧縮による色ズレ対策）
        if (!v && d[3] > 0) {
          let minDist = Infinity
          for (const o of legend) {
            const dist = (o.r - d[0]) ** 2 + (o.g - d[1]) ** 2 + (o.b - d[2]) ** 2
            if (dist < minDist) {
              minDist = dist
              v = o
            }
          }
          // 距離が大きすぎる場合は無関係な色とみなす
          if (minDist > 5000) v = null
        }
      }
      // map.getCanvas().style.cursor = 'default'
      resolve(v)
    }
    img.onerror = function () {
      // map.getCanvas().style.cursor = 'default'
      resolve(null)
    }
    img.src = url.replace('{z}', z).replace('{y}', y).replace('{x}', x)
  })
}
// 土壌インベントリー用: 最近傍RGBマッチ + ズームレベル別粒度対応
function getLegendItemNearest(legend, url, lat, lng, z) {
  return new Promise(function (resolve) {
    const p = latLngToTile(lat, lng, z)
    const x = Math.floor(p.x)
    const y = Math.floor(p.y)
    const i = (p.x - x) * 256
    const j = (p.y - y) * 256
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = function () {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.width = 1
      canvas.height = 1
      context.drawImage(img, i, j, 1, 1, 0, 0, 1, 1)
      const d = context.getImageData(0, 0, 1, 1).data
      if (d[3] === 0) { resolve(null); return }
      // ユークリッド距離で最近傍マッチ
      let minDist = Infinity
      let bestIdx = -1
      for (let k = 0; k < legend.length; k++) {
        const dr = d[0] - legend[k].r
        const dg = d[1] - legend[k].g
        const db = d[2] - legend[k].b
        const dist = dr * dr + dg * dg + db * db
        if (dist < minDist) {
          minDist = dist
          bestIdx = k
          if (dist === 0) break
        }
      }
      if (bestIdx === -1 || (d[0] === 0 && d[1] === 0 && d[2] === 0)) { resolve(null); return }
      const matched = legend[bestIdx]
      // ズームレベルで粒度調整（元サイトの仕様に準拠）
      let soilID = matched.soilID
      let resultItem = matched
      if (z < 10) {
        const code2 = soilID.substring(0, 2)
        const parent = legend.find((o) => o.soilID === code2)
        if (parent) resultItem = parent
      } else if (z < 13) {
        const code4 = soilID.substring(0, 4)
        const parent = legend.find((o) => o.soilID === code4)
        if (parent) resultItem = parent
      }
      resolve(resultItem)
    }
    img.onerror = function () { resolve(null) }
    img.src = url.replace('{z}', z).replace('{y}', y).replace('{x}', x)
  })
}
function urlByLayerId(layerId) {
  let RasterTileUrl, legend, zoom
  switch (layerId) {
    case 'oh-kozui-saidai-layer':
    case 'oh-rgb-kozui-saidai-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/01_flood_l2_shinsuishin/{z}/{x}/{y}.png'
      legend = legend_shinsuishin
      zoom = 16
      break
    case 'oh-rgb-kozui-keikaku-layer':
      RasterTileUrl =
        'https://disaportaldata.gsi.go.jp/raster/01_flood_l1_shinsuishin_newlegend_kuni_data/{z}/{x}/{y}.png'
      legend = legend_shinsuishin
      zoom = 16
      break
    case 'oh-shitchi-layer':
    case 'oh-rgb-shitchi-layer':
      RasterTileUrl = 'https://cyberjapandata.gsi.go.jp/xyz/swale/{z}/{x}/{y}.png'
      legend = legend_shitchi
      zoom = 16
      break
    case 'oh-tsunami-layer':
    case 'oh-rgb-tsunami-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/04_tsunami_newlegend_data/{z}/{x}/{y}.png'
      legend = legend_hightide_tsunami
      zoom = 16
      break
    case 'oh-dosya-layer':
    case 'oh-rgb-dosya-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/05_dosekiryukeikaikuiki/{z}/{x}/{y}.png'
      legend = legend_dosyasaigai
      zoom = 16
      break
    case 'oh-dosekiryu-layer':
    case 'oh-rgb-dosekiryu-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/05_dosekiryukikenkeiryu/{z}/{x}/{y}.png'
      legend = legend_dosekiryu
      zoom = 16
      break
    case 'oh-rgb-kyukeisya-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/05_kyukeisyachihoukai/{z}/{x}/{y}.png'
      legend = legend_kyukeisya
      zoom = 16
      break
    case 'oh-rgb-jisuberi-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/05_jisuberikikenkasyo/{z}/{x}/{y}.png'
      legend = legend_jisuberi
      zoom = 16
      break
    case 'oh-rgb-tameike-layer':
      RasterTileUrl = 'https://disaportal.gsi.go.jp/data/raster/07_tameike/{z}/{x}/{y}.png'
      legend = legend_tameike
      zoom = 15
      break
    case 'oh-rgb-keizoku-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/01_flood_l2_keizoku_data/{z}/{x}/{y}.png'
      legend = legend_keizoku
      zoom = 16
      break
    case 'oh-rgb-kaokutoukai-hanran-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/01_flood_l2_kaokutoukai_hanran_data/{z}/{x}/{y}.png'
      legend = legend_kaokutoukai_hanran
      zoom = 16
      break
    case 'oh-rgb-hightide-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/03_hightide_l2_shinsuishin_data/{z}/{x}/{y}.png'
      legend = legend_hightide_tsunami
      zoom = 16
      break
    case 'oh-rgb-naisui-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/02_naisui_data/{z}/{x}/{y}.png'
      legend = legend_shinsuishin
      zoom = 16
      break
    case 'oh-rgb-nadare-layer':
      RasterTileUrl = 'https://disaportaldata.gsi.go.jp/raster/05_nadarekikenkasyo/{z}/{x}/{y}.png'
      legend = legend_nadare
      zoom = 16
      break
    case 'oh-rgb-seamless-layer':
      RasterTileUrl = 'https://gbank.gsj.jp/seamless/v2/api/1.2/tiles/{z}/{y}/{x}.png'
      legend = legend_seamless
      zoom = 13
      break
    case 'oh-rgb-tochiriyo-raster-layer':
    case 'oh-blend-tochiriyo-hillshade':
      RasterTileUrl = 'https://kenzkenz4.xsrv.jp/xyz/tochiriyo/{z}/{x}/{y}.png'
      legend = legend_tochiriyo
      zoom = 14
      break
    case 'oh-rgb-dojo-layer':
    case 'oh-blend-dojo-hillshade':
      RasterTileUrl = 'https://soil-inventory.rad.naro.go.jp/tile/figure/{z}/{x}/{y}.png'
      legend = legend_dojo
      zoom = 15
      break
    // --- 宮崎県防災ハイブリッドレイヤー ---
    case 'oh-hyuganada-totatsu-raster':
      RasterTileUrl = 'https://kenzkenz.net/miyazaki/hyuganadatotatsuraster/{z}/{x}/{y}.png'
      legend = legend_hyuganada_totatsu
      zoom = 15
      break
    case 'oh-hyuganada-shindo-raster':
      RasterTileUrl = 'https://kenzkenz.net/miyazaki/hyuganadashindoraster/{z}/{x}/{y}.png'
      legend = legend_hyuganada_shindo
      zoom = 13
      break
    case 'oh-nantora-shindo-raster':
      RasterTileUrl = 'https://kenzkenz.net/miyazaki/nantorashindoraster/{z}/{x}/{y}.png'
      legend = legend_nantora_shindo
      zoom = 13
      break
    case 'oh-nantora-ekijyoka-raster':
      RasterTileUrl = 'https://kenzkenz.net/miyazaki/nantoraekijyokaraster/{z}/{x}/{y}.png'
      legend = legend_nantora_ekijyoka
      zoom = 13
      break
    case 'oh-hyuganada-tsunami-raster':
      RasterTileUrl = 'https://kenzkenz.net/miyazaki/hyuganadatsunamiraster/{z}/{x}/{y}.png'
      legend = legend_hyuganada_tsunami
      zoom = 15
      break
    default:
      // 何も該当しない場合の処理
      RasterTileUrl = ''
      legend = null
      break
  }
  return [RasterTileUrl, legend, zoom]
}
let isGooglemap = true
let isRePopup = true

/**
 * Vueコンポーネントを使ってドロー地物のポップアップを作成
 */
function createDrawPopupWithVue(feature, map, coordinates, mapName) {
  const container = document.createElement('div')

  let popupInstance = null

  // MapLibreのポップアップを作成（先に作成してclosureで参照できるようにする）
  const center = map.getCenter()
  let position = coordinates
  let isSmartPhone = window.innerWidth < 450
  if (isSmartPhone) {
    position = [center.lng, center.lat]
  }

  popupInstance = new maplibregl.Popup({
    maxWidth: '380px',
    anchor: 'bottom', // 常に地物の上に表示
  }).setLngLat(position)

  // Vueアプリを作成（コールバック関数をpropsとして渡す）
  const app = createApp(DrawPopup, {
    featureId: feature.properties.id,
    geometryType: feature.geometry.type,
    properties: feature.properties,
    onClose: () => {
      if (popupInstance) popupInstance.remove()
    },
    onUpdateLabel: (id, value) => {
      updateFeatureProperty(id, 'label', value)
    },
    onUpdateTextColor: (id, value) => {
      updateFeatureProperty(id, 'color', value)
    },
    onUpdatePointColor: (id, value) => {
      updateFeatureProperty(id, 'point-color', value)
    },
    onUpdateTextSize: (id, value) => {
      updateFeatureProperty(id, 'text-size', value)
    },
    onUpdateTextStyle: (id, value) => {
      updateFeatureProperty(id, 'labelType', value)
      // labelType変更時は吹き出しマーカーを再評価
      markerAddAndRemove(true)
    },
    onUpdateOffset: (id, value) => {
      updateFeatureProperty(id, 'offsetValue', value)
    },
    onUpdateLineColor: (id, value) => {
      // ポリゴンと同じパターンで処理
      // 色名またはHEX値を受け取り、そのまま color プロパティに設定
      // ラインは不透明なので、RGBA変換は不要

      // 次回のライン作成時に使用するためにstoreに保存
      store.state.currentLineColor = value

      // color プロパティを更新
      updateFeatureProperty(id, 'color', value)
    },
    onUpdateLineWidth: (id, value) => {
      updateFeatureProperty(id, 'line-width', value)
      // 次回のライン作成時に使用するためにstoreに保存
      store.state.currentLineWidth = value
    },
    onUpdateArrow: (id, value) => {
      // 矢印タイプを更新
      updateFeatureProperty(id, 'arrow-type', value)
      // 次回のライン作成時に使用するためにstoreに保存
      store.state.currentArrowType = value
    },
    onUpdateCalc: (id, value) => {
      // レイヤーのフィルターは数値を期待しているため、boolean -> number変換
      updateFeatureProperty(id, 'calc', value ? 1 : 0)
      // 次回のライン作成時に使用するためにstoreに保存 + localStorage
      const calcValue = value ? 1 : 0
      store.state.currentLineCalcCheck = calcValue
      try { localStorage.setItem('oh3.currentLineCalcCheck', calcValue) } catch (_) {}
    },
    onUpdateKeiko: (id, value) => {
      // 蛍光ペンチェックボックス（フリーハンド用）
      updateFeatureProperty(id, 'keiko', value ? 1 : 0)
    },
    onUpdateLabelPosition: (id, value) => {
      updateFeatureProperty(id, 'labelType', value)
      // 次回のライン作成時に使用するためにstoreに保存
      store.state.currentLineLabelType = value
    },
    onLineOutput: (id) => {
      // 各種出力ボタンの処理（HTML版と同じ実装）
      const map = store.state.map01
      const source = map.getSource('click-circle-source')
      const geojson = getSourceData(source)
      if (!geojson || !geojson.features) return
      const tgtFeature = geojson.features.find((feature) => {
        return feature.properties.id === id
      })
      if (!tgtFeature) return
      store.state.tgtFeature = tgtFeature
      store.state.pointSima = true
    },
    onUpdatePolygonColor: (id, value) => {
      updateFeatureProperty(id, 'colorName', value)
      // 次回のポリゴン作成時に使用するためにstoreに保存
      store.state.currentPolygonColor = value
      // 色名からRGBA値を生成
      const colorMap = {
        red: 'rgba(255,0,0,0.6)',
        blue: 'rgba(0,0,255,0.6)',
        green: 'rgba(0,255,0,0.6)',
        orange: 'rgba(255,165,0,0.6)',
        hotpink: 'rgba(255,105,180,0.6)',
        yellow: 'rgba(255,255,0,0.6)',
        black: 'rgba(0,0,0,0.6)',
        'rgba(0,0,0,0)': 'rgba(0,0,0,0)',
      }
      // HEXカラーの場合はRGBAに変換
      let rgbaColor
      if (value.startsWith('#')) {
        const hex = value.replace('#', '')
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)
        rgbaColor = `rgba(${r},${g},${b},0.6)`
      } else {
        rgbaColor = colorMap[value] || colorMap['blue']
      }
      updateFeatureProperty(id, 'color', rgbaColor)
    },
    onUpdateShowArea: (id, value) => {
      updateFeatureProperty(id, 'isArea', value)
      // 次回のポリゴン作成時に使用するためにstoreに保存 + localStorage
      store.state.currentPolygonAreaCheck = value
      try { localStorage.setItem('oh3.currentPolygonAreaCheck', value) } catch (_) {}
    },
    onDelete: (id) => {
      deleteFeature(id)
      clearFeatureInfo()
      clearSelectedFeature(map)
      if (popupInstance) popupInstance.remove()
    },
    onDeleteOthers: (id) => {
      deleteOtherFeatures(id)
    },
    onOpenLongText: (id) => {
      // 長文ダイアログを開く処理
      // TODO: 長文ダイアログを開く実装
    },
    onOpenPicture: (id) => {
      // 画像・動画アップロードダイアログを開く処理
      // TODO: 画像アップロードダイアログを開く実装
    },
  })

  // Vuetifyを使用
  app.use(vuetify)

  // マウント
  app.mount(container)

  // ポップアップにコンテナを設定して表示
  popupInstance.setDOMContent(container).addTo(map)

  // クローズ時の処理
  popupInstance.on('close', () => {
    app.unmount()
    lavelUpdate(null, feature.properties.id)
  })

  // mapNameがない場合はmap01をデフォルトとする
  const targetMapName = mapName || 'map01'
  if (popups[targetMapName]) {
    popups[targetMapName].push(popupInstance)
  }

  const popupEl = popupInstance.getElement()
  setTimeout(() => {
    popupEl.style.zIndex = getNextZIndex()
  }, 100)

  popupEl.addEventListener('click', (e) => {
    e.currentTarget.style.zIndex = getNextZIndex()
  })

  if (isSmartPhone) {
    requestAnimationFrame(() => {
      const h = popupInstance.getElement().offsetHeight
      const screen = map.getCanvas().getBoundingClientRect()
      const lngLat = map.unproject([screen.width / 2, screen.height / 2 + h / 2])
      popupInstance.setLngLat(lngLat)
    })
  }

  return popupInstance
}

/**
 * 地物のプロパティを更新
 */
function updateFeatureProperty(id, property, value) {
  const geojson = JSON.parse(store.state.clickCircleGeojsonText)
  const feature = geojson.features.find((f) => f.properties.id === id)
  if (feature) {
    feature.properties[property] = value
    // offsetValueは必ず配列にする（文字列だとMapLibreのlookupSymbolFeaturesが壊れる）
    if (property === 'offsetValue' && !Array.isArray(feature.properties.offsetValue)) {
      try {
        const parsed = JSON.parse(feature.properties.offsetValue)
        feature.properties.offsetValue = Array.isArray(parsed) ? parsed : [0, 0]
      } catch (_) {
        feature.properties.offsetValue = [0, 0]
      }
    }
    store.state.clickCircleGeojsonText = JSON.stringify(geojson)
    const map = store.state.map01
    if (map && map.getSource('click-circle-source')) {
      map.getSource('click-circle-source').setData(geojson)
    }

    const isLineString = feature.geometry.type === 'LineString'

    // 矢印タイプ、ラベル位置、距離計測、色が変わった時はエンドポイントを再生成
    if (property === 'arrow-type' || property === 'labelType' || property === 'calc' || property === 'color') {
      generateStartEndPointsFromGeoJSON(geojson)
    }
    // LineStringのラベルテキストが変わった時もエンドポイントを再生成（HTML版と同じ動作）
    if (property === 'label' && isLineString) {
      generateStartEndPointsFromGeoJSON(geojson)
    }
    // 距離計測が変わった時は距離ラベルを再生成
    if (property === 'calc') {
      generateSegmentLabelGeoJSON(geojson)
    }
    // 面積表示が変わった時はポリゴン辺ラベルを再生成
    if (property === 'isArea') {
      updatePolygonEdgeLabels(geojson)
    }
    // pyramid.jsのupdateを呼び出す
    if (typeof lavelUpdate === 'function') {
      lavelUpdate(property, id)
    }

    // AIマーカーのラベル変更を検知してstoreに通知
    if (property === 'label' && feature.properties.aiGenerated) {
      const markerNumber = feature.properties.aiMarkerNumber
      store.state.aiMarkerLabelChanged = {
        markerNumber,
        newLabel: value,
        timestamp: Date.now()
      }
    }
  }
}

/**
 * 地物を削除
 */
function deleteFeature(id) {
  const geojson = JSON.parse(store.state.clickCircleGeojsonText)
  geojson.features = geojson.features.filter((f) => f.properties.id !== id)
  store.state.clickCircleGeojsonText = JSON.stringify(geojson)
  const map = store.state.map01
  if (map && map.getSource('click-circle-source')) {
    map.getSource('click-circle-source').setData(geojson)
    // 矢印のエンドポイントも更新
    generateStartEndPointsFromGeoJSON(geojson)
    // 確定ポリゴンの辺ラベルを更新
    updatePolygonEdgeLabels(geojson)
  }
}

/**
 * 指定したID以外の地物を削除
 */
function deleteOtherFeatures(id) {
  const geojson = JSON.parse(store.state.clickCircleGeojsonText)
  geojson.features = geojson.features.filter((f) => f.properties.id === id)
  store.state.clickCircleGeojsonText = JSON.stringify(geojson)
  const map = store.state.map01
  if (map && map.getSource('click-circle-source')) {
    map.getSource('click-circle-source').setData(geojson)
    // 矢印のエンドポイントも更新
    generateStartEndPointsFromGeoJSON(geojson)
    // 確定ポリゴンの辺ラベルを更新
    updatePolygonEdgeLabels(geojson)
  }
  // すべてのポップアップを閉じる（全マップ対象）
  closeAllPopups()
}

export function popup(e, map, mapName, mapFlg, isNoDrawer) {
  if (store.state.editEnabled) return
  // 標高図作成モード中はポップアップを開かない
  if (store.state.elevationDrawMode) {
    return
  }
  // ハザード域内人口分析が有効な場合、独自ポップアップを使うため既存ポップアップを抑制
  if (store.state.hazardPopActive?.[mapName]) {
    return
  }
  // メッシュ人口分析の描画中はポップアップを抑制
  if (store.state.meshPopActive?.[mapName]) {
    return
  }
  let html = ref('') // ← ここを ref 化
  watch(html, (newVal, oldVal) => {
    // ポップアップ位置はクリック地点、Street View等の座標は地物座標を使う
    createPopup(map, [e.lngLat.lng, e.lngLat.lat], html.value, mapName, undefined, coordinates)
    // alert(999)
  })
  const lngLat = e.lngLat
  const point = map.project(lngLat)
  let features = []
  try {
    // ラインなどの細い要素も検出しやすいように、tolerance（許容範囲）を10ピクセルに設定
    features = map.queryRenderedFeatures(point, { tolerance: 10 })
  } catch (err) {
    console.warn('[popup] queryRenderedFeatures failed', err)
    return
  }
  // let features = map.queryRenderedFeatures(e.point); // クリック位置のフィーチャーを全て取得

  // N13道路はtolerance付きポイント検索で先頭に追加（線が細くてもクリックしやすく）
  const n13LayerIds = ['oh-n13-road-major-layer', 'oh-n13-road-major-narrow-layer', 'oh-n13-road-major-label', 'oh-n13-road-minor-layer', 'oh-n13-road-minor-narrow-layer', 'oh-n13-road-minor-label']
  {
    const existingN13Layers = n13LayerIds.filter(id => map.getLayer(id))
    if (existingN13Layers.length > 0) {
      const n13Features = queryFeaturesWithHitRadius(map, point, existingN13Layers)
      if (n13Features.length > 0) {
        const nonN13 = features.filter(f => !n13LayerIds.includes(f.layer?.id))
        features = [...n13Features, ...nonN13]
      }
    }
  }

  // 流域内河川は広範囲で追加検索（クリックしやすくする）
  const watershedRiverLayerIds = ['watershed-clipped-rivers', 'watershed-filtered-rivers']
  const hasWatershedRiver = features.some(f => watershedRiverLayerIds.includes(f.layer?.id))
  if (!hasWatershedRiver) {
    // 流域河川が見つからなければ、30pxの範囲で追加検索
    const hitRadius = 30
    const bbox = [
      [point.x - hitRadius, point.y - hitRadius],
      [point.x + hitRadius, point.y + hitRadius]
    ]
    // 存在するレイヤーだけフィルタ
    const existingLayers = watershedRiverLayerIds.filter(id => map.getLayer(id))
    if (existingLayers.length > 0) {
      const additionalFeatures = map.queryRenderedFeatures(bbox, { layers: existingLayers })
      if (additionalFeatures.length > 0) {
        features = [...additionalFeatures, ...features]
      }
    }
  }

  if (features.length > 20) {
    features = features.slice(0, 20)
  }

  // 2025登記所地図と全国地番図公開マップ（都市地番図）が重なった場合、全国地番図公開マップを優先
  const hasChibanzuLayer = features.some(f => {
    const id = f.layer?.id;
    return id === 'oh-city-geojson-poligon-layer' ||
      id === 'oh-city-pmtiles-poligon-layer' ||
      id?.startsWith('oh-chibanzu-') ||
      (id?.startsWith('oh-chiban-') && id?.endsWith('-layer')); // oh-chiban-1411-福岡県-大牟田市-layer形式
  })
  const hasHomusyo2025Layer = features.some(f => f.layer?.id === 'oh-homusyo-2025-polygon')
  if (hasChibanzuLayer && hasHomusyo2025Layer) {
    // 2025登記所地図を除外して全国地番図公開マップを優先
    features = features.filter(f => f.layer?.id !== 'oh-homusyo-2025-polygon')
  }

  const drawLayerIds = [
    'arrows-endpoint-label-layer',
    'click-circle-symbol-layer',
    'click-circle-line-layer',
    'click-circle-keiko-line-layer',
    'click-circle-memo-mark-layer',
    'click-circle-label-layer',
    'click-circle-layer',
    'click-circle-polygon-symbol-layer',
    'click-circle-polygon-symbol-area-layer',
    'segment-label-layer',
  ]
  /**
   * バグ回避
   */

  if (store.state.isDraw) {
    if (features[0]?.layer?.id === 'zones-layer') {
      if (isRePopup === true) {
        forseMoveLayer(map)
        isRePopup = false
        popup(e, map, mapName, mapFlg)
      }
    }
  }

  // クリックした地物の中からレイヤーIDが"oh-amx-a-fude"かつポリゴンを探す
  let featureIndex
  const polygonFeature = features.find((feature, index) => {
    featureIndex = index
    return (
      feature.layer.id === 'oh-amx-a-fude' &&
      (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon')
    )
  })
  if (polygonFeature) {
    const chiban = features[featureIndex].properties.地番
  }

  let PolygonMetrics
  if (polygonFeature) {
    // Turf.jsのGeoJSONフォーマットに変換
    const polygon = {
      type: 'Feature',
      geometry: polygonFeature.geometry,
      properties: {},
    }
    // 面積と周長を計算
    PolygonMetrics = calculatePolygonMetrics(polygon)
  } else {
  }

  // クリック地物の座標を取得（ラベル等をクリックした場合でも地物の中心座標を使う）
  let coordinates
  if (features.length > 0) {
    const geom = features[0].geometry
    if (geom.type === 'Point') {
      coordinates = geom.coordinates.slice()
    } else {
      // Polygon/MultiPolygon/LineString等 → centroidで中心座標を取得
      try {
        const center = turf.centroid(features[0])
        coordinates = center.geometry.coordinates
      } catch (_e) {
        coordinates = [e.lngLat.lng, e.lngLat.lat]
      }
    }
  } else {
    coordinates = [e.lngLat.lng, e.lngLat.lat]
  }

  if (map.getLayer('oh-point-layer')) {
    store.state.clickedCoordinates = coordinates
    return
  }

  let regionType = ''
  let isBreak = false
  let homusyo2025Handled = false // 登記所地図の重複処理防止

  // 警察署ハイライトをクリア（警察署がクリックされていない場合のみ）
  const hasKeisatsuFeature = features.some(f => f.layer.id.startsWith('oh-keisatsu'))
  if (!hasKeisatsuFeature && map.getLayer('oh-keisatsu-polygon')) {
    map.setPaintProperty('oh-keisatsu-polygon', 'fill-color', 'rgba(0, 100, 200, 0.15)')
    if (map.getLayer('oh-keisatsu-polygon-line')) {
      map.setPaintProperty('oh-keisatsu-polygon-line', 'line-color', 'rgba(0, 100, 200, 0.8)')
      map.setPaintProperty('oh-keisatsu-polygon-line', 'line-width', 1.5)
    }
  }

  // 消防署ハイライトをクリア（消防署がクリックされていない場合のみ）
  const hasShoboshoFeature = features.some(f => f.layer.id.startsWith('oh-shobosho'))
  if (!hasShoboshoFeature && map.getLayer('oh-shobosho-polygon')) {
    map.setPaintProperty('oh-shobosho-polygon', 'fill-color', 'rgba(255, 87, 34, 0.15)')
    if (map.getLayer('oh-shobosho-polygon-line')) {
      map.setPaintProperty('oh-shobosho-polygon-line', 'line-color', 'rgba(255, 87, 34, 0.8)')
      map.setPaintProperty('oh-shobosho-polygon-line', 'line-width', 1.5)
    }
  }

  // 上水道給水区域ハイライトをクリア
  const hasJosuidouFeature = features.some(f => f.layer.id.startsWith('oh-josuidou-area'))
  if (!hasJosuidouFeature && map.getLayer('oh-josuidou-area')) {
    map.setPaintProperty('oh-josuidou-area', 'fill-color', [
      'case',
      ['==', ['get', 'P21A_003'], 1], 'rgba(30, 136, 229, 0.15)',
      ['==', ['get', 'P21A_003'], 2], 'rgba(67, 160, 71, 0.15)',
      ['==', ['get', 'P21A_003'], 3], 'rgba(255, 152, 0, 0.15)',
      'rgba(30, 136, 229, 0.15)',
    ])
    if (map.getLayer('oh-josuidou-area-line')) {
      map.setPaintProperty('oh-josuidou-area-line', 'line-color', [
        'case',
        ['==', ['get', 'P21A_003'], 1], 'rgba(30, 136, 229, 0.8)',
        ['==', ['get', 'P21A_003'], 2], 'rgba(67, 160, 71, 0.8)',
        ['==', ['get', 'P21A_003'], 3], 'rgba(255, 152, 0, 0.8)',
        'rgba(30, 136, 229, 0.8)',
      ])
      map.setPaintProperty('oh-josuidou-area-line', 'line-width', 1.5)
    }
  }

  for (const feature of features) {
    // features.forEach(feature => {
    const layerId = feature.layer.id
    let props = feature.properties
    const styleLayers = map.getStyle()?.layers ?? []
    if (store.state.isKuiuchi) return
    /**
     * ドロー時はドローレイヤー以外のポップアップを無効化する。
     */
    if (store.state.isDraw) {
      const result = drawLayerIds.includes(layerId)
      if (!result) return
    }
    /**
     * 標高図作成モード中は、描画レイヤーのクリックでもポップアップを開かない
     */
    if (store.state.elevationDrawMode) {
      return
    }
    /**
     * マイレイヤー（pmtiles）のポップアップ処理
     * ポイントレイヤーとラベルレイヤーの両方に対応
     */
    if (layerId.startsWith('oh-pmtiles-') && (layerId.endsWith('-point-layer') || layerId.endsWith('-label-layer'))) {
      // プロパティをHTML表示用に整形
      let html0 = '<div class="layer-label-div">マイレイヤー</div>'
      html0 += '<div class="popup-base">'

      // labelプロパティがあれば大きく表示
      if (props.label) {
        html0 += `<span style="font-size: 20px; font-weight: bold;">${props.label}</span><hr>`
      }

      // その他のプロパティを表示（内部プロパティは除外）
      const excludeKeys = ['label', 'id', '_id', 'layer', 'source', 'sourceLayer']
      for (const [key, value] of Object.entries(props)) {
        if (excludeKeys.includes(key)) continue
        if (value === null || value === undefined || value === '') continue
        html0 += `<span style="font-size: 14px;"><strong>${key}</strong>: ${value}</span><br>`
      }
      html0 += '</div>'

      if (html.value.indexOf('oh-pmtiles-') === -1) {
        html.value += html0
      }
      isBreak = true
      continue // このフィーチャーの処理を終了し、ポップアップ表示へ
    }
    /**
     * 地籍調査進捗率v2のポップアップ処理
     */
    if (layerId === 'oh-chiseki-v2-fill' && html.value.indexOf('chiseki-v2-popup') === -1) {
      const p = props
      const progress = p.progress || 0
      const name = p.name || '不明'
      const pref = p.pref || ''
      const code = p.code || ''

      // 進捗率に応じた色
      let color = '#f44336'
      if (progress >= 80) color = '#1a6b1a'
      else if (progress >= 60) color = '#4caf50'
      else if (progress >= 40) color = '#8bc34a'
      else if (progress >= 20) color = '#ffeb3b'
      else if (progress >= 1) color = '#ff9800'
      const textColor = (progress >= 20 && progress < 40) ? '#333' : '#fff'

      html.value += '<div class="layer-label-div">地籍調査進捗率（R6年度末）</div>'
      html.value += '<div class="chiseki-v2-popup popup-body" style="padding: 8px 12px;">'
      html.value += `<div style="font-size: 12px; color: #666;">${pref}</div>`
      html.value += `<div style="font-size: 18px; font-weight: bold; margin: 2px 0 6px;">${name}</div>`
      html.value += `<div style="display: inline-block; padding: 3px 12px; border-radius: 12px; background: ${color}; color: ${textColor}; font-size: 16px; font-weight: 600;">進捗率 ${progress}%</div>`
      html.value += '</div>'

      // ハイライト: 対象市区町村のポリゴン枠を強調
      if (code && map.getSource('chiseki-v2-source')) {
        if (!map.getLayer('oh-chiseki-v2-highlight')) {
          map.addLayer({
            id: 'oh-chiseki-v2-highlight',
            type: 'line',
            source: 'chiseki-v2-source',
            paint: {
              'line-color': '#ff0000',
              'line-width': 3,
              'line-opacity': 0.9,
            },
            filter: ['==', ['get', 'code'], ''],
          })
        }
        map.setFilter('oh-chiseki-v2-highlight', ['==', ['get', 'code'], code])
      }

      isBreak = true
      continue
    }
    /**
     * 産総研5万分の1地質図（動的レイヤー）のポップアップ処理
     * レイヤーIDパターン: oh-gsj-{mapCode}-{originalLayerId}
     */
    if (layerId.startsWith('oh-gsj-') && html.value.indexOf('gsj-geology-popup') === -1) {
      // MAJOR_CODE → 地質種別名
      const gsjMajorNames = {
        1: '沖積層', 2: '段丘堆積物', 3: '第四紀火山岩類', 4: '鮮新世堆積岩',
        5: '鮮新世火山岩', 6: '中新世堆積岩', 7: '中新世火山岩', 8: '古第三紀堆積岩',
        9: '古第三紀火山岩', 10: '白亜紀堆積岩', 11: '白亜紀火山岩',
        12: '白亜紀深成岩', 13: 'ジュラ紀付加体', 14: '古生代堆積岩',
        15: '古生代火山岩', 16: '変成岩（高圧型）', 17: '変成岩（低圧型）',
        18: '超苦鉄質岩', 19: '苦鉄質深成岩', 20: '珪長質深成岩',
        21: '花崗岩類', 22: '深成岩（未区分）', 23: '先カンブリア代',
        24: '蛇紋岩', 25: '付加体（未区分）', 26: '堆積岩（未区分）',
        27: '火山岩（未区分）', 88: '水域'
      }

      // まずクリック地点のoh-gsj-*レイヤーを全て検索
      const allLayers = map.getStyle()?.layers || []
      const gsjLayers = allLayers
        .filter(l => l.id.startsWith('oh-gsj-'))
        .map(l => l.id)

      if (gsjLayers.length > 0) {
        const gsjFeatures = map.queryRenderedFeatures(map.project(coordinates), { layers: gsjLayers })
        if (gsjFeatures.length > 0) {
          // fillレイヤー(geo_A)を優先、なければ最初のフィーチャーを使う
          const fillFeature = gsjFeatures.find(f => f.layer?.id?.endsWith('-geo_A'))
          const feat = fillFeature || gsjFeatures[0]
          const gp = feat.properties || {}
          const geoSymbol = gp['SYMBOL'] || ''
          const majorCode = gp['MAJOR_CODE'] || 0
          const majorName = gsjMajorNames[majorCode] || ''
          // LEGENDフィールドを収集（LEGEND01〜LEGEND06）
          const legends = []
          for (let i = 1; i <= 6; i++) {
            const key = 'LEGEND0' + i
            if (gp[key]) legends.push(gp[key])
          }
          // メタデータから図幅名を取得
          const gsjMeta = feat.layer?.metadata || {}
          const gsjMapName = gsjMeta['oh3:map-name'] || ''
          const gsjMapCode = gsjMeta['oh3:geology-map'] || ''

          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId('oh-gsj-geology-map01', store.state.selectedLayers) + '</div>'

          let popupContent = '<div class="gsj-geology-popup popup-body">'

          if (geoSymbol) {
            popupContent +=
              '<span style="font-size: 20px; font-weight: bold;">' + geoSymbol + '</span>'
          }
          if (majorName) {
            popupContent +=
              '<span style="font-size: 12px; color: #666; margin-left: 6px;">' + majorName + '</span>'
          }
          if (geoSymbol || majorName) popupContent += '<br>'

          // 凡例テキスト表示
          legends.forEach((legend, idx) => {
            const size = idx === 0 ? 13 : 12
            const color = idx === 0 ? 'var(--oh3-color-text-primary)' : '#555'
            popupContent += '<span style="font-size: ' + size + 'px; color: ' + color + ';">' + legend + '</span><br>'
          })

          // SYMBOL/LEGENDどちらもない場合は全プロパティを表示
          if (!geoSymbol && legends.length === 0) {
            for (const [key, value] of Object.entries(gp)) {
              if (value === null || value === undefined || value === '') continue
              popupContent += '<span style="font-size: 12px;"><b>' + key + ':</b> ' + value + '</span><br>'
            }
          }

          if (gsjMapName) {
            popupContent += '<span style="font-size: 11px; color: #999;">図幅: ' + gsjMapName + ' (' + gsjMapCode + ')</span>'
          }

          popupContent += '</div>'
          html.value += popupContent

          isBreak = true
          continue
        }
      }
    }
    // oh-sima-*のポイント/頂点/ポリゴンレイヤーを正規化
    let switchLayerId = layerId
    if (layerId.includes('oh-sima-') && (layerId.endsWith('-point-layer') || layerId.endsWith('-vertex-layer') || layerId.endsWith('-point-label-layer') || layerId.endsWith('-elevation-label-layer'))) {
      switchLayerId = 'sima-points'
    } else if (layerId.includes('oh-sima-') && (layerId.endsWith('-polygon-layer') || layerId.endsWith('-polygon-label-layer'))) {
      switchLayerId = 'sima-polygon'
    }
    switch (switchLayerId) {
      case 'oh-overture-buildings-fill':
      case 'oh-overture-buildings-line': {
        features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-overture-buildings-fill'] })
        if (features.length === 0) break
        props = features[0].properties
        const bldgHeight = props['height'] != null ? props['height'] + ' m' : '-'
        const bldgFloors = props['num_floors'] || '-'
        const bldgClass = props['class'] || '-'
        const bldgSubtype = props['subtype'] || '-'
        // namesはJSON文字列の場合があるので処理
        let bldgName = '-'
        if (props['names']) {
          try {
            const namesObj = typeof props['names'] === 'string' ? JSON.parse(props['names']) : props['names']
            bldgName = namesObj.primary || namesObj.common || (Array.isArray(namesObj) ? namesObj[0] : '-')
          } catch { bldgName = String(props['names']) }
        }
        if (html.value.indexOf('overture-buildings') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId('oh-overture-buildings-fill', store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="overture-buildings popup-body">' +
            '<span style="font-size: 16px; font-weight: bold;">' + (bldgName !== '-' ? bldgName : '建物') + '</span><br>' +
            '<span style="font-size: 13px;"><b>高さ:</b> ' + bldgHeight + '</span><br>' +
            '<span style="font-size: 13px;"><b>階数:</b> ' + bldgFloors + '</span><br>' +
            '<span style="font-size: 13px;"><b>分類:</b> ' + bldgSubtype + '</span><br>' +
            '<span style="font-size: 13px;"><b>クラス:</b> ' + bldgClass + '</span>' +
            '</div>'
        }
        isBreak = true
        break
      }
      case 'oh-jshis-fault-fill':
      case 'oh-jshis-fault-line':
      case 'oh-jshis-fault-label': {
        features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-jshis-fault-fill'] })
        if (features.length === 0) break
        props = features[0].properties
        const faultName = props['LTENAME'] || '不明'
        const faultMagNum = props['MAG_NUM'] || 0
        const faultMag = faultMagNum > 0 ? 'M' + faultMagNum : '-'
        const faultT30pNum = props['T30P_NUM'] || 0
        const faultProb = faultT30pNum > 0 ? (faultT30pNum < 0.01 ? '< 0.01%' : faultT30pNum.toFixed(faultT30pNum >= 1 ? 1 : faultT30pNum >= 0.1 ? 2 : 3) + '%') : '不明'
        const faultLen = props['len'] ? props['len'] + ' km' : '-'
        const faultWid = props['wid'] ? props['wid'] + ' km' : '-'
        const faultDep = props['dep'] ? props['dep'] + ' km' : '-'
        if (html.value.indexOf('jshis-fault') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId('oh-jshis-fault-fill', store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="jshis-fault popup-body">' +
            '<span style="font-size: 18px; color: #cc0000; font-weight: bold;">' + faultName + '</span><br>' +
            '<span style="font-size: 13px;"><b>マグニチュード:</b> ' + faultMag + '</span><br>' +
            '<span style="font-size: 13px;"><b>30年発生確率:</b> ' + faultProb + '</span><br>' +
            '<span style="font-size: 13px;"><b>断層面の長さ:</b> ' + faultLen + '</span><br>' +
            '<span style="font-size: 13px;"><b>断層面の幅:</b> ' + faultWid + '</span><br>' +
            '<span style="font-size: 13px;"><b>上端深さ:</b> ' + faultDep + '</span>' +
            '</div>'
        }
        isBreak = true
        break
      }
      case 'oh-tokyoGreenPmtiles': {
        features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-tokyoGreenPmtiles'] })
        if (features.length === 0) return
        props = features[0].properties
        // 名称を優先順位で取得
        const greenName = props['名称'] || props['公園名'] || props['施設名称'] || props['住宅名称'] || props['協定名'] || props['地区計画名'] || '緑地'
        const greenCategory = props['_category_label'] || ''
        const greenCategoryId = props['_category'] || ''
        const greenColor = props['_color'] || '#228B22'
        // 属性を動的に構築
        const addProp = (label, key, suffix = '') => {
          const val = props[key]
          if (val && val !== '-' && val !== '0' && val !== 0) {
            return '<span style="font-size: 12px;"><b>' + label + ':</b> ' + val + suffix + '</span><br>'
          }
          return ''
        }
        const addPropNum = (label, key, suffix = '') => {
          const val = props[key]
          if (val && val !== 0) {
            return '<span style="font-size: 12px;"><b>' + label + ':</b> ' + Number(val).toLocaleString() + suffix + '</span><br>'
          }
          return ''
        }
        if (html.value.indexOf('tokyoGreenPmtiles') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="tokyoGreenPmtiles popup-body" style="max-width: 320px;">' +
            '<span style="font-size: 18px; color: #228B22; font-weight: bold;">' + greenName + '</span><br>' +
            '<span onclick="window.toggleGreenCategory(\'' + greenCategoryId + '\')" ' +
            'onmouseenter="this.style.transform=\'scale(1.05)\';this.style.boxShadow=\'0 2px 6px rgba(0,0,0,0.3)\'" ' +
            'onmouseleave="this.style.transform=\'scale(1)\';this.style.boxShadow=\'0 1px 2px rgba(0,0,0,0.2)\'" ' +
            'onmousedown="this.style.transform=\'scale(0.95)\'" ' +
            'onmouseup="this.style.transform=\'scale(1.05)\'" ' +
            'style="display: inline-block; padding: 3px 10px; margin: 4px 0; border-radius: 12px; ' +
            'background: ' + greenColor + '; color: white; font-size: 11px; cursor: pointer; ' +
            'font-weight: 500; box-shadow: 0 1px 2px rgba(0,0,0,0.2); transition: all 0.15s ease; ' +
            'user-select: none;">' +
            greenCategory + '</span><hr>' +
            // 基本情報
            addProp('区分', '区分') +
            addProp('区市町村', '区市町村') +
            addProp('位置', '位置') +
            addProp('所在地', '所在地') +
            // 面積関連
            addPropNum('面積', '面積m2', '㎡') +
            addPropNum('面積', '面積ha', 'ha') +
            addPropNum('計画面積', '計画面積m2', '㎡') +
            addPropNum('計画面積', '計画面積ha', 'ha') +
            // 日付関連
            addProp('開園', '開園年月日') +
            addProp('設置', '設置年月日') +
            addProp('指定', '指定年月日') +
            addProp('当初指定', '当初指定日') +
            addProp('締結', '締結年月日') +
            addProp('開設', '開設年月日') +
            // 根拠・指定関連
            addProp('指定根拠', '指定根拠') +
            addProp('設置等根拠', '設置等根拠') +
            addProp('適用根拠', '適用根拠') +
            // 管理・所管
            addProp('所管', '所管') +
            addProp('管理主体', '管理主体') +
            addProp('設置主体', '設置主体') +
            addProp('管理者', '管理者') +
            // 街路樹関連
            addProp('樹種', '樹種') +
            addProp('本数', '本数') +
            addProp('路線名', '路線名') +
            // 公園関連
            addProp('種別', '種別') +
            addProp('公園種別', '公園種別') +
            addProp('制度種別', '制度種別') +
            addProp('施設区分', '施設区分') +
            // 備考
            (props['備考'] && props['備考'] !== '-' ? '<span style="font-size: 10px; color: #888;"><b>備考:</b> ' + props['備考'] + '</span>' : '') +
            '</div>'
        }
        break
      }
      case 'oh-zosei-line':
      case 'oh-zosei-label':
      case 'oh-zosei': {
        features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-zosei'] })
        if (features.length === 0) return
        props = features[0].properties
        let name
        if (props.A54_001 === '1') {
          name = '谷埋め型'
        } else if (props.A54_001 === '2') {
          name = '腹付け型'
        } else if (props.A54_001 === '9') {
          name = '区分をしていない'
        }
        const syozai = props.A54_003 + props.A54_005
        const bango = props.A54_006
        if (html.value.indexOf('zosei') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="zosei popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            syozai +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            bango +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-mw5-center':
      case 'oh-mw5-xyz-center':
      case 'oh-blend-mw5-hillshade-center': {
        // ポップアップを出さず直接FWを開く
        window.openMw5Preview(props.id)
        break
      }
      case 'oh-tetsudo-blue-lines':
      case 'oh-tetsudo-red-lines': {
        const name = props.N05_002
        const kaisya = props.N05_003
        if (html.value.indexOf('tetsudo-red-lines') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="tetsudo-red-lines popup-body">' +
            '<span style="font-size: 12px;">運営会社=' +
            kaisya +
            '</span><hr>' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-tetsudo-points-blue':
      case 'oh-tetsudo-points-red': {
        const name = props.N05_002
        const kaisya = props.N05_003
        const eki = props.N05_011
        if (html.value.indexOf('tetsudo-points-red') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="tetsudo-points-red popup-body">' +
            '<span style="font-size: 12px;">運営会社=' +
            kaisya +
            '</span><hr>' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 20px;">' +
            eki +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-n13-road-major-label':
      case 'oh-n13-road-major-layer':
      case 'oh-n13-road-major-narrow-layer':
      case 'oh-n13-road-minor-label':
      case 'oh-n13-road-minor-layer':
      case 'oh-n13-road-minor-narrow-layer': {
        const n13AllLayers = ['oh-n13-road-major-layer', 'oh-n13-road-major-narrow-layer', 'oh-n13-road-major-label', 'oh-n13-road-minor-layer', 'oh-n13-road-minor-narrow-layer', 'oh-n13-road-minor-label'].filter(id => map.getLayer(id))
        if (!props?.N13_003 && !props?.N13_006) {
          const n13Features = queryFeaturesWithHitRadius(map, e.point, n13AllLayers)
          if (n13Features.length === 0) return
          props = n13Features[0].properties
        }
        const n13ClassMap = { '1': '国道', '2': '都道府県道', '3': '市区町村道等', '4': '高速自動車国道等', '5': 'その他', '6': '不明' }
        const n13TypeMap = { '1': '通常部', '2': '庭園路', '3': '徒歩道', '4': '石段', '5': '不明' }
        const n13StateMap = { '1': '通常部', '2': '橋・高架', '3': 'トンネル', '4': '雪覆い', '5': '建設中', '6': 'その他', '7': '不明' }
        const n13WidthMap = { '1': '3m未満', '2': '3m-5.5m未満', '3': '5.5m-13m未満', '4': '13m-19.5m未満', '5': '19.5m以上', '6': '不明' }
        const n13TollMap = { '1': '無料', '2': '有料' }
        const roadClass = n13ClassMap[String(props.N13_003)] || '不明'
        const roadType = n13TypeMap[String(props.N13_002)] || '不明'
        const roadState = n13StateMap[String(props.N13_004)] || '不明'
        const roadWidth = n13WidthMap[String(props.N13_006)] || '不明'
        const tollType = n13TollMap[String(props.N13_007)] || '不明'
        // 道路分類に応じた色
        const roadColorMap = { '高速自動車国道等': '#e53935', '国道': '#1565c0', '都道府県道': '#2e7d32', '市区町村道等': '#757575', 'その他': '#8d6e63' }
        const roadColor = roadColorMap[roadClass] || '#333'
        if (html.value.indexOf('n13-road') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="n13-road popup-body" style="max-width: 300px;">' +
            '<span style="font-size: 16px; font-weight: bold; color: ' + roadColor + ';">' + roadClass + '</span><br>' +
            '<span style="font-size: 12px;"><b>種別:</b> ' + roadType + '</span><br>' +
            '<span style="font-size: 12px;"><b>道路状態:</b> ' + roadState + '</span><br>' +
            '<span style="font-size: 12px;"><b>幅員:</b> ' + roadWidth + '</span><br>' +
            '<span style="font-size: 12px;"><b>有料区分:</b> ' + tollType + '</span><br>' +
            (props.N13_001 ? '<span style="font-size: 11px; color: #888;">データ登録日: ' + props.N13_001 + '</span><br>' : '') +
            '<span style="font-size: 11px; color: #888;">出典: 国土数値情報（道路）</span>' +
            '<div style="margin-top:6px;">' +
            '<span onclick="window.openRoadIsochrone(' + coordinates[0] + ',' + coordinates[1] + ')" ' +
            'style="display:inline-block; padding:4px 12px; border-radius:16px; background:#7B1FA2; color:white; font-size:12px; cursor:pointer; font-weight:500;">' +
            '🛣️ 道路到達圏</span></div>' +
            '</div>'
        }
        break
      }
      case 'oh-tetsudojikeiretsu-red-lines':
      case 'oh-tetsudojikeiretsu-blue-lines': {
        const name = props.N05_002
        const kaisya = props.N05_003
        if (html.value.indexOf('tetsudojikeiretsu-blue-lines') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="tetsudojikeiretsu-blue-lines popup-body">' +
            '<span style="font-size: 12px;">運営会社=' +
            kaisya +
            '</span><hr>' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-tetsudojikeiretsu-points': {
        const name = props.N05_002
        const kaisya = props.N05_003
        const eki = props.N05_011
        if (html.value.indexOf('tetsudojikeiretsu-points') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="tetsudojikeiretsu-points popup-body">' +
            '<span style="font-size: 12px;">運営会社=' +
            kaisya +
            '</span><hr>' +
            '<span style="font-size: 16px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 20px;">' +
            eki +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-michinoeki-label':
      case 'oh-michinoeki': {
        const name = props.P35_006
        const link = '<a href="' + props.P35_007 + '" target="_blank">道の駅ページへ</a>'
        if (html.value.indexOf('michinoeki') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="michinoeki popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '<div style="font-size: 14px;margin-top: 10px;">' +
            link +
            '</div>' +
            '</div>'
        }
        break
      }
      case 'oh-r07koji-label':
      case 'oh-r07koji-height':
      case 'oh-r07koji_point':
      case 'oh-koji-label':
      case 'oh-koji-height':
      case 'oh-koji_point': {
        const name = props.L01_025
        const kojikakaku = props.L01_008.toLocaleString() + '円'
        if (html.value.indexOf('koji_point') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="koji_point popup-body">' +
            '<span style="font-size: 16px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 20px;">' +
            kojikakaku +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-did': {
        const name = props.A16_003
        const jinko = props.A16_005.toLocaleString() + '人'
        if (html.value.indexOf('did') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="did popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 20px;">' +
            jinko +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-did-tl-s35': case 'oh-did-tl-s35_line':
      case 'oh-did-tl-s40': case 'oh-did-tl-s40_line':
      case 'oh-did-tl-s45': case 'oh-did-tl-s45_line':
      case 'oh-did-tl-s50': case 'oh-did-tl-s50_line':
      case 'oh-did-tl-s55': case 'oh-did-tl-s55_line':
      case 'oh-did-tl-s60': case 'oh-did-tl-s60_line':
      case 'oh-did-tl-h02': case 'oh-did-tl-h02_line':
      case 'oh-did-tl-h07': case 'oh-did-tl-h07_line':
      case 'oh-did-tl-h12': case 'oh-did-tl-h12_line':
      case 'oh-did-tl-h17': case 'oh-did-tl-h17_line':
      case 'oh-did-tl-h22': case 'oh-did-tl-h22_line':
      case 'oh-did-tl-h27': case 'oh-did-tl-h27_line':
      case 'oh-did-tl-r02': case 'oh-did-tl-r02_line': {
        const name = props['市町村名称'] || props.A16_003 || ''
        const jinkoRaw = props['人口'] ?? props.A16_005
        const jinko = jinkoRaw != null ? Number(jinkoRaw).toLocaleString() + '人' : ''
        const yearInfo = store.state.didTimeline?.currentYear
        const yearLabel = yearInfo ? yearInfo.label + '（' + yearInfo.western + '年）' : ''
        if (html.value.indexOf('did-timeline') === -1) {
          html.value +=
            '<div class="layer-label-div">DID人口集中地区（時系列）' + (yearLabel ? ' ' + yearLabel : '') + '</div>'
          html.value +=
            '<div class="did-timeline popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 20px;">' +
            jinko +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-gokaido':
      case 'oh-gokaido-label': {
        const name = props.Name || ''
        const displayName = name === '東海道2' ? '東海道' : name
        html.value +=
          '<div class="layer-label-div">五街道</div>' +
          '<div class="popup-body">' +
          '<span style="font-size: 20px;">' + displayName + '</span>' +
          '</div>'
        break
      }
      case 'oh-elevation3-mesh-fill':
      case 'oh-elevation3-mesh-line':
      case 'oh-elevation3-mesh-label':
      case 'oh-elevation3-mesh-height':
      case 'oh-elevation-mesh-fill':
      case 'oh-elevation-mesh-line':
      case 'oh-elevation-mesh-label':
      case 'oh-elevation-mesh-height': {
        const isElev3 = layerId.startsWith('oh-elevation3-')
        features = map.queryRenderedFeatures(map.project(coordinates), { layers: [isElev3 ? 'oh-elevation3-mesh-fill' : 'oh-elevation-mesh-fill'] })
        if (features.length === 0) return
        props = features[0].properties
        const elevDivClass = isElev3 ? 'elevation3-mesh' : 'elevation-mesh'
        if (html.value.indexOf(elevDivClass) === -1) {
          const avgElev = Number(props.avg_elev) || 0
          const maxElev = Number(props.max_elev) || 0
          const minElev = Number(props.min_elev) || 0
          const maxSlope = Number(props.max_slope) || 0
          const minSlope = Number(props.min_slope) || 0
          const avgSlope = Number(props.avg_slope) || 0
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="' + elevDivClass + ' popup-base-compact">' +
            '<span style="font-size: 16px; font-weight: bold;">標高</span><br>' +
            '<table style="font-size: 14px; border-collapse: collapse;">' +
            '<tr><td style="padding: 2px 8px;">平均</td><td style="padding: 2px 8px; font-weight: bold;">' + avgElev.toFixed(1) + ' m</td></tr>' +
            '<tr><td style="padding: 2px 8px;">最高</td><td style="padding: 2px 8px;">' + maxElev.toFixed(1) + ' m</td></tr>' +
            '<tr><td style="padding: 2px 8px;">最低</td><td style="padding: 2px 8px;">' + minElev.toFixed(1) + ' m</td></tr>' +
            '</table>' +
            '<span style="font-size: 16px; font-weight: bold;">傾斜度</span><br>' +
            '<table style="font-size: 14px; border-collapse: collapse;">' +
            '<tr><td style="padding: 2px 8px;">平均</td><td style="padding: 2px 8px; font-weight: bold;">' + avgSlope.toFixed(1) + '°</td></tr>' +
            '<tr><td style="padding: 2px 8px;">最大</td><td style="padding: 2px 8px;">' + maxSlope.toFixed(1) + '°</td></tr>' +
            '<tr><td style="padding: 2px 8px;">最小</td><td style="padding: 2px 8px;">' + minSlope.toFixed(1) + '°</td></tr>' +
            '</table>' +
            '<div style="font-size: 11px; color: #888; margin-top: 4px;">メッシュコード: ' + (props.mesh_code || '') + '</div>' +
            '</div>'
        }
        break
      }
      case 'oh-syochiiki-height':
      case 'oh-syochiiki-label':
      case 'oh-syochiiki-layer': {
        features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-syochiiki-layer'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.S_NAME
        if (html.value.indexOf('syochiiki-layer') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="syochiiki-layer popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 20px;">' +
            props.JINKO +
            '人</span>' +
            '<button class="pyramid-syochiiki-r02 pyramid-btn" year=2020 mapname="' +
            mapName +
            '" cdArea="' +
            props.KEY_CODE +
            '" syochiikiname="' +
            name +
            '">2020（R02）人口ピラミッド</button><br>' +
            '<button class="pyramid-syochiiki-h27 pyramid-btn" year=2015 mapname="' +
            mapName +
            '" cdArea="' +
            props.KEY_CODE +
            '" syochiikiname="' +
            name +
            '">2015（H27）人口ピラミッド</button><br>' +
            '<button class="pyramid-syochiiki-h22 pyramid-btn" year=2010 mapname="' +
            mapName +
            '" cdArea="' +
            props.KEY_CODE +
            '" syochiikiname="' +
            name +
            '">2010（H22）人口ピラミッド</button><br>' +
            '<button class="pyramid-syochiiki-h17 pyramid-btn" year=2010 mapname="' +
            mapName +
            '" cdArea="' +
            props.KEY_CODE +
            '" syochiikiname="' +
            name +
            '">2005（H17）人口ピラミッド</button><br>' +
            '<button style="margin-bottom: 10px;" class="jinkosuii pyramid-btn" mapname="' +
            mapName +
            '" cdArea="' +
            props.KEY_CODE +
            '" syochiikiname="' +
            name +
            '">人口推移</button>' +
            '</div>'
        }
        break
      }
      case 'oh-kyusekki': {
        let sekkiHtml = ''
        const sekki = [
          'ナイフ形石器',
          '台形（様）石器',
          '斧形石器',
          '剥片尖頭器',
          '角錐状石器・三稜尖頭器',
          '槍先形尖頭器',
          '両面調整石器',
          '細石刃・細石核等',
          '神子柴型石斧',
          '有茎（舌）尖頭器',
          '掻器・削器',
          '彫器',
          '砥石',
          '叩石',
          '台石',
          '礫器',
          'その他の石器',
          '草創期土器',
          'ブロック･ユニット',
          '礫群・配石',
          '炭化物集中',
          'その他の遺構',
          '特記事項',
        ]
        sekki.forEach((value) => {
          if (props[value]) {
            sekkiHtml = sekkiHtml + value + '=' + props[value] + '<br>'
          }
        })
        if (html.value.indexOf('kyusekki') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kyusekki" style=font-size:small;>' +
            '<h4>' +
            props.遺跡名 +
            '</h4>' +
            '読み方=' +
            props.遺跡名読み方 +
            '<br>' +
            '都道府県=' +
            props.都道府県 +
            '<br>' +
            '所在地=' +
            props.所在地 +
            '<br>' +
            '標高=' +
            props.標高 +
            '<br>' +
            '文献=' +
            props.文献 +
            '<br>' +
            '調査歴=' +
            props.調査歴 +
            '<br>' +
            '作成年月日=' +
            props.作成年月日 +
            '<br>' +
            '作成者=' +
            props.作成者 +
            '<br>' +
            sekkiHtml +
            '</div>'
        }
        break
      }
      case 'oh-iseki-p32':
      case 'oh-iseki-p32-heatmap':
      case 'oh-iseki-p32-label': {
        // 種別大区分コード変換
        const daikubunMap = {
          '1': '有形文化財',
          '2': '無形文化財',
          '3': '民俗文化財',
          '4': '記念物',
          '5': '文化的景観',
          '6': '伝統的建造物群',
          '7': '文化財の保存技術',
        }
        // 種別小区分コード変換
        const shokubunMap = {
          '11': '有形文化財',
          '21': '無形文化財',
          '31': '有形民俗文化財',
          '32': '無形民俗文化財',
          '41': '史跡（旧跡を含む）',
          '42': '名勝',
          '43': '天然記念物',
          '51': '重要文化的景観',
          '61': '伝統的建造物群保存地区',
          '71': '選定保存技術',
        }
        const daikubun = daikubunMap[props.P32_004] || props.P32_004 || '不明'
        const shokubun = shokubunMap[props.P32_005] || props.P32_005 || '不明'
        if (html.value.indexOf('iseki-p32') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="iseki-p32" style="font-size:small;">' +
            '<h4 style="margin:0 0 8px 0;font-size:16px;font-weight:600;">' + (props.P32_006 || '名称不明') + '</h4>' +
            '<div style="display:grid;grid-template-columns:auto 1fr;gap:2px 8px;">' +
            '<span style="color:#666;">種別大区分:</span><span>' + daikubun + '</span>' +
            '<span style="color:#666;">種別小区分:</span><span>' + shokubun + '</span>' +
            '<span style="color:#666;">所在地:</span><span>' + (props.P32_007 || '-') + '</span>' +
            '<span style="color:#666;">指定年月日:</span><span>' + (props.P32_008 || '-') + '</span>' +
            '</div>' +
            '</div>'
        }
        break
      }
      case 'oh-m125m-aging-height':
      case 'oh-m125m-aging-label':
      case 'oh-m125m-aging': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-m125m-aging'] })
        if (features.length === 0) return
        props = features[0].properties
        const jinko = Math.round(Number(props.jinko)) || 0
        const ronen = Math.round(Number(props.ronen)) || 0
        const agingRate = jinko > 0 ? ((ronen / jinko) * 100).toFixed(1) : '0.0'
        if (html.value.indexOf('m125m-aging') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="m125m-aging popup-body">' + buildMeshPopupHtml(jinko, ronen, agingRate) + '</div>'
        }
        break
      }
      case 'oh-m125m-height':
      case 'oh-m125m-label':
      case 'oh-m125m-line':
      case 'oh-m125m': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-m125m'] })
        if (features.length === 0) return
        props = features[0].properties
        const jinko = Math.round(Number(props.jinko)) || 0
        const ronen = Math.round(Number(props.ronen)) || 0
        const agingRate = jinko > 0 ? ((ronen / jinko) * 100).toFixed(1) : '0.0'
        if (html.value.indexOf('m125m') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="m125m popup-body">' + buildMeshPopupHtml(jinko, ronen, agingRate) + '</div>'
        }
        break
      }
      case 'oh-m250m-height':
      case 'oh-m250m-label':
      case 'oh-m250m-line':
      case 'oh-m250m': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-m250m'] })
        if (features.length === 0) return
        props = features[0].properties
        const jinko = Math.round(Number(props.jinko)) || 0
        const ronen = Math.round(Number(props.ronen)) || 0
        const agingRate = jinko > 0 ? ((ronen / jinko) * 100).toFixed(1) : '0.0'
        if (html.value.indexOf('m250m') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="m250m popup-body">' + buildMeshPopupHtml(jinko, ronen, agingRate) + '</div>'
        }
        break
      }
      case 'oh-m500m-height':
      case 'oh-m500m-label':
      case 'oh-m500m-line':
      case 'oh-m500m': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-m500m'] })
        if (features.length === 0) return
        props = features[0].properties
        const jinko = Math.round(Number(props.jinko)) || 0
        const ronen = Math.round(Number(props.ronen)) || 0
        const agingRate = jinko > 0 ? ((ronen / jinko) * 100).toFixed(1) : '0.0'
        if (html.value.indexOf('m500m') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="m500m popup-body">' + buildMeshPopupHtml(jinko, ronen, agingRate) + '</div>'
        }
        break
      }
      case 'oh-m1km-height':
      case 'oh-m1km-label':
      case 'oh-m1km-line':
      case 'oh-m1km': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-m1km'] })
        if (features.length === 0) return
        props = features[0].properties
        const jinko = Math.round(Number(props.jinko)) || 0
        const ronen = Math.round(Number(props.ronen)) || 0
        const agingRate = jinko > 0 ? ((ronen / jinko) * 100).toFixed(1) : '0.0'
        if (html.value.indexOf('m1km') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="m1km popup-body">' + buildMeshPopupHtml(jinko, ronen, agingRate) + '</div>'
        }
        break
      }
      case 'oh-m100m-height':
      case 'oh-m100m-label':
      case 'oh-m100m-line':
      case 'oh-m100m': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-m100m'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = '人口' + Math.round(Number(props.PopT)) + '人'
        if (html.value.indexOf('m100m') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="m100m popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-iryokikan-label':
      case 'oh-iryokikan': {
        const name = props.P04_002
        const address = props.P04_003
        const kamoku = props.P04_004
        if (html.value.indexOf('iryokikan') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="iryokikan popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            address +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            kamoku +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-nihonrekishi-label':
      case 'oh-nihonrekishi': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })

        if (features.length === 0) return
        props = features[0].properties
        const name = props.名称
        if (html.value.indexOf('nihonrekishi') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="nihonrekishi popup-base-lg">' +
            name +
            '</div>'
        }
        break
      }
      case 'oh-chikeibunrui': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-chikeibunrui'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.code
        let naritachi = '',
          risk = ''
        const list = codeShizen
        for (let i = 0; i < list.length; i++) {
          if (list[i][1] === name) {
            //ズーム率によって数値型になったり文字型になったりしている模様
            naritachi = list[i][2]
            risk = list[i][3]
            break
          }
        }
        if (html.value.indexOf('chikeibunrui') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="chikeibunrui popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            naritachi +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            risk +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-shizenchikei': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-shizenchikei'] })
        if (features.length === 0) return
        props = features[0].properties
        const code = props.code
        let name = '',
          naritachi = '',
          risk = ''
        const list = codeShizenOriginal
        for (let i = 0; i < list.length; i++) {
          if (list[i][0] === Number(code)) {
            name = list[i][1]
            naritachi = list[i][2]
            risk = list[i][3]
            break
          }
        }
        if (html.value.indexOf('shizenchikei') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="shizenchikei popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            naritachi +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            risk +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-jinkochikei': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-jinkochikei'] })
        if (features.length === 0) return
        props = features[0].properties
        const code = props.code
        let name = '',
          naritachi = '',
          risk = ''
        const list = codeShizenOriginal
        for (let i = 0; i < list.length; i++) {
          if (list[i][0] === Number(code)) {
            name = list[i][1]
            naritachi = list[i][2]
            risk = list[i][3]
            break
          }
        }
        if (html.value.indexOf('jinkochikei') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="jinkochikei popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            naritachi +
            '</span><hr>' +
            '<span style="font-size: 12px;">' +
            risk +
            '</span>' +
            '</div>'
        }
        break
      }
      // ここを改善する
      case 'oh-cyugakuR05': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-cyugakuR05'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.A32_004
        if (html.value.indexOf('cyugakuR05') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="cyugakuR05 popup-base-lg">' +
            name +
            '</div>'
        }
        break
      }
      case 'oh-cyugakuR05-line':
      case 'oh-cyugakuR05-label':
      case 'oh-cyugakuR05-point': {
        features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        let objName = 'P29_004'
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-cyugakuR05'] })
          objName = 'A32_004'
        }
        if (features.length === 0) return
        props = features[0].properties
        const name = props[objName]
        if (html.value.indexOf('cyugakuR05-point') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="cyugakuR05-point popup-base-lg">' +
            name +
            '</div>'
        }
        break
      }
      // ここを改善する
      case 'oh-syogakkoR05': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-syogakkoR05'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.A27_004
        if (html.value.indexOf('syogakkoR05') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="syogakkoR05 popup-base-lg">' +
            name +
            '</div>'
        }
        break
      }
      case 'oh-syogakkoR05-line':
      case 'oh-syogakkoR05-label':
      case 'oh-syogakkoR05-point': {
        features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        let objName = 'P29_004'
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-syogakkoR05'] })
          objName = 'A27_004'
        }
        if (features.length === 0) return
        props = features[0].properties
        const name = props[objName]
        if (html.value.indexOf('syogakkoR05-point') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="syogakkoR05-point popup-base-lg">' +
            name +
            '</div>'
        }
        break
      }
      case 'oh-bakumatsu-label':
      case 'oh-bakumatsu-line':
      case 'oh-bakumatsu-layer': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-bakumatsu-layer'] })
        if (features.length === 0) return
        props = features[0].properties
        const tdStyle = 'padding: 5px; border-bottom: 1px solid #ccc;'
        const tdStyleRight = 'padding: 5px; border-bottom: 1px solid #ccc; text-align: right;'
        const ryobun2p = props.領分２
          ? '<tr><td style="' + tdStyle + '">領分２</td><td style="' + tdStyle + '">' +
            props.領分２ +
            '</td><td style="' + tdStyleRight + '">' +
            Math.round(props.石高２).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun3p = props.領分３
          ? '<tr><td style="' + tdStyle + '">領分３</td><td style="' + tdStyle + '">' +
            props.領分３ +
            '</td><td style="' + tdStyleRight + '">' +
            Math.round(props.石高３).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun4p = props.領分４
          ? '<tr><td style="' + tdStyle + '">領分４</td><td style="' + tdStyle + '">' +
            props.領分４ +
            '</td><td style="' + tdStyleRight + '">' +
            Math.round(props.石高４).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun5p = props.領分５
          ? '<tr><td style="' + tdStyle + '">領分５</td><td style="' + tdStyle + '">' +
            props.領分５ +
            '</td><td style="' + tdStyleRight + '">' +
            Math.round(props.石高５).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun6p = props.領分６
          ? '<tr><td style="' + tdStyle + '">領分６</td><td style="' + tdStyle + '">' +
            props.領分６ +
            '</td><td style="' + tdStyleRight + '">' +
            Math.round(props.石高６).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun7p = props.領分７
          ? '<tr><td style="' + tdStyle + '">領分７</td><td style="' + tdStyle + '">' +
            props.領分７ +
            '</td><td style="' + tdStyleRight + '">' +
            Math.round(props.石高７).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun8p = props.領分８
          ? '<tr><td style="' + tdStyle + '">領分８</td><td style="' + tdStyle + '">' +
            props.領分８ +
            '</td><td style="' + tdStyleRight + '">' +
            Math.round(props.石高８).toLocaleString() +
            '</td></tr>'
          : ''
        const bakumatsuBtnId = 'bakumatsu-origin-btn-' + Date.now()
        const bakumatsuResultId = 'bakumatsu-origin-result-' + Date.now()
        if (html.value.indexOf('bakumatsu-layer') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kinseipoint bakumatsu-layer" style="width: 300px; font-family: sans-serif; padding: 12px;">' +
            '<div style="font-size: 20px; font-weight: 600; color: #333; margin-bottom: 4px;">' +
            props.村名0 +
            '<span style="font-size: 13px; font-weight: normal; color: #666; margin-left: 6px;">(' +
            props.よみ0 +
            ')</span></div>' +
            '<div style="font-size: 15px; color: #8b4513; font-weight: 500; margin-bottom: 10px;">石高計 ' +
            Math.round(props.石高計).toLocaleString() +
            '</div>' +
            '<table class="popup-table" style="width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 10px;">' +
            '<tr style="background: #f5f5f5;"><th style="padding: 5px; border-bottom: 1px solid #ccc; text-align: center;"></th><th style="padding: 5px; border-bottom: 1px solid #ccc; text-align: center;">領分</th><th style="padding: 5px; border-bottom: 1px solid #ccc; text-align: center;">石高</th></tr>' +
            '<tr><td style="' + tdStyle + '">領分１</td><td style="' + tdStyle + '">' +
            props.領分１ +
            '</td><td style="' + tdStyleRight + '">' +
            Math.round(props.石高１).toLocaleString() +
            '</td></tr>' +
            ryobun2p +
            ryobun3p +
            ryobun4p +
            ryobun5p +
            ryobun6p +
            ryobun7p +
            ryobun8p +
            '</table>' +
            '<div style="font-size: 13px; color: #555; line-height: 1.6;">' +
            '<div>令制国 = ' + props.令制国 + '国</div>' +
            '<div>国郡名 = ' + props.国郡名 + '</div>' +
            '<div>郡名 = ' + props.郡名 + '</div>' +
            '<div style="color: #999; font-size: 11px;">KEY = ' + props.KEY + '</div>' +
            '<div style="margin-top: 6px; color: #666;">' + props.PREF_NAME + props.CITY_NAME + '</div>' +
            '</div>' +
            '<div style="margin-top: 10px;">' +
            '<button id="' + bakumatsuBtnId + '" class="bakumatsu-origin-btn" style="padding: 6px 16px; background: linear-gradient(135deg, #8b4513, #a0522d); color: white; border: none; border-radius: 20px; font-size: 13px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" mura="' + props.村名0 + '" ryobun="' + props.領分１ + '" kokudaka="' + Math.round(props.石高計) + '" kuni="' + props.令制国 + '" gun="' + props.郡名 + '">📜 AIで読み解く</button>' +
            '<div style="margin-top: 4px; font-size: 10px; color: #000;">※村名・石高・領分からAIが解説</div>' +
            '<div id="' + bakumatsuResultId + '" style="display: none; margin-top: 8px; padding: 10px; background: #fff8e1; border-radius: 8px; font-size: 13px; line-height: 1.6; width: 100%; box-sizing: border-box;"></div>' +
            '</div>' +
            '</div>'
          // AIボタンのイベントリスナーを設定
          setTimeout(() => {
            const originBtn = document.getElementById(bakumatsuBtnId)
            if (originBtn) {
              originBtn.addEventListener('click', async () => {
                // トラッキング
                const tracker = getUsageTracker(undefined, store)
                if (tracker) tracker.trackEvent('button', 'click', 'bakumatsu-ai-read', { component: 'popup' })
                const resultDiv = document.getElementById(bakumatsuResultId)
                if (!resultDiv) return
                const mura = originBtn.getAttribute('mura')
                const ryobun = originBtn.getAttribute('ryobun')
                const kokudaka = originBtn.getAttribute('kokudaka')
                const kuni = originBtn.getAttribute('kuni')
                const gun = originBtn.getAttribute('gun')
                const cacheKey = `${mura}_${ryobun}_${kokudaka}_${kuni}_${gun}`
                // キャッシュがあれば即表示
                if (bakumatsuAiCache.has(cacheKey)) {
                  resultDiv.style.display = 'block'
                  resultDiv.innerHTML = bakumatsuAiCache.get(cacheKey)
                  return
                }
                originBtn.disabled = true
                originBtn.textContent = '📜 読み解き中...'
                resultDiv.style.display = 'block'
                resultDiv.innerHTML = '<span style="color: #8b4513;"><span style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(139,69,19,0.3); border-top-color: #8b4513; border-radius: 50%; animation: bakumatsu-spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px;"></span>AIが読み解いています...</span>'
                if (!document.getElementById('bakumatsu-spin-style')) {
                  const style = document.createElement('style')
                  style.id = 'bakumatsu-spin-style'
                  style.textContent = '@keyframes bakumatsu-spin { to { transform: rotate(360deg); } }'
                  document.head.appendChild(style)
                }
                try {
                  const response = await axios.post('/oh3/php/claudeBakumatsuOrigin.php', {
                    muraName: mura, ryobun, kokudaka, kuni, gun
                  })
                  if (response.data.success && response.data.explanation) {
                    const html = '<span style="color: #5d4037; font-weight: bold;">📚 AI解説</span><br>' + DOMPurify.sanitize(response.data.explanation) + '<div style="margin-top: 8px; padding-top: 6px; border-top: 1px dashed #d7ccc8; font-size: 10px; color: #000;">📝 村名・石高・領分のデータのみを元にAIが作成</div>'
                    bakumatsuAiCache.set(cacheKey, html)
                    resultDiv.innerHTML = html
                  } else {
                    resultDiv.innerHTML = '<span style="color: #c62828;">取得できませんでした</span>'
                  }
                } catch (error) {
                  console.error('村の由来取得エラー:', error)
                  resultDiv.innerHTML = '<span style="color: #c62828;">エラーが発生しました</span>'
                }
                originBtn.disabled = false
                originBtn.textContent = '📜 もう一度読み解く'
              })
            }
          }, 100)
        }
        break
      }
      case 'oh-bakumatsu-point': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-bakumatsu-point'] })
        if (features.length === 0) return
        props = features[0].properties
        const tdStyleP = 'padding: 5px; border-bottom: 1px solid #ccc;'
        const tdStyleRightP = 'padding: 5px; border-bottom: 1px solid #ccc; text-align: right;'
        const ryobun2p = props.領分２
          ? '<tr><td style="' + tdStyleP + '">領分２</td><td style="' + tdStyleP + '">' +
            props.領分２ +
            '</td><td style="' + tdStyleRightP + '">' +
            Math.round(props.石高２).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun3p = props.領分３
          ? '<tr><td style="' + tdStyleP + '">領分３</td><td style="' + tdStyleP + '">' +
            props.領分３ +
            '</td><td style="' + tdStyleRightP + '">' +
            Math.round(props.石高３).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun4p = props.領分４
          ? '<tr><td style="' + tdStyleP + '">領分４</td><td style="' + tdStyleP + '">' +
            props.領分４ +
            '</td><td style="' + tdStyleRightP + '">' +
            Math.round(props.石高４).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun5p = props.領分５
          ? '<tr><td style="' + tdStyleP + '">領分５</td><td style="' + tdStyleP + '">' +
            props.領分５ +
            '</td><td style="' + tdStyleRightP + '">' +
            Math.round(props.石高５).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun6p = props.領分６
          ? '<tr><td style="' + tdStyleP + '">領分６</td><td style="' + tdStyleP + '">' +
            props.領分６ +
            '</td><td style="' + tdStyleRightP + '">' +
            Math.round(props.石高６).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun7p = props.領分７
          ? '<tr><td style="' + tdStyleP + '">領分７</td><td style="' + tdStyleP + '">' +
            props.領分７ +
            '</td><td style="' + tdStyleRightP + '">' +
            Math.round(props.石高７).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun8p = props.領分８
          ? '<tr><td style="' + tdStyleP + '">領分８</td><td style="' + tdStyleP + '">' +
            props.領分８ +
            '</td><td style="' + tdStyleRightP + '">' +
            Math.round(props.石高８).toLocaleString() +
            '</td></tr>'
          : ''
        const bakumatsuBtnIdP = 'bakumatsu-origin-btn-p-' + Date.now()
        const bakumatsuResultIdP = 'bakumatsu-origin-result-p-' + Date.now()
        if (html.value.indexOf('bakumatsu-point') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kinseipoint bakumatsu-point" style="width: 300px; font-family: sans-serif; padding: 12px;">' +
            '<div style="font-size: 20px; font-weight: 600; color: #333; margin-bottom: 4px;">' +
            props.村名 +
            '<span style="font-size: 13px; font-weight: normal; color: #666; margin-left: 6px;">(' +
            props.よみ +
            ')</span></div>' +
            '<div style="font-size: 13px; color: #555; margin-bottom: 4px;">領分１ = ' + props.領分１ + '</div>' +
            '<div style="font-size: 15px; color: #8b4513; font-weight: 500; margin-bottom: 10px;">石高計 ' +
            Math.round(props.石高計).toLocaleString() +
            '</div>' +
            '<table class="popup-table" style="width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 10px;">' +
            '<tr style="background: #f5f5f5;"><th style="padding: 5px; border-bottom: 1px solid #ccc; text-align: center;"></th><th style="padding: 5px; border-bottom: 1px solid #ccc; text-align: center;">領分</th><th style="padding: 5px; border-bottom: 1px solid #ccc; text-align: center;">石高</th></tr>' +
            '<tr><td style="' + tdStyleP + '">領分１</td><td style="' + tdStyleP + '">' +
            props.領分１ +
            '</td><td style="' + tdStyleRightP + '">' +
            Math.round(props.石高１).toLocaleString() +
            '</td></tr>' +
            ryobun2p +
            ryobun3p +
            ryobun4p +
            ryobun5p +
            ryobun6p +
            ryobun7p +
            ryobun8p +
            '</table>' +
            '<div style="font-size: 13px; color: #555; line-height: 1.6;">' +
            '<div>国名 = ' + props.国名 + '</div>' +
            '<div>国郡 = ' + props.国郡 + '</div>' +
            '<div>郡名 = ' + props.郡名 + '</div>' +
            '<div>相給 = ' + props.相給 + '</div>' +
            '</div>' +
            '<div style="margin-top: 10px;">' +
            '<button id="' + bakumatsuBtnIdP + '" class="bakumatsu-origin-btn" style="padding: 6px 16px; background: linear-gradient(135deg, #8b4513, #a0522d); color: white; border: none; border-radius: 20px; font-size: 13px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" mura="' + props.村名 + '" ryobun="' + props.領分１ + '" kokudaka="' + Math.round(props.石高計) + '" kuni="' + props.国名 + '" gun="' + props.郡名 + '">📜 AIで読み解く</button>' +
            '<div style="margin-top: 4px; font-size: 10px; color: #000;">※村名・石高・領分からAIが解説</div>' +
            '<div id="' + bakumatsuResultIdP + '" style="display: none; margin-top: 8px; padding: 10px; background: #fff8e1; border-radius: 8px; font-size: 13px; line-height: 1.6; width: 100%; box-sizing: border-box;"></div>' +
            '</div>' +
            '</div>'
          setTimeout(() => {
            const originBtn = document.getElementById(bakumatsuBtnIdP)
            if (originBtn) {
              originBtn.addEventListener('click', async () => {
                // トラッキング
                const tracker = getUsageTracker(undefined, store)
                if (tracker) tracker.trackEvent('button', 'click', 'bakumatsu-ai-read', { component: 'popup' })
                const resultDiv = document.getElementById(bakumatsuResultIdP)
                if (!resultDiv) return
                const mura = originBtn.getAttribute('mura')
                const ryobun = originBtn.getAttribute('ryobun')
                const kokudaka = originBtn.getAttribute('kokudaka')
                const kuni = originBtn.getAttribute('kuni')
                const gun = originBtn.getAttribute('gun')
                const cacheKey = `${mura}_${ryobun}_${kokudaka}_${kuni}_${gun}`
                if (bakumatsuAiCache.has(cacheKey)) {
                  resultDiv.style.display = 'block'
                  resultDiv.innerHTML = bakumatsuAiCache.get(cacheKey)
                  return
                }
                originBtn.disabled = true
                originBtn.textContent = '📜 読み解き中...'
                resultDiv.style.display = 'block'
                resultDiv.innerHTML = '<span style="color: #8b4513;"><span style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(139,69,19,0.3); border-top-color: #8b4513; border-radius: 50%; animation: bakumatsu-spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px;"></span>AIが読み解いています...</span>'
                if (!document.getElementById('bakumatsu-spin-style')) {
                  const style = document.createElement('style')
                  style.id = 'bakumatsu-spin-style'
                  style.textContent = '@keyframes bakumatsu-spin { to { transform: rotate(360deg); } }'
                  document.head.appendChild(style)
                }
                try {
                  const response = await axios.post('/oh3/php/claudeBakumatsuOrigin.php', {
                    muraName: mura, ryobun, kokudaka, kuni, gun
                  })
                  if (response.data.success && response.data.explanation) {
                    const html = '<span style="color: #5d4037; font-weight: bold;">📚 AI解説</span><br>' + DOMPurify.sanitize(response.data.explanation) + '<div style="margin-top: 8px; padding-top: 6px; border-top: 1px dashed #d7ccc8; font-size: 10px; color: #000;">📝 村名・石高・領分のデータのみを元にAIが作成</div>'
                    bakumatsuAiCache.set(cacheKey, html)
                    resultDiv.innerHTML = html
                  } else {
                    resultDiv.innerHTML = '<span style="color: #c62828;">取得できませんでした</span>'
                  }
                } catch (error) {
                  console.error('村の由来取得エラー:', error)
                  resultDiv.innerHTML = '<span style="color: #c62828;">エラーが発生しました</span>'
                }
                originBtn.disabled = false
                originBtn.textContent = '📜 もう一度読み解く'
              })
            }
          }, 100)
        }
        break
      }
      case 'oh-bakumatsu-kokudaka-height': {
        const features = map.queryRenderedFeatures(map.project(coordinates), {
          layers: ['oh-bakumatsu-kokudaka-height'],
        })
        if (features.length === 0) return
        props = features[0].properties
        const tdStyleH = 'padding: 5px; border-bottom: 1px solid #ccc;'
        const tdStyleRightH = 'padding: 5px; border-bottom: 1px solid #ccc; text-align: right;'
        const ryobun2p = props.領分２
          ? '<tr><td style="' + tdStyleH + '">領分２</td><td style="' + tdStyleH + '">' +
            props.領分２ +
            '</td><td style="' + tdStyleRightH + '">' +
            Math.round(props.石高２).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun3p = props.領分３
          ? '<tr><td style="' + tdStyleH + '">領分３</td><td style="' + tdStyleH + '">' +
            props.領分３ +
            '</td><td style="' + tdStyleRightH + '">' +
            Math.round(props.石高３).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun4p = props.領分４
          ? '<tr><td style="' + tdStyleH + '">領分４</td><td style="' + tdStyleH + '">' +
            props.領分４ +
            '</td><td style="' + tdStyleRightH + '">' +
            Math.round(props.石高４).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun5p = props.領分５
          ? '<tr><td style="' + tdStyleH + '">領分５</td><td style="' + tdStyleH + '">' +
            props.領分５ +
            '</td><td style="' + tdStyleRightH + '">' +
            Math.round(props.石高５).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun6p = props.領分６
          ? '<tr><td style="' + tdStyleH + '">領分６</td><td style="' + tdStyleH + '">' +
            props.領分６ +
            '</td><td style="' + tdStyleRightH + '">' +
            Math.round(props.石高６).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun7p = props.領分７
          ? '<tr><td style="' + tdStyleH + '">領分７</td><td style="' + tdStyleH + '">' +
            props.領分７ +
            '</td><td style="' + tdStyleRightH + '">' +
            Math.round(props.石高７).toLocaleString() +
            '</td></tr>'
          : ''
        const ryobun8p = props.領分８
          ? '<tr><td style="' + tdStyleH + '">領分８</td><td style="' + tdStyleH + '">' +
            props.領分８ +
            '</td><td style="' + tdStyleRightH + '">' +
            Math.round(props.石高８).toLocaleString() +
            '</td></tr>'
          : ''
        const bakumatsuBtnIdH = 'bakumatsu-origin-btn-h-' + Date.now()
        const bakumatsuResultIdH = 'bakumatsu-result-h-' + Date.now()

        if (html.value.indexOf('bakumatsu-kokudaka-height') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kinseipoint bakumatsu-kokudaka-height" style="width: 300px; font-family: sans-serif; padding: 12px;">' +
            '<div style="font-size: 20px; font-weight: 600; color: #333; margin-bottom: 4px;">' +
            props.村名0 +
            '<span style="font-size: 13px; font-weight: normal; color: #666; margin-left: 6px;">(' +
            props.よみ0 +
            ')</span></div>' +
            '<div style="font-size: 15px; color: #8b4513; font-weight: 500; margin-bottom: 10px;">石高計 ' +
            Math.round(props.石高計).toLocaleString() +
            '</div>' +
            '<table class="popup-table" style="width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 10px;">' +
            '<tr style="background: #f5f5f5;"><th style="padding: 5px; border-bottom: 1px solid #ccc; text-align: center;"></th><th style="padding: 5px; border-bottom: 1px solid #ccc; text-align: center;">領分</th><th style="padding: 5px; border-bottom: 1px solid #ccc; text-align: center;">石高</th></tr>' +
            '<tr><td style="' + tdStyleH + '">領分１</td><td style="' + tdStyleH + '">' +
            props.領分１ +
            '</td><td style="' + tdStyleRightH + '">' +
            Math.round(props.石高１).toLocaleString() +
            '</td></tr>' +
            ryobun2p +
            ryobun3p +
            ryobun4p +
            ryobun5p +
            ryobun6p +
            ryobun7p +
            ryobun8p +
            '</table>' +
            '<div style="font-size: 13px; color: #555; line-height: 1.6;">' +
            '<div>令制国 = ' + props.令制国 + '国</div>' +
            '<div>国郡名 = ' + props.国郡名 + '</div>' +
            '<div>郡名 = ' + props.郡名 + '</div>' +
            '<div style="color: #999; font-size: 11px;">KEY = ' + props.KEY + '</div>' +
            '<div style="margin-top: 6px; color: #666;">' + props.PREF_NAME + props.CITY_NAME + '</div>' +
            '</div>' +
            '<div style="margin-top: 10px;">' +
            '<button id="' + bakumatsuBtnIdH + '" class="bakumatsu-origin-btn" style="padding: 6px 16px; background: linear-gradient(135deg, #8b4513, #a0522d); color: white; border: none; border-radius: 20px; font-size: 13px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" mura="' + props.村名0 + '" ryobun="' + props.領分１ + '" kokudaka="' + Math.round(props.石高計) + '" kuni="' + props.令制国 + '" gun="' + props.郡名 + '">📜 AIで読み解く</button>' +
            '<div style="margin-top: 4px; font-size: 10px; color: #000;">※村名・石高・領分からAIが解説</div>' +
            '<div id="' + bakumatsuResultIdH + '" style="display: none; margin-top: 8px; padding: 10px; background: #fff8e1; border-radius: 8px; font-size: 13px; line-height: 1.6; width: 100%; box-sizing: border-box;"></div>' +
            '</div>' +
            '</div>'
          // AIボタンのイベントリスナーを設定
          setTimeout(() => {
            const originBtn = document.getElementById(bakumatsuBtnIdH)
            if (originBtn) {
              originBtn.addEventListener('click', async () => {
                // トラッキング
                const tracker = getUsageTracker(undefined, store)
                if (tracker) tracker.trackEvent('button', 'click', 'bakumatsu-ai-read', { component: 'popup' })
                const resultDiv = document.getElementById(bakumatsuResultIdH)
                if (!resultDiv) return
                const mura = originBtn.getAttribute('mura')
                const ryobun = originBtn.getAttribute('ryobun')
                const kokudaka = originBtn.getAttribute('kokudaka')
                const kuni = originBtn.getAttribute('kuni')
                const gun = originBtn.getAttribute('gun')
                const cacheKey = `${mura}_${ryobun}_${kokudaka}_${kuni}_${gun}`
                if (bakumatsuAiCache.has(cacheKey)) {
                  resultDiv.style.display = 'block'
                  resultDiv.innerHTML = bakumatsuAiCache.get(cacheKey)
                  return
                }
                originBtn.disabled = true
                originBtn.textContent = '📜 読み解き中...'
                resultDiv.style.display = 'block'
                resultDiv.innerHTML = '<span style="color: #8b4513;"><span style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(139,69,19,0.3); border-top-color: #8b4513; border-radius: 50%; animation: bakumatsu-spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px;"></span>AIが読み解いています...</span>'
                if (!document.getElementById('bakumatsu-spin-style')) {
                  const style = document.createElement('style')
                  style.id = 'bakumatsu-spin-style'
                  style.textContent = '@keyframes bakumatsu-spin { to { transform: rotate(360deg); } }'
                  document.head.appendChild(style)
                }
                try {
                  const response = await axios.post('/oh3/php/claudeBakumatsuOrigin.php', {
                    muraName: mura, ryobun, kokudaka, kuni, gun
                  })
                  if (response.data.success && response.data.explanation) {
                    const html = '<span style="color: #5d4037; font-weight: bold;">📚 AI解説</span><br>' + DOMPurify.sanitize(response.data.explanation) + '<div style="margin-top: 8px; padding-top: 6px; border-top: 1px dashed #d7ccc8; font-size: 10px; color: #000;">📝 村名・石高・領分のデータのみを元にAIが作成</div>'
                    bakumatsuAiCache.set(cacheKey, html)
                    resultDiv.innerHTML = html
                  } else {
                    resultDiv.innerHTML = '<span style="color: #c62828;">取得できませんでした</span>'
                  }
                } catch (error) {
                  console.error('村の由来取得エラー:', error)
                  resultDiv.innerHTML = '<span style="color: #c62828;">エラーが発生しました</span>'
                }
                originBtn.disabled = false
                originBtn.textContent = '📜 もう一度読み解く'
              })
            }
          }, 100)
        }
        break
      }
      case 'oh-bakumatsu-line2':
      case 'oh-bakumatsu-han': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-bakumatsu-han'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.村名
        const kokudaka = Math.floor(Number(props.石高計))
        const ryobun = props.領分１
        html.value += `
                  <div style="font-family: sans-serif; line-height: 1.6;">
                    <div style="font-size: 18px; font-weight: 600; color: #333; margin-bottom: 4px;">村名 = ${name}</div>
                    <div style="font-size: 14px; color: #8b4513; font-weight: 500;">石高 = ${kokudaka.toLocaleString()}</div>
                    <div style="font-size: 13px; color: #555;">領分 = ${ryobun}</div>
                  </div>
                `
        break
      }
      case 'oh-bakumatsu-label2':
      case 'oh-bakumatsu-kokudaka': {
        let features
        if (map.getLayer('oh-bakumatsu-kokudaka')) {
          features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-bakumatsu-kokudaka'] })
        } else {
          features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-bakumatsu'] })
        }
        if (features.length === 0) return
        props = features[0].properties
        const name = props.村名
        const kokudaka = Math.floor(Number(props.石高計))
        const ryobun = props.領分１
        if (html.value.indexOf('bakumatsu-kokudaka') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += `
                  <div class="bakumatsu-kokudaka" style="font-family: sans-serif; line-height: 1.6;">
                    <div style="font-size: 18px; font-weight: 600; color: #333; margin-bottom: 4px;">村名 = ${name}</div>
                    <div style="font-size: 14px; color: #8b4513; font-weight: 500;">石高 = ${kokudaka.toLocaleString()}</div>
                    <div style="font-size: 13px; color: #555;">領分 = ${ryobun}</div>
                  </div>
                `
        }
        break
      }
      case 'oh-highwayLayer-green-lines':
      case 'oh-highwayLayer-red-lines': {
        const features = map.queryRenderedFeatures(map.project(coordinates), {
          layers: ['oh-highwayLayer-green-lines'],
        })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.N06_007
        const id = props.N06_004
        const kyouyounen = props.N06_001
        const kaishinen = props.N06_002
        if (html.value.indexOf('highwayLayer-red-lines') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class=highwayLayer-red-lines popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 12px;">id=' +
            id +
            '</span><br>' +
            '<span style="font-size: 12px;">供用開始年=' +
            kyouyounen +
            '</span><br>' +
            '<span style="font-size: 12px;">設置期間（開始年）=' +
            kaishinen +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-q-kyoryo-label':
      case 'oh-q-kyoryo': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-q-kyoryo'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props._html
        if (html.value.indexOf('q-kyoryo') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="q-kyoryo popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 12px;">よみ=' +
            props.No1 +
            '</span><br>' +
            '<span style="font-size: 12px;">路線名=' +
            props.No2 +
            '</span><br>' +
            '<span style="font-size: 12px;">完成年（度）=' +
            props.No3 +
            '</span><br>' +
            '<span style="font-size: 12px;">延長=' +
            props.No4 +
            '</span><br>' +
            '<span style="font-size: 12px;">幅員=' +
            props.No5 +
            '</span><br>' +
            '<span style="font-size: 12px;">管理者名=' +
            props.No6 +
            '</span><br>' +
            '<span style="font-size: 12px;">所在市区町村=' +
            props.No7 +
            props.No8 +
            '</span><br>' +
            '<span style="font-size: 12px;">点検実施年度=' +
            props.No9 +
            '</span><br>' +
            '<span style="font-size: 12px;">判定区分=' +
            props.No10 +
            '</span><br>' +
            '<span style="font-size: 12px;">Ｑ地図管理ID=' +
            props.No14 +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-q-tunnel-label':
      case 'oh-q-tunnel': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-q-tunnel'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props._html
        if (html.value.indexOf('q-tunnel') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="q-tunnel popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 12px;">よみ=' +
            props.No1 +
            '</span><br>' +
            '<span style="font-size: 12px;">路線名=' +
            props.No2 +
            '</span><br>' +
            '<span style="font-size: 12px;">完成年（度）=' +
            props.No3 +
            '</span><br>' +
            '<span style="font-size: 12px;">延長=' +
            props.No4 +
            '</span><br>' +
            '<span style="font-size: 12px;">幅員=' +
            props.No5 +
            '</span><br>' +
            '<span style="font-size: 12px;">管理者名=' +
            props.No6 +
            '</span><br>' +
            '<span style="font-size: 12px;">所在市区町村=' +
            props.No7 +
            props.No8 +
            '</span><br>' +
            '<span style="font-size: 12px;">点検実施年度=' +
            props.No9 +
            '</span><br>' +
            '<span style="font-size: 12px;">判定区分=' +
            props.No10 +
            '</span><br>' +
            '<span style="font-size: 12px;">Ｑ地図管理ID=' +
            props.No14 +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-yotochiiki-height':
      case 'oh-yotochiiki-line':
      case 'oh-yotochiiki': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-yotochiiki'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.用途地域
        if (html.value.indexOf('yotochiiki') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="yotochiiki popup-body">' +
            '<span style="font-size: 20px;">' +
            name +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-bus-label':
      case 'oh-bus-points': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.P11_001
        if (html.value.indexOf('bus-points') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="bus-points popup-body">' +
            '<div class="street-view" style="margin-top:0px;height: 200px;width: 300px"></div>' +
            '<span style="font-size: 16px;">' +
            name +
            '</span><br>' +
            '<span style="font-size: 12px;">事業者名=' +
            props.P11_002 +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-bus-lines': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-bus-lines'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.N07_001
        if (html.value.indexOf('bus-points') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="bus-points popup-body">' +
            '<span style="font-size: 16px;">' +
            name +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-koaza-label':
      case 'oh-koaza': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-koaza'] })
        if (features.length === 0) return
        props = features[0].properties
        const gun = props.GUN_NAME
        const name = props.KOAZA_NAME
        const a =
          '<a href="https://koaza.net/list/' +
          props.KUNI_NAME +
          props.GUN_NAME +
          '.html' +
          '#' +
          props.MURA_NAME +
          '" target="_blank">' +
          props.MURA_NAME +
          '</a>'
        // 現在の住所取得用のユニークID
        const addressId = 'koaza-current-address-' + Date.now()
        const originBtnId = 'koaza-origin-btn-' + Date.now()
        const originResultId = 'koaza-origin-result-' + Date.now()
        if (html.value.indexOf('kantokoaza') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kantokoaza" style="padding: 0 10px 10px 10px; font-weight: normal; color: #333;line-height: 25px;">' +
            '<span style="font-size: 20px;">小字名＝' +
            name +
            '</span><div style="margin-bottom: 10px;"></div>' +
            '<span style="font-size: 14px; opacity: 0.8;">郡名＝' +
            gun +
            '　村名＝' +
            a +
            '</span><br>' +
            '<div style="margin-top: 12px; padding: 8px 12px; background: #e3f2fd; border-radius: 8px;">' +
            '<span style="font-size: 12px; color: #1565c0; font-weight: bold;">📍 現在の住所</span><br>' +
            '<span id="' + addressId + '" style="font-size: 14px; color: #333;">取得中...</span>' +
            '</div>' +
            '<div style="margin-top: 8px;">' +
            '<button id="' + originBtnId + '" class="koaza-origin-btn" style="padding: 6px 16px; background: linear-gradient(135deg, #9c27b0, #7b1fa2); color: white; border: none; border-radius: 20px; font-size: 13px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" koaza="' + name + '" mura="' + props.MURA_NAME + '" gun="' + gun + '">🔮 地名の由来を聞く</button>' +
            '<div id="' + originResultId + '" style="display: none; margin-top: 8px; padding: 10px; background: #f3e5f5; border-radius: 8px; font-size: 13px; line-height: 1.6;"></div>' +
            '</div>' +
            '<button class="mura-name" style="margin-top: 8px; padding: 6px 16px; background: linear-gradient(135deg, #ff69b4, #ff1493); color: white; border: none; border-radius: 20px; font-size: 13px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2); transition: all 0.2s;" mapname="' +
            mapName +
            '" mura="' +
            props.MURA_NAME +
            '">村で抽出</button><br>' +
            '</div>'
          // 非同期で現在の住所を取得してDOMを更新（都道府県を含める）
          reverseGeocode(coordinates[0], coordinates[1], true).then(address => {
            const el = document.getElementById(addressId)
            if (el) {
              el.textContent = address || '取得できませんでした'
            }
          })
          // 地名の由来ボタンのイベントリスナーを設定
          setTimeout(() => {
            const originBtn = document.getElementById(originBtnId)
            if (originBtn) {
              originBtn.addEventListener('click', async () => {
                // トラッキング
                const tracker = getUsageTracker(undefined, store)
                if (tracker) tracker.trackEvent('button', 'click', 'koaza-ai-origin', { component: 'popup' })
                const resultDiv = document.getElementById(originResultId)
                if (!resultDiv) return
                // ボタンを無効化
                originBtn.disabled = true
                originBtn.textContent = '🔮 考え中...'
                resultDiv.style.display = 'block'
                resultDiv.innerHTML = '<span style="color: #7b1fa2;"><span style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(123,31,162,0.3); border-top-color: #7b1fa2; border-radius: 50%; animation: koaza-spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px;"></span>AIが地名の由来を調べています...</span>'
                // スピナーアニメーション用のスタイルを追加（なければ）
                if (!document.getElementById('koaza-spin-style')) {
                  const style = document.createElement('style')
                  style.id = 'koaza-spin-style'
                  style.textContent = '@keyframes koaza-spin { to { transform: rotate(360deg); } }'
                  document.head.appendChild(style)
                }
                try {
                  const response = await axios.post('/oh3/php/claudeKoazaOrigin.php', {
                    koazaName: originBtn.getAttribute('koaza'),
                    muraName: originBtn.getAttribute('mura'),
                    gunName: originBtn.getAttribute('gun')
                  })
                  if (response.data.success && response.data.explanation) {
                    resultDiv.innerHTML = '<span style="color: #4a148c; font-weight: bold;">📚 由来</span><br>' + DOMPurify.sanitize(response.data.explanation)
                  } else {
                    resultDiv.innerHTML = '<span style="color: #c62828;">取得できませんでした</span>'
                  }
                } catch (error) {
                  console.error('地名の由来取得エラー:', error)
                  resultDiv.innerHTML = '<span style="color: #c62828;">エラーが発生しました</span>'
                }
                // ボタンを再有効化
                originBtn.disabled = false
                originBtn.textContent = '🔮 もう一度聞く'
              })
            }
          }, 100)
        }
        break
      }
      // case 'oh-mura-label':
      // case 'oh-mura': {
      //   const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
      //   if (features.length === 0) return
      //   props = features[0].properties
      //   const gun = props.GUN_NAME
      //   const name = props.MURA_NAME
      //   const a =
      //     '<a href="https://koaza.net/list/' +
      //     props.KUNI_NAME +
      //     props.GUN_NAME +
      //     '.html' +
      //     '#' +
      //     props.MURA_NAME +
      //     '" target="_blank">' +
      //     name +
      //     '</a>'
      //   if (html.value.indexOf('kantokoaza') === -1) {
      //     html.value +=
      //       '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '(mura)</div>'
      //     html.value +=
      //       '<div class="kantokoaza popup-body">' +
      //       '<span style="font-size: 20px;">郡名＝' +
      //       gun +
      //       '</span><br>' +
      //       '<span style="font-size: 20px;">村名＝' +
      //       a +
      //       '</span>' +
      //       '<button class="mura-name pyramid-btn" mapname="' +
      //       mapName +
      //       '" mura="' +
      //       props.MURA_NAME +
      //       '">村で抽出</button><br>' +
      //       '</div>'
      //   }
      //   break
      // }
      case 'oh-city-t09-label':
      case 'oh-city-t09-line':
      case 'oh-city-t09': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-city-t09'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.N03_004
        const gun = props.N03_003 ? props.N03_003 : ''
        const pref = props.N03_001 || ''
        const originBtnId = 'city-t09-origin-btn-' + Date.now()
        const originResultId = 'city-t09-origin-result-' + Date.now()
        if (html.value.indexOf('city-t09') === -1) {
          html.value +=
            '<div class="layer-label-div" style="min-width: 200px;">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="city-t09 popup-body">' +
            '<span style="font-size:14px;">' +
            pref +
            gun +
            '</span><br>' +
            '<span style="font-size:20px;">' +
            name +
            '</span><br>' +
            '<div style="margin-top: 8px;">' +
            '<button id="' + originBtnId + '" class="city-origin-btn" style="padding: 6px 16px; background: linear-gradient(135deg, #4caf50, #388e3c); color: white; border: none; border-radius: 20px; font-size: 13px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" city="' + name + '" gun="' + gun + '" pref="' + pref + '" era="T09">🏛️ 大正9年の歴史を聞く</button>' +
            '<div id="' + originResultId + '" style="display: none; margin-top: 8px; padding: 10px; background: #e8f5e9; border-radius: 8px; font-size: 13px; line-height: 1.6;"></div>' +
            '</div>' +
            '</div>'
          // 市町村の歴史ボタンのイベントリスナーを設定
          setTimeout(() => {
            const originBtn = document.getElementById(originBtnId)
            if (originBtn) {
              originBtn.addEventListener('click', async () => {
                // トラッキング
                const tracker = getUsageTracker(undefined, store)
                if (tracker) tracker.trackEvent('button', 'click', 'city-ai-history-t09', { component: 'popup' })
                const resultDiv = document.getElementById(originResultId)
                if (!resultDiv) return
                originBtn.disabled = true
                originBtn.textContent = '🏛️ 考え中...'
                resultDiv.style.display = 'block'
                resultDiv.innerHTML = '<span style="color: #388e3c;"><span style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(56,142,60,0.3); border-top-color: #388e3c; border-radius: 50%; animation: city-spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px;"></span>AIが大正9年の歴史を調べています...</span>'
                if (!document.getElementById('city-spin-style')) {
                  const style = document.createElement('style')
                  style.id = 'city-spin-style'
                  style.textContent = '@keyframes city-spin { to { transform: rotate(360deg); } }'
                  document.head.appendChild(style)
                }
                try {
                  const response = await axios.post('/oh3/php/claudeCityHistory.php', {
                    cityName: originBtn.getAttribute('city'),
                    gunName: originBtn.getAttribute('gun'),
                    prefName: originBtn.getAttribute('pref'),
                    era: originBtn.getAttribute('era')
                  })
                  if (response.data.success && response.data.explanation) {
                    resultDiv.innerHTML = '<span style="color: #1b5e20; font-weight: bold;">📖 歴史</span><br>' + DOMPurify.sanitize(response.data.explanation)
                  } else {
                    resultDiv.innerHTML = '<span style="color: #c62828;">取得できませんでした</span>'
                  }
                } catch (error) {
                  console.error('市町村の歴史取得エラー:', error)
                  resultDiv.innerHTML = '<span style="color: #c62828;">エラーが発生しました</span>'
                }
                originBtn.disabled = false
                originBtn.textContent = '🏛️ もう一度聞く'
              })
            }
          }, 100)
        }
        break
      }
      case 'oh-city-r05-label':
      case 'oh-city-r05-line':
      case 'oh-city-r05': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-city-r05'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.N03_004
        const gun = props.N03_003 ? props.N03_003 : ''
        const pref = props.N03_001 || ''
        const originBtnId = 'city-r05-origin-btn-' + Date.now()
        const originResultId = 'city-r05-origin-result-' + Date.now()
        if (html.value.indexOf('city-r05') === -1) {
          html.value +=
            '<div class="layer-label-div" style="min-width: 200px;">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="city-r05 popup-body">' +
            '<span style="font-size:14px;">' +
            pref +
            gun +
            '</span><br>' +
            '<span style="font-size:20px;">' +
            name +
            '</span><br>' +
            '<div style="margin-top: 8px;">' +
            '<button id="' + originBtnId + '" class="city-origin-btn" style="padding: 6px 16px; background: linear-gradient(135deg, #2196f3, #1976d2); color: white; border: none; border-radius: 20px; font-size: 13px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" city="' + name + '" gun="' + gun + '" pref="' + pref + '" era="R05">🏛️ 令和5年の歴史を聞く</button>' +
            '<div id="' + originResultId + '" style="display: none; margin-top: 8px; padding: 10px; background: #e3f2fd; border-radius: 8px; font-size: 13px; line-height: 1.6;"></div>' +
            '</div>' +
            '</div>'
          // 市町村の歴史ボタンのイベントリスナーを設定
          setTimeout(() => {
            const originBtn = document.getElementById(originBtnId)
            if (originBtn) {
              originBtn.addEventListener('click', async () => {
                // トラッキング
                const tracker = getUsageTracker(undefined, store)
                if (tracker) tracker.trackEvent('button', 'click', 'city-ai-history-r05', { component: 'popup' })
                const resultDiv = document.getElementById(originResultId)
                if (!resultDiv) return
                originBtn.disabled = true
                originBtn.textContent = '🏛️ 考え中...'
                resultDiv.style.display = 'block'
                resultDiv.innerHTML = '<span style="color: #1976d2;"><span style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(25,118,210,0.3); border-top-color: #1976d2; border-radius: 50%; animation: city-spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px;"></span>AIが市町村の歴史を調べています...</span>'
                if (!document.getElementById('city-spin-style')) {
                  const style = document.createElement('style')
                  style.id = 'city-spin-style'
                  style.textContent = '@keyframes city-spin { to { transform: rotate(360deg); } }'
                  document.head.appendChild(style)
                }
                try {
                  const response = await axios.post('/oh3/php/claudeCityHistory.php', {
                    cityName: originBtn.getAttribute('city'),
                    gunName: originBtn.getAttribute('gun'),
                    prefName: originBtn.getAttribute('pref'),
                    era: originBtn.getAttribute('era')
                  })
                  if (response.data.success && response.data.explanation) {
                    resultDiv.innerHTML = '<span style="color: #0d47a1; font-weight: bold;">📖 歴史</span><br>' + DOMPurify.sanitize(response.data.explanation)
                  } else {
                    resultDiv.innerHTML = '<span style="color: #c62828;">取得できませんでした</span>'
                  }
                } catch (error) {
                  console.error('市町村の歴史取得エラー:', error)
                  resultDiv.innerHTML = '<span style="color: #c62828;">エラーが発生しました</span>'
                }
                originBtn.disabled = false
                originBtn.textContent = '🏛️ もう一度聞く'
              })
            }
          }, 100)
        }
        break
      }
        case 'oh-city-s25-label':
        case 'oh-city-s25-line':
        case 'oh-city-s25': {
            const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-city-s25'] })
            if (features.length === 0) return
            props = features[0].properties
            const name = props.N03_004
            const gun = props.N03_003 ? props.N03_003 : ''
            const pref = props.N03_001 || ''
            const originBtnId = 'city-s25-origin-btn-' + Date.now()
            const originResultId = 'city-s25-origin-result-' + Date.now()
            if (html.value.indexOf('city-s25') === -1) {
                html.value +=
                    '<div class="layer-label-div" style="min-width: 200px;">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
                html.value +=
                    '<div class="city-s25 popup-body">' +
                    '<span style="font-size:14px;">' +
                    pref +
                    gun +
                    '</span><br>' +
                    '<span style="font-size:20px;">' +
                    name +
                    '</span><br>' +
                    '<div style="margin-top: 8px;">' +
                    '<button id="' + originBtnId + '" class="city-origin-btn" style="padding: 6px 16px; background: linear-gradient(135deg, #795548, #5d4037); color: white; border: none; border-radius: 20px; font-size: 13px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" city="' + name + '" gun="' + gun + '" pref="' + pref + '" era="S25">🏛️ 昭和25年の歴史を聞く</button>' +
                    '<div id="' + originResultId + '" style="display: none; margin-top: 8px; padding: 10px; background: #efebe9; border-radius: 8px; font-size: 13px; line-height: 1.6;"></div>' +
                    '</div>' +
                    '</div>'
                // 市町村の歴史ボタンのイベントリスナーを設定
                setTimeout(() => {
                  const originBtn = document.getElementById(originBtnId)
                  if (originBtn) {
                    originBtn.addEventListener('click', async () => {
                      // トラッキング
                      const tracker = getUsageTracker(undefined, store)
                      if (tracker) tracker.trackEvent('button', 'click', 'city-ai-history-s25', { component: 'popup' })
                      const resultDiv = document.getElementById(originResultId)
                      if (!resultDiv) return
                      originBtn.disabled = true
                      originBtn.textContent = '🏛️ 考え中...'
                      resultDiv.style.display = 'block'
                      resultDiv.innerHTML = '<span style="color: #5d4037;"><span style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(93,64,55,0.3); border-top-color: #5d4037; border-radius: 50%; animation: city-spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px;"></span>AIが昭和25年の歴史を調べています...</span>'
                      if (!document.getElementById('city-spin-style')) {
                        const style = document.createElement('style')
                        style.id = 'city-spin-style'
                        style.textContent = '@keyframes city-spin { to { transform: rotate(360deg); } }'
                        document.head.appendChild(style)
                      }
                      try {
                        const response = await axios.post('/oh3/php/claudeCityHistory.php', {
                          cityName: originBtn.getAttribute('city'),
                          gunName: originBtn.getAttribute('gun'),
                          prefName: originBtn.getAttribute('pref'),
                          era: originBtn.getAttribute('era')
                        })
                        if (response.data.success && response.data.explanation) {
                          resultDiv.innerHTML = '<span style="color: #3e2723; font-weight: bold;">📖 歴史</span><br>' + DOMPurify.sanitize(response.data.explanation)
                        } else {
                          resultDiv.innerHTML = '<span style="color: #c62828;">取得できませんでした</span>'
                        }
                      } catch (error) {
                        console.error('市町村の歴史取得エラー:', error)
                        resultDiv.innerHTML = '<span style="color: #c62828;">エラーが発生しました</span>'
                      }
                      originBtn.disabled = false
                      originBtn.textContent = '🏛️ もう一度聞く'
                    })
                  }
                }, 100)
            }
            break
        }
      case 'oh-city-h12-label':
      case 'oh-city-h12-line':
      case 'oh-city-h12': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-city-h12'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.N03_004
        const gun = props.N03_003 ? props.N03_003 : ''
        const pref = props.N03_001 || ''
        const originBtnId = 'city-h12-origin-btn-' + Date.now()
        const originResultId = 'city-h12-origin-result-' + Date.now()
        if (html.value.indexOf('city-h12') === -1) {
          html.value +=
            '<div class="layer-label-div" style="min-width: 200px;">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="city-h12 popup-body">' +
            '<span style="font-size:14px;">' +
            pref +
            gun +
            '</span><br>' +
            '<span style="font-size:20px;">' +
            name +
            '</span><br>' +
            '<div style="margin-top: 8px;">' +
            '<button id="' + originBtnId + '" class="city-origin-btn" style="padding: 6px 16px; background: linear-gradient(135deg, #ff9800, #f57c00); color: white; border: none; border-radius: 20px; font-size: 13px; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.2);" city="' + name + '" gun="' + gun + '" pref="' + pref + '" era="H12">🏛️ 平成12年の歴史を聞く</button>' +
            '<div id="' + originResultId + '" style="display: none; margin-top: 8px; padding: 10px; background: #fff3e0; border-radius: 8px; font-size: 13px; line-height: 1.6;"></div>' +
            '</div>' +
            '</div>'
          // 市町村の歴史ボタンのイベントリスナーを設定
          setTimeout(() => {
            const originBtn = document.getElementById(originBtnId)
            if (originBtn) {
              originBtn.addEventListener('click', async () => {
                // トラッキング
                const tracker = getUsageTracker(undefined, store)
                if (tracker) tracker.trackEvent('button', 'click', 'city-ai-history-h12', { component: 'popup' })
                const resultDiv = document.getElementById(originResultId)
                if (!resultDiv) return
                originBtn.disabled = true
                originBtn.textContent = '🏛️ 考え中...'
                resultDiv.style.display = 'block'
                resultDiv.innerHTML = '<span style="color: #f57c00;"><span style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(245,124,0,0.3); border-top-color: #f57c00; border-radius: 50%; animation: city-spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px;"></span>AIが平成12年の歴史を調べています...</span>'
                if (!document.getElementById('city-spin-style')) {
                  const style = document.createElement('style')
                  style.id = 'city-spin-style'
                  style.textContent = '@keyframes city-spin { to { transform: rotate(360deg); } }'
                  document.head.appendChild(style)
                }
                try {
                  const response = await axios.post('/oh3/php/claudeCityHistory.php', {
                    cityName: originBtn.getAttribute('city'),
                    gunName: originBtn.getAttribute('gun'),
                    prefName: originBtn.getAttribute('pref'),
                    era: originBtn.getAttribute('era')
                  })
                  if (response.data.success && response.data.explanation) {
                    resultDiv.innerHTML = '<span style="color: #e65100; font-weight: bold;">📖 歴史</span><br>' + DOMPurify.sanitize(response.data.explanation)
                  } else {
                    resultDiv.innerHTML = '<span style="color: #c62828;">取得できませんでした</span>'
                  }
                } catch (error) {
                  console.error('市町村の歴史取得エラー:', error)
                  resultDiv.innerHTML = '<span style="color: #c62828;">エラーが発生しました</span>'
                }
                originBtn.disabled = false
                originBtn.textContent = '🏛️ もう一度聞く'
              })
            }
          }, 100)
        }
        break
      }
      case 'oh-tochiriyo-line':
      case 'oh-tochiriyo': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-tochiriyo'] })
        if (features.length === 0) return
        props = features[0].properties
        let shisetsu = ''
        switch (props.LU_1) {
          case '111': //官公庁施設
            shisetsu = '官公庁施設'
            break
          case '112': //教育文化施設
            switch (props.LU_2) {
              case '1': //教育施設
                shisetsu = '教育施設'
                break
              case '2': //文化施設
                shisetsu = '文化施設'
                break
              case '3': //宗教施設
                shisetsu = '宗教施設'
                break
            }
            break
          case '113': //厚生医療施設
            switch (props.LU_2) {
              case '1': //医療施設
                shisetsu = '医療施設'
                break
              case '2': //厚生施設
                shisetsu = '厚生施設'
                break
            }
            break
          case '114': //供給処理施設
            switch (props.LU_2) {
              case '1': //供給施設
                shisetsu = '供給施設'
                break
              case '2': //処理施設
                shisetsu = '処理施設'
                break
            }
            break
          case '121': //事務所建築物
            shisetsu = '事務所建築物'
            break
          case '122': //専用商業施設
            switch (props.LU_2) {
              case '1': //商業施設
                shisetsu = '専用商業施設'
                break
              case '2': //公衆浴場等
                shisetsu = '公衆浴場等'
                break
            }
            break
          case '123': //住商併用建物
            shisetsu = '住商併用建物'
            break
          case '124': //宿泊・遊興施設
            switch (props.LU_2) {
              case '1': //宿泊施設
                shisetsu = '宿泊施設'
                break
              case '2': //遊興施設
                shisetsu = '遊興施設'
                break
            }
            break
          case '125': //スポーツ・興行施設
            switch (props.LU_2) {
              case '1': //スポーツ施設
                shisetsu = 'スポーツ施設'
                break
              case '2': //興行施設
                shisetsu = '興行施設'
                break
            }
            break
          case '131': //独立住宅
            shisetsu = '独立住宅'
            break
          case '132': //集合住宅
            shisetsu = '集合住宅'
            break
          case '141': //専用工場
            shisetsu = '専用工場'
            break
          case '142': //住居併用工場
            shisetsu = '住居併用工場'
            break
          case '143': //倉庫運輸関係施設
            switch (props.LU_2) {
              case '1': //運輸施設等
                shisetsu = '運輸施設等'
                break
              case '2': //倉庫施設等
                shisetsu = '倉庫施設等'
                break
            }
            break
          case '150': //農林漁業施設
            shisetsu = '農林漁業施設'
            break
          case '210': //屋外利用地・仮設建物
            switch (props.LU_2) {
              case '1': //太陽光発電
                shisetsu = '太陽光発電'
                break
              case '2': //屋外駐車場
                shisetsu = '屋外駐車場'
                break
              case '3': //その他
                shisetsu = '屋外利用地・仮設建物、その他'
                break
            }
            break
          case '300': //公園、運動場等
            switch (props.LU_2) {
              case '1': //ゴルフ場
                shisetsu = 'ゴルフ場'
                break
              case '2': //その他
                shisetsu = '公園、運動場等、その他'
                break
            }
            break
          case '400': //未利用地等
            shisetsu = '未利用地等'
            break
          case '510': //道路
            shisetsu = '道路'
            break
          case '520': //鉄道・港湾等
            shisetsu = '鉄道・港湾等'
            break
          case '611': //田
            shisetsu = '田'
            break
          case '612': //畑
            shisetsu = '畑'
            break
          case '613': //樹園地
            shisetsu = '樹園地'
            break
          case '620': //採草放牧地
            shisetsu = '採草放牧地'
            break
          case '700': //水面・河川・水路
            shisetsu = '水面・河川・水路'
            break
          case '800': //原野
            shisetsu = '原野'
            break
          case '900': //森林
            shisetsu = '森林'
            break
          case '220': //その他
            shisetsu = 'その他'
            break
          case '0': //不明
            shisetsu = '不明'
            break
          case '9': //不整合
            shisetsu = '不整合'
            break
        }
        if (html.value.indexOf('tochiriyo') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="tochiriyo" style=width:200px;>' +
            '<span style="font-size: 20px">' +
            shisetsu +
            '</span>' +
            '<p>' +
            props.NAME1 +
            props.NAME2 +
            '</p>' +
            '<p>' +
            Math.floor(props.AREA) +
            'm2</p>' +
            '</div>'
        }
        break
      }
      case 'oh-hikari': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-hikari'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.light
        if (html.value.indexOf('hikari') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="hikari popup-body">' +
            '<span style="font-size:20px;">明るさ＝' +
            name +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-hikari-height': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-hikari-height'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.light
        if (html.value.indexOf('hikari-height') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="hikari-height popup-body">' +
            '<span style="font-size:20px;">明るさ＝' +
            name +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-nantora-height': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-nantora-height'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.最大浸水深
        if (html.value.indexOf('nantora-height') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="nantora-height popup-body">' +
            '<span style="font-size:20px;">最大浸水深＝' +
            name +
            'm</span>' +
            '</div>'
        }
        break
      }
      // --- 宮崎県防災ハイブリッドレイヤー ポップアップ ---
      case 'oh-hyuganada-totatsu-mvt': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-hyuganada-totatsu-mvt'] })
        if (features.length === 0) return
        props = features[0].properties
        const seconds = props['到達時間']
        if (html.value.indexOf('hyuganada-totatsu') === -1) {
          const m = Math.floor(seconds / 60)
          const s = (seconds - 60 * m).toFixed(0)
          html.value +=
            '<div class="layer-label-div-red">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="hyuganada-totatsu popup-body">' +
            '<span style="font-size:18px; color:red;">津波浸水開始時間（地震発生後）= ' + m + '分' + s + '秒</span>' +
            '</div>'
        }
        break
      }
      case 'oh-hyuganada-tsunami-mvt': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-hyuganada-tsunami-mvt'] })
        if (features.length === 0) return
        props = features[0].properties
        const depth = props['最大浸水深']
        if (html.value.indexOf('hyuganada-tsunami') === -1) {
          html.value +=
            '<div class="layer-label-div-red">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="hyuganada-tsunami popup-body">' +
            '<span style="font-size:18px; color:red;">津波浸水深＝' + depth + 'm</span>' +
            '</div>'
        }
        break
      }
      case 'oh-hyuganada-shindo-mvt': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-hyuganada-shindo-mvt'] })
        if (features.length === 0) return
        props = features[0].properties
        const maxJMA = props['最大JMA']
        if (html.value.indexOf('hyuganada-shindo') === -1) {
          let shindoLabel
          if (maxJMA < 3.5) shindoLabel = '3'
          else if (maxJMA < 4.5) shindoLabel = '4'
          else if (maxJMA < 5.0) shindoLabel = '5弱'
          else if (maxJMA < 5.5) shindoLabel = '5強'
          else if (maxJMA < 6.0) shindoLabel = '6弱'
          else if (maxJMA < 6.5) shindoLabel = '6強'
          else shindoLabel = '7'
          html.value +=
            '<div class="layer-label-div-red">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="hyuganada-shindo popup-body">' +
            '<span style="font-size:18px; color:red;">日向灘地震 震度' + shindoLabel + '（' + maxJMA + '）</span>' +
            '</div>'
        }
        break
      }
      case 'oh-nantora-shindo-mvt': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-nantora-shindo-mvt'] })
        if (features.length === 0) return
        props = features[0].properties
        const jmaVal = props['JMA独自']
        if (html.value.indexOf('nantora-shindo-mvt') === -1) {
          let shindoLabel
          if (jmaVal < 3.5) shindoLabel = '3'
          else if (jmaVal < 4.5) shindoLabel = '4'
          else if (jmaVal < 5.0) shindoLabel = '5弱'
          else if (jmaVal < 5.5) shindoLabel = '5強'
          else if (jmaVal < 6.0) shindoLabel = '6弱'
          else if (jmaVal < 6.5) shindoLabel = '6強'
          else shindoLabel = '7'
          html.value +=
            '<div class="layer-label-div-red">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="nantora-shindo-mvt popup-body">' +
            '<span style="font-size:18px; color:red;">南海トラフ 震度' + shindoLabel + '（' + jmaVal + '）</span>' +
            '</div>'
        }
        break
      }
      case 'oh-nantora-ekijyoka-mvt': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-nantora-ekijyoka-mvt'] })
        if (features.length === 0) return
        props = features[0].properties
        const plVal = props['PL独自']
        if (plVal < 0) break
        if (html.value.indexOf('nantora-ekijyoka-mvt') === -1) {
          let kanosei
          if (plVal === 0) kanosei = 'なし'
          else if (plVal <= 5) kanosei = '小'
          else if (plVal <= 15) kanosei = '中'
          else kanosei = '大'
          html.value +=
            '<div class="layer-label-div-red">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="nantora-ekijyoka-mvt popup-body">' +
            '<span style="font-size:18px; color:red;">南海トラフ 液状化可能性 ' + kanosei + '（PL=' + plVal + '）</span>' +
            '</div>'
        }
        break
      }
      case 'oh-tsunami-height': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-tsunami-height'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.A40_003
        if (html.value.indexOf('tsunami-height') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="tsunami-height popup-body">' +
            '<span style="font-size:20px;">' +
            name +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-hokkaidotsunami-height': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-hokkaidotsunami-height'] })
        if (features.length === 0) return
        props = features[0].properties
        let name = props.max
        if (name === undefined) name = props.MAX_SIN
        if (html.value.indexOf('hokkaidotsunami-height') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="hokkaidotsunami-height popup-body">' +
            '<span style="font-size:20px;">' +
            name +
            'm</span>' +
            '</div>'
        }
        break
      }
      case 'oh-city-gun': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-city-gun'] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.GUN
        const kuni = props.KUNI
        if (html.value.indexOf('gun') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="gun popup-body">' +
            '<span style="font-size:20px;">' +
            kuni +
            '</span><br>' +
            '<span style="font-size:20px;">' +
            name +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-tochiriyo100':
      case 'oh-blend-mw5-tochiriyo-vec': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        let text
        switch (props.土地利用種別) {
          case '0100': // 田
            text = '田'
            break
          case '0200': // その他の農用地
            text = 'その他の農用地'
            break
          case '0500': // 森林
            text = '森林'
            break
          case '0600': // 荒地
            text = '荒地'
            break
          case '0700': // 建物用地
            text = '建物用地'
            break
          case '0901': // 道路
            text = '道路'
            break
          case '0902': // 鉄道
            text = '鉄道'
            break
          case '1000': // その他の用地
            text = 'その他の用地'
            break
          case '1100': // 河川地及び湖沼
            text = '河川地及び湖沼'
            break
          case '1400': // 海浜
            text = '海浜'
            break
          case '1500': // 海水域
            text = '海水域'
            break
          case '1600': // ゴルフ場
            text = 'ゴルフ場'
            break
        }
        if (html.value.indexOf('tochiriyo') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="tochiriyo popup-body">' +
            '<span style="font-size:20px;">' +
            text +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-yubinku-line':
      case 'oh-yubinku-label':
      case 'oh-yubinku': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-yubinku'] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('yubinku') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="yubinku popup-body" style="max-width:300px;">' +
            '<span style="font-size:20px;">〒' + props.code + '</span><br>' +
            '<span style="font-size:16px; font-weight:bold;">' + (props.name || '') + '</span><br>' +
            '<span style="font-size:13px;">' + (props.ken || '') + ' ' + (props.shichoson || '') + '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-yochien-label':
      case 'oh-yochien-layer': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.P29_004
        const address = props.P29_005
        if (html.value.indexOf('yochien') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="yochien popup-body">' +
            '<span style="font-size:12px;">' +
            address +
            '</span><br>' +
            '<span style="font-size:20px;">' +
            name +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-jinjya-label':
      case 'oh-jinjya-layer': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        let href = ''
        if (props.description) {
          const description = props.description.split('\t')
          const url = description[3] ? description[3].split('<br>')[0] : ''
          if (url) {
            if (description[3].split('<br>').length > 1) {
              href = '<a href="' + url + '" target="_blank">神社と古事記を開く</a>'
            }
          }
        }
        const name = props.name
        if (html.value.indexOf('jinjya') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="jimjya popup-body">' +
            '<span style="font-size:20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size:14px;">' +
            href +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-kokuarea-layer': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        const name = props.name
        if (html.value.indexOf('kokuarea') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kokuarea popup-body">' +
            '<span style="font-size:20px;">' +
            name +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-hinanjyo-tsunami-label':
      case 'oh-hinanjyo-tsunami':
      case 'oh-hinanjyo-dosekiryu-label':
      case 'oh-hinanjyo-dosekiryu':
      case 'oh-hinanjyo-kozui-label':
      case 'oh-hinanjyo-kozui': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        const name = props.name
        const address = props.address
        if (html.value.indexOf('hinanjyo') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="hinanjyo popup-body">' +
            '<span style="font-size:20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size:14px;">' +
            address +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-densyohi-label':
      case 'oh-densyohi': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        const src = 'https://maps.gsi.go.jp/legend/disaster_lore/' + props.ID.split('-')[0] + '/' + props.ID + '.jpg'
        if (html.value.indexOf('densyohi') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="densyohi" style="width:300px;font-size:small">' +
            '<h4>' +
            props.碑名 +
            '</h4>' +
            '災害名=' +
            props.災害名 +
            '<br>' +
            '災害種別=' +
            props.災害種別 +
            '<br>' +
            '所在地=' +
            props.所在地 +
            '<br>' +
            '建立年=' +
            props.建立年 +
            '<hr>' +
            '' +
            props.伝承内容 +
            '<br>' +
            '<a href="' +
            src +
            '" target="_blank"><img style="object-fit:cover;height:200px;width:300px;" src="' +
            src +
            '"></a>' +
            '</div>'
        }
        break
      }
      case 'oh-r02suikei250m': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        const rate =
          '<span style="font-size:12px;">2070年人口/2020年人口＝</span>' +
          Math.floor((props.PTN_2070 / props.PTN_2020) * 100) +
          '%'
        if (html.value.indexOf('r02suikei250m') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="r02suikei250m popup-body">' +
            '<span class="popup-address" style="font-size:12px;"></span><br>' +
            // '<span style="font-size:12px;">MESH_ID=' + props.MESH_ID + '</span><br>' +
            '<span style="font-size:24px;">' +
            rate +
            '</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2070年人口＝</span>' +
            Math.floor(props.PTN_2070).toLocaleString(0) +
            '人</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2020年人口＝</span>' +
            Math.floor(props.PTN_2020).toLocaleString(0) +
            '人</span>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2050 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2050人口ピラミッド</button><br>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2040 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2040人口ピラミッド</button><br>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2030 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2030人口ピラミッド</button><br>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2020 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2020人口ピラミッド</button><br>' +
            // '<button class="suikei1km-jinkosuii pyramid-btn" mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">人口推移</button><br><br>' +
            '</div>'
        }
        break
      }
      case 'oh-r02suikei500m': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        const rate =
          '<span style="font-size:12px;">2070年人口/2020年人口＝</span>' +
          Math.floor((props.PTN_2070 / props.PTN_2020) * 100) +
          '%'
        if (html.value.indexOf('r02suikei500m') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="r02suikei500m popup-body">' +
            '<span class="popup-address" style="font-size:12px;"></span><br>' +
            // '<span style="font-size:12px;">MESH_ID=' + props.MESH_ID + '</span><br>' +
            '<span style="font-size:24px;">' +
            rate +
            '</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2070年人口＝</span>' +
            Math.floor(props.PTN_2070).toLocaleString(0) +
            '人</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2020年人口＝</span>' +
            Math.floor(props.PTN_2020).toLocaleString(0) +
            '人</span>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2050 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2050人口ピラミッド</button><br>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2040 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2040人口ピラミッド</button><br>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2030 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2030人口ピラミッド</button><br>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2020 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2020人口ピラミッド</button><br>' +
            // '<button class="suikei1km-jinkosuii pyramid-btn" mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">人口推移</button><br><br>' +
            '</div>'
        }
        break
      }
      case 'oh-r02suikei1km': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        const rate =
          '<span style="font-size:12px;">2070年人口/2020年人口＝</span>' +
          Math.floor((props.PTN_2070 / props.PTN_2020) * 100) +
          '%'
        if (html.value.indexOf('r02suikei1km') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="r02suikei1km popup-body">' +
            '<span class="popup-address" style="font-size:12px;"></span><br>' +
            // '<span style="font-size:12px;">MESH_ID=' + props.MESH_ID + '</span><br>' +
            '<span style="font-size:24px;">' +
            rate +
            '</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2070年人口＝</span>' +
            Math.floor(props.PTN_2070).toLocaleString(0) +
            '人</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2020年人口＝</span>' +
            Math.floor(props.PTN_2020).toLocaleString(0) +
            '人</span>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2050 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2050人口ピラミッド</button><br>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2040 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2040人口ピラミッド</button><br>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2030 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2030人口ピラミッド</button><br>' +
            // '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2020 mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">2020人口ピラミッド</button><br>' +
            // '<button class="suikei1km-jinkosuii pyramid-btn" mapname="' + mapName + '" MESH_ID="' + props.MESH_ID + '">人口推移</button><br><br>' +
            '</div>'
        }
        break
      }
      case 'oh-suikei1km': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        const rate =
          '<span style="font-size:12px;">2050年人口/2020年人口＝</span>' +
          Math.floor((props.PTN_2050 / props.PTN_2020) * 100) +
          '%'
        if (html.value.indexOf('suikei1km') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="suikei1km popup-body">' +
            '<span class="popup-address" style="font-size:12px;"></span><br>' +
            // '<span style="font-size:12px;">MESH_ID=' + props.MESH_ID + '</span><br>' +
            '<span style="font-size:24px;">' +
            rate +
            '</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2050年人口＝</span>' +
            Math.floor(props.PTN_2050).toLocaleString(0) +
            '人</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2020年人口＝</span>' +
            Math.floor(props.PTN_2020).toLocaleString(0) +
            '人</span>' +
            '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2050 mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">2050人口ピラミッド</button><br>' +
            '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2040 mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">2040人口ピラミッド</button><br>' +
            '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2030 mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">2030人口ピラミッド</button><br>' +
            '<button class="suikei1km-2050 pyramid-btn" suikeiYear=2020 mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">2020人口ピラミッド</button><br>' +
            '<button class="suikei1km-jinkosuii pyramid-btn" mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">人口推移</button><br><br>' +
            '</div>'
        }
        break
      }
      case 'oh-suikei500m': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        const rate =
          '<span style="font-size:12px;">2050年人口/2020年人口＝</span>' +
          Math.floor((props.PTN_2050 / props.PTN_2020) * 100) +
          '%'
        if (html.value.indexOf('suikei500m') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="suikei500m popup-body">' +
            '<span class="popup-address" style="font-size:12px;"></span><br>' +
            '<span style="font-size:12px;">MESH_ID=' +
            props.MESH_ID +
            '</span><br>' +
            '<span style="font-size:24px;">' +
            rate +
            '</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2050年人口＝</span>' +
            Math.floor(props.PTN_2050).toLocaleString(0) +
            '人</span><br>' +
            '<span style="font-size:20px;"><span style="font-size:12px;">2020年人口＝</span>' +
            Math.floor(props.PTN_2020).toLocaleString(0) +
            '人</span><br>' +
            '<button class="suikei500m-2050 pyramid-btn" suikeiYear=2050 mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">2050人口ピラミッド</button><br>' +
            '<button class="suikei500m-2050 pyramid-btn" suikeiYear=2040 mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">2040人口ピラミッド</button><br>' +
            '<button class="suikei500m-2050 pyramid-btn" suikeiYear=2030 mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">2030人口ピラミッド</button><br>' +
            '<button class="suikei500m-2050 pyramid-btn" suikeiYear=2020 mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">2020人口ピラミッド</button><br>' +
            '<button class="suikei500m-jinkosuii pyramid-btn" mapname="' +
            mapName +
            '" MESH_ID="' +
            props.MESH_ID +
            '">人口推移</button><br><br>' +
            '</div>'
        }
        break
      }
      case 'oh-kansui-label':
      case 'oh-kansui': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        const str = props.description
        const pattern = new RegExp('(?<= href=).*?(?=target)')
        const name = props.name
        if (html.value.indexOf('kansui') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kansui popup-body">' +
            '<span style="font-size:20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size:20px;"><a href=' +
            str.match(pattern)[0] +
            ' target="_blank">箇所表</a></span>' +
            '</div>'
        }
        break
      }
      case 'oh-teii': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        const name = props.G08_002
        if (html.value.indexOf('teii') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="teii popup-body">' +
            '<span style="font-size:20px;">最大浸水深＝' +
            name +
            'm</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-tokei': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        // const name = props.G08_002
        if (html.value.indexOf('tokei') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="tokei popup-body">' +
            '<span style="font-size:20px;">' +
            props.Type +
            '</span><br>' +
            '<span style="font-size:20px;">' +
            props.Pref +
            props.Cityname +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-kuikikubun': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        // const name = props.G08_002
        if (html.value.indexOf('kuikikubun') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kuikikubun popup-body">' +
            '<span style="font-size:20px;">' +
            props.区域区分 +
            '</span><br>' +
            '<span style="font-size:20px;">' +
            props.Pref +
            props.Cityname +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-yotochiikiP': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        // const name = props.G08_002
        if (html.value.indexOf('kuikikubun') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kuikikubun popup-body">' +
            '<span style="font-size:20px;">' +
            props.用途地域 +
            '</span><br>' +
            '<span style="font-size:20px;">' +
            props.Pref +
            props.Cityname +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-tkbt': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        // const name = props.G08_002
        if (html.value.indexOf('kuikikubun') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kuikikubun popup-body">' +
            '<span style="font-size:20px;">' +
            props.Type +
            '</span><br>' +
            '<span style="font-size:20px;">' +
            props.Pref +
            props.Cityname +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-kosyo': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        // const name = props.G08_002
        if (html.value.indexOf('kosyo') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kosyo popup-body">' +
            '<span style="font-size:20px;">' +
            props.W09_001 +
            '</span><br>' +
            '<span style="font-size:16px;">水深＝' +
            props.W09_003 +
            'm</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-dam-label':
      case 'oh-dam-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        function getDamType(W01_005) {
          switch (W01_005) {
            case 1:
              return 'アーチダム'
            case 2:
              return 'バットレスダム'
            case 3:
              return 'アースダム'
            case 4:
              return 'アスファルトフェイシングダム'
            case 5:
              return 'アスファルトコアダム'
            case 6:
              return 'フローティングゲートダム（可動堰）'
            case 7:
              return '重力式コンクリートダム'
            case 8:
              return '重力式アーチダム'
            case 9:
              return '重力式コンクリートダム・フィルダム複合ダム'
            case 10:
              return '中空重力式コンクリートダム'
            case 11:
              return 'マルティプルアーチダム'
            case 12:
              return 'ロックフィルダム'
            case 13:
              return '台形CSGダム'
            default:
              return '不明なダムタイプ'
          }
        }
        function getPurposes(W01_006) {
          // 文字列をカンマで分割して数値に変換
          const purposes = W01_006.split(',').map(Number)

          // 各値に対応する目的を取得
          return purposes.map((purpose) => {
            switch (purpose) {
              case 1:
                return '洪水調節、農地防災'
              case 2:
                return '不特定用水、河川維持用水'
              case 3:
                return '灌漑、特定(新規)灌漑用水'
              case 4:
                return '上水道用水'
              case 5:
                return '工業用水道用水'
              case 6:
                return '発電'
              case 7:
                return '消流雪用水'
              case 8:
                return 'レクリエーション'
              default:
                return '不明な目的'
            }
          })
        }
        function getProjectOperator(W01_011) {
          switch (W01_011) {
            case 1:
              return '国土交通省（各地方整備局、北海道開発局含む）'
            case 2:
              return '沖縄開発庁'
            case 3:
              return '農林水産省（各地方農政局含む）'
            case 4:
              return '都道府県'
            case 5:
              return '市区町村'
            case 6:
              return '水資源開発公団'
            case 7:
              return 'その他の公共企業体'
            case 8:
              return '土地改良区'
            case 9:
              return '利水組合・用水組合'
            case 10:
              return '電力会社・電源開発株式会社'
            case 11:
              return 'その他の企業'
            case 12:
              return '個人'
            case 13:
              return 'その他'
            default:
              return '不明な事業者'
          }
        }
        const damType = getDamType(Number(props.W01_005))
        const projectOperator = getProjectOperator(Number(props.W01_011))
        const purposes = getPurposes(props.W01_006)
        if (html.value.indexOf('kosyo') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kosyo popup-body">' +
            '<span style="font-size:20px;">ダム名＝' +
            props.W01_001 +
            '</span><hr>' +
            '<span style="font-size:16px;">タイプ＝' +
            damType +
            '</span><br>' +
            '<span style="font-size:16px;">水系名＝' +
            props.W01_003 +
            '</span><br>' +
            '<span style="font-size:16px;">河川名＝' +
            props.W01_004 +
            '</span><br>' +
            '<span style="font-size:16px;">目的＝' +
            purposes +
            '</span><br>' +
            '<span style="font-size:16px;">ダム規模（堤高）＝' +
            props.W01_007 +
            'm</span><br>' +
            '<span style="font-size:16px;">ダム規模（堤頂長）＝' +
            props.W01_008 +
            'm</span><br>' +
            '<span style="font-size:16px;">堤体積＝' +
            props.W01_009 +
            '000m3</span><br>' +
            '<span style="font-size:16px;">総貯水量＝' +
            props.W01_010 +
            '000m3</span><br>' +
            '<span style="font-size:16px;">事業者＝' +
            projectOperator +
            '</span><br>' +
            '<span style="font-size:16px;">竣工年＝' +
            props.W01_012 +
            '</span><br>' +
            '<span style="font-size:16px;">所在地＝' +
            props.W01_013 +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-hifuku': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        if (html.value.indexOf('hifuku') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="hifuku popup-body">' +
            '<span style="font-size:20px;">植生被覆率＝' +
            Math.round(Number(props.c_FRAC_VEG) * 1000) / 1000 +
            '</span><hr>' +
            '<span style="font-size:16px;">' +
            props.c_PREF_NAME +
            props.c_CITY_NAME +
            props.c_S_NAME +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-shizenisan': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        let name
        let link
        switch (props.WHC) {
          case '01':
            name = '知床'
            link =
              '<a href="https://www.env.go.jp/nature/isan/worldheritage/shiretoko/index.html" target="_blank">日本の世界自然遺産</a>'
            break
          case '02':
            name = '白神山地'
            link =
              '<a href="https://www.env.go.jp/nature/isan/worldheritage/shirakami/index.html" target="_blank">日本の世界自然遺産</a>'
            break
          case '03':
            name = '屋久島'
            link =
              '<a href="https://www.env.go.jp/nature/isan/worldheritage/yakushima/index.html" target="_blank">日本の世界自然遺産</a>'
            break
          case '04':
            name = '小笠原'
            link =
              '<a href="https://www.env.go.jp/nature/isan/worldheritage/ogasawara/index.html" target="_blank">日本の世界自然遺産</a>'
            break
          case '05':
            name = '奄美大島、徳之島、沖縄県北部及び西表島'
            link =
              '<a href="https://www.env.go.jp/nature/isan/worldheritage/amami/index.html" target="_blank">日本の世界自然遺産</a>'
            break
          default:
            name = '不明なWHCコード'
        }
        if (html.value.indexOf('shizenisan') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="shizenisan popup-body">' +
            '<span style="font-size:20px;">' +
            name +
            '</span><br>' +
            '<span style="font-size:16px;">' +
            link +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-bunkaisan-point-A34g':
      case 'oh-bunkaisan-point-A34e':
      case 'oh-bunkaisan-point-A34d':
      case 'oh-bunkaisan-line-A34c':
      case 'oh-bunkaisan-point-A34b':
      case 'oh-bunkaisan-layer-A34f':
      case 'oh-bunkaisan-layer-A34a': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        let layerName = layerId.split('-')[3]
        props = features[0].properties
        if (html.value.indexOf(layerName) === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="' +
            layerName +
            ' popup-body">' +
            '<span style="font-size:20px;">' +
            props[layerName + '_003'] +
            '</span><hr>' +
            '<span style="font-size:16px;">' +
            props[layerName + '_007'] +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-kokuritsukoen-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        if (html.value.indexOf('kokuritsukoen') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kokuritsukoen popup-body">' +
            '<span style="font-size:20px;">名称＝' +
            props.名称 +
            '</span><br>' +
            '<span style="font-size:16px;">地域区＝' +
            props.地域区 +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-shizenkoen-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        // props = features[0].properties
        // let regionType = ''
        switch (props.LAYER_NO) {
          case 1:
            regionType += '都市地域'
            break
          case 2:
            regionType += '市街化区域'
            break
          case 3:
            regionType += '市街化調整区域'
            break
          case 4:
            regionType += 'その他用途地域'
            break
          case 5:
            regionType += '農業地域'
            break
          case 6:
            regionType += '農用地区域'
            break
          case 7:
            regionType += '森林地域'
            break
          case 8:
            regionType += '国有林'
            break
          case 9:
            regionType += '地域森林計画対象民有林'
            break
          case 10:
            regionType += '・保安林<br>'
            break
          case 11:
            regionType += '・自然公園地域<br>'
            break
          case 12:
            regionType += '・特別地域<br>'
            break
          case 13:
            regionType += '・特別保護地区<br>'
            break
          case 14:
            regionType += '・自然保全地域<br>'
            break
          case 15:
            regionType += '・原生自然環境保全地域<br>'
            break
          case 16:
            regionType += '・特別地区<br>'
            break
        }
        if (html.value.indexOf('shizenkoen') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="shizenkoen popup-body">' +
            '<span style="font-size:14px;">市町村名＝' +
            props.CTV_NAME +
            '<hr>' +
            '<span style="font-size:14px;">土地利用基本計画＝<br><span class="region-type">' +
            regionType +
            '</span>' +
            '</div>'
        } else {
          const regionType2 = regionType.slice(0, -4)
          html.value = html.value.replace(
            /<span class="region-type">.*?<\/span>/,
            `<span class="region-type">${regionType2}</span>`
          )
        }
        break
      }
      case 'oh-plateau-tokyo23ku-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        if (html.value.indexOf('plateau-tokyo23ku') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="plateau-tokyo23ku popup-body">' +
            '<span style="font-size:24px;">高さ＝' +
            props.measuredHeight +
            'm</span><br>' +
            '<span style="font-size:14px;">建物ID＝' +
            props.建物ID +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'oh-geopark-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        let geoPark = ''
        if (props.attr === 'GGP') {
          geoPark = 'ユネスコ世界ジオパーク'
        } else {
          geoPark = 'ジオパーク'
        }
        if (html.value.indexOf('plateau-tokyo23ku') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="plateau-tokyo23ku popup-body">' +
            '<span style="font-size:20px;">名称＝' +
            props.name +
            geoPark +
            '</span><br>' +
            '<span style="font-size:14px;">運営組織＝' +
            props.organization +
            '</span><br>' +
            '<span style="font-size:14px;">事務局所在地＝' +
            props.office +
            '</span><br>' +
            '<span style="font-size:14px;">構成自治体＝' +
            props.member.replace(/<br>/gi, '・') +
            '</span><br>' +
            '<span style="font-size:14px;">' +
            '<a href="' +
            props.url +
            '" target="_blank">' +
            'リンク' +
            '</a></span>' +
            '</div>'
        }
        break
      }
      case 'oh-chiriin250m-height':
      case 'oh-chiriin250m': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        if (html.value.indexOf('chiriin250m') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="chiriin250m popup-body">' +
            // '<span class="popup-address" style="font-size:14px;"></span><br>' +
            '<span style="font-size:20px;">人口＝' +
            props['人口（人）'] +
            '人</span>' +
            '</div>'
        }
        break
      }
      case 'oh-chika-height': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        let yoto
        switch (props.L02_003) {
          case '000':
            yoto = '住宅地'
            break
          case '003':
            yoto = '宅地見込地'
            break
          case '005':
            yoto = '商業地'
            break
          case '009':
            yoto = '工業地'
            break
        }
        if (html.value.indexOf('chika-height') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="chika-height popup-body">' +
            '<span style="font-size:20px;">調査価格＝' +
            props.L02_006.toLocaleString() +
            '円</span><br>' +
            '<span style="font-size:14px;">' +
            props.L02_022 +
            '</span><br>' +
            '<span style="font-size:14px;">' +
            yoto +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-ekibetsukyakue-height':
      case 'oh-ekibetsukyaku-label':
      case 'oh-ekibetsukyaku-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        function localeString(prop) {
          if (prop) {
            return prop.toLocaleString()
          } else {
            return 0
          }
        }
        const kyakusu =
          '<span style="font-size:20px;">2022乗降客数＝' +
          localeString(props.S12_053) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2021乗降客数＝' +
          localeString(props.S12_049) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2020乗降客数＝' +
          localeString(props.S12_045) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2019乗降客数＝' +
          localeString(props.S12_041) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2018乗降客数＝' +
          localeString(props.S12_037) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2017乗降客数＝' +
          localeString(props.S12_033) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2016乗降客数＝' +
          localeString(props.S12_029) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2015乗降客数＝' +
          localeString(props.S12_025) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2014乗降客数＝' +
          localeString(props.S12_021) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2013乗降客数＝' +
          localeString(props.S12_017) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2012乗降客数＝' +
          localeString(props.S12_013) +
          '人/日</span><br>' +
          '<span style="font-size:20px;">2011乗降客数＝' +
          localeString(props.S12_009) +
          '人/日</span><br>'
        const dataset = [
          { year: 2011, value: props.S12_009 },
          { year: 2012, value: props.S12_013 },
          { year: 2013, value: props.S12_017 },
          { year: 2014, value: props.S12_021 },
          { year: 2015, value: props.S12_025 },
          { year: 2016, value: props.S12_029 },
          { year: 2017, value: props.S12_033 },
          { year: 2018, value: props.S12_037 },
          { year: 2019, value: props.S12_041 },
          { year: 2020, value: props.S12_045 },
          { year: 2021, value: props.S12_049 },
          { year: 2022, value: props.S12_053 },
        ]
        if (html.value.indexOf('ekibetsukyaku-line') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="ekibetsukyaku-line popup-body">' +
            '<span style="font-size:20px;">駅名＝' +
            props.S12_001 +
            '</span><br>' +
            '<span style="font-size:14px;">運営会社＝' +
            props.S12_002 +
            '</span><br>' +
            '<span style="font-size:14px;">路線名＝' +
            props.S12_003 +
            '</span><br>' +
            "<button style='margin-bottom:10px;' class='kyakusu-suii popup-btn' mapname='" +
            mapName +
            "' dataset='" +
            JSON.stringify(dataset) +
            "' stationname='" +
            props.S12_001 +
            "'>駅別乗降客数推移</button><br>" +
            kyakusu +
            '</div>'
        }
        break
      }
      case 'oh-jutaku-mesh-label':
      case 'oh-jutaku-mesh-line':
      case 'oh-jutaku-mesh': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-jutaku-mesh'] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('jutaku-mesh') === -1) {
          const v = (k) => Number(props[k] || 0).toLocaleString()
          const pct = (k) => (Number(props[k]) || 0).toFixed(1) + '%'
          const section = (title, rows) => {
            const r = rows.map(([l, k, fmt]) => `<tr><td style="padding:1px 6px;">${l}</td><td style="padding:1px 6px;text-align:right;font-weight:bold;">${fmt === '%' ? pct(k) : v(k)}</td></tr>`).join('')
            return `<tr><td colspan="2" style="padding:4px 6px 1px;font-weight:bold;border-top:1px solid #ddd;">${title}</td></tr>` + r
          }
          const tableHtml = '<table style="font-size:12px;">'
            + section('概要', [
              ['住宅に住む世帯数', 'h_total'], ['世帯人員総数', 'pop_total'],
            ])
            + section('所有関係', [
              ['持ち家', 'h_own'], ['公営等借家', 'h_pub'], ['民営借家', 'h_prv'],
              ['給与住宅', 'h_co'], ['間借り', 'h_room'],
            ])
            + section('率', [
              ['持家率', 'own_r', '%'], ['借家率', 'rent_r', '%'],
              ['一戸建率', 'det_r', '%'], ['共同住宅率', 'apt_r', '%'],
            ])
            + section('建て方', [
              ['一戸建', 'b_det'], ['長屋建', 'b_row'], ['共同住宅', 'b_apt'],
              ['共同住宅1-2階建', 'b_apt12'], ['共同住宅3-5階建', 'b_apt35'],
              ['共同住宅6-10階建', 'b_apt610'], ['共同住宅11-14階建', 'b_apt1114'],
              ['共同住宅15階建以上', 'b_apt15'],
            ])
            + '</table>'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="jutaku-mesh popup-body" style="max-height:400px;overflow-y:auto;">' + tableHtml + '</div>'
        }
        break
      }
      case 'oh-jinko-ido-label':
      case 'oh-jinko-ido-line':
      case 'oh-jinko-ido': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-jinko-ido'] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('jinko-ido') === -1) {
          const v = (k) => Number(props[k] || 0).toLocaleString()
          const pct = (k) => (Number(props[k]) || 0).toFixed(1) + '%'
          const section = (title, rows) => {
            const r = rows.map(([l, k, fmt]) => `<tr><td style="padding:1px 6px;">${l}</td><td style="padding:1px 6px;text-align:right;font-weight:bold;">${fmt === '%' ? pct(k) : v(k)}</td></tr>`).join('')
            return `<tr><td colspan="2" style="padding:4px 6px 1px;font-weight:bold;border-top:1px solid #ddd;">${title}</td></tr>` + r
          }
          const tableHtml = '<table style="font-size:12px;">'
            + section('従業上の地位', [
              ['雇用者（総数）', 'emp_t'], ['正規職員', 'reg_t'], ['派遣社員', 'tmp_t'],
              ['パート・アルバイト', 'prt_t'], ['自営業主', 'slf_t'], ['家族従業者', 'fam_t'],
            ])
            + section('就業形態率', [
              ['正規率', 'reg_r', '%'], ['パート率', 'prt_r', '%'], ['自営率', 'slf_r', '%'],
            ])
            + section('学歴（最終卒業）', [
              ['卒業者総数', 'grd_t'], ['小中学校', 'grd_el_t'], ['高校・旧中', 'grd_hi_t'],
              ['短大・高専', 'grd_co_t'], ['大学・大学院', 'grd_un_t'],
              ['大卒率', 'uni_r', '%'], ['高卒率', 'hi_r', '%'],
            ])
            + section('在学者', [
              ['在学者総数', 'stu_t'], ['小中学校', 'stu_el_t'], ['高校', 'stu_hi_t'],
              ['短大・高専', 'stu_co_t'], ['大学・大学院', 'stu_un_t'],
            ])
            + section('居住期間', [
              ['出生時から', 'res_b_t'], ['1年未満', 'res_1_t'], ['1〜5年', 'res_5_t'],
              ['5〜10年', 'res_10_t'], ['10〜20年', 'res_20_t'], ['20年以上', 'res_20p_t'],
            ])
            + section('経済構成世帯', [
              ['農林漁業', 'hh_agr'], ['混合', 'hh_mix'], ['非農林漁業', 'hh_nag'], ['非就業者', 'hh_non'],
            ])
            + section('従業地・通学地', [
              ['就業者・通学者', 'cwk_t'], ['就業者', 'cwk_w'], ['通学者', 'cwk_s'],
            ])
            + section('通勤・通学手段', [
              ['徒歩のみ', 'tr_wlk'], ['鉄道・電車', 'tr_rai'], ['乗合バス', 'tr_bus'],
              ['自家用車', 'tr_car'], ['オートバイ', 'tr_mtr'], ['自転車', 'tr_bic'],
              ['鉄道利用率', 'rai_r', '%'], ['自家用車率', 'car_r', '%'], ['自転車率', 'bic_r', '%'],
            ])
            + '</table>'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="jinko-ido popup-body" style="max-height:400px;overflow-y:auto;">' + tableHtml + '</div>'
        }
        break
      }
      case 'oh-keizai-census-label':
      case 'oh-keizai-census-line':
      case 'oh-keizai-census': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-keizai-census'] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('keizai-census') === -1) {
          const v = (k) => Number(props[k] || 0).toLocaleString()
          const section = (title, rows) => {
            const r = rows.map(([l, k]) => `<tr><td style="padding:1px 6px;">${l}</td><td style="padding:1px 6px;text-align:right;font-weight:bold;">${v(k)}</td></tr>`).join('')
            return `<tr><td colspan="2" style="padding:4px 6px 1px;font-weight:bold;border-top:1px solid #ddd;">${title}</td></tr>` + r
          }
          const tableHtml = '<table style="font-size:12px;">'
            + section('事業所数', [
              ['全産業', 'e_all'], ['全産業（公務除く）', 'e_ex'],
              ['第2次産業', 'e_2nd'], ['鉱業', 'e_C'], ['建設業', 'e_D'], ['製造業', 'e_E'],
              ['第3次産業', 'e_3rd'], ['電気・ガス・水道', 'e_F'], ['情報通信', 'e_G'],
              ['運輸・郵便', 'e_H'], ['卸売・小売', 'e_I'], ['金融・保険', 'e_J'],
              ['不動産', 'e_K'], ['学術研究', 'e_L'], ['宿泊・飲食', 'e_M'],
              ['生活関連', 'e_N'], ['教育', 'e_O'], ['医療・福祉', 'e_P'],
              ['複合サービス', 'e_Q'], ['その他サービス', 'e_R'], ['公務', 'e_S'],
            ])
            + section('従業者数', [
              ['全産業', 'w_all'], ['全産業（公務除く）', 'w_ex'],
              ['第2次産業', 'w_2nd'], ['鉱業', 'w_C'], ['建設業', 'w_D'], ['製造業', 'w_E'],
              ['第3次産業', 'w_3rd'], ['電気・ガス・水道', 'w_F'], ['情報通信', 'w_G'],
              ['運輸・郵便', 'w_H'], ['卸売・小売', 'w_I'], ['金融・保険', 'w_J'],
              ['不動産', 'w_K'], ['学術研究', 'w_L'], ['宿泊・飲食', 'w_M'],
              ['生活関連', 'w_N'], ['教育', 'w_O'], ['医療・福祉', 'w_P'],
              ['複合サービス', 'w_Q'], ['その他サービス', 'w_R'], ['公務', 'w_S'],
            ])
            + section('男女別従業者数', [
              ['男（全産業）', 'w_m'], ['女（全産業）', 'w_f'],
              ['男（公務除く）', 'w_m_ex'], ['女（公務除く）', 'w_f_ex'],
            ])
            + '</table>'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="keizai-census popup-body" style="max-height:400px;overflow-y:auto;">' + tableHtml + '</div>'
        }
        break
      }
      case 'oh-setai-mesh-label':
      case 'oh-setai-mesh-line':
      case 'oh-setai-mesh': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-setai-mesh'] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('setai-mesh') === -1) {
          const v = (k) => Number(props[k] || 0).toLocaleString()
          const pct = (k) => (Number(props[k]) || 0).toFixed(1) + '%'
          const section = (title, rows) => {
            const r = rows.map(([l, k, fmt]) => `<tr><td style="padding:1px 6px;">${l}</td><td style="padding:1px 6px;text-align:right;font-weight:bold;">${fmt === '%' ? pct(k) : v(k)}</td></tr>`).join('')
            return `<tr><td colspan="2" style="padding:4px 6px 1px;font-weight:bold;border-top:1px solid #ddd;">${title}</td></tr>` + r
          }
          const tableHtml = '<table style="font-size:12px;">'
            + section('人口', [
              ['総数', 'pop_t'], ['男', 'pop_m'], ['女', 'pop_f'],
            ])
            + section('年齢構成', [
              ['0-14歳', 'age0_14_t'], ['15-64歳', 'age15_64_t'], ['65歳以上', 'age65p_t'],
              ['75歳以上', 'age75p_t'], ['85歳以上', 'age85p_t'],
              ['高齢化率', 'eld_r', '%'], ['年少人口率', 'chi_r', '%'],
            ])
            + section('外国人', [
              ['総数', 'for_t'], ['男', 'for_m'], ['女', 'for_f'], ['外国人率', 'for_r', '%'],
            ])
            + section('世帯', [
              ['世帯総数', 'hh_all'], ['一般世帯', 'hh_gen'],
              ['1人世帯', 'hh_1'], ['2人世帯', 'hh_2'], ['3人世帯', 'hh_3'],
              ['4人世帯', 'hh_4'], ['5人世帯', 'hh_5'], ['6人世帯', 'hh_6'], ['7人以上', 'hh_7p'],
            ])
            + section('世帯類型', [
              ['核家族世帯', 'hh_nuc'], ['親族のみ', 'hh_rel'], ['核家族以外', 'hh_ext'],
              ['6歳未満あり', 'hh_u6'], ['65歳以上あり', 'hh_o65'],
              ['20-29歳1人世帯', 'hh_y1'], ['高齢単身', 'hh_eld1'], ['高齢夫婦', 'hh_eld2'],
            ])
            + section('率', [
              ['単独世帯率', 'sin_r', '%'], ['高齢単身率', 'eld1_r', '%'],
              ['核家族率', 'nuc_r', '%'], ['6歳未満世帯率', 'u6_r', '%'],
            ])
            + '</table>'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="setai-mesh popup-body" style="max-height:400px;overflow-y:auto;">' + tableHtml + '</div>'
        }
        break
      }
      case 'oh-uriage-mesh-label':
      case 'oh-uriage-mesh-line':
      case 'oh-uriage-mesh': {
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-uriage-mesh'] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('uriage-mesh') === -1) {
          const v = (k) => Number(props[k] || 0).toLocaleString()
          const pct = (k) => (Number(props[k]) || 0).toFixed(1) + '%'
          const section = (title, rows) => {
            const r = rows.map(([l, k, fmt]) => `<tr><td style="padding:1px 6px;">${l}</td><td style="padding:1px 6px;text-align:right;font-weight:bold;">${fmt === '%' ? pct(k) : v(k)}</td></tr>`).join('')
            return `<tr><td colspan="2" style="padding:4px 6px 1px;font-weight:bold;border-top:1px solid #ddd;">${title}</td></tr>` + r
          }
          const tableHtml = '<table style="font-size:12px;">'
            + section('産業別事業所数', [
              ['全産業', 'e_all'], ['鉱業', 'e_C'], ['建設業', 'e_D'], ['製造業', 'e_E'],
              ['電気ガス水道', 'e_F'], ['情報通信', 'e_G'], ['運輸郵便', 'e_H'],
              ['卸売小売', 'e_I'], ['金融保険', 'e_J'], ['不動産', 'e_K'],
              ['学術研究', 'e_L'], ['宿泊飲食', 'e_M'], ['生活関連', 'e_N'],
              ['教育', 'e_O'], ['医療福祉', 'e_P'], ['複合サービス', 'e_Q'], ['その他', 'e_R'],
            ])
            + section('従業者規模別（全産業）', [
              ['0-4人', 'sz_0_4'], ['5-9人', 'sz_5_9'], ['10-19人', 'sz_10_19'],
              ['20-29人', 'sz_20_29'], ['30-49人', 'sz_30_49'], ['50-99人', 'sz_50_99'],
              ['100-299人', 'sz_100_299'], ['300人以上', 'sz_300p'],
              ['小規模率', 'sm_r', '%'],
            ])
            + section('売上金額階級別（全産業）', [
              ['300万未満', 'sl_300u'], ['300-500万', 'sl_300_500'], ['500-1000万', 'sl_500_1k'],
              ['1000-3000万', 'sl_1k_3k'], ['3000-5000万', 'sl_3k_5k'], ['5000万-1億', 'sl_5k_1a'],
              ['1-10億', 'sl_1a_10a'], ['10-50億', 'sl_10a_50a'], ['50億以上', 'sl_50ap'],
              ['1億以上率', 'hi_sl_r', '%'],
            ])
            + '</table>'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="uriage-mesh popup-body" style="max-height:400px;overflow-y:auto;">' + tableHtml + '</div>'
        }
        break
      }
      case 'oh-traffic-accident-fatal':
      case 'oh-traffic-accident': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        function getDay(d) {
          const days = {
            1: '日',
            2: '月',
            3: '火',
            4: '水',
            5: '木',
            6: '金',
            7: '土',
          }
          return days[d] || '-'
        }

        function getWeather(d) {
          const weather = {
            1: '晴れ',
            2: '曇り',
            3: '雨',
            4: '霧',
            5: '雪',
          }
          return weather[d] || '不明'
        }

        function getCondition(d) {
          const conditions = {
            1: '良好だった',
            2: '湿っていた',
            3: '凍結していた',
            4: '積雪していた',
            5: '舗装されていなかった',
          }
          return conditions[d] || '不明'
        }

        function getRoadtype(d) {
          const roadTypes = {
            1: '上りカーブの',
            2: '下りカーブの',
            3: '平坦なカーブの',
            4: '上りカーブの',
            5: '下りカーブの',
            6: '平坦なカーブの',
            7: '上り直線の',
            8: '下り直線の',
            9: '平坦な直線の',
            0: '自由通行可能な',
          }
          return roadTypes[d] || ''
        }

        function getLocation(d) {
          const locations = {
            '01': '交差点',
            '07': '交差点',
            31: '交差点',
            37: '交差点',
            11: 'トンネル',
            12: '橋',
            13: '曲がり道',
            14: '道路',
            21: '踏切',
            22: '踏切',
            23: '踏切',
            '00': '場所',
          }
          return locations[d] || '場所'
        }

        function getSignal(d) {
          const signals = {
            1: '信号機がある',
            2: '信号機がある',
            3: '信号機がある',
            4: '信号機がある',
            5: '信号機が消灯中の',
            6: '信号機が故障中の',
            7: '信号機がない',
            8: '信号機がある',
          }
          return signals[d] || ''
        }

        function getType(d) {
          const types = {
            '01': '人と車両',
            21: '車両同士',
            41: '車両単独',
            61: '列車',
          }
          return types[d] || '状況不明'
        }

        function getAge(d) {
          const ages = {
            '01': '24歳以下の若年者',
            25: '25～34歳',
            35: '35～44歳',
            45: '45～54歳',
            55: '55～64歳',
            65: '65～74歳',
            75: '75歳以上の高齢者',
          }
          return ages[d] || '-'
        }
        let html0 = ''
        Object.keys(props).forEach(function (key) {
          html0 += key + '=' + props[key] + '<br>'
        })
        html0 =
          '<btn class="popup-btn popup-btn-traffic">全データ表示</btn><div style="display: none; padding: 10px">' + html0 + '</div>'

        const a_size = Number(props['負傷者数']) + Number(props['死者数'])
        let popupContent =
          '<p class="tipstyle02"><span class="style01">' +
          props['発生日時　　年'] +
          '年' +
          props['発生日時　　月'] +
          '月' +
          props['発生日時　　日'] +
          '日（' +
          getDay(props['曜日(発生年月日)']) +
          (props['祝日(発生年月日)'] === '0' ? '・祝' : '') +
          '）'
        popupContent +=
          props['発生日時　　時'] +
          '時' +
          props['発生日時　　分'] +
          '分頃</span>に発生した<span class="style01">' +
          getType(props['事故類型']) +
          'の事故</span>で、'
        popupContent +=
          (props['負傷者数'] != '0' ? '<span class="style01">' + props['負傷者数'] + '名が負傷</span>' : '') +
          (props['死者数'] != '0' ? ' ' : 'した。') +
          (props['死者数'] != '0' ? '<span class="style01">' + props['死者数'] + '名が亡くなった</span>。' : '') +
          '<br>'
        popupContent +=
          '当事者の年齢層は<span class="style01">' +
          getAge(props['年齢（当事者A）']) +
          (getAge(props['年齢（当事者B）']) != '-' ? 'と、' + getAge(props['年齢（当事者B）']) : '') +
          '</span>' +
          (a_size > 2 ? '（本票記載の２名のみ表示）' : '') +
          '。<br>'
        popupContent +=
          '現場は<span class="style01">' +
          getRoadtype(props['道路線形']) +
          (getLocation(props['道路形状']) != '交差点'
            ? getLocation(props['道路形状'])
            : getSignal(props['信号機']) + '交差点') +
          '</span>で、'
        popupContent +=
          '当時の天候は<span class="style01">' +
          getWeather(props['天候']) +
          '</span>、路面状態は<span class="style01">' +
          getCondition(props['路面状態']) +
          '</span>。</p>'
        if (html.value.indexOf('traffic-accident') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="traffic-accident popup-body">' +
            '<span style="font-size:14px;">' +
            popupContent +
            '</span>' +
            '</div>'
          html.value = html.value + html0
        }
        break
      }
      case 'oh-sekibutsu': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        function parseAndJoin(propArr) {
          if (propArr === undefined) {
            return ''
          } else {
            return JSON.parse(propArr).join(',')
          }
        }
        function parseAndJoinImage(propArr) {
          if (propArr === undefined) {
            return ''
          } else {
            const srcArr = JSON.parse(propArr).map((prop) => {
              return (
                '<a href="https://map.sekibutsu.info/images/' +
                prop +
                '" target="_blank"><img src="https://map.sekibutsu.info/images/' +
                prop +
                '"></a>'
              )
            })
            return srcArr.join('')
          }
        }

        const carousel =
          '<div class="carousel">' +
          '<div class="carousel-images">' +
          parseAndJoinImage(props.image) +
          '</div>' +
          '<div class="carousel-buttons">' +
          '<button class="carousel-button carousel-button-prev" id="prev">&#10094;</button>' +
          '<button class="carousel-button carousel-button-next" id="next">&#10095;</button>' +
          '</div>' +
          '</div>'

        if (html.value.indexOf('sekibutsu') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="sekibutsu popup-body">' +
            carousel +
            '<span style="font-size:20px;">場所＝' +
            parseAndJoin(props.place) +
            '</span><br>' +
            '<span style="font-size:12px;">所在地＝' +
            parseAndJoin(props.address) +
            '</span><br>' +
            '<span style="font-size:12px;">データ作成者＝' +
            props.contributor +
            '</span><br>' +
            '<span style="font-size:12px;">データ作成日＝' +
            props.created_at +
            '</span><br>' +
            '<span style="font-size:12px;">写真撮影日＝' +
            props.photo_date +
            '</span><br>' +
            '<span style="font-size:12px;">種類＝' +
            parseAndJoin(props.type) +
            '</span><br>' +
            '<span style="font-size:12px;">造立年（和暦）＝' +
            parseAndJoin(props.built_year) +
            '</span><br>' +
            '<span style="font-size:12px;">造立年（西暦）＝' +
            parseAndJoin(props.built_year_ce) +
            '</span><br>' +
            '<span style="font-size:12px;">タグ＝' +
            parseAndJoin(props.tag) +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-school': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        // 学校種別コードから名称に変換
        const schoolTypeMap = {
          '16001': '小学校',
          '16002': '中学校',
          '16003': '中等教育学校',
          '16004': '高等学校',
          '16005': '高等専門学校',
          '16006': '短期大学',
          '16007': '大学',
          '16011': '幼稚園',
          '16012': '特別支援学校',
          '16013': '幼保連携型認定こども園',
          '16014': '義務教育学校',
          '16015': '各種学校',
          '16016': '専修学校',
        }
        // 管理者コードから名称に変換
        const adminMap = {
          '1': '国立',
          '2': '都道府県立',
          '3': '市区町村立',
          '4': '私立',
          '0': 'その他',
        }
        const schoolType = schoolTypeMap[props.P29_003] || '不明'
        const admin = adminMap[props.P29_006] || '不明'
        // 休校コード: 0=調査なし, 1=開校中, 2=休校中
        const isClosedText = props.P29_007 === '2' ? '<span style="color:red;">（休校中）</span>' : ''
        if (html.value.indexOf('school-popup') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="school-popup popup-base">' +
            '<span style="font-size:18px; font-weight: bold;">' + (props.P29_004 || '名称不明') + '</span>' + isClosedText + '<br>' +
            '<span style="font-size:13px;">種別：' + schoolType + '</span><br>' +
            '<span style="font-size:13px;">設置者：' + admin + '</span><br>' +
            '<span style="font-size:13px;">住所：' + (props.P29_005 || '不明') + '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-ryuiki': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        if (html.value.indexOf('ryuiki') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="ryuiki popup-body">' +
            '<span style="font-size:20px;">水系名＝' +
            props.suikei +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-bunsuirei': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        if (html.value.indexOf('bunsuirei') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="bunsuirei popup-body">' +
            '<span style="font-size:20px;">水系名＝' +
            props.suikei +
            '</span><br>' +
            '<span style="font-size:14px;">水系コード＝' +
            props.W07_002 +
            '</span><br>' +
            '<span style="font-size:14px;">面積＝' +
            props.area +
            'km2</span><br>' +
            '<button style="margin-bottom: 10px;" class="bunsuikai-btn pyramid-btn" mapname="' +
            mapName +
            '" bunsuikai="' +
            props.W07_002 +
            '">分水界で抽出</button><br>' +
            '</div>'
        }
        break
      }
      case 'oh-kasen-label':
      case 'oh-kasen':
      case 'watershed-kasen-label':
      case 'watershed-kasen-line':
      case 'watershed-filtered-rivers':
      case 'watershed-clipped-rivers':
      case 'watershed-clipped-rivers-hitarea': {
        // 流域内河川は広めのバウンディングボックスで検索（クリックしやすくする）
        const point = map.project(coordinates)
        const hitRadius = 30 // ピクセル半径
        const bbox = [
          [point.x - hitRadius, point.y - hitRadius],
          [point.x + hitRadius, point.y + hitRadius]
        ]
        let features = map.queryRenderedFeatures(bbox, { layers: [layerId] })
        if (features.length === 0) {
          const point2 = map.project(e.lngLat)
          const bbox2 = [
            [point2.x - hitRadius, point2.y - hitRadius],
            [point2.x + hitRadius, point2.y + hitRadius]
          ]
          features = map.queryRenderedFeatures(bbox2, { layers: [layerId] })
        }
        props = features[0].properties
        // const name = props.G08_002
        let totalLength = ''
        if (props.W05_004 !== '名称不明') {
          totalLength = '<span style="font-size:16px;">全長＝' + (props.totallength / 1000).toFixed(2) + 'km</span><br>'
        }
        if (props.W05_002.endsWith('0000')) {
          totalLength = '<span style="font-size:16px;">全長＝' + (props.length / 1000).toFixed(2) + 'km</span><br>'
        }
        if (html.value.indexOf('kasen') === -1) {
          // 流域モードの独立レイヤーの場合は固定ラベル、それ以外はselectedLayersから取得
          const labelText = layerId.startsWith('watershed-')
            ? '河川（国土数値情報）'
            : getLabelByLayerId(layerId, store.state.selectedLayers)
          html.value +=
            '<div class="layer-label-div">' + labelText + '</div>'
          html.value +=
            '<div class="kasen popup-body">' +
            '<span style="font-size:20px;">河川名＝' +
            props.W05_004 +
            '</span><hr>' +
            '<span style="font-size:16px;">水系名＝' +
            props.suikei +
            '</span><br>' +
            totalLength +
            '<button style="margin-bottom: 10px;" class="kasen-highlights pyramid-btn" mapname="' +
            mapName +
            '" kasen="' +
            props.W05_002 +
            '" kasenmei="' +
            props.W05_004 +
            '">河川を強調</button><br>' +
            '</div>'
        }
        break
      }
      case 'oh-kanko-line':
      case 'oh-kanko-point':
      case 'oh-kanko': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        if (html.value.indexOf('kanko') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kanko popup-body">' +
            '<span style="font-size:20px;">名称＝' +
            props.P12_003 +
            '</span><br>' +
            '<span style="font-size:14px;">種別＝' +
            props.P12_004 +
            '</span>' +
            '</div>'
        }
        break
      }
      case 'user-geojson-polygons':
      case 'user-geojson-lines':
      case 'user-geojson-points':
      case 'oh-osm-overpass-layer-line':
      case 'oh-osm-overpass-layer-polygon':
      case 'oh-osm-overpass-layer-point':
      case 'ai-osm-search-polygon':
      case 'ai-osm-search-polygon-line':
      case 'ai-osm-search-line':
      case 'ai-osm-search-point':
      case 'spreadsheet-sync-point':
      case 'spreadsheet-sync-labels': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties

        // 単純に列を列挙
        let html0 = '<div style="font-size: 13px; line-height: 1.6; padding: 0 10px;">'
        Object.keys(props).forEach(function (key) {
          // 内部用プロパティはスキップ
          if (key.startsWith('_')) return
          const val = props[key]
          if (val === null || val === undefined || val === '') return
          html0 += `<div><span style="color: #666;">${key}:</span> ${val}</div>`
        })
        html0 += '</div>'

        // レイヤー種別に応じたラベル
        let popupLabel = 'データ'
        if (layerId.startsWith('oh-osm-overpass') || layerId.startsWith('ai-osm-search')) {
          popupLabel = 'OSM検索結果'
        } else if (layerId.startsWith('user-geojson')) {
          popupLabel = 'GeoJSON'
        } else if (layerId.startsWith('spreadsheet')) {
          popupLabel = 'スプレッドシート同期'
        }
        html.value += '<div class="layer-label-div">' + popupLabel + '</div>'
        html.value += html0
        break
      }
      case 'oh-syukuhaku':
      case 'oh-syukuhaku-height': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        if (html.value.indexOf('osm') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="osm popup-body">' +
            '<span style="font-size:18px;">総収容人数＝' +
            Number(props.P09_013).toLocaleString() +
            '人</span><br>' +
            '<span style="font-size:18px;">総数客室数＝' +
            Number(props.P09_014).toLocaleString() +
            '室</span><hr>' +
            '<span style="font-size:16px;">ホテル＝' +
            Number(props.P09_002).toLocaleString() +
            '施設</span><br>' +
            '<span style="font-size:16px;">旅館＝' +
            Number(props.P09_003).toLocaleString() +
            '施設</span><br>' +
            '<span style="font-size:16px;">民宿＝' +
            Number(props.P09_005).toLocaleString() +
            '施設</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-fude': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        let nouti
        if (props.land_type === 100) {
          nouti = '田'
        } else if (props.land_type === 200) {
          nouti = '畑'
        }
        if (html.value.indexOf('fude') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="fude popup-body">' +
            '<span style="font-size:26px;">' +
            nouti +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-zeni-center':
      case 'oh-zeni-circle': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('zeni') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="zeni popup-body">'
          Object.keys(props).forEach(function (key) {
            html0 += key + '=' + props[key] + '<br>'
          })
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-nogyochiiki': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        let nouti
        if (props.LAYER_NO === 5) {
          nouti = '農業地域'
        } else if (props.LAYER_NO === 6) {
          nouti = '農用地区域'
        }
        if (html.value.indexOf('nogyochiiki') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="nogyochiiki popup-body">' +
            '<span style="font-size:26px;">' +
            nouti +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-kizyunten-name-label':
      case 'oh-kizyunten-point-label':
      case 'oh-kizyunten-point': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        // クリックした地物の座標を取得
        const kizyuntenGeom = features[0].geometry
        const kizyuntenLng = kizyuntenGeom.type === 'Point' ? kizyuntenGeom.coordinates[0] : coordinates[0]
        const kizyuntenLat = kizyuntenGeom.type === 'Point' ? kizyuntenGeom.coordinates[1] : coordinates[1]
        if (html.value.indexOf('kizyunten') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="kizyunten popup-body">' +
            '<span style="font-size:24px;">点名＝' +
            props.点名 +
            '</span><hr>' +
            '<span style="font-size:20px;">基準点種別＝' +
            props.基準点種別 +
            '</span><br>' +
            '<span style="font-size:14px;">基準点コード＝' +
            props.基準点コード +
            '</span><br>' +
            '<span style="font-size:14px;">成果状態＝' +
            props.成果状態 +
            '</span><br>' +
            '<div class="street-view" style="margin-top:0px;height: 200px;width: 300px"></div>' +
            // ルーティングボタンを追加
            '<div class="popup-route-buttons" style="margin-top: 12px; display: flex; gap: 8px;">' +
            '<button class="popup-route-btn" data-lng="' + kizyuntenLng + '" data-lat="' + kizyuntenLat + '" style="padding: 3px 8px; border: 1px solid #1976D2; background: #1976D2; color: white; border-radius: 3px; cursor: pointer; font-size: 12px;">ルート</button>' +
            '<button class="popup-route-clear-btn" style="padding: 3px 8px; border: 1px solid #757575; background: #f5f5f5; color: #333; border-radius: 3px; cursor: pointer; font-size: 12px;">ルート削除</button>' +
            '</div>' +
            '</div>'
        }
        break
      }
      case 'oh-amx-vertex': {
        if (store.state.isRenzoku) return
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-amx-a-fude'] })

        function getNearestCoordinate(targetCoord, featureCoords) {
          if (!Array.isArray(featureCoords) || featureCoords.length === 0) {
            console.error('Invalid featureCoords:', featureCoords)
            return null
          }

          // ネストされた配列を展開（Polygon に対応）
          let flatCoords = featureCoords
            .flat(1)
            .filter(
              (coord) =>
                Array.isArray(coord) &&
                coord.length >= 2 &&
                typeof coord[0] === 'number' &&
                typeof coord[1] === 'number'
            )

          if (flatCoords.length === 0) {
            console.error('No valid coordinates found in featureCoords.')
            return null
          }

          let minDistance = Infinity
          let nearestCoord = null

          flatCoords.forEach((coord) => {
            let distance = getDistance(targetCoord, coord)
            if (distance < minDistance) {
              minDistance = distance
              nearestCoord = coord
            }
          })

          return nearestCoord
        }

        // Haversine 公式を使用した距離計算
        function getDistance(coord1, coord2) {
          const toRad = (deg) => (deg * Math.PI) / 180
          const R = 6371e3 // 地球の半径（メートル）

          const lat1 = toRad(coord1[1])
          const lat2 = toRad(coord2[1])
          const deltaLat = toRad(coord2[1] - coord1[1])
          const deltaLon = toRad(coord2[0] - coord1[0])

          const a =
            Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2)
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

          return R * c // メートル単位
        }

        const featureCoords = features[0].geometry.coordinates
        const targetCoord = [coordinates.lng, coordinates.lat]

        const nearest = getNearestCoordinate(targetCoord, featureCoords)

        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('amx-a-fude') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="amx-a-fude popup-body">' +
            '<span style="font-size:16px;">座標はおおよそです。</span><hr>' +
            '<span style="font-size:18px;">座標＝' +
            nearest[0].toFixed(5) +
            ', ' +
            nearest[1].toFixed(5) +
            '</span><hr>' +
            '<span style="font-size:18px;">' +
            store.state.zahyokei +
            '</span><br>' +
            '<span style="font-size:18px;">座標＝' +
            wsg84ToJgd(nearest)[0].toFixed(3) +
            ', ' +
            wsg84ToJgd(nearest)[1].toFixed(3) +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-amx-label':
      case 'oh-amx-a-fude-line':
      case 'oh-amx-a-fude': {
        if (store.state.isRenzoku) return
        const features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-amx-a-fude'] })
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('amx-a-fude') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="amx-a-fude popup-body">'
          Object.keys(props).forEach(function (key) {
            html0 += key + '=' + props[key] + '<br>'
          })
          html0 += '<div style="font-size: 14px;margin-top: 5px;" class="feature-area"></div>'
          html0 += '<div class="street-view" style="margin-top:0px;height: 200px;width: 300px"></div>'
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-offline-area-polygon': // オフライン用レイヤー
      case 'oh-homusyo-2025-polygon': {
        // 全国地番図公開マップと重なっている場合は2025登記所地図の処理をスキップ
        if (layerId === 'oh-homusyo-2025-polygon' && hasChibanzuLayer) {
          break
        }
        // 重複処理防止（同じレイヤーグループの2回目以降はスキップ）
        if (homusyo2025Handled) break
        homusyo2025Handled = true
        // オフラインレイヤーの場合も同じ処理
        const targetLayer =
          layerId === 'oh-offline-area-polygon' ? 'oh-offline-area-polygon' : 'oh-homusyo-2025-polygon'
        // ラベル/ラインクリック時にcoordinatesがズレるのを防ぐため、クリック地点で再クエリ
        const features = map.queryRenderedFeatures(map.project([e.lngLat.lng, e.lngLat.lat]), { layers: [targetLayer] })
        if (features.length === 0) return

        // ストリートビューFW更新は initApp.ts の map.on('click') ハンドラで一元管理している
        // （ここで consumeToken を呼ぶと2重課金になるため削除）

        // 周辺施設検索（トークン消費なし）
        if (store.state.nearbyPlacesFlg) {
          ;(async () => {
            store.state.nearbyPlacesLoading = true
            try {
              const { fetchNearbyPlaces, showNearbyMarkers, clearNearbyMarkers } = await import('@/js/placesService')
              clearNearbyMarkers()
              const places = await fetchNearbyPlaces(coordinates[1], coordinates[0])
              store.state.nearbyPlacesData = places
              if (places.length > 0 && map) showNearbyMarkers(places, map)
            } catch (e) {
              console.error('[Places] error:', e)
              store.state.nearbyPlacesData = []
            } finally {
              store.state.nearbyPlacesLoading = false
            }
          })()
        }

        // ⭐️---------------------------------------------------------------------------------------------------
        if (store.state.isRenzoku) {
          const targetId = `${features[0].properties['筆ID']}_${features[0].properties['地図名']}_tokizyo`
          if (!store.state.highlightedChibans.has(targetId)) {
            // ハイライトなし → ドロワー開く
            store.state.popupFeatureProperties = features[0].properties
            store.state.popupFeatureCoordinates = coordinates
            store.state.rightDrawerTitle = '2025登記所地図'
            if (!store.state.isDrawPoint) {
              store.commit('setRightDrawer', true)
            }
          } else {
            // ハイライトあり → ドロワー閉じる
            store.commit('setRightDrawer', false)
          }
          continue // 他レイヤー（公示価格等）のポップアップも処理する
        }
        // -----------------------------------------------------------------------------------------------------

        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('oh-homusyo-2025-polygon') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="oh-homusyo-2025-polygon popup-body">'
          Object.keys(props).forEach(function (key) {
            if (!key.includes('コード')) {
              html0 += key + '=' + props[key] + '<br>'
            }
          })
          html0 += '<div style="font-size: 14px;margin-top: 5px;" class="feature-area"></div>'
          html0 += '<div class="street-view" style="margin-top:0px;height: 200px;width: 300px"></div>'
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-homusyo-2026-fill':
      case 'oh-homusyo-2026-line':
      case 'oh-homusyo-2026-label':
      case 'oh-homusyo-2025-fill':
      case 'oh-homusyo-2025-line':
      case 'oh-homusyo-2025-label': {
        // 重複処理防止（fill/line/labelのfall-throughで複数回入るのを防ぐ）
        if (homusyo2025Handled) break
        homusyo2025Handled = true
        // 基準点がクリック位置にある場合は筆の処理をスキップ（基準点ポップアップを優先）
        {
          const kijPrefix = layerId.startsWith('oh-homusyo-2026') ? 'oh-homusyo-2026' : 'oh-homusyo-2025'
          const kijLayers = [`${kijPrefix}-kijyunten`, `${kijPrefix}-kijyunten-label`].filter(id => map.getLayer(id))
          if (kijLayers.length > 0) {
            const kijHit = map.queryRenderedFeatures(map.project([e.lngLat.lng, e.lngLat.lat]), { layers: kijLayers })
            if (kijHit.length > 0) break
          }
        }
        // ペイントモード中はポップアップ・右ドロワーを開かない
        if ((store.state.dialogsInfo?.map01 || []).some(d => d.id === 'oh-homusyo-2025-coloring')) break
        // ラベル/ラインクリック時にcoordinatesがズレるのを防ぐため、クリック地点(e.lngLat)で再クエリ
        const homusyoFillLayer = layerId.startsWith('oh-homusyo-2026') ? 'oh-homusyo-2026-fill' : 'oh-homusyo-2025-fill'
        const features = map.queryRenderedFeatures(map.project([e.lngLat.lng, e.lngLat.lat]), { layers: [homusyoFillLayer] })
        if (features.length === 0) return

        // ストリートビューFW更新は initApp.ts の map.on('click') ハンドラで一元管理している
        // （ここで consumeToken を呼ぶと2重課金になるため削除）

        // 周辺施設検索（トークン消費なし）
        if (store.state.nearbyPlacesFlg) {
          ;(async () => {
            store.state.nearbyPlacesLoading = true
            try {
              const { fetchNearbyPlaces, showNearbyMarkers, clearNearbyMarkers } = await import('@/js/placesService')
              clearNearbyMarkers()
              const places = await fetchNearbyPlaces(coordinates[1], coordinates[0])
              store.state.nearbyPlacesData = places
              if (places.length > 0 && map) showNearbyMarkers(places, map)
            } catch (e) {
              console.error('[Places] error:', e)
              store.state.nearbyPlacesData = []
            } finally {
              store.state.nearbyPlacesLoading = false
            }
          })()
        }

        // 連続選択モード
        const homusyoSuffix = layerId.startsWith('oh-homusyo-2026') ? '_tokizyo2026' : '_tokizyo'
        const homusyoTitle = layerId.startsWith('oh-homusyo-2026') ? '2026登記所地図' : '2025登記所地図'
        if (store.state.isRenzoku) {
          const targetId = `${features[0].properties['地図番号']}_${features[0].properties['筆ID']}_${features[0].properties['地番']}${homusyoSuffix}`
          if (!store.state.highlightedChibans.has(targetId)) {
            store.state.popupFeatureProperties = features[0].properties
            store.state.popupFeatureCoordinates = coordinates
            store.state.rightDrawerTitle = homusyoTitle
            if (!store.state.isDrawPoint) {
              store.commit('setRightDrawer', true)
            }
          } else {
            store.commit('setRightDrawer', false)
          }
          continue // 他レイヤー（公示価格等）のポップアップも処理する
        }
        // 通常モード: ポップアップ + 右ドロワー
        props = features[0].properties
        let htmlTestXml = ''
        if (html.value.indexOf('oh-homusyo-2025-fill') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          htmlTestXml += '<div class="oh-homusyo-2025-fill popup-body">'
          Object.keys(props).forEach(function (key) {
            if (!key.includes('コード') && key !== '筆ID') {
              htmlTestXml += key + '=' + props[key] + '<br>'
            }
          })
          htmlTestXml += '<div style="font-size: 14px;margin-top: 5px;" class="feature-area"></div>'
          htmlTestXml += '<div class="street-view" style="margin-top:0px;height: 200px;width: 300px"></div>'
          htmlTestXml += '<div>'
          html.value += htmlTestXml
        }
        break
      }
      case 'oh-offline-area-kijyunten': // オフライン用レイヤー
      case 'oh-offline-area-kijyunten-label': // オフライン用レイヤー
      case 'oh-homusyo-2026-kijyunten-label':
      case 'oh-homusyo-2026-kijyunten':
      case 'oh-homusyo-2025-kijyunten-label':
      case 'oh-homusyo-2025-kijyunten': {
        // オフラインレイヤーの場合は対応するレイヤー名を使用
        const kijyuntenLayer = layerId.startsWith('oh-offline-area-') ? layerId : layerId
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [kijyuntenLayer] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        // クリックした地物の座標を取得
        const kijyuntenGeom = features[0].geometry
        const kijyuntenLng = kijyuntenGeom.type === 'Point' ? kijyuntenGeom.coordinates[0] : coordinates[0]
        const kijyuntenLat = kijyuntenGeom.type === 'Point' ? kijyuntenGeom.coordinates[1] : coordinates[1]
        let html0 = ''
        if (html.value.indexOf('kijyunten') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="street-view" style="margin-top:10px;height: 200px;width: 300px"></div>'
          html0 += '<div class="kijyunten popup-body">'
          Object.keys(props).forEach(function (key) {
            html0 += key + '=' + props[key] + '<br>'
          })
          // ルーティングボタンを追加
          html0 += '<div class="popup-route-buttons" style="margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap;">'
          // html0 += `<button class="popup-route-btn" data-lng="${kijyuntenLng}" data-lat="${kijyuntenLat}" style="padding: 3px 8px; border: 1px solid #1976D2; background: #1976D2; color: white; border-radius: 3px; cursor: pointer; font-size: 12px;">ルート</button>`
          html0 += `<button class="popup-nearest-vertex-btn" data-lng="${kijyuntenLng}" data-lat="${kijyuntenLat}" data-layer="${layerId}" style="padding: 3px 8px; border: 1px solid #4CAF50; background: #4CAF50; color: white; border-radius: 3px; cursor: pointer; font-size: 12px;">最寄り筆界点</button>`
          html0 += '<button class="popup-route-clear-btn" style="padding: 3px 8px; border: 1px solid #757575; background: #f5f5f5; color: #333; border-radius: 3px; cursor: pointer; font-size: 12px;">クリア</button>'
          html0 += '</div>'
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-homusyo-2026-zukaku-label':
      case 'oh-homusyo-2026-zukaku-line':
      case 'oh-homusyo-2026-zukaku':
      case 'oh-homusyo-2025-zukaku-label':
      case 'oh-homusyo-2025-zukaku': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('zukaku') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          // html0 += '<div class="street-view" style="margin-top:10px;height: 200px;width: 300px"></div>'
          html0 += '<div class="zukaku popup-body">'
          Object.keys(props).forEach(function (key) {
            html0 += key + '=' + props[key] + '<br>'
          })
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-ibaraki-shinrin-rinpan':
      case 'oh-ibaraki-shinrin-rinpan-line':
      case 'oh-ibaraki-shinrin-rinpan-label':
      case 'oh-ibaraki-shinrin-shohan':
      case 'oh-ibaraki-shinrin-shohan-line': {
        // 小班を優先的にクエリ、なければ林班
        let features = map.queryRenderedFeatures(map.project(coordinates), {
          layers: ['oh-ibaraki-shinrin-shohan', 'oh-ibaraki-shinrin-rinpan'],
        })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), {
            layers: ['oh-ibaraki-shinrin-shohan', 'oh-ibaraki-shinrin-rinpan'],
          })
        }
        if (features.length === 0) return
        if (html.value.indexOf('ibaraki-shinrin') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="ibaraki-shinrin popup-body" style="font-weight:normal;color:#333;line-height:25px;padding:10px;">'
          const shown = new Set()
          for (let i = 0; i < features.length && i < 3; i++) {
            const p = features[i].properties
            const sl = features[i].sourceLayer || features[i].layer?.['source-layer'] || ''
            const key = `${sl}-${p.RINPAN || ''}-${p['小班'] || ''}-${p['市町村'] || p.SICHOSON_N || ''}`
            if (shown.has(key)) continue
            shown.add(key)
            if (shown.size > 1) html.value += '<hr style="border:none;border-top:1px dashed #ccc;margin:8px 0;">'
            if (sl === 'shohan') {
              // 小班の詳細情報
              html.value += '<span style="font-size:15px;font-weight:bold;">📍 ' + (p['市町村'] || '-') + '</span><br>'
              html.value += '<span style="font-size:14px;">計画区＝' + (p['計画区'] || '-') + '</span><br>'
              html.value += '<span style="font-size:14px;">林班＝' + (p['林班'] || '-') + '　小班＝' + (p['小班'] || '-') + '</span><br>'
              html.value += '<span style="font-size:14px;">林種＝<b>' + (p['林種'] || '-') + '</b></span><br>'
              html.value += '<span style="font-size:14px;">樹種＝' + (p['中樹種'] || '-') + '</span><br>'
              html.value += '<span style="font-size:14px;">面積＝' + (p['面積'] != null ? p['面積'] + ' ha' : '-') + '</span><br>'
              html.value += '<span style="font-size:14px;">林齢＝' + (p['林齢'] || '-') + '年　齢級＝' + (p['齢級'] || '-') + '</span><br>'
              html.value += '<span style="font-size:14px;">所有形態＝' + (p['所有形態'] || '-') + '</span><br>'
            } else {
              // 林班の基本情報
              html.value += '<span style="font-size:15px;font-weight:bold;">📍 ' + (p.SICHOSON_N || '-') + '</span><br>'
              html.value += '<span style="font-size:14px;">林班＝<b>' + (p.RINPAN || '-') + '</b></span><br>'
            }
          }
          html.value += '</div>'
        }
        break
      }
      case 'oh-fukuokakenshinrin': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf(layerId) === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="' + layerId + ' popup-body">'
          Object.keys(props).forEach(function (key) {
            html0 += key + '=' + props[key] + '<br>'
          })
          if (PolygonMetrics) {
            html0 +=
              '<br><span style="font-size: 14px;">面積:' +
              PolygonMetrics.area +
              ' 周長:' +
              PolygonMetrics.perimeter +
              '<br>境界点数:' +
              PolygonMetrics.vertexCount +
              '</span>'
          }
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-toshikan-label-layer':
      case 'oh-toshikan-label':
      case 'oh-toshikan-layer':
      case 'oh-gaiku-label-layer':
      case 'oh-gaiku-label':
      case 'oh-gaiku-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        // クリックした地物の座標を取得
        const gaikuGeom = features[0].geometry
        const gaikuLng = gaikuGeom.type === 'Point' ? gaikuGeom.coordinates[0] : coordinates[0]
        const gaikuLat = gaikuGeom.type === 'Point' ? gaikuGeom.coordinates[1] : coordinates[1]
        let html0 = ''
        if (html.value.indexOf('gaiku') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="street-view" style="margin-top:10px;height: 200px;width: 300px"></div>'
          html0 += '<div class="gaiku popup-body">'
          Object.keys(props).forEach(function (key) {
            html0 += key + '=' + props[key] + '<br>'
          })
          // ルーティングボタンを追加（汎用クラス名を使用）
          html0 += '<div class="popup-route-buttons" style="margin-top: 12px; display: flex; gap: 8px;">'
          html0 += `<button class="popup-route-btn" data-lng="${gaikuLng}" data-lat="${gaikuLat}" style="padding: 3px 8px; border: 1px solid #1976D2; background: #1976D2; color: white; border-radius: 3px; cursor: pointer; font-size: 12px;">ルート</button>`
          html0 += '<button class="popup-route-clear-btn" style="padding: 3px 8px; border: 1px solid #757575; background: #f5f5f5; color: #333; border-radius: 3px; cursor: pointer; font-size: 12px;">ルート削除</button>'
          html0 += '</div>'
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-fukuokakenhazard': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        props = features[0].properties
        let kuiki
        if (props.kubun === '特別警戒区域') {
          kuiki = '<span style="color: red">' + props.kubun + '</span>'
        } else {
          kuiki = props.kubun
        }
        if (html.value.indexOf('fukuokakenhazard') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="fukuokakenhazard popup-body">' +
            '<span style="font-size:22px;">' +
            props.genshoname +
            ' ' +
            kuiki +
            '</span><hr>' +
            '<span style="font-size:14px;">' +
            props.shozaichi +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-minden-circle':
      case 'oh-ntrip-circle': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('ntrip') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="ntrip popup-body">'
          Object.keys(props).forEach(function (key) {
            html0 += key + '=' + props[key] + '<br>'
          })
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'geojson-polygon-layer':
      case 'geojson-polygon-line-layer':
      case 'geojson-line-layer':
      case 'geojson-point-layer':
      case 'kml-polygon-layer':
      case 'kml-polygon-line-layer':
      case 'kml-line-layer':
      case 'kml-point-layer':
      case 'dxf-polygon-layer':
      case 'dxf-polygon-line-layer':
      case 'dxf-line-layer':
      case 'dxf-point-layer':
      case 'dxf-label':
      case 'gpx-polygon-layer':
      case 'gpx-polygon-line-layer':
      case 'gpx-line-layer':
      case 'gpx-point-layer':
      case 'zip-polygon-layer':
      case 'zip-polygon-line-layer':
      case 'zip-line-layer':
      case 'zip-point-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('kml') === -1) {
          html.value += '<div class="layer-label-div">　</div>'
          html0 += '<div class="kml popup-body">'
          Object.keys(props).forEach(function (key) {
            html0 += key + '=' + props[key] + '<br>'
          })
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'click-points-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        const jgdCoord = wsg84ToJgd(coordinates)

        if (html.value.indexOf('click-points-layer') === -1) {
          html.value += '<div class="layer-label-div">' + '座標取得＆SIMA出力' + '</div>'
          html.value +=
            '<div class="click-points-layer popup-body">' +
            '<span style="font-size:16px;">経度:' +
            coordinates[0].toFixed(3) +
            ' 緯度:' +
            coordinates[1].toFixed(3) +
            '</span><hr>' +
            '<span style="font-size:16px;">' +
            store.state.zahyokei +
            '</span><br>' +
            '<span style="font-size:16px;">' +
            jgdCoord[0].toFixed(3) +
            ', ' +
            jgdCoord[1].toFixed(3) +
            '</span><hr>' +
            '<button class="sima-output pyramid-btn" id="' +
            props.id +
            '" zahyokei="' +
            store.state.zahyokei +
            '">SIMA出力一点</button><br>' +
            '<button class="sima-output-all pyramid-btn" zahyokei="' +
            store.state.zahyokei +
            '">SIMA出力全部</button><br>' +
            '<button class="point-delete pyramid-btn" id="' +
            props.id +
            '">このポイントを削除</button><br>' +
            '<button class="point-delete-all pyramid-btn">ポイントを全て削除</button><br>' +
            '</div>'
        }
        break
      }
      case 'sima-points':
      case 'sima-Vertex': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('sima-points') === -1) {
          html.value += '<div class="layer-label-div">SIMA ポイント</div>'
          let content = '<div class="sima-points popup-base">'
          if (props.id) content += '<b>ID:</b> ' + props.id + '<br>'
          if (props.chiban) content += '<b>名称:</b> ' + props.chiban + '<br>'
          if (props.simaX !== undefined && props.simaY !== undefined) content += '<b>X:</b> ' + props.simaX + '　<b>Y:</b> ' + props.simaY + '<br>'
          if (props.h && Number(props.h) !== 0) content += '<b>標高:</b> ' + props.h + 'm<br>'
          content += '</div>'
          html.value += content
        }
        break
      }
      case 'sima-layer':
      case 'sima-polygon': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('sima-polygon') === -1) {
          html.value += '<div class="layer-label-div">SIMA 区画</div>'
          let content = '<div class="sima-polygon popup-base">'
          if (props.id) content += '<b>ID:</b> ' + props.id + '<br>'
          if (props.chiban) content += '<b>地番:</b> ' + props.chiban + '<br>'
          // 頂点XY座標一覧
          let vertices = props.vertices
          if (vertices) {
            if (typeof vertices === 'string') {
              try { vertices = JSON.parse(vertices) } catch (_) { vertices = null }
            }
            if (Array.isArray(vertices) && vertices.length > 0) {
              // 最後の頂点が最初と同じIDなら除外（ポリゴン閉合用の重複）
              if (vertices.length > 1 && vertices[vertices.length - 1].id === vertices[0].id) {
                vertices = vertices.slice(0, -1)
              }
              // 公共座標(simaX, simaY)から面積・周長を計算
              const n = vertices.length
              let area = 0
              let perimeter = 0
              for (let i = 0; i < n; i++) {
                const cur = vertices[i]
                const next = vertices[(i + 1) % n]
                const x0 = Number(cur.simaX), y0 = Number(cur.simaY)
                const x1 = Number(next.simaX), y1 = Number(next.simaY)
                // Shoelace formula
                area += x0 * y1 - x1 * y0
                // 周長
                perimeter += Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2)
              }
              area = Math.abs(area) / 2
              const tsubo = area / 3.305785
              content += '<b>面積:</b> ' + area.toFixed(2) + ' m²（' + tsubo.toFixed(2) + ' 坪）'
              if (area >= 10000) content += '（' + (area / 10000).toFixed(4) + ' ha）'
              content += '<br>'
              content += '<b>周長:</b> ' + perimeter.toFixed(2) + ' m<br>'
              content += '<b>境界点数:</b> ' + n + '<br>'

              content += '<b>頂点座標:</b><br>'
              content += '<table style="border-collapse:collapse;font-size:12px;width:100%;margin-top:4px;">'
              content += '<tr style="background:#e0e0e0;"><th style="padding:2px 6px;">ID</th><th style="padding:2px 6px;">X</th><th style="padding:2px 6px;">Y</th></tr>'
              vertices.forEach(function(v) {
                content += '<tr><td style="padding:2px 6px;text-align:center;">' + v.id + '</td>'
                content += '<td style="padding:2px 6px;text-align:right;">' + v.simaX + '</td>'
                content += '<td style="padding:2px 6px;text-align:right;">' + v.simaY + '</td></tr>'
              })
              content += '</table>'
            }
          }
          content += '</div>'
          html.value += content
        }
        break
      }
      case 'oh-fj-ekijyoka-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('ekijyoka') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="ekijyoka popup-body">' +
            '<span style="font-size:16px;">' +
            props.topographic_classification_name_ja +
            '</span><br>' +
            '<span style="font-size:16px;">液状化発生傾向=' +
            props.liquefaction_tendency_level +
            '</span><br>' +
            '<span style="font-size:16px;">' +
            props.note +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-fj-ricchitekiseika-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('ricchitekiseika') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="ricchitekiseika popup-body">' +
            '<span style="font-size:20px;">区域名＝' +
            props.kubun_name_ja +
            '</span><br>' +
            '<span style="font-size:16px;">都道府県名＝' +
            props.prefecture +
            '</span><br>' +
            '<span style="font-size:16px;">行政区域コード=' +
            props.city_code +
            '</span><br>' +
            '<span style="font-size:16px;">市町村名＝' +
            props.city_name +
            '</span><br>' +
            '<span style="font-size:16px;">区域設定年月日＝' +
            props.decision_date +
            '</span><br>' +
            '<span style="font-size:16px;">設定区分＝' +
            props.decision_classification +
            '</span><br>' +
            '<span style="font-size:16px;">設定者名＝' +
            props.decision_maker +
            '</span><br>' +
            '<span style="font-size:16px;">告示番号＝' +
            props.notice_number +
            '</span><br>' +
            '<span style="font-size:16px;">区域コード＝' +
            props.kubun_id +
            '</span><br>' +
            '<span style="font-size:16px;">区域区分＝' +
            props.area_classification_ja +
            '</span><br>' +
            '<span style="font-size:16px;">当初決定日＝' +
            props.first_decision_date +
            '</span><br>' +
            '<span style="font-size:16px;">告示番号S＝' +
            props.notice_number_s +
            '</span><br>' +
            '</div>'
        }
        break
      }
      case 'oh-saigai-rireki-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('saigai-rireki') === -1) {
          // 災害分類コードから日本語名へのマッピング
          const disasterTypeMap = {
            '11': '浸水域等',
            '12': '堤防決壊箇所等',
            '13': '高潮浸水域等',
            '14': '高潮破堤箇所等',
            '21': 'がけ崩れ等',
            '22': '地すべり等',
            '23': '河道閉塞箇所等',
            '24': '土石流等',
            '33': '液状化',
            '34': '地震土砂災害',
            '37': '津波高',
            '38': '津波浸水域',
          }
          // 日付フォーマット（YYYYMMDD → YYYY年MM月DD日）
          const formatDate = (dateStr) => {
            if (!dateStr || dateStr.length !== 8) return dateStr || '不明'
            return `${dateStr.slice(0, 4)}年${dateStr.slice(4, 6)}月${dateStr.slice(6, 8)}日`
          }
          const disasterType = disasterTypeMap[props.disastertype_code] || props.disaster_name_ja || '不明'
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value +=
            '<div class="saigai-rireki popup-base">' +
            '<span style="font-size:18px; font-weight:bold;">' + disasterType + '</span><br>' +
            '<span style="font-size:14px;">発生日：' + formatDate(props.disaster_date) + '</span><br>' +
            '<span style="font-size:14px;">出典：' + (props.disaster_source || '不明') + '</span>' +
            '</div>'
        }
        break
      }
      // ============================================================================================================
      // reinfolib（不動産情報ライブラリ）ポップアップ - 全32レイヤー対応
      // ============================================================================================================

      // 都市計画区域/区域区分 (XKT001)
      case 'oh-reinfolib-toshikeikaku-layer':
      case 'oh-reinfolib-toshikeikaku-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-toshikeikaku') === -1) {
          const areaMap = {
            '1': '市街化区域',
            '2': '市街化調整区域',
            '3': '非線引き都市計画区域',
          }
          const areaName = areaMap[props.area_classification_code] || props.area_classification_ja || '都市計画区域'
          const pref = props.prefecture || ''
          const city = props.city_name || ''
          const location = pref && city ? pref + city : (city || pref || '')

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-toshikeikaku popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">' + areaName + '</span><br>' +
            (location ? '<span style="font-size:14px;">' + location + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 用途地域 (XKT002)
      case 'oh-reinfolib-yotochiiki-layer':
      case 'oh-reinfolib-yotochiiki-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-yotochiiki') === -1) {
          const useMap = {
            '1': '第1種低層住居専用地域',
            '2': '第2種低層住居専用地域',
            '3': '第1種中高層住居専用地域',
            '4': '第2種中高層住居専用地域',
            '5': '第1種住居地域',
            '6': '第2種住居地域',
            '7': '準住居地域',
            '8': '田園住居地域',
            '9': '近隣商業地域',
            '10': '商業地域',
            '11': '準工業地域',
            '12': '工業地域',
            '13': '工業専用地域',
          }
          // use_area_ja または youto_id からマッピング
          const useName = props.use_area_ja || useMap[String(props.youto_id)] || '用途地域'
          const kenpei = props.u_building_coverage_ratio_ja || ''
          const youseki = props.u_floor_area_ratio_ja || ''
          const pref = props.prefecture || ''
          const city = props.city_name || ''
          const location = pref && city ? pref + city : (city || pref || '')

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-yotochiiki popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">' + useName + '</span><br>' +
            (location ? '<span style="font-size:12px; color: #666;">' + location + '</span><br>' : '') +
            (kenpei ? '<span style="font-size:14px;">建蔽率：' + kenpei + '</span><br>' : '') +
            (youseki ? '<span style="font-size:14px;">容積率：' + youseki + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 立地適正化計画 (XKT003)
      case 'oh-reinfolib-ricchi-layer':
      case 'oh-reinfolib-ricchi-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-ricchi') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-ricchi popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">立地適正化計画区域</span><br>' +
            (props.city_name ? '<span style="font-size:14px;">' + props.city_name + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 小学校区 (XKT004)
      case 'oh-reinfolib-shogakkoku-layer':
      case 'oh-reinfolib-shogakkoku-layer-line':
      case 'oh-reinfolib-shogakkoku-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-shogakkoku') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-shogakkoku popup-base">'
          // A27_004_ja が学校名（小学校区）
          const schoolName = props.A27_004_ja || props.school_name || '不明'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + schoolName + '</span><br>'
          // 市区町村名
          if (props.A27_002) {
            html.value += '<span style="font-size:14px; color:#666;">' + props.A27_002 + '</span><br>'
          }
          // 住所
          if (props.A27_005) {
            html.value += '<span style="font-size:12px; color:#888;">' + props.A27_005 + '</span><br>'
          }
          html.value += '</div>'
        }
        break
      }

      // 中学校区 (XKT005)
      case 'oh-reinfolib-chugakkoku-layer':
      case 'oh-reinfolib-chugakkoku-layer-line':
      case 'oh-reinfolib-chugakkoku-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-chugakkoku') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-chugakkoku popup-base">'
          // A32_004_ja が学校名
          const schoolName = props.A32_004_ja || props.school_name || '不明'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + schoolName + '</span><br>'
          // 市区町村名
          if (props.A32_002) {
            html.value += '<span style="font-size:14px; color:#666;">' + props.A32_002 + '</span><br>'
          }
          // 住所
          if (props.A32_005) {
            html.value += '<span style="font-size:12px; color:#888;">' + props.A32_005 + '</span><br>'
          }
          html.value += '</div>'
        }
        break
      }

      // 学校（施設）(XKT006)
      case 'oh-reinfolib-shogakko-layer':
      case 'oh-reinfolib-shogakko-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-shogakko') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-shogakko popup-base">'
          // P29_004_ja が正しいプロパティ名
          const name = props.P29_004_ja || props.P29_004 || props.name || '不明'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + name + '</span><br>'
          // 学校種別
          if (props.P29_003_name_ja) {
            html.value += '<span style="font-size:14px; color:#666;">' + props.P29_003_name_ja + '</span><br>'
          }
          // 住所
          if (props.P29_005_ja) {
            html.value += '<span style="font-size:12px; color:#888;">' + props.P29_005_ja + '</span><br>'
          }
          html.value += '</div>'
        }
        break
      }

      // 保育園・幼稚園等 (XKT007)
      case 'oh-reinfolib-hoikuen-layer':
      case 'oh-reinfolib-hoikuen-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-hoikuen') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-hoikuen popup-base">'
          // preSchoolName_ja が正しいプロパティ名
          const name = props.preSchoolName_ja || props.P29_004 || props.name || '不明'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + name + '</span><br>'
          // 施設種別があれば表示
          if (props.welfareFacilityClassCode) {
            const classNames = {
              '01': '保育所',
              '02': '幼稚園',
              '03': '認定こども園',
              '04': '小規模保育',
              '05': '企業主導型保育'
            }
            const className = classNames[props.welfareFacilityClassCode] || ''
            if (className) html.value += '<span style="font-size:14px; color:#666;">' + className + '</span><br>'
          }
          html.value += '</div>'
        }
        break
      }

      // 医療施設 (XKT010)
      case 'oh-reinfolib-iryo-layer':
      case 'oh-reinfolib-iryo-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-iryo') === -1) {
          const facilityName = props.P04_002_ja || props.P04_002 || ''
          const facilityType = props.P04_001_name_ja || ''  // 診療所/病院など
          const address = props.P04_003_ja || props.P04_003 || ''
          const subject = props.medical_subject_ja || props.P04_004 || ''  // 診療科目

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-iryo popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #1565c0;">' + (facilityName || '医療機関') + '</span>' +
            (facilityType ? '<span style="font-size:12px; color: #666; margin-left: 8px;">(' + facilityType + ')</span>' : '') + '<br>' +
            (subject ? '<span style="font-size:13px;">診療科目：' + subject + '</span><br>' : '') +
            (address ? '<span style="font-size:12px; color: #666;">' + address + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 福祉施設 (XKT011)
      case 'oh-reinfolib-fukushi-jido-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-fukushi') === -1) {
          const facilityName = props.P14_008_ja || ''  // 施設名
          const categoryL = props.P14_005_name_ja || ''  // 大分類（老人福祉施設など）
          const categoryM = props.P14_006_name_ja || ''  // 中分類（老人デイサービスセンターなど）
          const pref = props.P14_001 || ''
          const city = props.P14_002 || ''
          const address = props.P14_004_ja || ''
          const location = pref + city + (address ? address : '')

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-fukushi popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #7b1fa2;">' + (facilityName || '福祉施設') + '</span><br>' +
            (categoryM ? '<span style="font-size:13px;">' + categoryM + '</span><br>' : '') +
            (categoryL && categoryL !== categoryM ? '<span style="font-size:12px; color: #666;">分類：' + categoryL + '</span><br>' : '') +
            (location ? '<span style="font-size:12px; color: #666;">' + location + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 将来推計人口250mメッシュ (XKT013)
      case 'oh-reinfolib-jinkou-suikei-layer':
      case 'oh-reinfolib-jinkou-suikei-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-jinkou') === -1) {
          const selectedYear = store.state.suikeiYear || 2020
          const agePrefix = store.state.suikeiAgeGroup || 'PTN'
          const ageLabels = { PTN: '総人口', PTA: '0-14歳', PTB: '15-64歳', PTC: '65歳以上', PTD: '75歳以上' }
          const ageLabel = ageLabels[agePrefix] || '総人口'
          const showYears = [2020, 2025, 2030, 2040, 2050, 2060, 2070]
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-jinkou popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">人口推計（' + ageLabel + '）</span><br>'
          for (const y of showYears) {
            const val = props[agePrefix + '_' + y]
            if (val == null) continue
            const pop = Math.round(val).toLocaleString()
            const isCurrent = y === selectedYear
            const style = isCurrent
              ? 'font-size:15px; font-weight:bold; color:var(--main-color, #1976d2);'
              : 'font-size:13px; color:var(--oh3-color-text-secondary);'
            const marker = isCurrent ? ' ◀' : ''
            html.value += '<span style="' + style + '">' + y + '年：' + pop + '人' + marker + '</span><br>'
          }
          // 2020年比の変化率（選択中の年度）
          const baseProp = agePrefix + '_2020'
          const targetProp = agePrefix + '_' + selectedYear
          if (selectedYear !== 2020 && props[baseProp] && props[targetProp]) {
            const base = Math.round(props[baseProp])
            const target = Math.round(props[targetProp])
            if (base > 0) {
              const changeRate = (((target - base) / base) * 100).toFixed(1)
              const sign = changeRate >= 0 ? '+' : ''
              const color = changeRate >= 0 ? '#2e7d32' : '#b71c1c'
              html.value += '<span style="font-size:13px; font-weight:bold; color:' + color + ';">2020→' + selectedYear + '年: ' + sign + changeRate + '%</span><br>'
            }
          }
          html.value += '</div>'
        }
        break
      }

      // 将来推計人口 R6国政局推計（250m/1km共通）
      case 'oh-suikei-r6-250m-layer':
      case 'oh-suikei-r6-250m-layer-line':
      case 'oh-suikei-r6-1km-layer':
      case 'oh-suikei-r6-1km-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('suikei-r6') === -1) {
          const selectedYear = store.state.suikeiYear || 2020
          const agePrefix = store.state.suikeiAgeGroup || 'PTN'
          const ageLabels = { PTN: '総人口', PTA: '0-14歳', PTB: '15-64歳', PTE: '65歳以上', PTD: '75歳以上' }
          const ageLabel = ageLabels[agePrefix] || '総人口'
          // PTNは2020-2070、他は2025-2070
          const showYears = agePrefix === 'PTN'
            ? [2020, 2025, 2030, 2040, 2050, 2060, 2070]
            : [2025, 2030, 2040, 2050, 2060, 2070]
          const baseYearForAge = agePrefix === 'PTN' ? 2020 : 2025
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="suikei-r6 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">人口推計（' + ageLabel + '）</span><br>'
          if (props.MESH_ID) {
            html.value += '<span style="font-size:12px; color:var(--oh3-color-text-tertiary);">メッシュ: ' + props.MESH_ID + '</span><br>'
          }
          for (const y of showYears) {
            const val = props[agePrefix + '_' + y]
            if (val == null) continue
            const pop = Math.round(val).toLocaleString()
            const isCurrent = y === selectedYear
            const style = isCurrent
              ? 'font-size:15px; font-weight:bold; color:var(--main-color, #1976d2);'
              : 'font-size:13px; color:var(--oh3-color-text-secondary);'
            const marker = isCurrent ? ' ◀' : ''
            html.value += '<span style="' + style + '">' + y + '年：' + pop + '人' + marker + '</span><br>'
          }
          // 基準年比の変化率
          const baseProp = agePrefix + '_' + baseYearForAge
          const targetProp = agePrefix + '_' + selectedYear
          if (selectedYear !== baseYearForAge && props[baseProp] && props[targetProp]) {
            const base = Math.round(props[baseProp])
            const target = Math.round(props[targetProp])
            if (base > 0) {
              const changeRate = (((target - base) / base) * 100).toFixed(1)
              const sign = changeRate >= 0 ? '+' : ''
              const color = changeRate >= 0 ? '#2e7d32' : '#b71c1c'
              html.value += '<span style="font-size:13px; font-weight:bold; color:' + color + ';">' + baseYearForAge + '→' + selectedYear + '年: ' + sign + changeRate + '%</span><br>'
            }
          }
          html.value += '</div>'
        }
        break
      }

      // 防火・準防火地域 (XKT014)
      case 'oh-reinfolib-bokachiiki-layer':
      case 'oh-reinfolib-bokachiiki-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-bokachiiki') === -1) {
          // 正しいプロパティ: fire_prevention_ja (例: '準防火地域')
          const fireName = props.fire_prevention_ja || props.fire_prevention_district_ja || '不明'
          const location = props.city_name || ''
          const pref = props.prefecture || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-bokachiiki popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">' + fireName + '</span><br>' +
            (location ? '<span style="font-size:14px;">' + pref + location + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 駅別乗降客数 (XKT015)
      case 'oh-reinfolib-eki-jokokyaku-layer':
      case 'oh-reinfolib-eki-jokokyaku-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-eki') === -1) {
          // 正しいプロパティ: S12_001_ja(駅名), S12_002_ja(鉄道会社), S12_003_ja(路線名)
          // 乗降客数は S12_057(最新), S12_053, S12_049... と年度別に複数ある
          const stationName = props.S12_001_ja || props.S12_001 || props.name || '不明'
          const company = props.S12_002_ja || props.S12_002 || ''
          const lineName = props.S12_003_ja || props.S12_003 || ''
          // 乗降客数：最新年度から順に探す
          const passengers = props.S12_057 || props.S12_053 || props.S12_049 || props.S12_045 || props.S12_041 || props.S12_033 || 0
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-eki popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">' + stationName + '駅</span><br>' +
            (passengers > 0 ? '<span style="font-size:14px;">乗降客数：' + Number(passengers).toLocaleString() + '人/日</span><br>' : '') +
            (lineName ? '<span style="font-size:14px;">路線：' + (company ? company + ' ' : '') + lineName + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 災害危険区域 (XKT016)
      case 'oh-reinfolib-saigai-kiken-layer':
      case 'oh-reinfolib-saigai-kiken-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-saigai-kiken') === -1) {
          // 正しいプロパティ: A48_005_ja(地区名), A48_007_name_ja(災害種別), A48_008_ja(原因), A48_002(市区町村)
          const areaName = props.A48_005_ja || props.area_name || ''
          const disasterType = props.A48_007_name_ja || props.disaster_type || ''
          const cause = props.A48_008_ja || ''
          const city = props.A48_002 || props.city_name || ''
          const pref = props.A48_001 || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-saigai-kiken popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #dc143c;">災害危険区域</span><br>' +
            (areaName ? '<span style="font-size:14px;">' + areaName + '</span><br>' : '') +
            (disasterType ? '<span style="font-size:14px;">種別：' + disasterType + (cause ? '（' + cause + '）' : '') + '</span><br>' : '') +
            (city ? '<span style="font-size:14px;">' + pref + city + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 図書館 (XKT017)
      case 'oh-reinfolib-toshokan-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-toshokan') === -1) {
          // 正しいプロパティ: P27_005_ja(施設名), P27_006_ja(住所), P27_004_name_ja(施設種別)
          const libName = props.P27_005_ja || props.P27_003 || props.name || '図書館'
          const address = props.P27_006_ja || ''
          const type = props.P27_004_name_ja || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-toshokan popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">' + libName + '</span><br>' +
            (address ? '<span style="font-size:14px;">' + address + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 市区町村役場及び集会施設等 (XKT018)
      case 'oh-reinfolib-yakuba-layer':
      case 'oh-reinfolib-yakuba-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-yakuba') === -1) {
          // 正しいプロパティ: P05_003_ja(施設名), P05_004_ja(住所), P05_002_name_ja(施設種別)
          const facilityName = props.P05_003_ja || props.name || '役場・施設'
          const address = props.P05_004_ja || ''
          const facilityType = props.P05_002_name_ja || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-yakuba popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">' + facilityName + '</span><br>' +
            (facilityType ? '<span style="font-size:14px; color: #666;">' + facilityType + '</span><br>' : '') +
            (address ? '<span style="font-size:14px;">' + address + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 警察署（国土数値情報）
      case 'oh-keisatsu-polygon':
      case 'oh-keisatsu-polygon-line':
      case 'oh-keisatsu-point':
      case 'oh-keisatsu-point-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        // ポリゴンをハイライト表示（setPaintPropertyで色を変更）
        const polygonName = props.P18_005 || props.P18_001
        if (polygonName && map.getLayer('oh-keisatsu-polygon')) {
          const matchCondition = ['==', ['get', 'P18_005'], polygonName]
          map.setPaintProperty('oh-keisatsu-polygon', 'fill-color', [
            'case',
            matchCondition,
            'rgba(255, 165, 0, 0.6)', // ハイライト色（オレンジ）
            'rgba(0, 100, 200, 0.15)', // デフォルト色
          ])
          map.setPaintProperty('oh-keisatsu-polygon-line', 'line-color', [
            'case',
            matchCondition,
            'rgba(255, 100, 0, 1)', // ハイライト色
            'rgba(0, 100, 200, 0.8)', // デフォルト色
          ])
          map.setPaintProperty('oh-keisatsu-polygon-line', 'line-width', [
            'case',
            matchCondition,
            4, // ハイライト時の線幅
            1.5, // デフォルトの線幅
          ])
        }

        if (html.value.indexOf('keisatsu-popup') === -1) {
          // ポイントデータ: P18_001(名称), P18_002(行政コード), P18_003(種別), P18_004(所在地)
          // ポリゴンデータ: P18_005(名称), P18_006〜P18_020(管轄町丁目)
          const name = props.P18_001 || props.P18_005 || '警察署'
          const address = props.P18_004 || ''
          const typeCode = props.P18_003
          const typeName = typeCode === 1 ? '警察本部' : typeCode === 2 ? '警察署' : typeCode === 3 ? '交番・駐在所' : ''
          // 管轄区域の町丁目を収集
          const jurisdictions = []
          for (let i = 6; i <= 20; i++) {
            const key = 'P18_0' + (i < 10 ? '0' + i : i)
            if (props[key]) jurisdictions.push(props[key])
          }
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="keisatsu-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #1565c0;">🚔 ' + name + '</span><br>' +
            (typeName ? '<span style="font-size:13px; color: #666;">' + typeName + '</span><br>' : '') +
            (address ? '<span style="font-size:13px;">' + address + '</span><br>' : '') +
            (jurisdictions.length > 0 ? '<span style="font-size:12px; color: #888;">管轄: ' + jurisdictions.slice(0, 5).join('、') + (jurisdictions.length > 5 ? ' 他' : '') + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 郵便局（国土数値情報）
      case 'oh-yubinkyoku-point':
      case 'oh-yubinkyoku-point-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('yubinkyoku-popup') === -1) {
          // P30_005(名称), P30_006(所在地), P30_004(種別コード)
          const name = props.P30_005 || '郵便局'
          const address = props.P30_006 || ''
          const typeCode = props.P30_004
          // 種別: 1=普通郵便局, 2=特定郵便局, 3=簡易郵便局
          const typeName = typeCode === 1 ? '普通郵便局' : typeCode === 2 ? '特定郵便局' : typeCode === 3 ? '簡易郵便局' : ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="yubinkyoku-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #e53935;">' + name + '</span><br>' +
            (typeName ? '<span style="font-size:13px; color: #666;">' + typeName + '</span><br>' : '') +
            (address ? '<span style="font-size:13px;">' + address + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 消防署（国土数値情報）
      case 'oh-shobosho-polygon':
      case 'oh-shobosho-polygon-line':
      case 'oh-shobosho-point':
      case 'oh-shobosho-point-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        // ポリゴンをハイライト表示（setPaintPropertyで色を変更）
        const polygonName = props.P17_005 || props.P17_001
        if (polygonName && map.getLayer('oh-shobosho-polygon')) {
          const matchCondition = ['==', ['get', 'P17_005'], polygonName]
          map.setPaintProperty('oh-shobosho-polygon', 'fill-color', [
            'case',
            matchCondition,
            'rgba(255, 200, 0, 0.6)', // ハイライト色（黄色）
            'rgba(255, 87, 34, 0.15)', // デフォルト色
          ])
          map.setPaintProperty('oh-shobosho-polygon-line', 'line-color', [
            'case',
            matchCondition,
            'rgba(255, 150, 0, 1)', // ハイライト色
            'rgba(255, 87, 34, 0.8)', // デフォルト色
          ])
          map.setPaintProperty('oh-shobosho-polygon-line', 'line-width', [
            'case',
            matchCondition,
            4, // ハイライト時の線幅
            1.5, // デフォルトの線幅
          ])
        }

        if (html.value.indexOf('shobosho-popup') === -1) {
          // ポイント: P17_001(名称), P17_002(行政コード), P17_003(種別), P17_004(所在地)
          // ポリゴン: P17_005(名称), P17_006〜P17_026(管轄町丁目)
          const name = props.P17_001 || props.P17_005 || '消防署'
          const address = props.P17_004 || ''
          const typeCode = props.P17_003
          const typeName = typeCode === 1 ? '消防本部' : typeCode === 2 ? '消防署' : typeCode === 3 ? '分署・出張所' : ''
          // 管轄区域の町丁目を収集
          const jurisdictions = []
          for (let i = 6; i <= 26; i++) {
            const key = 'P17_0' + (i < 10 ? '0' + i : i)
            if (props[key]) jurisdictions.push(props[key])
          }
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="shobosho-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #ff5722;">' + name + '</span><br>' +
            (typeName ? '<span style="font-size:13px; color: #666;">' + typeName + '</span><br>' : '') +
            (address ? '<span style="font-size:13px;">' + address + '</span><br>' : '') +
            (jurisdictions.length > 0 ? '<span style="font-size:12px; color: #888;">管轄: ' + jurisdictions.slice(0, 5).join('、') + (jurisdictions.length > 5 ? ' 他' : '') + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 燃料給油所（国土数値情報）
      case 'oh-gas-station-point':
      case 'oh-gas-station-point-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('gas-station-popup') === -1) {
          // P07_001(店舗分類コード: 1=SS), P07_002(所在地)
          const address = props.P07_002 || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="gas-station-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #43a047;">ガソリンスタンド</span><br>' +
            (address ? '<span style="font-size:13px;">' + address + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 都市公園（国土数値情報）
      case 'oh-city-park-point':
      case 'oh-city-park-point-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('city-park-popup') === -1) {
          const name = props.P13_003 || ''
          const pref = props.P13_005 || ''
          const city = props.P13_006 || ''
          const location = pref + city
          const year = props.P13_007 || ''
          const area = props.P13_008 ? Number(props.P13_008).toLocaleString() + ' m²' : ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="city-park-popup popup-base">' +
            (name ? '<span style="font-size:16px; font-weight:bold; color: #2e7d32;">' + name + '</span><br>' : '') +
            (location ? '<span style="font-size:13px;">' + location + '</span><br>' : '') +
            (year ? '<span style="font-size:12px; color:#666;">開園: ' + year + '年</span><br>' : '') +
            (area ? '<span style="font-size:12px; color:#666;">面積: ' + area + '</span>' : '') +
            '</div>'
        }
        break
      }

      // 工業用地（国土数値情報）
      case 'oh-industrial-complex':
      case 'oh-industrial-complex-line':
      case 'oh-industrial-isolated':
      case 'oh-industrial-isolated-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('industrial-land-popup') === -1) {
          const isComplex = layerId.includes('complex')
          const typeName = isComplex ? '工業団地' : '単独工場用地'
          const name = props.L05_002 || ''
          const city = props.L05_004 || ''
          const area = props.L05_010 ? props.L05_010 + ' ha' : ''
          const company = props.L05_015 || props.L05_016 || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="industrial-land-popup popup-base">' +
            '<span style="font-size:12px; color:#7b1fa2;">' + typeName + '</span><br>' +
            (name ? '<span style="font-size:16px; font-weight:bold; color: #7b1fa2;">' + name + '</span><br>' : '') +
            (city ? '<span style="font-size:13px;">' + city + '</span><br>' : '') +
            (area ? '<span style="font-size:12px; color:#666;">面積: ' + area + '</span><br>' : '') +
            (company ? '<span style="font-size:12px; color:#666;">' + company + '</span>' : '') +
            '</div>'
        }
        break
      }

      // 漁港（国土数値情報）
      case 'oh-gyoko-point':
      case 'oh-gyoko-point-label':
      case 'oh-gyoko-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('gyoko-popup') === -1) {
          // ポイントデータ属性:
          // C09_001: 漁港コード, C09_002: 漁港名, C09_003: 行政区域コード
          // C09_004: 漁港種別 (1:第1種, 2:第2種, 3:第3種, 4:特定第3種)
          // C09_005: 漁港管理者区分 (1:市町村, 2:都道府県)
          // C09_006: 都道府県名, C09_007: 漁港指定日, C09_008: 漁港管理者名
          // C09_009: 外郭施設延長, C09_010: けい留施設延長
          const name = props.C09_002 || '漁港'
          const pref = props.C09_006 || ''
          const manager = props.C09_008 || ''
          const typeCode = Number(props.C09_004)
          const typeName = typeCode === 1 ? '第1種' : typeCode === 2 ? '第2種' : typeCode === 3 ? '第3種' : typeCode === 4 ? '特定第3種' : ''
          const date = props.C09_007 || ''
          const gaikaku = props.C09_009 ? Number(props.C09_009).toLocaleString() + ' m' : ''
          const keiryu = props.C09_010 ? Number(props.C09_010).toLocaleString() + ' m' : ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="gyoko-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #0097a7;">⚓ ' + name + '</span><br>' +
            (typeName ? '<span style="font-size:13px; color: #666;">' + typeName + '漁港</span><br>' : '') +
            (pref ? '<span style="font-size:13px;">' + pref + '</span><br>' : '') +
            (manager ? '<span style="font-size:12px; color:#666;">管理: ' + manager + '</span><br>' : '') +
            (date ? '<span style="font-size:12px; color:#888;">指定日: ' + date + '</span><br>' : '') +
            (gaikaku ? '<span style="font-size:12px; color:#888;">外郭施設: ' + gaikaku + '</span><br>' : '') +
            (keiryu ? '<span style="font-size:12px; color:#888;">けい留施設: ' + keiryu + '</span>' : '') +
            '</div>'
        }
        break
      }

      // 過疎地域（平成12年・国土数値情報）
      case 'oh-kaso-h12':
      case 'oh-kaso-h12-line':
      case 'oh-kaso-h12-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('kaso-h12-popup') === -1) {
          // 属性:
          // A17_003: 都道府県名, A17_004: 支庁名, A17_005: 郡名
          // A17_006: 市区町村名, A17_007: 過疎区分コード
          // A17_008: 開始年月日, A17_010: 終了年月日
          const pref = props.A17_003 || ''
          const gun = props.A17_005 || ''
          const muni = props.A17_006 || ''
          const kasoCode = Number(props.A17_007)
          const kasoName = kasoCode === 1 ? '過疎地域' : kasoCode === 2 ? 'みなし過疎' : kasoCode === 3 ? '一部過疎' : '過疎地域'
          const startDate = props.A17_008 || ''
          const endDate = props.A17_010 || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kaso-h12-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #8b4513;">🏚️ ' + muni + '</span><br>' +
            '<span style="font-size:13px; color: #666;">' + kasoName + '</span><br>' +
            (pref ? '<span style="font-size:13px;">' + pref + (gun ? ' ' + gun : '') + '</span><br>' : '') +
            (startDate ? '<span style="font-size:12px; color:#888;">指定日: ' + startDate + '</span><br>' : '') +
            (endDate && endDate !== '2010-03-31' ? '<span style="font-size:12px; color:#888;">終了日: ' + endDate + '</span>' : '') +
            '</div>'
        }
        break
      }

      // 振興山村（2016年・国土数値情報）
      case 'oh-shinko-sanson':
      case 'oh-shinko-sanson-line':
      case 'oh-shinko-sanson-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('shinko-sanson-popup') === -1) {
          // 属性:
          // A24_003: 都道府県名, A24_006: 市区町村名, A24_007: 市区町村名（フル）
          // A24_010: 指定年度
          const pref = props.A24_003 || ''
          const muni = props.A24_006 || ''
          const muniFull = props.A24_007 || ''
          const year = props.A24_010 || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="shinko-sanson-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #228b22;">🏔️ ' + muni + '</span><br>' +
            '<span style="font-size:13px; color: #666;">振興山村</span><br>' +
            (pref ? '<span style="font-size:13px;">' + pref + '</span><br>' : '') +
            (muniFull && muniFull !== muni ? '<span style="font-size:12px; color:#666;">' + muniFull + '</span><br>' : '') +
            (year ? '<span style="font-size:12px; color:#888;">指定年度: ' + year + '</span>' : '') +
            '</div>'
        }
        break
      }

      // 特定農山村地域（平成28年・国土数値情報）
      case 'oh-tokutei-nosan':
      case 'oh-tokutei-nosan-line':
      case 'oh-tokutei-nosan-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('tokutei-nosan-popup') === -1) {
          // 属性:
          // A25_003: 都道府県名, A25_004: 支庁名, A25_005: 郡名
          // A25_006: 市区町村名, A25_007: 市区町村名（フル）
          const pref = props.A25_003 || ''
          const shicho = props.A25_004 || ''
          const gun = props.A25_005 || ''
          const muni = props.A25_006 || ''
          const muniFull = props.A25_007 || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tokutei-nosan-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #e65100;">🌾 ' + muni + '</span><br>' +
            '<span style="font-size:13px; color: #666;">特定農山村地域</span><br>' +
            (pref ? '<span style="font-size:13px;">' + pref + (shicho ? ' ' + shicho : '') + (gun ? ' ' + gun : '') + '</span><br>' : '') +
            (muniFull && muniFull !== muni ? '<span style="font-size:12px; color:#666;">' + muniFull + '</span>' : '') +
            '</div>'
        }
        break
      }

      // 豪雪地帯（国土数値情報）- ポリゴン
      case 'oh-gousetsu-chitai':
      case 'oh-gousetsu-chitai-line':
      case 'oh-gousetsu-chitai-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('gousetsu-chitai-popup') === -1) {
          // 属性:
          // A22_100002: 市区町村名, A22_100005: 分類コード（1=特別豪雪地帯、その他=豪雪地帯）
          const muni = props.A22_100002 || props.A22_100004 || ''
          const bunruiCode = Number(props.A22_100005)
          const bunruiName = bunruiCode === 1 ? '特別豪雪地帯' : '豪雪地帯'
          const color = bunruiCode === 1 ? '#003399' : '#4682b4'
          const emoji = '❄️'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="gousetsu-chitai-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: ' + color + ';">' + emoji + ' ' + muni + '</span><br>' +
            '<span style="font-size:13px; color: #666;">' + bunruiName + '</span>' +
            '</div>'
        }
        break
      }

      // 豪雪地帯（国土数値情報）- 気象観測点
      case 'oh-gousetsu-point':
      case 'oh-gousetsu-point-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('gousetsu-point-popup') === -1) {
          // 属性:
          // A22_000002: 観測地点名, A22_000003: 所在地
          // A22_010001: 平均値(cm), A22_010002: 最大値(cm)
          const name = props.A22_000002 || ''
          const location = props.A22_000003 || ''
          const avgSnow = props.A22_010001 ? Math.round(props.A22_010001) : null
          const maxSnow = props.A22_010002 ? Math.round(props.A22_010002) : null
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="gousetsu-point-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #c0392b;">🌨️ ' + name + '</span><br>' +
            (location ? '<span style="font-size:12px; color: #666;">' + location + '</span><br>' : '') +
            (avgSnow !== null ? '<span style="font-size:13px;">平均最深積雪: <b>' + avgSnow + '</b> cm</span><br>' : '') +
            (maxSnow !== null ? '<span style="font-size:13px;">最大最深積雪: <b>' + maxSnow + '</b> cm</span>' : '') +
            '</div>'
        }
        break
      }

      // ヘリポート（平成25年・国土数値情報）
      case 'oh-heliport':
      case 'oh-heliport-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('heliport-popup') === -1) {
          // 属性:
          // N11_001: 名称, N11_002: 種別コード, N11_003: 種別名
          // N11_004: 設置者コード, N11_005: 所在市区町村, N11_007: サイズ
          const name = props.N11_001 || ''
          const typeName = props.N11_003 || ''
          const city = props.N11_005 || ''
          const size = props.N11_007 || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="heliport-popup popup-base popup-min-w280">' +
            '<span style="font-size:16px; font-weight:bold; color: #1565c0;">🚁 ' + name + '</span><br>' +
            (typeName ? '<span style="font-size:13px; color: #666;">' + typeName + '</span><br>' : '') +
            (city ? '<span style="font-size:13px;">' + city + '</span><br>' : '') +
            (size ? '<span style="font-size:12px; color:#888;">サイズ: ' + size + '</span>' : '') +
            '</div>'
        }
        break
      }

      // 上水道・給水区域（国土数値情報）
      case 'oh-josuidou-area':
      case 'oh-josuidou-area-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        // ポリゴンをハイライト表示
        const areaName = props.P21A_002
        if (areaName && map.getLayer('oh-josuidou-area')) {
          const matchCondition = ['==', ['get', 'P21A_002'], areaName]
          map.setPaintProperty('oh-josuidou-area', 'fill-color', [
            'case',
            matchCondition,
            'rgba(255, 165, 0, 0.5)',
            ['==', ['get', 'P21A_003'], 1], 'rgba(30, 136, 229, 0.15)',
            ['==', ['get', 'P21A_003'], 2], 'rgba(67, 160, 71, 0.15)',
            ['==', ['get', 'P21A_003'], 3], 'rgba(255, 152, 0, 0.15)',
            'rgba(30, 136, 229, 0.15)',
          ])
          map.setPaintProperty('oh-josuidou-area-line', 'line-color', [
            'case',
            matchCondition,
            'rgba(255, 100, 0, 1)',
            ['==', ['get', 'P21A_003'], 1], 'rgba(30, 136, 229, 0.8)',
            ['==', ['get', 'P21A_003'], 2], 'rgba(67, 160, 71, 0.8)',
            ['==', ['get', 'P21A_003'], 3], 'rgba(255, 152, 0, 0.8)',
            'rgba(30, 136, 229, 0.8)',
          ])
          map.setPaintProperty('oh-josuidou-area-line', 'line-width', [
            'case',
            matchCondition,
            4,
            1.5,
          ])
        }

        if (html.value.indexOf('josuidou-area-popup') === -1) {
          const s = 'style="font-size:13px;padding:2px 5px;"'
          const operator = props.P21A_001 || ''
          const name = props.P21A_002 || '上水道事業'
          const typeCode = props.P21A_003
          const typeName = typeCode === 1 ? '上水道' : typeCode === 2 ? '簡易水道（公営）' : typeCode === 3 ? '簡易水道（非公営）' : ''
          const population = props.P21A_004 ? Number(props.P21A_004).toLocaleString() + ' 人' : ''
          const waterSupply = props.P21A_005 ? Number(props.P21A_005).toLocaleString() + ' m³/日' : ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="josuidou-area-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #0277bd;">💧 ' + name + '</span><br>' +
            (typeName ? '<span style="font-size:13px; color: #666;">' + typeName + '</span><br>' : '') +
            (operator ? '<span style="font-size:13px;">' + operator + '</span><br>' : '') +
            '<table class="popup-table" style="margin-top:4px;">' +
            (population ? '<tr><th ' + s + '>給水人口</th><td ' + s + '>' + population + '</td></tr>' : '') +
            (waterSupply ? '<tr><th ' + s + '>日最大給水量</th><td ' + s + '>' + waterSupply + '</td></tr>' : '') +
            '</table>' +
            '</div>'
        }
        break
      }

      // 上水道・浄水場（国土数値情報）
      case 'oh-josuidou-point':
      case 'oh-josuidou-point-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('josuidou-point-popup') === -1) {
          const s = 'style="font-size:13px;padding:2px 5px;"'
          const operator = props.P21B_001 || ''
          const projectName = props.P21B_002 || ''
          const facilityName = props.P21B_003 || '浄水場'
          const waterSupply = props.P21B_004 ? Number(props.P21B_004).toLocaleString() + ' m³/日' : ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="josuidou-point-popup popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #0277bd;">🏭 ' + facilityName + '</span><br>' +
            (projectName ? '<span style="font-size:13px; color: #666;">' + projectName + '</span><br>' : '') +
            (operator ? '<span style="font-size:13px;">' + operator + '</span><br>' : '') +
            (waterSupply ? '<table class="popup-table" style="margin-top:4px;"><tr><th ' + s + '>日最大給水量</th><td ' + s + '>' + waterSupply + '</td></tr></table>' : '') +
            '</div>'
        }
        break
      }

      // GSI単写真（国土地理院）
      case 'oh-gsi-pp-full': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('gsi-pp-popup') === -1) {
          // 属性: ID, 撮影年月日, 撮影縮尺, 撮影高度, カラー種別, 撮影計画機関
          const id = props.ID || ''
          const date = props['撮影年月日'] || ''
          const scale = props['撮影縮尺'] || ''
          const altitude = props['撮影高度'] || ''
          const colorType = props['カラー種別'] || ''
          const org = props['撮影計画機関'] || ''

          // 撮影年月日を年月日形式に整形
          let dateStr = ''
          if (date) {
            try {
              const d = new Date(date)
              if (!isNaN(d.getTime())) {
                dateStr = d.getFullYear() + '年' + (d.getMonth() + 1) + '月' + d.getDate() + '日'
              } else {
                dateStr = date
              }
            } catch {
              dateStr = date
            }
          }

          // 撮影機関に応じた色（layers.jsと同じ）
          const orgColors = {
            '国土地理院': '#1976D2',  // 青
            '米軍': '#4CAF50',        // 緑
            '陸軍': '#795548'         // 茶
          }
          const orgColor = orgColors[org] || '#9E9E9E'
          const isColor = colorType === 'カラー'
          const typeIcon = isColor ? '🌈' : '⚫'

          // 国土地理院の写真閲覧ページURL
          const gsiPhotoUrl = id ? 'https://service.gsi.go.jp/map-photos/app/map?search=photo&id=' + id : ''

          // サムネイル画像用のユニークID
          const thumbImgId = 'gsi-pp-thumb-' + id

          // APIからJSON取得してサムネイルを表示（プロキシ経由）
          if (id) {
            fetch('/oh3/php/gsiPhotoProxy.php?id=' + id)
              .then(res => res.json())
              .then(json => {
                // サムネイルURLを構築
                const thumbUrl = 'https://service.gsi.go.jp/map-photos/contents/screen/mapphoto/img/' + json.results.url_image_thumb
                // 画像要素を更新
                const imgEl = document.getElementById(thumbImgId)
                if (imgEl) {
                  imgEl.src = thumbUrl
                  imgEl.style.display = 'block'
                }
              })
              .catch(err => {
                console.warn('[GSI PP API] Error:', err)
              })
          }

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="gsi-pp-popup popup-base-relaxed popup-min-w280">' +
            '<img id="' + thumbImgId + '" style="width:300px; height:200px; object-fit:cover; object-position:center; border-radius:4px; margin-bottom:8px; display:none;" onerror="this.style.display=\'none\'" />' +
            (org ? '<span style="font-size:14px; font-weight:bold; color: ' + orgColor + ';">' + org + '</span><br>' : '') +
            (dateStr ? '<span style="font-size:13px;">🗓️ ' + dateStr + '</span><br>' : '') +
            (colorType ? '<span style="font-size:13px;">' + typeIcon + ' ' + colorType + '</span><br>' : '') +
            (scale ? '<span style="font-size:13px;">縮尺: 1/' + Number(scale).toLocaleString() + '</span><br>' : '') +
            (altitude ? '<span style="font-size:13px;">撮影高度: ' + Number(altitude).toLocaleString() + 'm</span><br>' : '') +
            (gsiPhotoUrl ? '<a href="' + gsiPhotoUrl + '" target="_blank" style="display:inline-block; margin-top:8px; padding:6px 12px; background:' + orgColor + '; color:#fff; border-radius:4px; text-decoration:none; font-size:13px;">地図・空中写真閲覧サービスへ</a>' : '') +
            '</div>'
        }
        break
      }

      // 日本の主な山岳（国土地理院）
      case 'oh-mountain':
      case 'oh-mountain-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('mountain-popup') === -1) {
          const name = props['山名'] || ''
          const kana = props['よみがな'] || ''
          const pref = props['都道府県'] || ''
          const area = props['所在等'] || ''
          const elev = props['標高'] || 0
          const sankaku = props['三角点名等'] || ''
          const note = props['備考'] || ''

          // 標高に応じた色
          let elevColor = '#4CAF50'
          if (elev >= 3000) elevColor = '#880E4F'
          else if (elev >= 2500) elevColor = '#D32F2F'
          else if (elev >= 2000) elevColor = '#FF5722'
          else if (elev >= 1500) elevColor = '#FF9800'
          else if (elev >= 1000) elevColor = '#FFC107'
          else if (elev >= 500) elevColor = '#8BC34A'

          const coord = features[0].geometry.coordinates
          const gsi3dUrl = 'https://maps.gsi.go.jp/index_3d.html?lat=' + coord[1] + '&lon=' + coord[0] + '&z=15&pxsize=2048&ls=std'

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="mountain-popup popup-base-relaxed popup-min-w300">' +
            '<div style="font-size:17px; font-weight:bold; color: ' + elevColor + '; margin-bottom: 4px;">⛰️ ' + name + '</div>' +
            (kana ? '<div style="font-size: 12px; color: #888; margin-bottom: 8px;">' + kana + '</div>' : '') +
            '<div style="display: inline-block; background: ' + elevColor + '; color: #fff; padding: 2px 12px; border-radius: 12px; font-size: 14px; font-weight: bold; margin-bottom: 8px;">' + elev + 'm</div>' +
            '<div style="font-size: 13px; color: #555; margin-bottom: 4px;">📍 ' + pref + ' ' + area + '</div>' +
            (sankaku ? '<div style="font-size: 13px; color: #666; margin-bottom: 4px;">📐 ' + sankaku + '</div>' : '') +
            (note ? '<div style="font-size: 12px; color: #888; margin-bottom: 8px;">💡 ' + note + '</div>' : '') +
            '<div style="margin-top: 8px; display:flex; gap:6px; flex-wrap:wrap;">' +
              '<a href="' + gsi3dUrl + '" target="_blank" style="display:inline-block; padding:4px 10px; background:#5D4037; color:#fff; border-radius:4px; text-decoration:none; font-size:12px;">地理院立体地図</a>' +
              '<span onclick="openDioramaAt(' + coord[0] + ',' + coord[1] + ',14)" style="display:inline-block; padding:4px 10px; background:#475569; color:#fff; border-radius:4px; cursor:pointer; font-size:12px;">3Dジオラマ</span>' +
            '</div>' +
            '</div>'
        }
        break
      }

      // 自然災害伝承碑（国土地理院）
      case 'oh-densho':
      case 'oh-densho-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('densho-popup') === -1) {
          const id = props['ID'] || props['id'] || ''
          const name = props['碑名'] || ''
          const year = props['建立年'] || ''
          const location = props['所在地'] || ''
          const disasterName = props['災害名'] || ''
          const disasterType = props['災害種別'] || ''
          const content = props['伝承内容'] || ''

          // 画像URL（IDから生成）
          let imgHtml = ''
          if (id) {
            const idPrefix = id.split('-')[0]
            const imgSrc = 'https://maps.gsi.go.jp/legend/disaster_lore/' + idPrefix + '/' + id + '.jpg'
            imgHtml = '<div style="margin-top: 10px; cursor: pointer;" onclick="openGlobalMediaPreview(\'' + imgSrc + '\', \'image\', \'' + name.replace(/'/g, "\\'") + '\')"><img style="object-fit: cover; width: 100%; max-height: 200px; border-radius: 6px;" src="' + imgSrc + '" onerror="this.parentElement.style.display=\'none\'"></div>'
          }

          // 災害種別に応じた色
          let typeColor = '#8B4513'
          if (disasterType.includes('津波')) typeColor = '#1565c0'
          else if (disasterType.includes('地震')) typeColor = '#d32f2f'
          else if (disasterType.includes('洪水') || disasterType.includes('水害')) typeColor = '#1976d2'
          else if (disasterType.includes('土砂') || disasterType.includes('崩壊')) typeColor = '#795548'
          else if (disasterType.includes('火山') || disasterType.includes('噴火')) typeColor = '#ff5722'
          else if (disasterType.includes('高潮')) typeColor = '#00838f'

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="densho-popup popup-base-relaxed popup-min-w300">' +
            '<div style="font-size:17px; font-weight:bold; color: ' + typeColor + '; margin-bottom: 8px;">🪨 ' + name + '</div>' +
            (disasterType ? '<div style="display: inline-block; background: ' + typeColor + '; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + disasterType + '</div>' : '') +
            (disasterName ? '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 6px;">📅 ' + disasterName + '</div>' : '') +
            (year ? '<div style="font-size: 13px; color: #666; margin-bottom: 4px;">建立: ' + year + '年</div>' : '') +
            (location ? '<div style="font-size: 13px; color: #666; margin-bottom: 8px;">📍 ' + location + '</div>' : '') +
            (content ? '<div style="font-size: 13px; color: #444; background: #f5f5f5; padding: 10px; border-radius: 6px; max-height: 150px; overflow-y: auto;">' + content + '</div>' : '') +
            imgHtml +
            '</div>'
        }
        break
      }

      // 都道府県指定文化財（国土数値情報）
      case 'oh-bunkazai':
      case 'oh-bunkazai-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('bunkazai-popup') === -1) {
          const name = props['名称'] || ''
          const pref = props['都道府県'] || ''
          const bunrui = props['分類'] || ''
          const shubetsu = props['種別'] || ''
          const location = props['所在地'] || ''
          const shiteiDate = props['指定日'] || ''

          // 種別に応じた色
          let typeColor = '#795548'
          if (shubetsu.includes('建造物')) typeColor = '#8B4513'
          else if (shubetsu.includes('史跡')) typeColor = '#2E7D32'
          else if (shubetsu.includes('天然記念物')) typeColor = '#1565C0'
          else if (shubetsu.includes('民俗')) typeColor = '#7B1FA2'
          else if (shubetsu.includes('名勝')) typeColor = '#F57C00'
          else if (shubetsu.includes('芸能')) typeColor = '#C2185B'

          // 種別に応じたアイコン
          let typeIcon = '🏛️'
          if (shubetsu.includes('建造物')) typeIcon = '🏛️'
          else if (shubetsu.includes('史跡')) typeIcon = '🗿'
          else if (shubetsu.includes('天然記念物')) typeIcon = '🌲'
          else if (shubetsu.includes('民俗')) typeIcon = '🎭'
          else if (shubetsu.includes('名勝')) typeIcon = '🏞️'
          else if (shubetsu.includes('芸能')) typeIcon = '🎎'

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="bunkazai-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: ' + typeColor + '; margin-bottom: 8px;">' + typeIcon + ' ' + name + '</div>' +
            (shubetsu ? '<div style="display: inline-block; background: ' + typeColor + '; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + shubetsu + '</div>' : '') +
            (pref ? '<div style="font-size: 14px; font-weight: 500; color: #333; margin-bottom: 4px;">' + pref + '</div>' : '') +
            (location ? '<div style="font-size: 13px; color: #666; margin-bottom: 4px;">📍 ' + location + '</div>' : '') +
            (shiteiDate ? '<div style="font-size: 13px; color: #666;">📅 指定: ' + shiteiDate + '</div>' : '') +
            '</div>'
        }
        break
      }

      // =====================================================================================================================
      // OH2からの遺跡・文化財レイヤー ポップアップ
      // =====================================================================================================================

      // 国指定文化財等データベース（文化庁）
      case 'oh-bunkazaidb':
      case 'oh-bunkazaidb-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('bunkazaidb-popup') === -1) {
          const name = props['名称'] || ''
          const kind = props['文化財種類'] || ''
          const pref = props['都道府県'] || ''
          const location = props['所在地'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="bunkazaidb-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #2E7D32; margin-bottom: 4px;">🏛️ ' + name + '</div>' +
            (kind ? '<div style="display: inline-block; background: #2E7D32; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + kind + '</div>' : '') +
            (pref ? '<div style="font-size: 13px; color: #666;">' + pref + '</div>' : '') +
            (location ? '<div style="font-size: 13px; color: #666;">📍 ' + location + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 全国の文化財データ（S50）
      case 'oh-zenkoku-bunkazai':
      case 'oh-zenkoku-bunkazai-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('zenkoku-bunkazai-popup') === -1) {
          const text = props['P01_002'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="zenkoku-bunkazai-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #FF8C00; margin-bottom: 4px;">🏛️ ' + text + '</div>' +
            '</div>'
        }
        break
      }

      // 全国縄文・弥生集落遺跡
      case 'oh-yayoi-iseki':
      case 'oh-yayoi-iseki-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('yayoi-iseki-popup') === -1) {
          const name = props['遺跡名'] || ''
          const age = props['暦年代'] || ''
          const kind = props['遺跡種別'] || ''
          const pref = props['所在地'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="yayoi-iseki-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #8B4513; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            (age ? '<div style="display: inline-block; background: #8B4513; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + age + '</div>' : '') +
            (kind ? '<div style="font-size: 13px; color: #666;"><b>種別:</b> ' + kind + '</div>' : '') +
            (pref ? '<div style="font-size: 13px; color: #666;">📍 ' + pref + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 北海道埋蔵文化財包蔵地
      case 'oh-hokkaido-maibun-fill':
      case 'oh-hokkaido-maibun-line':
      case 'oh-hokkaido-maibun-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-hokkaido-maibun-fill', 'oh-hokkaido-maibun-line'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-hokkaido-maibun-fill', 'oh-hokkaido-maibun-line'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('hokkaido-maibun-popup') === -1) {
          const name = props['SiteName'] || ''
          const shubetsu = props['種別'] || ''
          const jidai = props['時代'] || ''
          const shozaichi = props['所在地'] || ''
          const ritchi = props['立地'] || ''
          const hyoukou = props['標高'] || ''
          const shutsudo = props['出土遺物'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="hokkaido-maibun-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #D32F2F; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            (jidai ? '<div style="display: inline-block; background: #D32F2F; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + jidai + '</div>' : '') +
            (shubetsu ? '<div style="font-size: 13px; color: #666;"><b>種別:</b> ' + shubetsu + '</div>' : '') +
            (shozaichi ? '<div style="font-size: 13px; color: #666;">📍 ' + shozaichi + '</div>' : '') +
            (ritchi ? '<div style="font-size: 13px; color: #666;"><b>立地:</b> ' + ritchi + '</div>' : '') +
            (hyoukou ? '<div style="font-size: 13px; color: #666;"><b>標高:</b> ' + hyoukou + '</div>' : '') +
            (shutsudo ? '<div style="font-size: 13px; color: #444; background: #f5f5f5; padding: 8px; border-radius: 6px; margin-top: 6px;"><b>出土遺物:</b> ' + shutsudo + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 岩手県遺跡地図
      case 'oh-iwate-iseki-line':
      case 'oh-iwate-iseki-fill':
      case 'oh-iwate-iseki-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-iwate-iseki-line', 'oh-iwate-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-iwate-iseki-line', 'oh-iwate-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('iwate-iseki-popup') === -1) {
          const name = props['ISEKIMEI'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="iwate-iseki-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #FF8C00; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            '</div>'
        }
        break
      }

      // 宮城県遺跡地図
      case 'oh-miyagi-iseki-line':
      case 'oh-miyagi-iseki-fill':
      case 'oh-miyagi-iseki-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-miyagi-iseki-line', 'oh-miyagi-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-miyagi-iseki-line', 'oh-miyagi-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('miyagi-iseki-popup') === -1) {
          const name = props['名称'] || ''
          const shubetsu = props['種別'] || ''
          const jidai = props['時代'] || ''
          const shozaichi = props['所在地'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="miyagi-iseki-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #1565C0; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            (jidai ? '<div style="display: inline-block; background: #1565C0; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + jidai + '</div>' : '') +
            (shubetsu ? '<div style="font-size: 13px; color: #666;"><b>種別:</b> ' + shubetsu + '</div>' : '') +
            (shozaichi ? '<div style="font-size: 13px; color: #666;">📍 ' + shozaichi + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 富山県埋蔵文化財
      case 'oh-toyama-iseki-line':
      case 'oh-toyama-iseki-fill':
      case 'oh-toyama-iseki-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-toyama-iseki-line', 'oh-toyama-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-toyama-iseki-line', 'oh-toyama-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('toyama-iseki-popup') === -1) {
          const name = props['名称'] || ''
          const jidai = props['主時代'] || ''
          const shichoson = props['市町村'] || ''
          const shozaichi = props['所在地'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="toyama-iseki-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #7B1FA2; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            (jidai ? '<div style="display: inline-block; background: #7B1FA2; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + jidai + '</div>' : '') +
            (shichoson ? '<div style="font-size: 13px; color: #666;">' + shichoson + '</div>' : '') +
            (shozaichi ? '<div style="font-size: 13px; color: #666;">📍 ' + shozaichi + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 東京都文化財
      case 'oh-tokyo-bunkazai':
      case 'oh-tokyo-bunkazai-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tokyo-bunkazai-popup') === -1) {
          const name = props['名称'] || ''
          const kind = props['種別'] || ''
          const location = props['所在地'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tokyo-bunkazai-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #2E7D32; margin-bottom: 4px;">🏛️ ' + name + '</div>' +
            (kind ? '<div style="display: inline-block; background: #2E7D32; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + kind + '</div>' : '') +
            (location ? '<div style="font-size: 13px; color: #666;">📍 ' + location + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 世田谷区埋蔵文化財
      case 'oh-setagaya-maibun-line':
      case 'oh-setagaya-maibun-fill':
      case 'oh-setagaya-maibun-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-setagaya-maibun-line', 'oh-setagaya-maibun-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-setagaya-maibun-line', 'oh-setagaya-maibun-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('setagaya-maibun-popup') === -1) {
          const name = props['SAFIELD005'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="setagaya-maibun-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #FF8C00; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            '</div>'
        }
        break
      }

      // 四日市市遺跡
      case 'oh-yokkaichi-iseki-line':
      case 'oh-yokkaichi-iseki-fill':
      case 'oh-yokkaichi-iseki-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-yokkaichi-iseki-line', 'oh-yokkaichi-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-yokkaichi-iseki-line', 'oh-yokkaichi-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('yokkaichi-iseki-popup') === -1) {
          const name = props['遺跡名称'] || ''
          const shubetsu = props['遺跡種別'] || ''
          const jidai = props['時代'] || ''
          const shozaichi = props['所在地'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="yokkaichi-iseki-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #E65100; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            (jidai ? '<div style="display: inline-block; background: #E65100; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + jidai + '</div>' : '') +
            (shubetsu ? '<div style="font-size: 13px; color: #666;"><b>種別:</b> ' + shubetsu + '</div>' : '') +
            (shozaichi ? '<div style="font-size: 13px; color: #666;">📍 ' + shozaichi + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 岡山県埋蔵文化財
      case 'oh-okayama-maibun':
      case 'oh-okayama-maibun-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-okayama-maibun', 'oh-okayama-maibun-line'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-okayama-maibun', 'oh-okayama-maibun-line'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('okayama-maibun-popup') === -1) {
          const name = props['遺跡名'] || ''
          const jidai = props['主な時代'] || ''
          const shubetsu = props['詳細な種'] || ''
          const shozaichi = props['所在地'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="okayama-maibun-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #2E7D32; margin-bottom: 4px;">🗿 ' + (name || '岡山県埋蔵文化財') + '</div>' +
            (jidai ? '<div style="display: inline-block; background: #2E7D32; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + jidai + '</div>' : '') +
            (shubetsu ? '<div style="font-size: 13px; color: #666;"><b>種別:</b> ' + shubetsu + '</div>' : '') +
            (shozaichi ? '<div style="font-size: 13px; color: #666;">📍 ' + shozaichi + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 和歌山県埋蔵文化財
      case 'oh-wakayama-maibun-line':
      case 'oh-wakayama-maibun-fill':
      case 'oh-wakayama-maibun-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-wakayama-maibun-line', 'oh-wakayama-maibun-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-wakayama-maibun-line', 'oh-wakayama-maibun-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('wakayama-maibun-popup') === -1) {
          const name = props['遺跡名'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="wakayama-maibun-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #00838F; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            '</div>'
        }
        break
      }

      // 山口県遺跡地図
      case 'oh-yamaguchi-iseki-line':
      case 'oh-yamaguchi-iseki-fill':
      case 'oh-yamaguchi-iseki-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-yamaguchi-iseki-line', 'oh-yamaguchi-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-yamaguchi-iseki-line', 'oh-yamaguchi-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('yamaguchi-iseki-popup') === -1) {
          const name = props['遺跡名'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="yamaguchi-iseki-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #C62828; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            '</div>'
        }
        break
      }

      // 熊本県遺跡
      case 'oh-kumamoto-iseki':
      case 'oh-kumamoto-iseki-fill':
      case 'oh-kumamoto-iseki-line':
      case 'oh-kumamoto-iseki-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-kumamoto-iseki', 'oh-kumamoto-iseki-fill', 'oh-kumamoto-iseki-line'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-kumamoto-iseki', 'oh-kumamoto-iseki-fill', 'oh-kumamoto-iseki-line'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kumamoto-iseki-popup') === -1) {
          const name = props['m_cont2'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kumamoto-iseki-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #1B5E20; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            '</div>'
        }
        break
      }

      // 沖縄県遺跡
      case 'oh-okinawa-iseki-line':
      case 'oh-okinawa-iseki-fill':
      case 'oh-okinawa-iseki-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-okinawa-iseki-line', 'oh-okinawa-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-okinawa-iseki-line', 'oh-okinawa-iseki-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('okinawa-iseki-popup') === -1) {
          const name = props['遺跡名'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="okinawa-iseki-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #0277BD; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            '</div>'
        }
        break
      }

      // 富田林市埋蔵文化財
      case 'oh-tondabayashi-maibun-line':
      case 'oh-tondabayashi-maibun-fill':
      case 'oh-tondabayashi-maibun-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-tondabayashi-maibun-line', 'oh-tondabayashi-maibun-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-tondabayashi-maibun-line', 'oh-tondabayashi-maibun-fill'].filter(id => map.getLayer(id)) })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tondabayashi-maibun-popup') === -1) {
          const name = props['名称'] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tondabayashi-maibun-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #AD1457; margin-bottom: 4px;">🗿 ' + name + '</div>' +
            '</div>'
        }
        break
      }

      // 日本歴史地名大系 施設・地点項目
      case 'oh-nrct-poi':
      case 'oh-nrct-poi-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('nrct-poi-popup') === -1) {
          const name = props['名称'] || ''
          const yomi = props['読み'] || ''
          const address = props['住所'] || ''
          const category2 = props['分類2'] || ''

          // 分類に応じた色
          let catColor = '#795548'
          if (category2 === '埋葬・信仰・宗教施設') catColor = '#8B4513'
          else if (category2 === '史跡・文化財・遺跡') catColor = '#9C27B0'
          else if (category2 === '古墳・貝塚') catColor = '#4CAF50'
          else if (category2 === '城・軍事施設') catColor = '#F44336'
          else if (category2 === '産業施設') catColor = '#2196F3'

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="nrct-poi-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: ' + catColor + '; margin-bottom: 4px;">' + name + '</div>' +
            (yomi ? '<div style="font-size: 12px; color: #888; margin-bottom: 8px;">' + yomi + '</div>' : '') +
            (category2 ? '<div style="display: inline-block; background: ' + catColor + '; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + category2 + '</div>' : '') +
            (address ? '<div style="font-size: 13px; color: #666;">📍 ' + address + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 日本歴史地名大系 地名項目
      case 'oh-nrct-mct':
      case 'oh-nrct-mct-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('nrct-mct-popup') === -1) {
          const name = props['名称'] || ''
          const yomi = props['読み'] || ''
          const parent = props['上位地名'] || ''
          const address = props['出典住所'] || ''
          const historyName = props['歴史地名'] || ''

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="nrct-mct-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: #e65100; margin-bottom: 4px;">' + name + '</div>' +
            (yomi ? '<div style="font-size: 12px; color: #888; margin-bottom: 8px;">' + yomi + '</div>' : '') +
            (parent ? '<div style="font-size: 13px; color: #666; margin-bottom: 4px;">🏛️ ' + parent + '</div>' : '') +
            (address ? '<div style="font-size: 13px; color: #666; margin-bottom: 4px;">📍 ' + address + '</div>' : '') +
            (historyName ? '<div style="font-size: 12px; color: #9C27B0; background: #f3e5f5; padding: 6px 10px; border-radius: 6px; margin-top: 6px;">歴史地名: ' + historyName + '</div>' : '') +
            '</div>'
        }
        break
      }

      // エコリス 第6-7回植生図（ベクター）
      case 'oh-ecoris-v67-kozan':
      case 'oh-ecoris-v67-kokemomo':
      case 'oh-ecoris-v67-kokemomo-d':
      case 'oh-ecoris-v67-buna':
      case 'oh-ecoris-v67-buna-d':
      case 'oh-ecoris-v67-yabutsubaki':
      case 'oh-ecoris-v67-yabutsubaki-d':
      case 'oh-ecoris-v67-kawabe':
      case 'oh-ecoris-v67-shokurin-other':
      case 'oh-ecoris-v67-akamatsu':
      case 'oh-ecoris-v67-sugihinoki':
      case 'oh-ecoris-v67-chikurin':
      case 'oh-ecoris-v67-bokusou':
      case 'oh-ecoris-v67-kousaku':
      case 'oh-ecoris-v67-suiden':
      case 'oh-ecoris-v67-shigaichi':
      case 'oh-ecoris-v67-suiiki':
      case 'oh-ecoris-v67-line':
      case 'oh-ecoris-v67-label': {
        // 全fillレイヤーを検索対象にする
        const v67AllLayers = [
          'oh-ecoris-v67-kozan', 'oh-ecoris-v67-kokemomo', 'oh-ecoris-v67-kokemomo-d',
          'oh-ecoris-v67-buna', 'oh-ecoris-v67-buna-d',
          'oh-ecoris-v67-yabutsubaki', 'oh-ecoris-v67-yabutsubaki-d',
          'oh-ecoris-v67-kawabe', 'oh-ecoris-v67-shokurin-other',
          'oh-ecoris-v67-akamatsu', 'oh-ecoris-v67-sugihinoki',
          'oh-ecoris-v67-chikurin', 'oh-ecoris-v67-bokusou',
          'oh-ecoris-v67-kousaku', 'oh-ecoris-v67-suiden',
          'oh-ecoris-v67-shigaichi', 'oh-ecoris-v67-suiiki',
        ].filter(l => { try { return map.getLayer(l) } catch { return false } })
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: v67AllLayers })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: v67AllLayers })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('ecoris-v67-popup') === -1) {
          const hanreiN = props['hanrei_n'] || ''
          const daiN = props['dai_n'] || ''
          const chuN = props['chu_n'] || ''
          const shokuN = props['shoku_n'] || ''
          const survYear = props['surv_year'] || ''

          // shoku_c → 色マッピング
          const v67ColorMap = {
            '1': '#FDF1CE', '2': '#997F60', '3': '#A58F74', '4': '#178017',
            '5': '#5B9700', '6': '#003300', '7': '#004A00', '8': '#FFFF00',
            '10': '#868585',
          }
          let catColor = v67ColorMap[props['shoku_c']] || '#666'
          // 植林地・竹林等の特殊色
          if (daiN === '植林地') catColor = '#D77214'
          if (chuN === 'アカマツ植林') catColor = '#aa7720'
          if (chuN === 'スギ・ヒノキ・サワラ植林') catColor = '#697720'
          if (daiN === '竹林') catColor = '#CCCC20'
          if (daiN === '牧草地・ゴルフ場・芝地') catColor = '#69FF00'
          if (daiN === '耕作地') catColor = '#999662'
          if (hanreiN === '水田雑草群落') catColor = '#8cd27d'
          if (chuN === '開放水域') catColor = '#99FFFF'

          // 明るい色の場合は文字色を暗くする
          const lightColors = ['#FDF1CE', '#FFFF00', '#69FF00', '#CCCC20', '#99FFFF', '#8cd27d']
          const chipTextColor = lightColors.includes(catColor) ? '#333' : '#fff'
          const titleColor = lightColors.includes(catColor) ? '#333' : catColor

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="ecoris-v67-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:16px; font-weight:bold; color: ' + titleColor + '; margin-bottom: 4px;">' + hanreiN + '</div>' +
            (shokuN ? '<div style="display: inline-block; background: ' + catColor + '; color: ' + chipTextColor + '; padding: 2px 10px; border-radius: 12px; font-size: 11px; margin-bottom: 6px;">' + shokuN + '</div>' : '') +
            (daiN ? '<div style="font-size: 13px; color: #666; margin-bottom: 2px;">大分類: ' + daiN + '</div>' : '') +
            (chuN ? '<div style="font-size: 13px; color: #666; margin-bottom: 2px;">中分類: ' + chuN + '</div>' : '') +
            (survYear ? '<div style="font-size: 12px; color: #888;">調査年: ' + survYear + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 現存植生図2024（環境省生物多様性センター）
      case 'oh-veg2024-fill':
      case 'oh-veg2024-line': {
        const veg2024Layers = ['oh-veg2024-fill'].filter(l => { try { return map.getLayer(l) } catch { return false } })
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: veg2024Layers })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: veg2024Layers })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('veg2024-popup') === -1) {
          const hanrei = props['凡例名'] || ''
          const shizendo = props['植生自然度'] || ''
          const shizendoKubun = props['植生自然度区分'] || ''
          const shokuseiKubun = props['植生区分'] || ''

          // 植生自然度 → 色マッピング
          const veg2024ColorMap = {
            '1': '#b0b0b0', '2': '#e8d87c', '3': '#c4d45a', '4': '#a8d88c',
            '5': '#7cc464', '6': '#6aaa5c', '7': '#4e9450', '8': '#2e7d3a',
            '9': '#1a5e28', '10': '#9b6eb5', '98': '#a0845c', '99': '#6ca0d4',
          }
          const catColor = veg2024ColorMap[shizendo] || '#ccc'
          const lightColors = ['#b0b0b0', '#e8d87c', '#c4d45a', '#a8d88c', '#7cc464', '#6ca0d4']
          const chipTextColor = lightColors.includes(catColor) ? '#333' : '#fff'

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="veg2024-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:16px; font-weight:bold; color: #333; margin-bottom: 4px;">' + hanrei + '</div>' +
            (shizendoKubun ? '<div style="display: inline-block; background: ' + catColor + '; color: ' + chipTextColor + '; padding: 2px 10px; border-radius: 12px; font-size: 11px; margin-bottom: 6px;">' + shizendoKubun + '</div>' : '') +
            (shokuseiKubun ? '<div style="font-size: 13px; color: #666; margin-bottom: 2px;">' + shokuseiKubun + '</div>' : '') +
            (shizendo ? '<div style="font-size: 12px; color: #888;">植生自然度: ' + shizendo + '</div>' : '') +
            '</div>'
        }
        break
      }

      // エコリス 特定植物群落
      case 'oh-ecoris-sp-area':
      case 'oh-ecoris-sp-line':
      case 'oh-ecoris-sp-point': {
        const queryLayers = ['oh-ecoris-sp-area', 'oh-ecoris-sp-line', 'oh-ecoris-sp-point'].filter(
          l => { try { return map.getLayer(l) } catch { return false } }
        )
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: queryLayers })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: queryLayers })
        }
        if (features.length === 0) return
        props = features[0].properties

        if (html.value.indexOf('ecoris-sp-popup') === -1) {
          const name = props['NAME'] || '不明'
          const kubun = props['kubun'] || ''
          const area = props['area'] || ''
          const senteikai = props['senteikai']
          const senteikijun = props['senteikijun'] || ''

          // kubun大分類の色判定
          function getSpCatColor(k) {
            if (!k) return '#999'
            const map = [
              [['高山', '雪田'], '#6FA8DC'],
              [['亜寒帯'], '#3D85C6'],
              [['冷温帯'], '#38761D'],
              [['暖温帯'], '#6AA84F'],
              [['亜熱帯', '亜温帯'], '#93C47D'],
              [['湿地', '河辺', '浮水', '浮葉', '流水'], '#45B5AA'],
              [['海浜'], '#E6B422'],
              [['火山', '岩上', '蘚苔', '草本', 'シダ'], '#B07BAC'],
              [['マングロ', 'タケ', 'ヤシ'], '#E69138'],
              [['個体群'], '#E06666'],
            ]
            for (const [prefixes, color] of map) {
              if (prefixes.some(p => k.startsWith(p))) return color
            }
            return '#999'
          }
          const catColor = getSpCatColor(kubun)

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="ecoris-sp-popup popup-base-relaxed popup-min-w280">' +
            '<div style="font-size:17px; font-weight:bold; color: ' + catColor + '; margin-bottom: 4px;">' + name + '</div>' +
            (kubun ? '<div style="display: inline-block; background: ' + catColor + '; color: #fff; padding: 2px 10px; border-radius: 12px; font-size: 12px; margin-bottom: 8px;">' + kubun + '</div>' : '') +
            (senteikai ? '<div style="font-size: 13px; color: #666; margin-bottom: 4px;">調査: 第' + senteikai + '回</div>' : '') +
            (area && area !== '不明' ? '<div style="font-size: 13px; color: #666; margin-bottom: 4px;">面積: ' + area + ' ha</div>' : '') +
            (senteikijun ? '<div style="font-size: 12px; color: #888;">選定基準: ' + senteikijun + '</div>' : '') +
            '</div>'
        }
        break
      }

      // 自然公園地域 (XKT019)
      case 'oh-reinfolib-shizen-koen-layer':
      case 'oh-reinfolib-shizen-koen-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-shizen-koen') === -1) {
          // 正しいプロパティ: OBJ_NAME_ja(公園名), CTV_NAME(市区町村), FIS_YEAR(年度)
          const parkName = props.OBJ_NAME_ja || props.A10_002 || ''
          const city = props.CTV_NAME || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-shizen-koen popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">自然公園地域</span><br>' +
            (parkName ? '<span style="font-size:14px;">' + parkName + '</span><br>' : '') +
            (city ? '<span style="font-size:14px;">' + city + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 大規模盛土造成地マップ (XKT020)
      case 'oh-reinfolib-morido-layer':
      case 'oh-reinfolib-morido-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-morido') === -1) {
          // 正しいプロパティ: embankment_classification(造成タイプ), city_name(市区町村), prefecture_name(都道府県)
          const fillType = props.embankment_classification || ''
          const city = props.city_name || ''
          const pref = props.prefecture_name || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-morido popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #8b4513;">大規模盛土造成地</span><br>' +
            (fillType ? '<span style="font-size:14px;">種別：' + fillType + '</span><br>' : '') +
            (city ? '<span style="font-size:14px;">' + pref + city + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 地すべり防止区域 (XKT021)
      // 正しいプロパティ: region_name(地域名), city_name(市区町村), prefecture_name(都道府県), landslide_area(面積), charge_ministry_name(所管), notice_date(告示日)
      case 'oh-reinfolib-jisuberi-layer':
      case 'oh-reinfolib-jisuberi-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-jisuberi') === -1) {
          const regionName = props.region_name || ''
          const city = props.city_name || ''
          const pref = props.prefecture_name || ''
          const area = props.landslide_area || ''
          const ministry = props.charge_ministry_name || ''
          const noticeDate = props.notice_date || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-jisuberi popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #8b4513;">地すべり防止区域</span><br>' +
            (regionName ? '<span style="font-size:14px;">地域名：' + regionName + '</span><br>' : '') +
            ((pref || city) ? '<span style="font-size:14px;">所在地：' + pref + city + '</span><br>' : '') +
            (area ? '<span style="font-size:14px;">面積：' + area + '</span><br>' : '') +
            (ministry ? '<span style="font-size:14px;">所管：' + ministry + '</span><br>' : '') +
            (noticeDate ? '<span style="font-size:14px;">告示日：' + noticeDate + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 急傾斜地崩壊危険区域 (XKT022)
      // 正しいプロパティ: region_name(地域名), city_name(市区町村), prefecture_name(都道府県), landslide_area(面積), public_notice_date(告示日)
      case 'oh-reinfolib-kyukeisha-layer':
      case 'oh-reinfolib-kyukeisha-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-kyukeisha') === -1) {
          const regionName = props.region_name || ''
          const city = props.city_name || ''
          const pref = props.prefecture_name || ''
          const area = props.landslide_area || ''
          const noticeDate = props.public_notice_date || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-kyukeisha popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #b22222;">急傾斜地崩壊危険区域</span><br>' +
            (regionName ? '<span style="font-size:14px;">地域名：' + regionName + '</span><br>' : '') +
            ((pref || city) ? '<span style="font-size:14px;">所在地：' + pref + city + '</span><br>' : '') +
            (area ? '<span style="font-size:14px;">面積：' + area + '</span><br>' : '') +
            (noticeDate ? '<span style="font-size:14px;">告示日：' + noticeDate + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 地区計画 (XKT023)
      case 'oh-reinfolib-chiku-keikaku-layer':
      case 'oh-reinfolib-chiku-keikaku-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-chiku-keikaku') === -1) {
          const planType = props.plan_type_ja || '地区計画'
          const planName = props.plan_name || ''
          const city = props.city_name || ''
          const pref = props.prefecture || ''
          const noticeNo = props.notice_number || ''
          const location = pref && city ? pref + city : (city || pref || '')

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-chiku-keikaku popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #00695c;">' + planType + '</span><br>' +
            (planName ? '<span style="font-size:14px;">' + planName + '</span><br>' : '') +
            (location ? '<span style="font-size:12px; color: #666;">' + location + '</span><br>' : '') +
            (noticeNo ? '<span style="font-size:11px; color: #888;">' + noticeNo + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 高度利用地区 (XKT024)
      case 'oh-reinfolib-kodo-riyo-layer':
      case 'oh-reinfolib-kodo-riyo-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-kodo-riyo') === -1) {
          // プロパティ名の日本語マッピング
          const labelMap = {
            advanced_name: '地区名',
            advanced_type_ja: '種別',
            prefecture: '都道府県',
            city_name: '市区町村',
            decision_date: '決定日',
            notice_number: '告示番号',
            decision_maker: '決定者',
            first_decision_date: '当初決定日'
          }
          let content = '<span style="font-size:16px; font-weight:bold;">高度利用地区</span><br>'
          // _で始まるプロパティを除き、値があるものを全て表示
          for (const [key, value] of Object.entries(props)) {
            if (key.startsWith('_') || !value || value === '' || key === 'kubun_id' || key === 'group_code') continue
            const label = labelMap[key] || key
            const displayValue = String(value).replace(/\n/g, ' ')
            content += '<span style="font-size:14px;">' + label + '：' + displayValue + '</span><br>'
          }
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-kodo-riyo popup-base">' + content + '</div>'
        }
        break
      }

      // 液状化発生傾向図 (XKT025)
      case 'oh-reinfolib-ekijoka-layer':
      case 'oh-reinfolib-ekijoka-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-ekijoka') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-ekijoka popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">液状化発生傾向</span><br>' +
            '</div>'
        }
        break
      }

      // 洪水浸水想定区域 (XKT026)
      // 正しいプロパティ: A31a_202(河川名), A31a_204(管理者), A31a_205(浸水深ランク)
      case 'oh-reinfolib-kozui-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-kozui') === -1) {
          // A31a_205: 浸水深ランク (1=0.5m未満, 2=0.5～3m, 3=3～5m, 4=5～10m, 5=10～20m, 6=20m以上)
          const depthRankMap = {
            '1': '0.5m未満',
            '2': '0.5m～3.0m',
            '3': '3.0m～5.0m',
            '4': '5.0m～10.0m',
            '5': '10.0m～20.0m',
            '6': '20.0m以上',
          }
          const riverName = props.A31a_202 || ''
          const manager = props.A31a_204 || ''
          const depthRank = depthRankMap[String(props.A31a_205)] || props.A31_101 || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-kozui popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #1e90ff;">洪水浸水想定区域</span><br>' +
            (riverName ? '<span style="font-size:14px;">河川名：' + riverName + '</span><br>' : '') +
            (depthRank ? '<span style="font-size:14px;">浸水深：' + depthRank + '</span><br>' : '') +
            (manager ? '<span style="font-size:14px;">管理者：' + manager + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 高潮浸水想定区域 (XKT027)
      // 正しいプロパティ: A49_001(都道府県), A49_003(浸水深), target_year(対象年)
      case 'oh-reinfolib-takashio-layer':
      case 'oh-reinfolib-takashio-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-takashio') === -1) {
          const depth = props.A49_003 || ''
          const prefecture = props.A49_001 || ''
          const year = props.target_year || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-takashio popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #008080;">高潮浸水想定区域</span><br>' +
            (depth ? '<span style="font-size:14px;">浸水深：' + depth + '</span><br>' : '') +
            (prefecture ? '<span style="font-size:14px;">都道府県：' + prefecture + '</span><br>' : '') +
            (year ? '<span style="font-size:14px;">対象年：' + year + '年</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 津波浸水想定 (XKT028)
      // 正しいプロパティ: A40_001(都道府県), A40_003(浸水深), target_year(対象年)
      case 'oh-reinfolib-tsunami-layer':
      case 'oh-reinfolib-tsunami-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-tsunami') === -1) {
          const depth = props.A40_003 || ''
          const prefecture = props.A40_001 || ''
          const year = props.target_year || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-tsunami popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #4b0082;">津波浸水想定区域</span><br>' +
            (depth ? '<span style="font-size:14px;">浸水深：' + depth + '</span><br>' : '') +
            (prefecture ? '<span style="font-size:14px;">都道府県：' + prefecture + '</span><br>' : '') +
            (year ? '<span style="font-size:14px;">対象年：' + year + '年</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 土砂災害警戒区域 (XKT029)
      // 正しいプロパティ: A33_001(現象種別), A33_002(区域区分), A33_005(区域名), A33_006(所在地), A33_007(告示日)
      case 'oh-reinfolib-dosya-layer':
      case 'oh-reinfolib-dosya-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-dosya') === -1) {
          // A33_001: 現象種別 (1=急傾斜地の崩壊, 2=土石流, 3=地すべり)
          const phenomenonMap = {
            '1': '急傾斜地の崩壊',
            '2': '土石流',
            '3': '地すべり',
          }
          // A33_002: 区域区分 (1=警戒区域, 2=特別警戒区域)
          const zoneMap = {
            '1': '警戒区域',
            '2': '特別警戒区域',
          }
          const phenomenon = phenomenonMap[String(props.A33_001)] || ''
          const zoneType = zoneMap[String(props.A33_002)] || ''
          const areaName = props.A33_005 || ''
          const location = props.A33_006 || ''
          const noticeDate = props.A33_007 || ''
          const isSpecial = props.A33_002 === '2' || props.A33_002 === 2
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-dosya popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: ' + (isSpecial ? '#ff0000' : '#ffc800') + ';">土砂災害' + zoneType + '</span><br>' +
            (phenomenon ? '<span style="font-size:14px;">現象種別：' + phenomenon + '</span><br>' : '') +
            (areaName ? '<span style="font-size:14px;">区域名：' + areaName + '</span><br>' : '') +
            (location ? '<span style="font-size:14px;">所在地：' + location + '</span><br>' : '') +
            (noticeDate ? '<span style="font-size:14px;">告示日：' + noticeDate + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 都市計画道路 (XKT030)
      // 正しいプロパティ: city_name(市区町村), prefecture(都道府県), decision_date(決定日), notice_number(告示番号)
      case 'oh-reinfolib-toshikeikaku-doro-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-toshikeikaku-doro') === -1) {
          const roadName = props.road_name || ''
          const city = props.city_name || ''
          const pref = props.prefecture || ''
          const decisionDate = props.decision_date || ''
          const noticeNumber = props.notice_number || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-toshikeikaku-doro popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #ff0000;">都市計画道路</span><br>' +
            (roadName ? '<span style="font-size:14px;">路線名：' + roadName + '</span><br>' : '') +
            ((pref || city) ? '<span style="font-size:14px;">所在地：' + pref + city + '</span><br>' : '') +
            (decisionDate ? '<span style="font-size:14px;">決定日：' + decisionDate + '</span><br>' : '') +
            (noticeNumber ? '<span style="font-size:14px;">告示番号：' + noticeNumber + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 人口集中地区 (XKT031)
      case 'oh-reinfolib-did-layer':
      case 'oh-reinfolib-did-layer-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-did') === -1) {
          // A16データ
          const cityName = props.A16_003 || ''
          const pop2020 = props.A16_007 || ''  // 今回調査人口
          const area2020 = props.A16_008 || '' // 今回調査面積(km²)
          const year = props.A16_011 || ''     // 調査年次

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-did popup-base">' +
            '<span style="font-size:16px; font-weight:bold;">人口集中地区（DID）</span><br>' +
            (cityName ? '<span style="font-size:14px;">市区町村：' + cityName + '</span><br>' : '') +
            (pop2020 ? '<span style="font-size:12px;">人口：' + Number(pop2020).toLocaleString() + '人</span><br>' : '') +
            (area2020 ? '<span style="font-size:12px;">面積：' + area2020 + ' km²</span><br>' : '') +
            (year ? '<span style="font-size:11px; color: #666;">調査年：' + year + '年</span>' : '') +
            '</div>'
        }
        break
      }

      // 指定緊急避難場所 (XGT001)
      case 'oh-reinfolib-hinanbasho-layer':
      case 'oh-reinfolib-hinanbasho-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-hinanbasho') === -1) {
          // 施設情報
          const facilityName = props.facility_name_ja || ''
          const address = props.address_ja || ''

          // 災害種別フラグの解析（boolean flags）
          const hazardTypes = []
          if (props.flood_flag === true || props.flood_flag === 'true') hazardTypes.push('洪水')
          if (props.landslide_flag === true || props.landslide_flag === 'true') hazardTypes.push('崖崩れ・土石流')
          if (props.high_tide_flag === true || props.high_tide_flag === 'true') hazardTypes.push('高潮')
          if (props.earthquake_flag === true || props.earthquake_flag === 'true') hazardTypes.push('地震')
          if (props.tsunami_flag === true || props.tsunami_flag === 'true') hazardTypes.push('津波')
          if (props.large_fire_flag === true || props.large_fire_flag === 'true') hazardTypes.push('大規模な火事')
          if (props.inland_flooding_flag === true || props.inland_flooding_flag === 'true') hazardTypes.push('内水氾濫')
          if (props.volcanic_phenomenon_flag === true || props.volcanic_phenomenon_flag === 'true') hazardTypes.push('火山現象')

          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-hinanbasho popup-base">' +
            '<span style="font-size:16px; font-weight:bold; color: #006400;">' + (facilityName || '避難場所') + '</span><br>' +
            (address ? '<span style="font-size:12px;">住所：' + address + '</span><br>' : '') +
            (hazardTypes.length > 0 ? '<span style="font-size:12px; color: #d32f2f;">対応災害：' + hazardTypes.join('、') + '</span><br>' : '') +
            '</div>'
        }
        break
      }

      // 災害履歴 (XST001)
      // 注: oh-saigai-rireki-layer は既に上で定義済み

      // 不動産取引価格情報 (XPT001)
      case 'oh-reinfolib-torihiki-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-torihiki') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-torihiki popup-base">'
          // 種類・価格（メイン情報）
          if (props.land_type_name_ja) {
            html.value += '<span style="font-size:16px; font-weight:bold; color: #ffd700;">' + props.land_type_name_ja + '</span><br>'
          }
          if (props.u_transaction_price_total_ja) {
            html.value += '<span style="font-size:16px; font-weight:bold;">' + props.u_transaction_price_total_ja + '</span><br>'
          }
          // 詳細情報
          if (props.u_area_ja) html.value += '<span style="font-size:14px;">面積：' + props.u_area_ja + '</span><br>'
          if (props.floor_plan_name_ja) html.value += '<span style="font-size:14px;">間取り：' + props.floor_plan_name_ja + '</span><br>'
          if (props.u_construction_year_ja) html.value += '<span style="font-size:14px;">築年：' + props.u_construction_year_ja + '</span><br>'
          if (props.point_in_time_name_ja) html.value += '<span style="font-size:14px;">取引時期：' + props.point_in_time_name_ja + '</span><br>'
          if (props.land_use_name_ja) html.value += '<span style="font-size:14px;">用途地域：' + props.land_use_name_ja + '</span><br>'
          if (props.district_name_ja) html.value += '<span style="font-size:12px; color:#666;">' + (props.prefecture_name_ja || '') + (props.city_name_ja || '') + props.district_name_ja + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 地価公示 (XPT002)
      case 'oh-reinfolib-chikakoji-layer':
      case 'oh-reinfolib-chikakoji-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-chikakoji') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-chikakoji popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold; color: #1976d2;">地価公示</span>'
          if (props.target_year_name_ja) html.value += '<span style="font-size:12px; color:#666; margin-left:8px;">（' + props.target_year_name_ja + '）</span>'
          html.value += '<br>'
          if (props.u_current_years_price_ja) {
            html.value += '<span style="font-size:16px; font-weight:bold;">' + props.u_current_years_price_ja + '</span><br>'
          }
          if (props.year_on_year_change_rate) {
            const rate = parseFloat(props.year_on_year_change_rate)
            const color = rate > 0 ? '#e53935' : rate < 0 ? '#1e88e5' : '#666'
            const sign = rate > 0 ? '+' : ''
            html.value += '<span style="font-size:14px; color:' + color + ';">前年比：' + sign + rate.toFixed(1) + '%</span><br>'
          }
          if (props.location_number_ja) html.value += '<span style="font-size:14px;">所在：' + props.location_number_ja + '</span><br>'
          if (props.regulations_use_category_name_ja) html.value += '<span style="font-size:14px;">用途地域：' + props.regulations_use_category_name_ja + '</span><br>'
          if (props.u_regulations_building_coverage_ratio_ja && props.u_regulations_floor_area_ratio_ja) {
            html.value += '<span style="font-size:14px;">建蔽率/容積率：' + props.u_regulations_building_coverage_ratio_ja + ' / ' + props.u_regulations_floor_area_ratio_ja + '</span><br>'
          }
          if (props.nearest_station_name_ja) {
            html.value += '<span style="font-size:14px;">最寄駅：' + props.nearest_station_name_ja
            if (props.u_road_distance_to_nearest_station_name_ja) {
              html.value += '（' + props.u_road_distance_to_nearest_station_name_ja + '）'
            }
            html.value += '</span><br>'
          }
          if (props.standard_lot_number_ja) html.value += '<span style="font-size:12px; color:#666;">標準地番号：' + props.standard_lot_number_ja + '</span><br>'
          // 価格推移表ボタン
          const kojiCoords = features[0].geometry.coordinates
          html.value += '<div style="display:flex; gap:8px; margin-top:8px; flex-wrap:wrap;">'
          html.value += '<button class="oh-price-history-btn" ' +
            'data-lng="' + kojiCoords[0] + '" ' +
            'data-lat="' + kojiCoords[1] + '" ' +
            'data-type="koji" ' +
            'data-location="' + (props.location_number_ja || '') + '" ' +
            'data-lot-number="' + (props.standard_lot_number_ja || '') + '" ' +
            'style="padding:6px 12px; background:#1976d2; color:#fff; border:none; border-radius:4px; cursor:pointer; font-size:13px;">' +
            '📈 価格推移表</button>'
          // AI物件調査レポートボタン
          html.value += '<button class="oh-property-report-btn" ' +
            'data-lng="' + kojiCoords[0] + '" ' +
            'data-lat="' + kojiCoords[1] + '" ' +
            'style="padding:6px 12px; background:#0277BD; color:#fff; border:none; border-radius:4px; cursor:pointer; font-size:13px;">' +
            '🏠 AI物件調査</button>'
          html.value += '</div>'
          html.value += '</div>'
        }
        break
      }

      // 都道府県地価調査 (XPT002) - 別レイヤー
      case 'oh-reinfolib-chikachosa-layer':
      case 'oh-reinfolib-chikachosa-layer-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('reinfolib-chikachosa') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="reinfolib-chikachosa popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold; color: #388e3c;">都道府県地価調査</span>'
          if (props.target_year_name_ja) html.value += '<span style="font-size:12px; color:#666; margin-left:8px;">（' + props.target_year_name_ja + '）</span>'
          html.value += '<br>'
          if (props.u_current_years_price_ja) {
            html.value += '<span style="font-size:16px; font-weight:bold;">' + props.u_current_years_price_ja + '</span><br>'
          }
          if (props.year_on_year_change_rate) {
            const rate = parseFloat(props.year_on_year_change_rate)
            const color = rate > 0 ? '#e53935' : rate < 0 ? '#1e88e5' : '#666'
            const sign = rate > 0 ? '+' : ''
            html.value += '<span style="font-size:14px; color:' + color + ';">前年比：' + sign + rate.toFixed(1) + '%</span><br>'
          }
          if (props.location_number_ja) html.value += '<span style="font-size:14px;">所在：' + props.location_number_ja + '</span><br>'
          if (props.regulations_use_category_name_ja) html.value += '<span style="font-size:14px;">用途地域：' + props.regulations_use_category_name_ja + '</span><br>'
          if (props.u_regulations_building_coverage_ratio_ja && props.u_regulations_floor_area_ratio_ja) {
            html.value += '<span style="font-size:14px;">建蔽率/容積率：' + props.u_regulations_building_coverage_ratio_ja + ' / ' + props.u_regulations_floor_area_ratio_ja + '</span><br>'
          }
          if (props.nearest_station_name_ja) {
            html.value += '<span style="font-size:14px;">最寄駅：' + props.nearest_station_name_ja
            if (props.u_road_distance_to_nearest_station_name_ja) {
              html.value += '（' + props.u_road_distance_to_nearest_station_name_ja + '）'
            }
            html.value += '</span><br>'
          }
          if (props.standard_lot_number_ja) html.value += '<span style="font-size:12px; color:#666;">標準地番号：' + props.standard_lot_number_ja + '</span><br>'
          // 価格推移表ボタン
          const chosaCoords = features[0].geometry.coordinates
          html.value += '<div style="display:flex; gap:8px; margin-top:8px; flex-wrap:wrap;">'
          html.value += '<button class="oh-price-history-btn" ' +
            'data-lng="' + chosaCoords[0] + '" ' +
            'data-lat="' + chosaCoords[1] + '" ' +
            'data-type="chosa" ' +
            'data-location="' + (props.location_number_ja || '') + '" ' +
            'data-lot-number="' + (props.standard_lot_number_ja || '') + '" ' +
            'style="padding:6px 12px; background:#388e3c; color:#fff; border:none; border-radius:4px; cursor:pointer; font-size:13px;">' +
            '📈 価格推移表</button>'
          // AI物件調査レポートボタン
          html.value += '<button class="oh-property-report-btn" ' +
            'data-lng="' + chosaCoords[0] + '" ' +
            'data-lat="' + chosaCoords[1] + '" ' +
            'style="padding:6px 12px; background:#0277BD; color:#fff; border:none; border-radius:4px; cursor:pointer; font-size:13px;">' +
            '🏠 AI物件調査</button>'
          html.value += '</div>'
          html.value += '</div>'
        }
        break
      }

      // ============================================================================================================
      // reinfolib ポップアップ終了
      // ============================================================================================================

      // ============================================================================================================
      // 国土数値情報 ポップアップ
      // ============================================================================================================

      // ダム（W01）
      case 'oh-kk-dam':
      case 'oh-kk-dam-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-dam') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-dam popup-base">'
          html.value += '<span style="font-size:18px; font-weight:bold;">' + (props.W01_001 || '') + '</span><br>'
          if (props.W01_003) html.value += '<span style="font-size:14px;">水系: ' + props.W01_003 + '</span><br>'
          if (props.W01_004) html.value += '<span style="font-size:14px;">河川: ' + props.W01_004 + '</span><br>'
          if (props.W01_007) html.value += '<span style="font-size:14px;">堤高: ' + props.W01_007 + 'm</span><br>'
          if (props.W01_010) html.value += '<span style="font-size:14px;">総貯水量: ' + props.W01_010 + '千m³</span><br>'
          if (props.W01_012) html.value += '<span style="font-size:14px;">竣工年: ' + props.W01_012 + '</span><br>'
          if (props.W01_013) html.value += '<span style="font-size:12px; color:#666;">所在地: ' + props.W01_013 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 湖沼（W09）
      case 'oh-kk-kosho': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kosho') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kosho popup-base">'
          html.value += '<span style="font-size:18px; font-weight:bold;">' + (props.W09_001 || '') + '</span><br>'
          if (props.W09_002) html.value += '<span style="font-size:12px; color:#666;">所在地: ' + props.W09_002 + '</span><br>'
          if (props.W09_003) html.value += '<span style="font-size:14px;">最大水深: ' + props.W09_003 + 'm</span><br>'
          if (props.W09_004) html.value += '<span style="font-size:14px;">水面標高: ' + props.W09_004 + 'm</span><br>'
          if (props.W09_005) html.value += '<span style="font-size:14px;">周囲長: ' + props.W09_005 + 'km</span><br>'
          if (props.W09_006) html.value += '<span style="font-size:14px;">面積: ' + props.W09_006 + 'km²</span><br>'
          if (props.W09_007) html.value += '<span style="font-size:14px;">透明度: ' + props.W09_007 + 'm</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 河川（W05）
      case 'oh-kk-kasen':
      case 'oh-kk-kasen-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kasen') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kasen popup-base">'
          html.value += '<span style="font-size:18px; font-weight:bold;">' + (props.W05_004 || '') + '</span><br>'
          if (props.W05_002) html.value += '<span style="font-size:14px;">水系: ' + props.W05_002 + '</span><br>'
          if (props.W05_001) html.value += '<span style="font-size:12px; color:#666;">河川コード: ' + props.W05_001 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 海岸線（C23）
      case 'oh-kk-kaigan': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kaigan-pop') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kaigan-pop popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">海岸線</span><br>'
          if (props.C23_001) html.value += '<span style="font-size:14px;">保全区域番号: ' + props.C23_001 + '</span><br>'
          if (props.C23_002) html.value += '<span style="font-size:14px;">保全区域名: ' + props.C23_002 + '</span><br>'
          if (props.C23_003) html.value += '<span style="font-size:14px;">海岸管理者: ' + props.C23_003 + '</span><br>'
          if (props.C23_004) html.value += '<span style="font-size:14px;">関係海岸: ' + props.C23_004 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 海岸保全施設（P23）
      case 'oh-kk-kaigan-hozen':
      case 'oh-kk-kaigan-hozen-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kaigan-hozen') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kaigan-hozen popup-base">'
          if (props.P23_001) html.value += '<span style="font-size:18px; font-weight:bold;">' + props.P23_001 + '</span><br>'
          if (props.P23_002) html.value += '<span style="font-size:14px;">施設種別: ' + props.P23_002 + '</span><br>'
          if (props.P23_003) html.value += '<span style="font-size:14px;">海岸名: ' + props.P23_003 + '</span><br>'
          if (props.P23_004) html.value += '<span style="font-size:12px; color:#666;">管理者: ' + props.P23_004 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 低位地帯（G08）
      case 'oh-kk-teii-chitai': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-teii') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-teii popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">低位地帯</span><br>'
          if (props.G08_002) html.value += '<span style="font-size:14px;">標高: ' + props.G08_002 + 'm</span><br>'
          if (props.G08_003) html.value += '<span style="font-size:14px;">満潮位: ' + props.G08_003 + 'm</span><br>'
          if (props.G08_004) html.value += '<span style="font-size:14px;">計画高水位: ' + props.G08_004 + 'm</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 大規模盛土造成地（A54）
      case 'oh-kk-morido': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-morido') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-morido popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">大規模盛土造成地</span><br>'
          const classMap = { 1: '谷埋め型', 2: '腹付け型' }
          const className = classMap[props.fillSlopeClassification] || ''
          if (className) html.value += '<span style="font-size:14px;">分類: ' + className + '</span><br>'
          if (props.prefectureName) html.value += '<span style="font-size:14px;">都道府県: ' + props.prefectureName + '</span><br>'
          if (props.localGovernmentName) html.value += '<span style="font-size:14px;">市区町村: ' + props.localGovernmentName + '</span><br>'
          if (props.fillSlopeNumber) html.value += '<span style="font-size:14px;">盛土番号: ' + props.fillSlopeNumber + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 地価公示（L01）
      case 'oh-kk-chika-koji':
      case 'oh-kk-chika-koji-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-chika-koji') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-chika-koji popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold; color:#1565C0;">地価公示</span><br>'
          if (props.L01_008) html.value += '<span style="font-size:18px; font-weight:bold;">' + Number(props.L01_008).toLocaleString() + '円/m²</span><br>'
          if (props.L01_009) {
            const rate = parseFloat(props.L01_009)
            const color = rate > 0 ? '#e53935' : rate < 0 ? '#1e88e5' : '#666'
            const sign = rate > 0 ? '+' : ''
            html.value += '<span style="font-size:14px; color:' + color + ';">変動率: ' + sign + rate + '%</span><br>'
          }
          if (props.L01_025) html.value += '<span style="font-size:14px;">住所: ' + props.L01_025 + '</span><br>'
          if (props.L01_024) html.value += '<span style="font-size:12px; color:#666;">地域: ' + props.L01_024 + '</span><br>'
          if (props.L01_028) html.value += '<span style="font-size:12px; color:#666;">利用区分: ' + props.L01_028 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 地価調査（L02）
      case 'oh-kk-chika-chosa':
      case 'oh-kk-chika-chosa-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-chika-chosa') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-chika-chosa popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold; color:#7B1FA2;">地価調査</span><br>'
          if (props.L02_006) html.value += '<span style="font-size:18px; font-weight:bold;">' + Number(props.L02_006).toLocaleString() + '円/m²</span><br>'
          if (props.L02_007) {
            const rate = parseFloat(props.L02_007)
            const color = rate > 0 ? '#e53935' : rate < 0 ? '#1e88e5' : '#666'
            const sign = rate > 0 ? '+' : ''
            html.value += '<span style="font-size:14px; color:' + color + ';">変動率: ' + sign + rate + '%</span><br>'
          }
          if (props.L02_022) html.value += '<span style="font-size:14px;">住所: ' + props.L02_022 + '</span><br>'
          if (props.L02_021) html.value += '<span style="font-size:12px; color:#666;">地域: ' + props.L02_021 + '</span><br>'
          if (props.L02_025) html.value += '<span style="font-size:12px; color:#666;">利用区分: ' + props.L02_025 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 農業地域（A12）
      case 'oh-kk-nogyo': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-nogyo') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-nogyo popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">農業地域</span><br>'
          if (props.A12_001) html.value += '<span style="font-size:14px;">地域区分: ' + props.A12_001 + '</span><br>'
          if (props.A12_002) html.value += '<span style="font-size:14px;">都道府県: ' + props.A12_002 + '</span><br>'
          if (props.A12_003) html.value += '<span style="font-size:14px;">市区町村: ' + props.A12_003 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 森林地域（A13）
      case 'oh-kk-shinrin': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-shinrin') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-shinrin popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">森林地域</span><br>'
          if (props.A13_001) html.value += '<span style="font-size:14px;">区分: ' + props.A13_001 + '</span><br>'
          if (props.A13_002) html.value += '<span style="font-size:14px;">都道府県: ' + props.A13_002 + '</span><br>'
          if (props.A13_003) html.value += '<span style="font-size:14px;">市区町村: ' + props.A13_003 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 国有林野（A45）
      case 'oh-kk-kokuyurin': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kokuyurin') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kokuyurin popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">国有林野</span><br>'
          if (props.A45_002) html.value += '<span style="font-size:14px;">森林管理局: ' + props.A45_002 + '</span><br>'
          if (props.A45_003) html.value += '<span style="font-size:14px;">森林管理署: ' + props.A45_003 + '</span><br>'
          if (props.A45_004) html.value += '<span style="font-size:14px;">森林事務所: ' + props.A45_004 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 行政区域（N03）
      case 'oh-kk-gyosei': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-gyosei') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-gyosei popup-base">'
          let name = ''
          if (props.N03_001) name += props.N03_001
          if (props.N03_003) name += ' ' + props.N03_003
          if (props.N03_004) name += ' ' + props.N03_004
          if (props.N03_005) name += ' ' + props.N03_005
          html.value += '<span style="font-size:16px; font-weight:bold;">' + name + '</span><br>'
          if (props.N03_002) html.value += '<span style="font-size:14px;">' + props.N03_002 + '</span><br>'
          if (props.N03_007) html.value += '<span style="font-size:12px; color:#666;">行政区域コード: ' + props.N03_007 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // DID人口集中地区（A16）
      case 'oh-kk-did': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-did') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-did popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.A16_003 || '') + '</span><br>'
          if (props.A16_005) html.value += '<span style="font-size:14px;">人口: ' + Number(props.A16_005).toLocaleString() + '人</span><br>'
          if (props.A16_006) html.value += '<span style="font-size:14px;">面積: ' + props.A16_006 + ' km²</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 小学校区（A27）
      case 'oh-kk-shogakkoku': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-shogakkoku') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-shogakkoku popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.A27_004 || '') + '</span><br>'
          if (props.A27_002) html.value += '<span style="font-size:14px;">設置者: ' + props.A27_002 + '</span><br>'
          if (props.A27_005) html.value += '<span style="font-size:14px;">所在地: ' + props.A27_005 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 中学校区（A32）
      case 'oh-kk-chugakkoku': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-chugakkoku') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-chugakkoku popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.A32_004 || '') + '</span><br>'
          if (props.A32_002) html.value += '<span style="font-size:14px;">設置者: ' + props.A32_002 + '</span><br>'
          if (props.A32_005) html.value += '<span style="font-size:14px;">所在地: ' + props.A32_005 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 医療圏（A38 / P23データ）
      case 'oh-kk-iryouken': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-iryouken') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-iryouken popup-base">'
          if (props._type === 'a') {
            html.value += '<span style="font-size:16px; font-weight:bold;">医療施設</span><br>'
            if (props.P23a_003) html.value += '<span style="font-size:14px;">都道府県: ' + props.P23a_003 + '</span><br>'
          } else {
            html.value += '<span style="font-size:16px; font-weight:bold;">医療圏</span><br>'
            if (props.P23b_002) html.value += '<span style="font-size:14px;">圏域名: ' + props.P23b_002 + '</span><br>'
            if (props.P23b_003) html.value += '<span style="font-size:14px;">都道府県: ' + props.P23b_003 + '</span><br>'
          }
          html.value += '</div>'
        }
        break
      }

      // 景観計画区域（A35a）
      case 'oh-kk-keikan-keikaku': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-keikan-keikaku') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-keikan-keikaku popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.A35a_004 || '景観計画区域') + '</span><br>'
          if (props.A35a_003) html.value += '<span style="font-size:14px;">策定主体: ' + props.A35a_003 + '</span><br>'
          if (props.A35a_005) html.value += '<span style="font-size:14px;">策定日: ' + props.A35a_005 + '</span><br>'
          if (props.A35a_006) html.value += '<span style="font-size:14px;">面積: ' + Number(props.A35a_006).toLocaleString() + ' ha</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 景観地区・準景観地区（A35b）
      case 'oh-kk-keikan-chiku': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-keikan-chiku') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-keikan-chiku popup-base">'
          if (props.A35d_005) {
            html.value += '<span style="font-size:16px; font-weight:bold;">' + props.A35d_005 + '</span><br>'
            if (props.A35d_003) html.value += '<span style="font-size:14px;">策定主体: ' + props.A35d_003 + '</span><br>'
            if (props.A35d_006) html.value += '<span style="font-size:14px;">区域名: ' + props.A35d_006 + '</span><br>'
            if (props.A35d_007) html.value += '<span style="font-size:14px;">面積: ' + Number(props.A35d_007).toLocaleString() + ' ha</span><br>'
            if (props.A35d_008) html.value += '<span style="font-size:14px;">決定日: ' + props.A35d_008 + '</span><br>'
          } else if (props.A35f_005) {
            html.value += '<span style="font-size:16px; font-weight:bold;">' + props.A35f_005 + '</span><br>'
            if (props.A35f_003) html.value += '<span style="font-size:14px;">策定主体: ' + props.A35f_003 + '</span><br>'
            if (props.A35f_006) html.value += '<span style="font-size:14px;">区域名: ' + props.A35f_006 + '</span><br>'
          } else {
            html.value += '<span style="font-size:16px; font-weight:bold;">景観地区</span><br>'
          }
          html.value += '</div>'
        }
        break
      }

      // 景観重要建造物・樹木（A35c）
      case 'oh-kk-keikan-kenzobutsu':
      case 'oh-kk-keikan-kenzobutsu-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-keikan-kenzobutsu') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-keikan-kenzobutsu popup-base">'
          if (props.A35g_007) {
            html.value += '<span style="font-size:16px; font-weight:bold;">' + props.A35g_007 + '</span><br>'
            if (props.A35g_003) html.value += '<span style="font-size:14px;">指定主体: ' + props.A35g_003 + '</span><br>'
            if (props.A35g_005) html.value += '<span style="font-size:14px;">指定番号: ' + props.A35g_005 + '</span><br>'
            if (props.A35g_008) html.value += '<span style="font-size:14px;">所在地: ' + props.A35g_008 + '</span><br>'
            if (props.A35g_009) html.value += '<span style="font-size:12px; color:#666;">' + props.A35g_009 + '</span><br>'
          } else if (props.A35h_007) {
            html.value += '<span style="font-size:16px; font-weight:bold;">景観重要樹木: ' + props.A35h_007 + '</span><br>'
            if (props.A35h_003) html.value += '<span style="font-size:14px;">指定主体: ' + props.A35h_003 + '</span><br>'
            if (props.A35h_005) html.value += '<span style="font-size:14px;">指定番号: ' + props.A35h_005 + '</span><br>'
            if (props.A35h_008) html.value += '<span style="font-size:14px;">所在地: ' + props.A35h_008 + '</span><br>'
            if (props.A35h_009) html.value += '<span style="font-size:12px; color:#666;">' + props.A35h_009 + '</span><br>'
          }
          html.value += '</div>'
        }
        break
      }

      // 伝統的建造物群保存地区（A43）
      case 'oh-kk-dentou-hozon': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-dentou-hozon') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-dentou-hozon popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.A43_004 || '') + '</span><br>'
          if (props.A43_001 && props.A43_002) html.value += '<span style="font-size:14px;">' + props.A43_001 + ' ' + props.A43_002 + '</span><br>'
          if (props.A43_007) html.value += '<span style="font-size:14px;">類型: ' + props.A43_007 + '</span><br>'
          if (props.A43_006) html.value += '<span style="font-size:14px;">所在地: ' + props.A43_006 + '</span><br>'
          if (props.A43_008) html.value += '<span style="font-size:14px;">選定日: ' + props.A43_008 + '</span><br>'
          if (props.A43_009) html.value += '<span style="font-size:14px;">面積: ' + props.A43_009 + ' ha</span><br>'
          if (props.A43_010) html.value += '<a href="' + props.A43_010 + '" target="_blank" style="font-size:12px;">詳細情報</a><br>'
          html.value += '</div>'
        }
        break
      }

      // 歴史的風致維持向上計画の重点地区（A44）
      case 'oh-kk-rekishi-fuuchi': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-rekishi-fuuchi') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-rekishi-fuuchi popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.A44_004 || '') + '</span><br>'
          if (props.A44_001 && props.A44_002) html.value += '<span style="font-size:14px;">' + props.A44_001 + ' ' + props.A44_002 + '</span><br>'
          if (props.A44_005) html.value += '<span style="font-size:14px;">認定日: ' + props.A44_005 + '</span><br>'
          if (props.A44_008) html.value += '<span style="font-size:14px;">重点区域面積: ' + props.A44_008 + ' ha</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 都市地域（A09）
      case 'oh-kk-toshi-chiiki': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-toshi-chiiki') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-toshi-chiiki popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">都市地域</span><br>'
          if (props.prefec_cd) html.value += '<span style="font-size:14px;">都道府県コード: ' + props.prefec_cd + '</span><br>'
          if (props.area_cd) html.value += '<span style="font-size:14px;">行政区域コード: ' + props.area_cd + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 用途地域（A29）
      case 'oh-kk-youto-chiiki': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-youto-chiiki') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-youto-chiiki popup-base">'
          const youtoMap = {
            '1': '第一種低層住居専用地域', '2': '第二種低層住居専用地域',
            '3': '第一種中高層住居専用地域', '4': '第二種中高層住居専用地域',
            '5': '第一種住居地域', '6': '第二種住居地域', '7': '準住居地域',
            '8': '近隣商業地域', '9': '商業地域',
            '10': '準工業地域', '11': '工業地域', '12': '工業専用地域',
            '13': '田園住居地域',
          }
          const youtoName = youtoMap[props.A29_004] || props.A29_005 || ''
          html.value += '<span style="font-size:16px; font-weight:bold;">' + youtoName + '</span><br>'
          if (props.A29_002 && props.A29_003) html.value += '<span style="font-size:14px;">' + props.A29_002 + ' ' + props.A29_003 + '</span><br>'
          if (props.A29_006) html.value += '<span style="font-size:14px;">建蔽率: ' + props.A29_006 + '%</span><br>'
          if (props.A29_007) html.value += '<span style="font-size:14px;">容積率: ' + props.A29_007 + '%</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 立地適正化計画区域（A50）
      case 'oh-kk-ricchi-tekiseika': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-ricchi-tekiseika') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-ricchi-tekiseika popup-base">'
          const kuikiMap = { '1': '都市機能誘導区域', '2': '居住誘導区域' }
          const kuikiName = kuikiMap[props.A50_006] || props.A50_007 || '立地適正化計画区域'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + kuikiName + '</span><br>'
          if (props.A50_002 && props.A50_004) html.value += '<span style="font-size:14px;">' + props.A50_002 + ' ' + props.A50_004 + '</span><br>'
          if (props.A50_005) html.value += '<span style="font-size:14px;">区域設定日: ' + props.A50_005 + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 歴史的風土保存区域（A42）
      case 'oh-kk-rekishi-fudo': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-rekishi-fudo') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-rekishi-fudo popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.A42_004 || '歴史的風土保存区域') + '</span><br>'
          if (props.A42_001 && props.A42_002) html.value += '<span style="font-size:14px;">' + props.A42_001 + ' ' + props.A42_002 + '</span><br>'
          if (props.A42_007) html.value += '<span style="font-size:14px;">面積: ' + props.A42_007 + ' ha</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 都市公園（P13）
      case 'oh-kk-toshi-koen':
      case 'oh-kk-toshi-koen-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-toshi-koen') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-toshi-koen popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.P13_003 || '') + '</span><br>'
          if (props.P13_005 && props.P13_006) html.value += '<span style="font-size:14px;">' + props.P13_005 + ' ' + props.P13_006 + '</span><br>'
          if (props.P13_008) html.value += '<span style="font-size:14px;">面積: ' + Number(props.P13_008).toLocaleString() + ' m²</span><br>'
          if (props.P13_007) html.value += '<span style="font-size:14px;">供用開始: ' + props.P13_007 + '年</span><br>'
          html.value += '</div>'
        }
        break
      }

      // A55 都市計画決定情報（国土数値情報）
      // 用途地域（A55）
      case 'oh-kk-a55-youto':
      case 'oh-kk-a55-youto-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-a55-youto') === -1) {
          const youtoMap = { 1: '第1種低層住居専用地域', 2: '第2種低層住居専用地域', 3: '第1種中高層住居専用地域', 4: '第2種中高層住居専用地域', 5: '第1種住居地域', 6: '第2種住居地域', 7: '準住居地域', 8: '田園住居地域', 9: '近隣商業地域', 10: '商業地域', 11: '準工業地域', 12: '工業地域', 13: '工業専用地域' }
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-a55-youto popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.YoutoName || youtoMap[props.YoutoCode] || '') + '</span><br>'
          if (props.FAR) html.value += '容積率: ' + props.FAR + '%<br>'
          if (props.BCR) html.value += '建蔽率: ' + props.BCR + '%<br>'
          if (props.Cityname) html.value += props.Pref + ' ' + props.Cityname + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 区域区分（線引き）（A55）
      case 'oh-kk-a55-senbiki':
      case 'oh-kk-a55-senbiki-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-a55-senbiki') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-a55-senbiki popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.AreaType || '') + '</span><br>'
          if (props.Cityname) html.value += props.Pref + ' ' + props.Cityname + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 都市計画区域（A55）
      case 'oh-kk-a55-tokei':
      case 'oh-kk-a55-tokei-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-a55-tokei') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-a55-tokei popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.TokeiType || '') + '</span><br>'
          if (props.TokeiName && props.TokeiName.trim()) html.value += props.TokeiName + '<br>'
          if (props.Cityname) html.value += props.Pref + ' ' + props.Cityname + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 防火・準防火地域（A55）
      case 'oh-kk-a55-bouka':
      case 'oh-kk-a55-bouka-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-a55-bouka') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-a55-bouka popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.AreaType || '') + '</span><br>'
          if (props.Cityname) html.value += props.Pref + ' ' + props.Cityname + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 高度地区（A55）
      case 'oh-kk-a55-koudoti':
      case 'oh-kk-a55-koudoti-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-a55-koudoti') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-a55-koudoti popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.DistType || '高度地区') + '</span><br>'
          if (props.Cityname) html.value += props.Pref + ' ' + props.Cityname + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 高度利用地区（A55）
      case 'oh-kk-a55-koudori':
      case 'oh-kk-a55-koudori-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-a55-koudori') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-a55-koudori popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.DistName || '高度利用地区') + '</span><br>'
          if (props.DistType) html.value += props.DistType + '<br>'
          if (props.Cityname) html.value += props.Pref + ' ' + props.Cityname + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 地区計画（A55）
      case 'oh-kk-a55-chikukei':
      case 'oh-kk-a55-chikukei-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-a55-chikukei') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-a55-chikukei popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.DistName || '地区計画') + '</span><br>'
          if (props.DistType) html.value += props.DistType + '<br>'
          if (props.FNDate) html.value += '決定日: ' + props.FNDate + '<br>'
          if (props.Cityname) html.value += props.Pref + ' ' + props.Cityname + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 特別用途地区（A55）
      case 'oh-kk-a55-tkbt':
      case 'oh-kk-a55-tkbt-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-a55-tkbt') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-a55-tkbt popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + (props.YoutoName || '') + '</span><br>'
          if (props.DistType) html.value += props.DistType + '<br>'
          if (props.Cityname) html.value += props.Pref + ' ' + props.Cityname + '<br>'
          html.value += '</div>'
        }
        break
      }

      // --- 大都市圏・条件不利地域 ---

      // 三大都市圏計画区域（A03）
      case 'oh-kk-sandai-toshiken':
      case 'oh-kk-sandai-toshiken-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-sandai-toshiken') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-sandai-toshiken popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 過疎地域（A17）
      case 'oh-kk-kaso-chiiki':
      case 'oh-kk-kaso-chiiki-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kaso-chiiki') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kaso-chiiki popup-base">'
          const kubun = { '1': '過疎地域', '2': 'みなし過疎', '3': '一部過疎' }
          const k = props.A17_009 || props.A17_008
          if (k) html.value += '<span style="font-size:16px; font-weight:bold;">' + (kubun[String(k)] || k) + '</span><br>'
          if (props.A17_003) html.value += props.A17_003
          if (props.A17_006) html.value += ' ' + props.A17_006
          if (props.A17_003 || props.A17_006) html.value += '<br>'
          html.value += '</div>'
        }
        break
      }
      // 振興山村（A24）
      case 'oh-kk-shinko-sanson':
      case 'oh-kk-shinko-sanson-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-shinko-sanson') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-shinko-sanson popup-base">'
          if (props.A24_003) html.value += props.A24_003
          if (props.A24_006) html.value += ' ' + props.A24_006
          if (props.A24_003 || props.A24_006) html.value += '<br>'
          if (props.A24_007) html.value += '名称: ' + props.A24_007 + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 特定農山村地域（A25）
      case 'oh-kk-tokutei-nosan':
      case 'oh-kk-tokutei-nosan-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-tokutei-nosan') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-tokutei-nosan popup-base">'
          if (props.A25_003) html.value += props.A25_003
          if (props.A25_006) html.value += ' ' + props.A25_006
          if (props.A25_003 || props.A25_006) html.value += '<br>'
          html.value += '</div>'
        }
        break
      }
      // 離島振興対策実施地域（A19）
      case 'oh-kk-rito-shinko':
      case 'oh-kk-rito-shinko-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-rito-shinko') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-rito-shinko popup-base">'
          if (props.A19_003) html.value += props.A19_003
          if (props.A19_006) html.value += ' ' + props.A19_006
          if (props.A19_003 || props.A19_006) html.value += '<br>'
          if (props.A19_007) html.value += '島名: ' + props.A19_007 + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 半島振興対策実施地域（A18）
      case 'oh-kk-hanto-shinko':
      case 'oh-kk-hanto-shinko-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-hanto-shinko') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-hanto-shinko popup-base">'
          if (props.A18_003) html.value += props.A18_003
          if (props.A18_006) html.value += ' ' + props.A18_006
          if (props.A18_003 || props.A18_006) html.value += '<br>'
          if (props.A18_007) html.value += '半島名: ' + props.A18_007 + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 豪雪地帯（A22）
      case 'oh-kk-gosetsu-chitai':
      case 'oh-kk-gosetsu-chitai-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-gosetsu-chitai') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-gosetsu-chitai popup-base">'
          const kubun = { '1': '豪雪地帯', '2': '特別豪雪地帯' }
          const k = props.A22_009 || props.A22_008
          if (k) html.value += '<span style="font-size:16px; font-weight:bold;">' + (kubun[String(k)] || k) + '</span><br>'
          if (props.A22_003) html.value += props.A22_003
          if (props.A22_006) html.value += ' ' + props.A22_006
          if (props.A22_003 || props.A22_006) html.value += '<br>'
          html.value += '</div>'
        }
        break
      }
      // 特殊土壌地帯（A23）
      case 'oh-kk-tokushu-dojo':
      case 'oh-kk-tokushu-dojo-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-tokushu-dojo') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-tokushu-dojo popup-base">'
          if (props.A23_003) html.value += props.A23_003
          if (props.A23_006) html.value += ' ' + props.A23_006
          if (props.A23_003 || props.A23_006) html.value += '<br>'
          if (props.A23_007) html.value += '土壌名: ' + props.A23_007 + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 半島循環道路（A37）
      case 'oh-kk-hanto-douro': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-hanto-douro') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-hanto-douro popup-base">'
          if (props.A37_003) html.value += props.A37_003
          if (props.A37_006) html.value += ' ' + props.A37_006
          if (props.A37_003 || props.A37_006) html.value += '<br>'
          if (props.A37_007) html.value += '路線名: ' + props.A37_007 + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 密集市街地（A39）
      case 'oh-kk-misshu-shigaichi':
      case 'oh-kk-misshu-shigaichi-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-misshu-shigaichi') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-misshu-shigaichi popup-base">'
          if (props.A39_003) html.value += '<span style="font-weight:bold;">' + props.A39_003 + '</span><br>'
          if (props.A39_006) html.value += '地区名: ' + props.A39_006 + '<br>'
          if (props.A39_007) html.value += '面積: ' + props.A39_007 + ' ha<br>'
          html.value += '</div>'
        }
        break
      }
      // A20 奄美群島 - データ提供終了
      // A21 小笠原諸島 - データ提供終了

      // --- 災害・防災 ---

      // 指定避難所（内閣府）
      case 'oh-kk-hinan-jo':
      case 'oh-kk-hinan-jo-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-hinan-jo') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-hinan-jo popup-base">'
          if (props['施設・場所名']) html.value += '<span style="font-size:16px; font-weight:bold;">' + props['施設・場所名'] + '</span><br>'
          if (props['都道府県名及び市町村名']) html.value += props['都道府県名及び市町村名'] + '<br>'
          if (props['住所']) html.value += '住所: ' + props['住所'] + '<br>'
          if (props['受入対象者']) html.value += '受入対象者: ' + props['受入対象者'] + '<br>'
          if (props['その他市町村長が必要と認める事項']) html.value += props['その他市町村長が必要と認める事項'] + '<br>'
          if (props['備考']) html.value += '備考: ' + props['備考'] + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 指定緊急避難場所（内閣府）
      case 'oh-kk-hinan-basho':
      case 'oh-kk-hinan-basho-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-hinan-basho') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-hinan-basho popup-base">'
          if (props['施設・場所名']) html.value += '<span style="font-size:16px; font-weight:bold;">' + props['施設・場所名'] + '</span><br>'
          if (props['都道府県名及び市町村名']) html.value += props['都道府県名及び市町村名'] + '<br>'
          if (props['住所']) html.value += '住所: ' + props['住所'] + '<br>'
          // 対応災害種別
          const saigai = []
          if (props['洪水'] === '1') saigai.push('🌊洪水')
          if (props['崖崩れ、土石流及び地滑り'] === '1') saigai.push('⛰️崖崩れ等')
          if (props['高潮'] === '1') saigai.push('🌊高潮')
          if (props['地震'] === '1') saigai.push('🏚️地震')
          if (props['津波'] === '1') saigai.push('🌊津波')
          if (props['大規模な火事'] === '1') saigai.push('🔥火事')
          if (props['内水氾濫'] === '1') saigai.push('💧内水氾濫')
          if (props['火山現象'] === '1') saigai.push('🌋火山')
          if (saigai.length > 0) html.value += '対応: ' + saigai.join(' ') + '<br>'
          if (props['備考']) html.value += '備考: ' + props['備考'] + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 避難施設（P20）
      case 'oh-kk-hinan-shisetsu':
      case 'oh-kk-hinan-shisetsu-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-hinan-shisetsu') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-hinan-shisetsu popup-base">'
          if (props.P20_002) html.value += '<span style="font-size:16px; font-weight:bold;">' + props.P20_002 + '</span><br>'
          if (props.P20_003) html.value += props.P20_003 + '<br>'
          if (props.P20_004) html.value += '所在地: ' + props.P20_004 + '<br>'
          const shisetsu = { '1': '一次避難所', '2': '広域避難場所', '3': 'その他' }
          if (props.P20_005) html.value += '種別: ' + (shisetsu[String(props.P20_005)] || props.P20_005) + '<br>'
          if (props.P20_006) html.value += '収容人数: ' + Number(props.P20_006).toLocaleString() + '人<br>'
          if (props.P20_007) html.value += '施設規模: ' + props.P20_007 + '<br>'
          // 到達圏ボタン
          const geom = features[0].geometry
          const hinanLng = geom.type === 'Point' ? geom.coordinates[0] : coordinates.lng
          const hinanLat = geom.type === 'Point' ? geom.coordinates[1] : coordinates.lat
          const hinanName = props.P20_002 || '避難施設'
          html.value += '<div style="margin-top:6px;">' +
            '<span onclick="window.openIsochroneFromFacility(' + hinanLng + ',' + hinanLat + ',\'' + hinanName.replace(/'/g, "\\'") + '\')" ' +
            'style="display:inline-block; padding:4px 12px; border-radius:16px; background:#37474F; color:white; font-size:12px; cursor:pointer; font-weight:500;">' +
            '🏃 避難圏域を表示</span></div>'
          html.value += '</div>'
        }
        break
      }
      // 竜巻等の突風（A30b）
      case 'oh-kk-tatsumaki':
      case 'oh-kk-tatsumaki-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-tatsumaki') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-tatsumaki popup-base">'
          if (props.A30b_002) html.value += '<span style="font-size:16px; font-weight:bold;">' + props.A30b_002 + '</span><br>'
          if (props.A30b_003) html.value += '発生日時: ' + props.A30b_003 + '<br>'
          if (props.A30b_005) html.value += props.A30b_005 + '<br>'
          if (props.A30b_013) html.value += '藤田スケール: F' + props.A30b_013 + '<br>'
          if (props.A30b_014) html.value += '被害概要: ' + props.A30b_014 + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 地すべり防止区域（A46）
      case 'oh-kk-jisuberi':
      case 'oh-kk-jisuberi-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-jisuberi') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-jisuberi popup-base">'
          const name = props['A46-a_004'] || props.A46_004
          if (name) html.value += '<span style="font-size:16px; font-weight:bold;">' + name + '</span><br>'
          const city = props['A46-a_003'] || props.A46_003
          if (city) html.value += city + '<br>'
          const addr = props['A46-a_005'] || props.A46_005
          if (addr) html.value += '所在地: ' + addr + '<br>'
          const date = props['A46-a_006'] || props.A46_006
          if (date) html.value += '指定日: ' + date + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 急傾斜地崩壊危険区域（A47）
      case 'oh-kk-kyukeisha':
      case 'oh-kk-kyukeisha-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kyukeisha') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kyukeisha popup-base">'
          const name = props['A47-a_004'] || props.A47_004
          if (name) html.value += '<span style="font-size:16px; font-weight:bold;">' + name + '</span><br>'
          const city = props['A47-a_003'] || props.A47_003
          if (city) html.value += city + '<br>'
          const addr = props['A47-a_005'] || props.A47_005
          if (addr) html.value += '所在地: ' + addr + '<br>'
          const date = props['A47-a_006'] || props.A47_006
          if (date) html.value += '指定日: ' + date + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 災害危険区域（A48）
      case 'oh-kk-saigai-kiken':
      case 'oh-kk-saigai-kiken-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-saigai-kiken') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-saigai-kiken popup-base">'
          if (props.A48_005) html.value += '<span style="font-size:16px; font-weight:bold;">' + props.A48_005 + '</span><br>'
          if (props.A48_001) html.value += props.A48_001
          if (props.A48_002) html.value += ' ' + props.A48_002
          if (props.A48_001 || props.A48_002) html.value += '<br>'
          const saigai = { '1': '洪水', '2': '高潮', '3': '津波', '4': '地すべり', '5': 'がけ崩れ', '6': 'その他' }
          if (props.A48_008) html.value += '災害種別: ' + (saigai[String(props.A48_008)] || props.A48_008) + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 砂防指定地（A52）
      case 'oh-kk-sabo-shiteichi':
      case 'oh-kk-sabo-shiteichi-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-sabo-shiteichi') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-sabo-shiteichi popup-base">'
          const name = props['A52-a_004'] || props.A52_004
          if (name) html.value += '<span style="font-size:16px; font-weight:bold;">' + name + '</span><br>'
          const city = props['A52-a_003'] || props.A52_003
          if (city) html.value += city + '<br>'
          const addr = props['A52-a_005'] || props.A52_005
          if (addr) html.value += '所在地: ' + addr + '<br>'
          html.value += '</div>'
        }
        break
      }
      // 津波浸水想定（A40）
      case 'oh-kk-tsunami-shinsui':
      case 'oh-kk-tsunami-shinsui-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-tsunami-shinsui') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-tsunami-shinsui popup-base">'
          if (props.A40_003) html.value += props.A40_003 + '<br>'
          if (props.A40_004) html.value += '想定: ' + props.A40_004 + '<br>'
          if (props.A40_005) html.value += '浸水深: ' + props.A40_005 + '<br>'
          if (!props.A40_003 && !props.A40_004) {
            const plist = Object.entries(props).filter(([k]) => !k.startsWith('_'))
            plist.slice(0, 6).forEach(([k, v]) => { if (v) html.value += k + ': ' + v + '<br>' })
          }
          html.value += '</div>'
        }
        break
      }
      // 高潮浸水想定区域（A49）
      case 'oh-kk-takashio-shinsui':
      case 'oh-kk-takashio-shinsui-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-takashio-shinsui') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-takashio-shinsui popup-base">'
          if (props.A49_003) html.value += props.A49_003 + '<br>'
          if (props.A49_004) html.value += '浸水深: ' + props.A49_004 + '<br>'
          if (!props.A49_003) {
            const plist = Object.entries(props).filter(([k]) => !k.startsWith('_'))
            plist.slice(0, 6).forEach(([k, v]) => { if (v) html.value += k + ': ' + v + '<br>' })
          }
          html.value += '</div>'
        }
        break
      }
      // 雨水出水浸水想定（A51）
      case 'oh-kk-usui-shinsui':
      case 'oh-kk-usui-shinsui-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-usui-shinsui') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-usui-shinsui popup-base">'
          if (props.A51_003) html.value += props.A51_003 + '<br>'
          if (props.A51_005) html.value += '浸水深: ' + props.A51_005 + '<br>'
          if (!props.A51_003 && !props.A51_005) {
            const plist = Object.entries(props).filter(([k]) => !k.startsWith('_'))
            plist.slice(0, 6).forEach(([k, v]) => { if (v) html.value += k + ': ' + v + '<br>' })
          }
          html.value += '</div>'
        }
        break
      }
      // 土砂災害警戒区域（A33）
      case 'oh-kk-dosha-saigai':
      case 'oh-kk-dosha-saigai-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-dosha-saigai') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-dosha-saigai popup-base">'
          if (props.A33_006) html.value += '<span style="font-size:16px; font-weight:bold;">' + props.A33_006 + '</span><br>'
          if (props.A33_003) html.value += props.A33_003 + '<br>'
          const type33 = { '1': '土石流', '2': '急傾斜地の崩壊', '3': '地すべり' }
          if (props.A33_004) html.value += '現象種別: ' + (type33[String(props.A33_004)] || props.A33_004) + '<br>'
          const kubun33 = { '1': '警戒区域', '2': '特別警戒区域' }
          if (props.A33_005) html.value += '区分: ' + (kubun33[String(props.A33_005)] || props.A33_005) + '<br>'
          if (!props.A33_003 && !props.A33_006) {
            const plist = Object.entries(props).filter(([k]) => !k.startsWith('_'))
            plist.slice(0, 6).forEach(([k, v]) => { if (v) html.value += k + ': ' + v + '<br>' })
          }
          html.value += '</div>'
        }
        break
      }
      // 洪水浸水想定区域（A31a）
      case 'oh-kk-kozui-shinsui': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kozui-shinsui') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kozui-shinsui popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // A53 多段階浸水想定 - 国土数値情報にデータなし（既存のtadankaiレイヤーを使用）

      // --- 3.地域 > 施設 ---

      // 国・都道府県の機関（P28）
      case 'oh-kk-kuni-kikan':
      case 'oh-kk-kuni-kikan-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kuni-kikan') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kuni-kikan popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 市町村役場等（P05）
      case 'oh-kk-yakuba':
      case 'oh-kk-yakuba-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-yakuba') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-yakuba popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 市区町村役場（P34）
      case 'oh-kk-shikuchoson-yakuba':
      case 'oh-kk-shikuchoson-yakuba-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-shikuchoson-yakuba') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-shikuchoson-yakuba popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 公共施設（P02）
      case 'oh-kk-kokyou-shisetsu':
      case 'oh-kk-kokyou-shisetsu-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kokyou-shisetsu') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kokyou-shisetsu popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 下水道関連施設（P22）
      case 'oh-kk-gesuidou':
      case 'oh-kk-gesuidou-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-gesuidou') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-gesuidou popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 廃棄物処理施設（P15）
      case 'oh-kk-haikibutsu':
      case 'oh-kk-haikibutsu-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-haikibutsu') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-haikibutsu popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 発電施設（P03）
      case 'oh-kk-hatsuden':
      case 'oh-kk-hatsuden-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-hatsuden') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-hatsuden popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // ニュータウン（P26）
      case 'oh-kk-newtown':
      case 'oh-kk-newtown-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-newtown') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-newtown popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 研究機関（P16）
      case 'oh-kk-kenkyu-kikan':
      case 'oh-kk-kenkyu-kikan-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kenkyu-kikan') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kenkyu-kikan popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 地場産業関連施設（P24）
      case 'oh-kk-jiba-sangyo':
      case 'oh-kk-jiba-sangyo-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-jiba-sangyo') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-jiba-sangyo popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 物流拠点（P31）
      case 'oh-kk-butsuryu-kyoten':
      case 'oh-kk-butsuryu-kyoten-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-butsuryu-kyoten') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-butsuryu-kyoten popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 集客施設（P33）
      case 'oh-kk-shukyaku':
      case 'oh-kk-shukyaku-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-shukyaku') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-shukyaku popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 福祉施設（P14）
      case 'oh-kk-fukushi':
      case 'oh-kk-fukushi-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-fukushi') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-fukushi popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 文化施設（P27）
      case 'oh-kk-bunka-shisetsu':
      case 'oh-kk-bunka-shisetsu-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-bunka-shisetsu') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-bunka-shisetsu popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      // --- 3.地域 > 地域資源・観光 ---

      // 都道府県指定文化財（P32）
      case 'oh-kk-bunkazai':
      case 'oh-kk-bunkazai-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-bunkazai') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-bunkazai popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 世界文化遺産（A34）
      case 'oh-kk-sekai-bunka-isan':
      case 'oh-kk-sekai-bunka-isan-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-sekai-bunka-isan') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-sekai-bunka-isan popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 世界自然遺産（A28）
      case 'oh-kk-sekai-shizen-isan':
      case 'oh-kk-sekai-shizen-isan-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-sekai-shizen-isan') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-sekai-shizen-isan popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 地域資源（P19）
      case 'oh-kk-chiiki-shigen':
      case 'oh-kk-chiiki-shigen-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-chiiki-shigen') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-chiiki-shigen popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      // --- 3.地域 > 保護保全 ---

      // 自然公園地域（A10）
      case 'oh-kk-shizen-koen':
      case 'oh-kk-shizen-koen-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-shizen-koen') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-shizen-koen popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 自然保全地域（A11）
      case 'oh-kk-shizen-hozen':
      case 'oh-kk-shizen-hozen-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-shizen-hozen') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-shizen-hozen popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 鳥獣保護区（A15）
      case 'oh-kk-chouju-hogo':
      case 'oh-kk-chouju-hogo-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-chouju-hogo') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-chouju-hogo popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      // --- 4.交通 ---

      // 緊急輸送道路（N10）
      case 'oh-kk-kinkyu-yuso': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kinkyu-yuso') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kinkyu-yuso popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 重要物流道路（N12）
      case 'oh-kk-butsuryu-douro': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-butsuryu-douro') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-butsuryu-douro popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 高速バス停留所（P36）
      case 'oh-kk-kosoku-bus-tei':
      case 'oh-kk-kosoku-bus-tei-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kosoku-bus-tei') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kosoku-bus-tei popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 空港（C28）
      case 'oh-kk-kuukou':
      case 'oh-kk-kuukou-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kuukou') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kuukou popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 港湾（C02）
      case 'oh-kk-kowan':
      case 'oh-kk-kowan-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kowan') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kowan popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 定期旅客航路（N09）
      case 'oh-kk-teiki-kouro': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-teiki-kouro') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-teiki-kouro popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 港湾間流通量（S10a）
      case 'oh-kk-kowan-ryutsu': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kowan-ryutsu') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kowan-ryutsu popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 空港間流通量（S10b）
      case 'oh-kk-kuukou-ryutsu': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kuukou-ryutsu') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kuukou-ryutsu popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      // 貨物旅客地域流動量（S05-d）
      case 'oh-kk-kamotsu-ryokaku': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-kamotsu-ryokaku') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-kamotsu-ryokaku popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      case 'oh-kk-rito-tokei':
      case 'oh-kk-rito-tokei-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-rito-tokei') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-rito-tokei popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      case 'oh-kk-gosetsu-tokei':
      case 'oh-kk-gosetsu-tokei-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-gosetsu-tokei') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-gosetsu-tokei popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      case 'oh-kk-hanto-tokei':
      case 'oh-kk-hanto-tokei-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-hanto-tokei') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-hanto-tokei popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      case 'oh-kk-amami-tokei':
      case 'oh-kk-amami-tokei-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-amami-tokei') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-amami-tokei popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      case 'oh-kk-ogasawara-tokei':
      case 'oh-kk-ogasawara-tokei-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-ogasawara-tokei') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-ogasawara-tokei popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      case 'oh-kk-eki-joko-pt':
      case 'oh-kk-eki-joko-pt-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-eki-joko-pt') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-eki-joko-pt popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      // 国土数値情報 メッシュデータ ポップアップ（新規8データセット）
      case 'oh-kk-hyoko5-mesh':
      case 'oh-kk-hyoko5-mesh-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-hyoko5-mesh') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-hyoko5-mesh popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      case 'oh-kk-heinenchi-mesh': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-heinenchi-mesh') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-heinenchi-mesh popup-base">'
          // 年間サマリを見やすく表示（0.1単位を実単位に変換）
          const p = props
          const v = (key, div = 1, unit = '') => {
            const raw = p[key]
            if (raw === undefined || raw === null) return 'データなし'
            return (raw / div).toFixed(div > 1 ? 1 : 0) + unit
          }
          html.value += '<table class="popup-table">'
          html.value += '<tr><td>メッシュコード</td><td>' + (p.G02_001 || '') + '</td></tr>'
          html.value += '<tr><td>年降水量</td><td>' + v('G02_014', 10, 'mm') + '</td></tr>'
          html.value += '<tr><td>年平均気温</td><td>' + v('G02_053', 10, '℃') + '</td></tr>'
          html.value += '<tr><td>年平均最高気温</td><td>' + v('G02_051', 10, '℃') + '</td></tr>'
          html.value += '<tr><td>年平均最低気温</td><td>' + v('G02_052', 10, '℃') + '</td></tr>'
          html.value += '<tr><td>年最深積雪</td><td>' + v('G02_058', 1, 'cm') + '</td></tr>'
          html.value += '<tr><td>年日照時間</td><td>' + v('G02_071', 10, 'h') + '</td></tr>'
          html.value += '<tr><td>年日射量</td><td>' + v('G02_084', 10, 'MJ/m²') + '</td></tr>'
          html.value += '</table>'
          html.value += '</div>'
        }
        break
      }
      case 'oh-kk-tochiriyo3-mesh': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-tochiriyo3-mesh') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-tochiriyo3-mesh popup-base">'
          const luTypes = [
            { key: '田', label: '田', color: '#ff0' },
            { key: 'その他の農用地', label: '他農用地', color: '#fc9' },
            { key: '森林', label: '森林', color: '#0a0' },
            { key: '荒地', label: '荒地', color: '#0f9' },
            { key: '建物用地', label: '建物用地', color: '#f00' },
            { key: '道路', label: '道路', color: '#888' },
            { key: '鉄道', label: '鉄道', color: '#b4b4b4' },
            { key: 'その他の用地', label: '他の用地', color: '#c84610' },
            { key: '河川地及び湖沼', label: '河川湖沼', color: '#00f' },
            { key: '海浜', label: '海浜', color: '#ff9' },
            { key: '海水域', label: '海水域', color: '#0cf' },
            { key: 'ゴルフ場', label: 'ゴルフ場', color: '#6c6' },
          ]
          const total = luTypes.reduce((s, t) => s + (props[t.key] || 0), 0)
          if (props['メッシュ']) html.value += '<div style="margin-bottom:4px">メッシュ: ' + props['メッシュ'] + '</div>'
          for (const t of luTypes) {
            const val = props[t.key] || 0
            if (val <= 0) continue
            const pct = total > 0 ? (val / total * 100).toFixed(1) : 0
            html.value += '<div style="display:flex;align-items:center;gap:4px;font-size:12px;line-height:1.6">'
            html.value += '<span style="width:58px;flex-shrink:0">' + t.label + '</span>'
            html.value += '<span style="flex:1;height:10px;background:#eee;border-radius:3px;overflow:hidden"><span style="display:block;height:100%;width:' + pct + '%;background:' + t.color + '"></span></span>'
            html.value += '<span style="width:40px;text-align:right;flex-shrink:0">' + pct + '%</span>'
            html.value += '</div>'
          }
          html.value += '</div>'
        }
        break
      }
      case 'oh-kk-tochiriyo-toshi-mesh': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-tochiriyo-toshi-mesh') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-tochiriyo-toshi-mesh popup-base">'
          const toshiTypes = {
            '0100': ['田', '#ff0'], '0200': ['その他の農用地', '#fc9'], '0500': ['森林', '#0a0'],
            '0600': ['荒地', '#0f9'], '0701': ['低層建物', '#f66'], '0702': ['低層建物（密集）', '#f00'],
            '0703': ['高層建物', '#b40000'], '0704': ['工場', '#960096'],
            '0901': ['道路', '#888'], '0902': ['鉄道', '#b4b4b4'],
            '1001': ['公共施設等用地', '#c84610'], '1002': ['空地', '#dcc8b4'], '1003': ['公園・緑地', '#32c832'],
            '1100': ['河川・湖沼', '#00f'], '1400': ['海浜', '#ff9'], '1500': ['海水域', '#0cf'], '1600': ['ゴルフ場', '#6c6'],
          }
          const code = props['土地利用種別']
          const info = toshiTypes[code] || ['不明(' + code + ')', '#999']
          html.value += '<div style="display:flex;align-items:center;gap:6px;font-size:13px">'
          html.value += '<span style="width:16px;height:16px;border-radius:3px;background:' + info[1] + ';border:1px solid #999;flex-shrink:0"></span>'
          html.value += '<span>' + info[0] + '</span></div>'
          if (props['細分メッシュコード']) html.value += '<div style="font-size:11px;color:#888;margin-top:2px">メッシュ: ' + props['細分メッシュコード'] + '</div>'
          html.value += '</div>'
        }
        break
      }
      case 'oh-kk-tochiriyo-shosai-mesh':
      case 'oh-kk-tochiriyo-shosai-mesh-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-tochiriyo-shosai-mesh') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-tochiriyo-shosai-mesh popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      case 'oh-kk-dosha-nadare-mesh':
      case 'oh-kk-dosha-nadare-mesh-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-dosha-nadare-mesh') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-dosha-nadare-mesh popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }
      case 'oh-kk-kozui-mesh-10':
      case 'oh-kk-kozui-mesh-20':
      case 'oh-kk-kozui-mesh-30':
      case 'oh-kk-kozui-mesh-41':
      case 'oh-kk-kozui-mesh-42': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        const meshDupKey = 'kk-kozui-mesh-' + layerId.replace('oh-kk-kozui-mesh-', '')
        if (html.value.indexOf(meshDupKey) === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="' + meshDupKey + ' popup-base">'
          const rank = props.rank
          if (layerId === 'oh-kk-kozui-mesh-10' || layerId === 'oh-kk-kozui-mesh-20') {
            const depthLabels = { 1: '0.5m未満', 2: '0.5〜3.0m', 3: '3.0〜5.0m', 4: '5.0〜10.0m', 5: '10.0〜20.0m', 6: '20.0m以上' }
            const depthColors = { 1: '#FFFFB2', 2: '#FFED64', 3: '#FFB432', 4: '#FF6400', 5: '#E61E00', 6: '#960000' }
            html.value += '<span style="color:' + (depthColors[rank] || '#333') + '; font-weight:bold;">●</span> '
            html.value += '浸水深: ' + (depthLabels[rank] || 'ランク' + rank)
          } else if (layerId === 'oh-kk-kozui-mesh-30') {
            const timeLabels = { 1: '12時間未満', 2: '12時間〜1日', 3: '1〜3日', 4: '3日〜1週間', 5: '1〜2週間' }
            html.value += '浸水継続時間: ' + (timeLabels[rank] || 'ランク' + rank)
          } else if (layerId === 'oh-kk-kozui-mesh-41') {
            html.value += '<span style="color:#C80000; font-weight:bold;">家屋倒壊等氾濫想定区域（氾濫流）</span>'
          } else if (layerId === 'oh-kk-kozui-mesh-42') {
            html.value += '<span style="color:#800080; font-weight:bold;">家屋倒壊等氾濫想定区域（河岸浸食）</span>'
          }
          html.value += '</div>'
        }
        break
      }
      case 'oh-kk-douro-mitsudo-mesh':
      case 'oh-kk-douro-mitsudo-mesh-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kk-douro-mitsudo-mesh') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kk-douro-mitsudo-mesh popup-base">'
          html.value += kkPropsHtml(props)
          html.value += '</div>'
        }
        break
      }

      // GSI 街区レベル位置参照情報（住居表示住所）
      case 'oh-gsi-jusho':
      case 'oh-gsi-jusho-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('gsi-jusho') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="gsi-jusho popup-base">'
          html.value += '<span style="font-size:18px; font-weight:bold;">' + (props['町又は字の名称'] || '') + ' ' + (props['街区符号'] || '') + '-' + (props['基礎番号'] || '') + '</span><br>'
          html.value += '市区町村コード: ' + (props['市区町村コード'] || '') + '<br>'
          html.value += '住所コード: ' + (props['住所コード'] || '') + '<br>'
          html.value += '地図情報レベル: ' + (props['地図情報レベル'] || '') + '<br>'
          html.value += '</div>'
        }
        break
      }

      // ドローン飛行禁止区域（レッドゾーン）
      case 'oh-drone-zone-red-fill':
      case 'oh-drone-zone-red-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('drone-zone-red') === -1) {
          html.value += '<div class="layer-label-div">小型無人機等飛行禁止区域</div>'
          html.value += '<div class="drone-zone-red popup-base" style="border-left: 4px solid #cc0000; padding-left: 8px;">'
          if (props.name) {
            html.value += '<span style="font-size:16px; font-weight:bold;">' + props.name + '</span><br>'
          }
          html.value += '<span style="font-size:14px; color: #cc0000; font-weight:bold;">レッドゾーン</span><br>'
          html.value += '<span style="font-size:12px;">対象施設及びその指定敷地等</span>'
          html.value += '</div>'
        }
        break
      }

      // ドローン飛行禁止区域（イエローゾーン）
      // レッドゾーンと重なる場合はレッドゾーンのみ表示
      case 'oh-drone-zone-yellow-fill':
      case 'oh-drone-zone-yellow-line': {
        // レッドゾーンが既に表示されていたらスキップ
        if (html.value.indexOf('drone-zone-red') !== -1) break
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        // クリック地点にレッドゾーンのフィーチャもあるか確認
        const redLayers = ['oh-drone-zone-red-fill', 'oh-drone-zone-red-line'].filter(id => map.getLayer(id))
        if (redLayers.length > 0) {
          const redFeatures = map.queryRenderedFeatures(map.project(coordinates), { layers: redLayers })
          if (redFeatures.length > 0) break
        }
        props = features[0].properties
        if (html.value.indexOf('drone-zone-yellow') === -1) {
          html.value += '<div class="layer-label-div">小型無人機等飛行禁止区域</div>'
          html.value += '<div class="drone-zone-yellow popup-base" style="border-left: 4px solid #cccc00; padding-left: 8px;">'
          html.value += '<span style="font-size:14px; color: #999900; font-weight:bold;">イエローゾーン</span><br>'
          html.value += '<span style="font-size:12px;">対象施設周辺地域</span>'
          html.value += '</div>'
        }
        break
      }

      // 無人航空機飛行計画データ
      case 'oh-drone-plan-fill':
      case 'oh-drone-plan-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('drone-plan-popup') === -1) {
          html.value += '<div class="layer-label-div">🛸 無人航空機 飛行計画</div>'
          html.value += '<div class="drone-plan-popup popup-base" style="border-left: 4px solid #2196F3; padding-left: 8px;">'

          // 飛行目的
          const purposes = []
          if (props['飛行目的（業務）_空撮'] === 1) purposes.push('空撮')
          if (props['飛行目的（業務）_報道取材'] === 1) purposes.push('報道取材')
          if (props['飛行目的（業務）_警備'] === 1) purposes.push('警備')
          if (props['飛行目的（業務）_農林水産業'] === 1) purposes.push('農林水産')
          if (props['飛行目的（業務）_測量'] === 1) purposes.push('測量')
          if (props['飛行目的（業務）_環境調査'] === 1) purposes.push('環境調査')
          if (props['飛行目的（業務）_設備メンテナンス'] === 1) purposes.push('メンテナンス')
          if (props['飛行目的（業務）_インフラ点検・保守 '] === 1) purposes.push('インフラ点検')
          if (props['飛行目的（業務）_資材管理'] === 1) purposes.push('資材管理')
          if (props['飛行目的（業務）_輸送・宅配'] === 1) purposes.push('輸送・宅配')
          if (props['飛行目的（業務）_自然観測'] === 1) purposes.push('自然観測')
          if (props['飛行目的（業務）_事故・災害対応等'] === 1) purposes.push('事故・災害')
          if (props['飛行目的（業務）_その他'] === 1) purposes.push('業務その他')
          if (props['飛行目的（業務以外）_趣味'] === 1) purposes.push('趣味')
          if (props['飛行目的（業務以外）_その他'] === 1) purposes.push('その他')
          if (purposes.length > 0) {
            html.value += '<b>飛行目的:</b> ' + purposes.join('・') + '<br>'
          }

          // 出発地
          if (props['出発地']) {
            html.value += '<b>出発地:</b> ' + props['出発地'] + '<br>'
          }

          // 飛行情報
          if (props['飛行高度']) html.value += '<b>飛行高度:</b> ' + props['飛行高度'] + 'm<br>'
          if (props['飛行速度']) html.value += '<b>飛行速度:</b> ' + props['飛行速度'] + 'm/s<br>'
          if (props['最大飛行時間']) html.value += '<b>最大飛行時間:</b> ' + props['最大飛行時間'] + '分<br>'

          // 機体情報
          if (props['機体の種類']) html.value += '<b>機体:</b> ' + props['機体の種類'] + '<br>'
          if (props['最大離陸重量']) html.value += '<b>最大離陸重量:</b> ' + props['最大離陸重量'] + 'kg<br>'

          // 飛行空域
          const airspaces = []
          if (props['飛行空域_DID'] === 1) airspaces.push('DID')
          if (props['飛行空域_150m'] === 1) airspaces.push('150m超')
          if (props['飛行空域_空港周辺'] === 1) airspaces.push('空港周辺')
          if (airspaces.length > 0) {
            html.value += '<b>飛行空域:</b> ' + airspaces.join('・') + '<br>'
          }

          // 飛行方法
          const methods = []
          if (props['飛行方法_夜間'] === 1) methods.push('夜間')
          if (props['飛行方法_目視外'] === 1) methods.push('目視外')
          if (props['飛行方法_30m'] === 1) methods.push('30m未満')
          if (props['飛行方法_催し物'] === 1) methods.push('催し物上空')
          if (props['飛行方法_危険物'] === 1) methods.push('危険物')
          if (props['飛行方法_物件投下'] === 1) methods.push('物件投下')
          if (methods.length > 0) {
            html.value += '<b>飛行方法:</b> ' + methods.join('・') + '<br>'
          }

          // 飛行予定日時
          if (props['飛行予定日時_開始']) {
            const start = props['飛行予定日時_開始'].replace('T', ' ').slice(0, 16)
            const end = props['飛行予定日時_終了'] ? props['飛行予定日時_終了'].replace('T', ' ').slice(0, 16) : ''
            html.value += '<b>飛行予定:</b> ' + start + (end ? ' 〜 ' + end : '') + '<br>'
          }

          html.value += '</div>'
        }
        break
      }

      // ============================================================================================================
      // 国土数値情報 ポップアップ終了
      // ============================================================================================================

      case 'oh-saga-kijyunten-label':
      case 'oh-saga-kijyunten-point': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('saga-kijyunten') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="street-view" style="margin-top:0px;height: 200px;width: 300px"></div>'
          html0 += '<div class="saga-kijyunten popup-body">'
          Object.keys(props).forEach(function (key) {
            html0 += key + '=' + props[key] + '<br>'
          })
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-city-geojson-poligon-layer':
      case 'oh-city-pmtiles-poligon-layer': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return

        // ⭐️---------------------------------------------------------------------------------------------------
        let sec
        if (store.state.showChibanzuDrawer) {
          store.commit('setChibanzuDrawer', false)
          sec = 500
        } else {
          sec = 0
        }
        store.state.popupFeatureProperties = features[0].properties
        store.state.popupFeatureCoordinates = coordinates
        const tgtElm = document.querySelector('.comment-text')
        if (tgtElm) tgtElm.innerHTML = ''
        const tgtElm2 = document.querySelector('.v-cool small')
        if (tgtElm2) tgtElm2.innerHTML = ''
        setTimeout(() => {
          store.commit('setRightDrawer', false)
          store.commit('setChibanzuDrawer', true)
        }, sec)
        // -----------------------------------------------------------------------------------------------------
        break
      }
      case 'oh-point-layer': {
        html.value = 'dumy'
        break
      }
      case 'oh-kokuyurin-polygon': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('kokuyurin') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="kokuyurin popup-body">'
          html0 += '<span style="font-size:16px;">森林管理局＝' + props.A45_008 + '</span><br>'
          html0 += '<span style="font-size:16px;">森林管理署＝' + props.A45_009 + '</span><br>'
          html0 += '<span style="font-size:16px;">小班＝' + props.A45_010 + '</span><br>'
          html0 += '<span style="font-size:16px;">林小班＝' + props.A45_011 + '</span><br>'
          html0 += '<span style="font-size:16px;">国有林名称＝' + props.A45_013 + '</span><br>'
          html0 += '<span style="font-size:16px;">県市町村名＝' + props.A45_014 + '</span><br>'
          html0 += '<span style="font-size:16px;">雑種１＝' + props.A45_015 + '</span><br>'
          html0 += '<span style="font-size:16px;">雑種２＝' + props.A45_018 + '</span><br>'
          html0 += '<span style="font-size:16px;">林種＝' + props.A45_025 + '</span><br>'
          html0 += '<span style="font-size:16px;">機能類型＝' + props.A45_026 + '</span><br>'
          html0 += '<span style="font-size:16px;">小班面積ha＝' + props.A45_027 + '</span><br>'
          html0 += '<span style="font-size:16px;">保安林＝' + props.A45_028 + '</span><br>'
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-shinrin-polygon':
      case 'oh-shinrin-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-shinrin-polygon'] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-shinrin-polygon'] })
        }
        if (features.length === 0) return
        // LAYER_NO を森林地域区分名に変換
        const layerNoName = {
          7: '森林地域',
          8: '国有林',
          9: '地域森林計画対象民有林',
          10: '保安林',
        }
        // 都道府県コードから都道府県名に変換
        const prefNames = {
          1: '北海道', 2: '青森県', 3: '岩手県', 4: '宮城県', 5: '秋田県',
          6: '山形県', 7: '福島県', 8: '茨城県', 9: '栃木県', 10: '群馬県',
          11: '埼玉県', 12: '千葉県', 13: '東京都', 14: '神奈川県', 15: '新潟県',
          16: '富山県', 17: '石川県', 18: '福井県', 19: '山梨県', 20: '長野県',
          21: '岐阜県', 22: '静岡県', 23: '愛知県', 24: '三重県', 25: '滋賀県',
          26: '京都府', 27: '大阪府', 28: '兵庫県', 29: '奈良県', 30: '和歌山県',
          31: '鳥取県', 32: '島根県', 33: '岡山県', 34: '広島県', 35: '山口県',
          36: '徳島県', 37: '香川県', 38: '愛媛県', 39: '高知県', 40: '福岡県',
          41: '佐賀県', 42: '長崎県', 43: '熊本県', 44: '大分県', 45: '宮崎県',
          46: '鹿児島県', 47: '沖縄県',
        }
        // レイヤーラベルは1回だけ表示
        if (html.value.indexOf('shinrin') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="shinrin" style="font-weight: normal; color: #333; line-height: 25px; padding: 12px 14px;">'
          // 重複チェック用Set（同じポリゴン情報を複数回表示しない）
          const shownKeys = new Set()
          // 全ての重なるポリゴンの情報を表示
          for (let i = 0; i < features.length; i++) {
            const props = features[i].properties
            // 重複チェック（LAYER_NO + OBJ_NAME + AREA_SIZE で一意とみなす）
            const key = `${props.LAYER_NO}-${props.OBJ_NAME || ''}-${props.AREA_SIZE || ''}`
            if (shownKeys.has(key)) continue
            shownKeys.add(key)
            // 2つ目以降は区切り線を追加
            if (i > 0 && shownKeys.size > 1) {
              html.value += '<hr style="border: none; border-top: 1px dashed #ccc; margin: 8px 0;">'
            }
            const kubunName = layerNoName[props.LAYER_NO] || '不明'
            const prefName = prefNames[props.PREFEC_CD] || '-'
            html.value += '<span style="font-size:18px; font-weight:bold; color:#228B22;">' + kubunName + '</span><br>'
            if (props.OBJ_NAME) {
              html.value += '<span style="font-size:14px;">名称＝' + props.OBJ_NAME + '</span><br>'
            }
            html.value += '<span style="font-size:14px;">都道府県＝' + prefName + '</span><br>'
            if (props.AREA_SIZE) {
              html.value += '<span style="font-size:14px;">面積＝' + props.AREA_SIZE.toFixed(2) + ' ha</span><br>'
            }
            html.value += '<span style="font-size:12px; color:#666;">データ年度＝' + (props.FIS_YEAR || '-') + '</span><br>'
            if (props.REMARK_STR) {
              html.value += '<span style="font-size:12px; color:#666;">備考＝' + props.REMARK_STR + '</span><br>'
            }
          }
          html.value += '</div>'
        }
        break
      }
      case 'oh-shinrinkeikaku-kokuyurin':
      case 'oh-shinrinkeikaku-minyurin':
      case 'oh-shinrinkeikaku-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-shinrinkeikaku-kokuyurin', 'oh-shinrinkeikaku-minyurin'] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-shinrinkeikaku-kokuyurin', 'oh-shinrinkeikaku-minyurin'] })
        }
        if (features.length === 0) return
        if (html.value.indexOf('shinrinkeikaku') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="shinrinkeikaku" style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">'
          const shownKeys = new Set()
          for (let i = 0; i < features.length; i++) {
            const props = features[i].properties
            const key = `${props['民国別'] || ''}-${props['林班'] || ''}-${props['市町村名称'] || ''}`
            if (shownKeys.has(key)) continue
            shownKeys.add(key)
            if (shownKeys.size > 1) {
              html.value += '<hr style="border: none; border-top: 1px dashed #ccc; margin: 8px 0;">'
            }
            const minkoku = props['民国別'] || '-'
            const color = minkoku === '国有林' ? '#004d00' : '#00e600'
            html.value += '<span style="font-size:16px; font-weight:bold; color:' + color + ';">' + minkoku + '</span><br>'
            if (props['都道府県名称']) html.value += '<span style="font-size:14px;">' + props['都道府県名称'] + ' ' + (props['市町村名称'] || '') + '</span><br>'
            if (props['森林計画区名称']) html.value += '<span style="font-size:14px;">森林計画区＝' + props['森林計画区名称'] + '</span><br>'
            if (props['林班']) html.value += '<span style="font-size:14px;">林班＝' + props['林班'] + '</span><br>'
            if (props['森林管理局名称']) html.value += '<span style="font-size:12px; color:#666;">管理局＝' + props['森林管理局名称'] + '</span><br>'
            if (props['森林管理署等名称']) html.value += '<span style="font-size:12px; color:#666;">管理署＝' + props['森林管理署等名称'] + '</span><br>'
            if (props['広域流域名称']) html.value += '<span style="font-size:12px; color:#666;">広域流域＝' + props['広域流域名称'] + '</span><br>'
            if (props['データ時点']) html.value += '<span style="font-size:12px; color:#666;">データ時点＝' + props['データ時点'] + '</span><br>'
          }
          html.value += '</div>'
        }
        break
      }
      // 県別 森林計画図ポップアップ（全県共通ハンドラー）
      case 'oh-hokkaido-forest-fill': case 'oh-hokkaido-forest-line': case 'oh-hokkaido-forest-label':
      case 'oh-iwate-forest-fill': case 'oh-iwate-forest-line': case 'oh-iwate-forest-label':
      case 'oh-niigata-forest-fill': case 'oh-niigata-forest-line': case 'oh-niigata-forest-label':
      case 'oh-gunma-forest-fill': case 'oh-gunma-forest-line': case 'oh-gunma-forest-label':
      case 'oh-tochigi-forest-fill': case 'oh-tochigi-forest-line': case 'oh-tochigi-forest-label':
      case 'oh-nagano-forest-fill': case 'oh-nagano-forest-line': case 'oh-nagano-forest-label':
      case 'oh-shizuoka-forest-fill': case 'oh-shizuoka-forest-line': case 'oh-shizuoka-forest-label':
      case 'oh-wakayama-forest-fill': case 'oh-wakayama-forest-line': case 'oh-wakayama-forest-label':
      case 'oh-ehime-forest-fill': case 'oh-ehime-forest-line': case 'oh-ehime-forest-label':
      case 'oh-yamaguchi-forest-fill': case 'oh-yamaguchi-forest-line': case 'oh-yamaguchi-forest-label':
      case 'oh-kumamoto-forest-fill': case 'oh-kumamoto-forest-line': case 'oh-kumamoto-forest-label':
      case 'oh-nagasaki-forest-fill': case 'oh-nagasaki-forest-line': case 'oh-nagasaki-forest-label': {
        // layerIdから県キーを抽出: "oh-{key}-forest-fill" → key
        const prefKey = layerId.replace(/^oh-/, '').replace(/-forest-(fill|line|label)$/, '')
        const fillLayerId = 'oh-' + prefKey + '-forest-fill'
        const fillLayers = [fillLayerId].filter(l => map.getLayer(l))
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: fillLayers })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: fillLayers })
        }
        if (features.length === 0) return
        const divClass = prefKey + '-forest'
        if (html.value.indexOf(divClass) === -1) {
          const popupCfg = prefForestPopupConfig[prefKey] || {}
          const prefName = prefForestNames[prefKey] || ''
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="' + divClass + '" style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">'
          const shownKeys = new Set()
          for (let i = 0; i < features.length; i++) {
            const props = features[i].properties
            // 重複判定キー
            const muniVal = popupCfg.muni ? (props[popupCfg.muni] || '') : ''
            const rinpanVal = popupCfg.rinpan ? (props[popupCfg.rinpan] || '') : ''
            const shohanVal = popupCfg.shohan ? (props[popupCfg.shohan] || '') : (popupCfg.shohanExpr ? popupCfg.shohanExpr.map(f => props[f] || '').join('') : '')
            const nameVal = popupCfg.nameField ? (props[popupCfg.nameField] || '') : ''
            const dupKey = muniVal + '-' + rinpanVal + '-' + shohanVal + '-' + nameVal
            if (shownKeys.has(dupKey)) continue
            shownKeys.add(dupKey)
            if (shownKeys.size > 1) {
              html.value += '<hr style="border: none; border-top: 1px dashed #ccc; margin: 8px 0;">'
            }
            // 市町村名
            html.value += '<span style="font-size:16px; font-weight:bold; color:#228B22;">🌲 ' + (muniVal || prefName) + '</span><br>'
            // NAME（群馬形式: 林班-小班がNAMEフィールドに入っている場合）
            if (popupCfg.nameField && nameVal) {
              if (nameVal.includes('-')) {
                const parts = nameVal.split('-')
                html.value += '<span style="font-size:14px;">林班＝' + parts[0] + '　小班＝' + parts[1] + '</span><br>'
              } else {
                html.value += '<span style="font-size:14px;">林班＝' + nameVal + '</span><br>'
              }
              if (popupCfg.codeField && props[popupCfg.codeField]) {
                html.value += '<span style="font-size:12px; color:#666;">計画コード＝' + props[popupCfg.codeField] + '</span><br>'
              }
            } else {
              // 林班・小班
              if (rinpanVal) html.value += '<span style="font-size:14px;">林班＝' + rinpanVal + '</span>'
              if (shohanVal) html.value += '<span style="font-size:14px;">　小班＝' + shohanVal + '</span>'
              if (rinpanVal || shohanVal) html.value += '<br>'
            }
            // 追加フィールド
            if (popupCfg.extra) {
              for (const [field, displayName] of popupCfg.extra) {
                const val = props[field]
                if (val != null && val !== '' && val !== 0) {
                  html.value += '<span style="font-size:13px; color:#555;">' + displayName + '＝' + val + '</span><br>'
                }
              }
            }
          }
          html.value += '</div>'
        }
        break
      }
      case 'oh-fr-mesh-national-fill':
      case 'oh-fr-mesh-national-line': {
        // 全国版森林資源メッシュ（フィールド名が県別PMTilesと異なる）
        const nationalFillLayers = ['oh-fr-mesh-national-fill'].filter(l => map.getLayer(l))
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: nationalFillLayers })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: nationalFillLayers })
        }
        if (features.length === 0) return
        if (html.value.indexOf('fr-mesh20m') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="fr-mesh20m" style="font-weight: normal; color: #333; line-height: 22px; padding: 10px;">'
          const props = features[0].properties
          const jushu = props['レーザ解析樹種'] || props['森林簿樹種1'] || '-'
          html.value += '<span style="font-size:16px; font-weight:bold; color:#228B22;">' + jushu + '</span>'
          if (props['森林簿樹種1'] && props['レーザ解析樹種'] && props['森林簿樹種1'] !== props['レーザ解析樹種']) html.value += '<span style="font-size:12px; color:#666;"> (' + props['森林簿樹種1'] + ')</span>'
          html.value += '<br>'
          if (props['DCHMに基づく樹冠高'] != null) html.value += '<span style="font-size:14px;">樹冠高: <b>' + Number(props['DCHMに基づく樹冠高']).toFixed(1) + 'm</b></span><br>'
          if (props['立木密度'] != null) html.value += '<span style="font-size:14px;">立木密度: <b>' + Number(props['立木密度']).toFixed(0) + '本/ha</b></span><br>'
          if (props['林齢'] != null) html.value += '<span style="font-size:14px;">林齢: <b>' + props['林齢'] + '年</b></span><br>'
          if (props['林種']) html.value += '<span style="font-size:13px;">林種: ' + props['林種'] + '</span><br>'
          if (props['平均標高'] != null) html.value += '<span style="font-size:13px;">平均標高: ' + Number(props['平均標高']).toFixed(0) + 'm</span><br>'
          if (props['計測年月日']) html.value += '<span style="font-size:11px; color:#999;">計測日: ' + props['計測年月日'] + '</span><br>'
          html.value += '</div>'
        }
        break
      }
      // 県別PMTiles（全国版に統合したため無効化）
      // case 'oh-fr-mesh20m-tochigi-fill':
      // case 'oh-fr-mesh20m-tochigi-line':
      // case 'oh-fr-mesh20m-hyogo-fill':
      // case 'oh-fr-mesh20m-hyogo-line':
      // case 'oh-fr-mesh20m-kochi-fill':
      // case 'oh-fr-mesh20m-kochi-line':
      // case 'oh-fr-mesh20m-tottori-fill':
      // case 'oh-fr-mesh20m-tottori-line': {
      //   ...
      //   break
      // }
      case 'oh-choju-polygon':
      case 'oh-choju-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-choju-polygon'] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-choju-polygon'] })
        }
        if (features.length === 0) return
        // 保護区分コードから保護区分名に変換
        const hogokubunName = {
          '1': '鳥獣保護区',
          '2': '特別保護地区',
          '3': '休猟区',
          '4': '特例休猟区',
        }
        // 指定機関コードから指定機関名に変換
        const shiteikikanName = {
          '1': '国指定',
          '2': '県指定',
        }
        // 都道府県コードから都道府県名に変換
        const prefNames = {
          1: '北海道', 2: '青森県', 3: '岩手県', 4: '宮城県', 5: '秋田県',
          6: '山形県', 7: '福島県', 8: '茨城県', 9: '栃木県', 10: '群馬県',
          11: '埼玉県', 12: '千葉県', 13: '東京都', 14: '神奈川県', 15: '新潟県',
          16: '富山県', 17: '石川県', 18: '福井県', 19: '山梨県', 20: '長野県',
          21: '岐阜県', 22: '静岡県', 23: '愛知県', 24: '三重県', 25: '滋賀県',
          26: '京都府', 27: '大阪府', 28: '兵庫県', 29: '奈良県', 30: '和歌山県',
          31: '鳥取県', 32: '島根県', 33: '岡山県', 34: '広島県', 35: '山口県',
          36: '徳島県', 37: '香川県', 38: '愛媛県', 39: '高知県', 40: '福岡県',
          41: '佐賀県', 42: '長崎県', 43: '熊本県', 44: '大分県', 45: '宮崎県',
          46: '鹿児島県', 47: '沖縄県',
        }
        // レイヤーラベルは1回だけ表示
        if (html.value.indexOf('choju') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="choju" style="width: 200px !important; font-weight: normal; color: #333; line-height: 25px;">'
          // 重複チェック用Set
          const shownKeys = new Set()
          // 全ての重なるポリゴンの情報を表示
          for (let i = 0; i < features.length; i++) {
            const props = features[i].properties
            // 重複チェック（名称 + 保護区分 + 都道府県 で一意とみなす）
            const key = `${props.A15_001 || ''}-${props.A15_004 || ''}-${props.A15_002 || ''}`
            if (shownKeys.has(key)) continue
            shownKeys.add(key)
            // 2つ目以降は区切り線を追加
            if (shownKeys.size > 1) {
              html.value += '<hr style="border: none; border-top: 1px dashed #ccc; margin: 8px 0;">'
            }
            const kubunName = hogokubunName[props.A15_004] || '不明'
            const kikanName = shiteikikanName[props.A15_003] || '-'
            const prefName = prefNames[parseInt(props.A15_002)] || '-'
            html.value += '<span style="font-size:18px; font-weight:bold; color:#FF8C00;">' + kubunName + '</span><br>'
            if (props.A15_001) {
              html.value += '<span style="font-size:14px;">名称＝' + props.A15_001 + '</span><br>'
            }
            html.value += '<span style="font-size:14px;">都道府県＝' + prefName + '</span><br>'
            html.value += '<span style="font-size:14px;">指定機関＝' + kikanName + '</span><br>'
          }
          html.value += '</div>'
        }
        break
      }
      case 'oh-kozui-polygon':
      case 'oh-kozui-3d': {
        // 3D版と2D版でクエリするレイヤーを切り替え
        const kozuiQueryLayer = layerId === 'oh-kozui-3d' ? 'oh-kozui-3d' : 'oh-kozui-polygon'
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [kozuiQueryLayer] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [kozuiQueryLayer] })
        }
        if (features.length === 0) return
        // 浸水深ランクから浸水深に変換
        const shinsuidepthName = {
          1: '0.5m未満',
          2: '0.5m〜3m未満',
          3: '3m〜5m未満',
          4: '5m〜10m未満',
          5: '10m〜20m未満',
          6: '20m以上',
        }
        // レイヤーラベルは1回だけ表示
        if (html.value.indexOf('kozui') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kozui" style="width: 220px !important; font-weight: normal; color: #333; line-height: 25px;">'
          // 最初の1つだけ表示
          const props = features[0].properties
          const depthName = shinsuidepthName[props.A31a_205] || '不明'
          html.value += '<span style="font-size:18px; font-weight:bold; color:#4169E1;">洪水浸水想定区域</span><br>'
          html.value += '<span style="font-size:14px;">河川名＝' + (props.A31a_202 || '-') + '</span><br>'
          html.value += '<span style="font-size:14px;">都道府県＝' + (props.A31a_204 || '-') + '</span><br>'
          html.value += '<span style="font-size:16px; font-weight:bold; color:#DC143C;">浸水深＝' + depthName + '</span><br>'
          html.value += '</div>'
        }
        break
      }
      case 'oh-teiichi-polygon':
      case 'oh-teiichi-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-teiichi-polygon'] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-teiichi-polygon'] })
        }
        if (features.length === 0) return
        // レイヤーラベルは1回だけ表示
        if (html.value.indexOf('teiichi') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="teiichi" style="padding: 10px;width: 220px !important; font-weight: normal; color: #333; line-height: 25px;">'
          // 重複チェック用Set
          const shownKeys = new Set()
          // 全ての重なるポリゴンの情報を表示（最大5件）
          for (let i = 0; i < Math.min(features.length, 5); i++) {
            const props = features[i].properties
            // 重複チェック（面積 + 浸水深で一意とみなす）
            const key = `${props.G08_001 || ''}-${props.G08_002 || ''}`
            if (shownKeys.has(key)) continue
            shownKeys.add(key)
            // 2つ目以降は区切り線を追加
            if (shownKeys.size > 1) {
              html.value += '<hr style="border: none; border-top: 1px dashed #ccc; margin: 8px 0;">'
            }
            html.value += '<span style="font-size:18px; font-weight:bold; color:#6B8E23;">低位地帯</span><br>'
            if (props.G08_001) {
              html.value += '<span style="font-size:14px;">面積＝' + props.G08_001.toFixed(2) + ' ha</span><br>'
            }
            if (props.G08_002) {
              html.value += '<span style="font-size:16px; font-weight:bold; color:#DC143C;">最大浸水深＝' + props.G08_002.toFixed(1) + ' m</span><br>'
            }
          }
          html.value += '</div>'
        }
        break
      }
      case 'oh-tadankai-polygon':
      case 'oh-tadankai-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-tadankai-polygon'] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-tadankai-polygon'] })
        }
        if (features.length === 0) return
        // 浸水深ランクから浸水深に変換
        const tadankaiDepthName = {
          1: '0.5m未満',
          2: '0.5m〜3m未満',
          3: '3m〜5m未満',
          4: '5m〜10m未満',
          5: '10m〜20m未満',
          6: '20m以上',
        }
        // 降雨規模の表示名
        const rainfallScaleName = {
          10: '10年に1回程度',
          30: '30年に1回程度',
          50: '50年に1回程度',
          100: '100年に1回程度',
          150: '150年に1回程度',
          200: '200年に1回程度',
        }
        // レイヤーラベルは1回だけ表示
        if (html.value.indexOf('tadankai') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tadankai" style="width: 240px !important; font-weight: normal; color: #333; line-height: 25px;">'
          // 重複チェック用Set
          const shownKeys = new Set()
          // 全ての重なるポリゴンの情報を表示（最大5件）
          for (let i = 0; i < Math.min(features.length, 5); i++) {
            const props = features[i].properties
            // 重複チェック（水系コード + 浸水深ランク + 降雨規模で一意とみなす）
            const key = `${props.A53_001 || ''}-${props.A53_003 || ''}-${props.A53_005 || ''}`
            if (shownKeys.has(key)) continue
            shownKeys.add(key)
            // 2つ目以降は区切り線を追加
            if (shownKeys.size > 1) {
              html.value += '<hr style="border: none; border-top: 1px dashed #ccc; margin: 8px 0;">'
            }
            html.value += '<span style="font-size:18px; font-weight:bold; color:#4169E1;">多段階浸水想定</span><br>'
            if (props.A53_002) {
              html.value += '<span style="font-size:14px;">水系名＝' + props.A53_002 + '</span><br>'
            }
            if (props.A53_005) {
              const scaleName = rainfallScaleName[props.A53_005] || props.A53_005 + '年'
              html.value += '<span style="font-size:14px;">降雨規模＝' + scaleName + '</span><br>'
            }
            if (props.A53_003) {
              const depthName = tadankaiDepthName[props.A53_003] || '不明'
              html.value += '<span style="font-size:16px; font-weight:bold; color:#DC143C;">浸水深＝' + depthName + '</span><br>'
            }
          }
          html.value += '</div>'
        }
        break
      }
      case 'oh-gyosei-history-query':
      case 'oh-gyosei-history-polygon':
      case 'oh-gyosei-history-line': {
        // クエリ用レイヤー（フィルターなし）から全期間の地物を取得
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: ['oh-gyosei-history-query'] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: ['oh-gyosei-history-query'] })
        }
        if (features.length === 0) return

        // === 右ドロワー版 ===
        // 重複除去してSTART順にソート
        const uniqueFeatures = []
        const seenKeys = new Set()
        for (const f of features) {
          const props = f.properties
          const key = `${props.CODE5 || ''}-${props.START || ''}-${props.END || ''}`
          if (!seenKeys.has(key)) {
            seenKeys.add(key)
            uniqueFeatures.push(props)
          }
        }
        // START日付でソート（新しい順 = 現在が上）
        uniqueFeatures.sort((a, b) => (b.START || '').localeCompare(a.START || ''))

        // スライダーで選択した年の市区町村名を取得
        const selectedYear = store.state.gyoseiHistoryYear?.[mapName] || 2025
        const selectedDate = `${selectedYear}-01-01`
        const selectedMunicipality = uniqueFeatures.find(p =>
          (p.START || '') <= selectedDate && (p.END || '9999-12-31') > selectedDate
        ) || uniqueFeatures[0]

        // 履歴データをJSON化（AI用）- 変遷ポイント（NAME/TYPEが変わる境目）を抽出
        const historyData = []
        let i = 0
        while (i < uniqueFeatures.length) {
          const current = uniqueFeatures[i]
          const currentKey = `${current.NAME || ''}|${current.TYPE || ''}`
          // 同じNAME+TYPEの連続範囲を探す
          let j = i
          while (j < uniqueFeatures.length) {
            const f = uniqueFeatures[j]
            const key = `${f.NAME || ''}|${f.TYPE || ''}`
            if (key !== currentKey) break
            j++
          }
          // グループの最初（最新）と最後（最古）を追加
          const first = uniqueFeatures[i]
          const last = uniqueFeatures[j - 1]
          historyData.push({
            name: first.NAME || '不明',
            type: first.TYPE || '?',
            start: last.START ? last.START.substring(0, 4) : '?', // 最古の開始年
            end: first.END === '2999-12-31' ? '現在' : (first.END ? first.END.substring(0, 4) : '?'), // 最新の終了年
            ken: first.KEN || '',
            gun: first.GUN || ''
          })
          i = j
        }

        // 複数画面モード（2画面・4画面）のときはポップアップ版を使用
        const isMultiScreenMode = store.state.map2Flg || store.state.is4ScreenMode

        if (!isMultiScreenMode) {
          // === 1画面モード: 右ドロワー版 ===
          // 既存のマーカーを削除
          if (store.state.gyoseiHistoryDrawer.marker) {
            store.state.gyoseiHistoryDrawer.marker.remove()
          }

          // クリック地点にマーカーを追加
          const markerEl = document.createElement('div')
          markerEl.style.cssText = 'width:16px; height:16px; background:#FF6600; border:2px solid #fff; border-radius:50%; box-shadow:0 2px 4px rgba(0,0,0,0.3);'
          const marker = new maplibregl.Marker({ element: markerEl })
            .setLngLat(coordinates)
            .addTo(map)

          // ドロワーを開く
          store.commit('setGyoseiHistoryDrawer', {
            open: true,
            mapName: mapName,
            marker: marker,
            data: {
              uniqueFeatures,
              selectedMunicipality,
              selectedYear,
              historyData,
              coordinates
            }
          })
          // ポップアップは表示しない
          return
        }

        // === 複数画面モード: ポップアップ版 ===
        // レイヤーラベルは1回だけ表示
        if (html.value.indexOf('gyosei-history') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="gyosei-history" style="width: 350px !important; font-weight: normal; color: #333; line-height: 22px;">'

          // 重複除去してSTART順にソート
          const uniqueFeatures = []
          const seenKeys = new Set()
          for (const f of features) {
            const props = f.properties
            const key = `${props.CODE5 || ''}-${props.START || ''}-${props.END || ''}`
            if (!seenKeys.has(key)) {
              seenKeys.add(key)
              uniqueFeatures.push(props)
            }
          }
          // START日付でソート（新しい順 = 現在が上）
          uniqueFeatures.sort((a, b) => (b.START || '').localeCompare(a.START || ''))

          // スライダーで選択した年の市区町村名を取得
          const selectedYear = store.state.gyoseiHistoryYear?.[mapName] || new Date().getFullYear()
          const selectedDate = `${selectedYear}-01-01`
          const selectedMunicipality = uniqueFeatures.find(p =>
            (p.START || '') <= selectedDate && (p.END || '9999-12-31') > selectedDate
          ) || uniqueFeatures[0]

          // TYPE による色分け
          const typeColor = {
            '市': '#E74C3C',
            '区': '#E67E22',
            '町': '#27AE60',
            '村': '#3498DB',
          }

          // 選択した年の市町村名をヘッダーに表示
          if (selectedMunicipality) {
            const color = typeColor[selectedMunicipality.TYPE] || '#333'
            html.value += '<span style="font-size:18px; font-weight:bold; color:' + color + ';">'
            html.value += (selectedMunicipality.NAME || '不明')
            if (selectedMunicipality.TYPE) {
              html.value += '（' + selectedMunicipality.TYPE + '）'
            }
            html.value += '</span>'
            html.value += '<span style="font-size:12px; color:#888; margin-left:8px;">' + selectedYear + '年時点</span><br>'
            if (selectedMunicipality.KEN) {
              let fullAddress = selectedMunicipality.KEN
              if (selectedMunicipality.GUN) fullAddress += ' ' + selectedMunicipality.GUN
              html.value += '<span style="font-size:12px; color:#666;">' + fullAddress + '</span><br>'
            }
          }

          // 履歴表示
          html.value += '<div style="margin-top:8px; padding-top:8px; border-top:1px solid #ddd;">'
          html.value += '<span style="font-size:12px; font-weight:bold; color:#666;">📜 この地点の履歴</span><br>'
          html.value += '<span style="font-size:9px; color:#999; line-height:1.4;">※東北〜四国・福岡・佐賀・長崎: 1947/5/3〜<br/>※北海道・熊本・大分・宮崎・鹿児島・沖縄: 1970/4/1〜</span><br>'

          const gyoseiHistoryItemClass = 'gyosei-history-item-' + Date.now()
          for (let i = 0; i < Math.min(uniqueFeatures.length, 10); i++) {
            const props = uniqueFeatures[i]
            const color = typeColor[props.TYPE] || '#666'
            const startYear = props.START ? props.START.substring(0, 4) : '?'
            const endYear = props.END === '2999-12-31' ? '現在' : (props.END ? props.END.substring(0, 4) : '?')
            const isCurrent = props.END === '2999-12-31'

            html.value += '<div class="' + gyoseiHistoryItemClass + '" data-index="' + i + '" data-code5="' + (props.CODE5 || '') + '" data-start="' + (props.START || '') + '" data-end="' + (props.END || '') + '" style="margin:2px 0; padding:2px 4px; background:' + (isCurrent ? 'rgba(0,100,200,0.1)' : 'transparent') + '; border-radius:3px; cursor:pointer; transition: all 0.2s; position:relative;">'
            html.value += '<span class="pin-icon" style="display:none; position:absolute; right:4px; top:50%; transform:translateY(-50%); font-size:12px;">📌</span>'
            html.value += '<span style="font-size:11px; color:#888; display:inline-block; width:75px;">' + startYear + '〜' + endYear + '</span>'
            html.value += '<span style="font-size:12px; color:' + color + '; font-weight:' + (isCurrent ? 'bold' : 'normal') + ';">'
            html.value += (props.NAME || '不明') + '（' + (props.TYPE || '?') + '）'
            html.value += '</span>'
            html.value += '</div>'
          }

          if (uniqueFeatures.length > 10) {
            html.value += '<span style="font-size:11px; color:#999;">...他 ' + (uniqueFeatures.length - 10) + ' 件</span>'
          }

          // Wikipediaリンク（選択した年の市区町村名で検索）
          if (selectedMunicipality && selectedMunicipality.NAME) {
            const wikiUrl = 'https://ja.wikipedia.org/wiki/' + encodeURIComponent(selectedMunicipality.NAME)
            html.value += '<div style="margin-top:8px; padding-top:6px; border-top:1px solid #ddd; text-align:center;">'
            html.value += '<a href="' + wikiUrl + '" target="_blank" style="font-size:12px; color:#3366cc;">📖 ' + selectedMunicipality.NAME + ' - Wikipedia</a>'
            html.value += '</div>'
          }

          // AIで解説ボタン
          const gyoseiAiBtnId = 'gyosei-ai-btn-' + Date.now()
          const gyoseiAiResultId = 'gyosei-ai-result-' + Date.now()
          // 履歴データをJSON化 - 変遷ポイント（NAME/TYPEが変わる境目）を抽出
          const historyData = []
          let hi = 0
          while (hi < uniqueFeatures.length) {
            const current = uniqueFeatures[hi]
            const currentKey = `${current.NAME || ''}|${current.TYPE || ''}`
            let hj = hi
            while (hj < uniqueFeatures.length) {
              const f = uniqueFeatures[hj]
              const key = `${f.NAME || ''}|${f.TYPE || ''}`
              if (key !== currentKey) break
              hj++
            }
            const first = uniqueFeatures[hi]
            const last = uniqueFeatures[hj - 1]
            historyData.push({
              name: first.NAME || '不明',
              type: first.TYPE || '?',
              start: last.START ? last.START.substring(0, 4) : '?',
              end: first.END === '2999-12-31' ? '現在' : (first.END ? first.END.substring(0, 4) : '?'),
              ken: first.KEN || '',
              gun: first.GUN || ''
            })
            hi = hj
          }
          html.value += '<div style="margin-top:8px; text-align:center;">'
          html.value += '<button id="' + gyoseiAiBtnId + '" style="padding: 6px 16px; background: #1976D2; color: white; border: none; border-radius: 4px; font-size: 13px; cursor: pointer;">🤖 AIで合併を解説</button>'
          html.value += '<div id="' + gyoseiAiResultId + '" style="display: none; margin-top: 8px; padding: 10px; background: #f0f4ff; border-radius: 8px; font-size: 13px; line-height: 1.6; text-align: left;"></div>'
          html.value += '</div>'

          html.value += '</div>'
          html.value += '</div>'

          // AIボタンのイベントリスナー
          setTimeout(() => {
            const aiBtn = document.getElementById(gyoseiAiBtnId)
            if (aiBtn) {
              // データ属性として保存
              aiBtn.dataset.history = JSON.stringify(historyData)
              aiBtn.dataset.currentName = selectedMunicipality?.NAME || ''
              aiBtn.dataset.currentKen = selectedMunicipality?.KEN || ''
              aiBtn.addEventListener('click', async () => {
                // トラッキング
                const tracker = getUsageTracker(undefined, store)
                if (tracker) tracker.trackEvent('button', 'click', 'gyosei-history-ai', { component: 'popup' })
                const resultDiv = document.getElementById(gyoseiAiResultId)
                if (!resultDiv) return
                // ボタンを無効化
                aiBtn.disabled = true
                aiBtn.textContent = '🤖 考え中...'
                resultDiv.style.display = 'block'
                resultDiv.innerHTML = '<span style="color: #667eea;"><span style="display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(102,126,234,0.3); border-top-color: #667eea; border-radius: 50%; animation: gyosei-spin 0.8s linear infinite; vertical-align: middle; margin-right: 6px;"></span>AIが合併の歴史を調べています...</span>'
                // スピナーアニメーション用のスタイルを追加
                if (!document.getElementById('gyosei-spin-style')) {
                  const style = document.createElement('style')
                  style.id = 'gyosei-spin-style'
                  style.textContent = '@keyframes gyosei-spin { to { transform: rotate(360deg); } }'
                  document.head.appendChild(style)
                }
                try {
                  const response = await axios.post(`${PHP_API_BASE}/claudeGyoseiHistory.php`, {
                    history: JSON.parse(aiBtn.dataset.history),
                    currentName: aiBtn.dataset.currentName,
                    currentKen: aiBtn.dataset.currentKen
                  })
                  if (response.data.success && response.data.explanation) {
                    resultDiv.innerHTML = '<span style="color: #4a148c; font-weight: bold;">📚 解説</span><br>' + DOMPurify.sanitize(response.data.explanation)
                  } else {
                    resultDiv.innerHTML = '<span style="color: #c62828;">取得できませんでした</span>'
                  }
                } catch (error) {
                  console.error('AI解説取得エラー:', error)
                  resultDiv.innerHTML = '<span style="color: #c62828;">エラーが発生しました</span>'
                }
                aiBtn.textContent = '🤖 AIで合併を解説'
                aiBtn.disabled = false
              })
            }

            // 履歴リストのホバー・クリックでポリゴンをハイライト
            const historyItems = document.querySelectorAll('.' + gyoseiHistoryItemClass)
            let pinnedItem = null // ピン留めされたアイテム

            historyItems.forEach(item => {
              // ホバー
              item.addEventListener('mouseenter', () => {
                // ピン留めされてる場合はホバーハイライトを出さない
                if (item === pinnedItem) return

                const code5 = item.dataset.code5
                const start = item.dataset.start
                const end = item.dataset.end
                if (!code5 || !start) return

                // ホバー用ハイライトレイヤーがなければ作成（オレンジ）
                if (!map.getLayer('oh-gyosei-history-highlight')) {
                  map.addLayer({
                    id: 'oh-gyosei-history-highlight',
                    type: 'line',
                    source: 'gyosei-history-source',
                    'source-layer': 'polygon',
                    paint: {
                      'line-color': '#FF6600',
                      'line-width': 4,
                      'line-opacity': 0.9
                    },
                    filter: ['==', ['get', 'CODE5'], '___NONE___']
                  })
                }

                // フィルターを設定してハイライト
                map.setFilter('oh-gyosei-history-highlight', [
                  'all',
                  ['==', ['get', 'CODE5'], code5],
                  ['==', ['get', 'START'], start],
                  ['==', ['get', 'END'], end]
                ])

                // 履歴アイテムのスタイル変更
                item.style.background = 'rgba(255, 102, 0, 0.2)'
              })

              item.addEventListener('mouseleave', () => {
                // ピン留めされてる場合はスタイルを戻さない
                if (item === pinnedItem) return

                // ホバーハイライトを解除
                if (map.getLayer('oh-gyosei-history-highlight')) {
                  map.setFilter('oh-gyosei-history-highlight', ['==', ['get', 'CODE5'], '___NONE___'])
                }
                // 履歴アイテムのスタイルを戻す
                const isCurrent = item.dataset.end === '2999-12-31'
                item.style.background = isCurrent ? 'rgba(0,100,200,0.1)' : 'transparent'
                item.style.border = 'none'
              })

              // クリックでピン留め
              item.addEventListener('click', () => {
                const code5 = item.dataset.code5
                const start = item.dataset.start
                const end = item.dataset.end
                if (!code5 || !start) return

                // 同じアイテムをクリックしたらピン解除
                if (pinnedItem === item) {
                  pinnedItem = null
                  // ピン留めハイライトを解除
                  if (map.getLayer('oh-gyosei-history-pinned')) {
                    map.setFilter('oh-gyosei-history-pinned', ['==', ['get', 'CODE5'], '___NONE___'])
                  }
                  // スタイルを戻す
                  const isCurrent = item.dataset.end === '2999-12-31'
                  item.style.background = isCurrent ? 'rgba(0,100,200,0.1)' : 'transparent'
                  item.style.border = 'none'
                  item.querySelector('.pin-icon').style.display = 'none'
                  return
                }

                // 以前のピン留めを解除
                if (pinnedItem) {
                  const prevCurrent = pinnedItem.dataset.end === '2999-12-31'
                  pinnedItem.style.background = prevCurrent ? 'rgba(0,100,200,0.1)' : 'transparent'
                  pinnedItem.style.border = 'none'
                  pinnedItem.querySelector('.pin-icon').style.display = 'none'
                }

                pinnedItem = item

                // ピン留め用ハイライトレイヤーがなければ作成（青・太め）
                if (!map.getLayer('oh-gyosei-history-pinned')) {
                  map.addLayer({
                    id: 'oh-gyosei-history-pinned',
                    type: 'line',
                    source: 'gyosei-history-source',
                    'source-layer': 'polygon',
                    paint: {
                      'line-color': '#1976D2',
                      'line-width': 6,
                      'line-opacity': 1
                    },
                    filter: ['==', ['get', 'CODE5'], '___NONE___']
                  })
                }

                // フィルターを設定してピン留めハイライト
                map.setFilter('oh-gyosei-history-pinned', [
                  'all',
                  ['==', ['get', 'CODE5'], code5],
                  ['==', ['get', 'START'], start],
                  ['==', ['get', 'END'], end]
                ])

                // スタイルを変更
                item.style.background = 'rgba(25, 118, 210, 0.2)'
                item.style.border = '2px solid #1976D2'
                item.querySelector('.pin-icon').style.display = 'inline'
              })
            })
          }, 100)
        }
        break
      }
      case 'oh-tochikukaku-fill': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        let html0 = ''
        if (html.value.indexOf('tochikukaku') === -1) {
          html.value +=
            '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html0 += '<div class="tochikukaku popup-body">'
          html0 += '<span style="font-size:16px;">' + props.DistType + '</span><br>'
          html0 += '<span style="font-size:16px;">' + props.Pref + '</span><br>'
          html0 += '<span style="font-size:16px;">' + props.Cityname + '</span><br>'
          html0 += '<div>'
          html.value += html0
        }
        break
      }

      // === 都市計画決定GISデータ 2024 ===

      // 用途地域(2024)
      case 'oh-youto2024-fill':
      case 'oh-youto2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('youto2024') === -1) {
          const youtoMap = {
            1: '第1種低層住居専用地域',
            2: '第2種低層住居専用地域',
            3: '第1種中高層住居専用地域',
            4: '第2種中高層住居専用地域',
            5: '第1種住居地域',
            6: '第2種住居地域',
            7: '準住居地域',
            8: '田園住居地域',
            9: '近隣商業地域',
            10: '商業地域',
            11: '準工業地域',
            12: '工業地域',
            13: '工業専用地域',
          }
          const youtoName = youtoMap[props.YoutoCode] || '用途地域'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="youto2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + youtoName + '</span><br>'
          if (props.Kenpei) html.value += '<span style="font-size:14px;">建蔽率：' + props.Kenpei + '%</span><br>'
          if (props.Youseki) html.value += '<span style="font-size:14px;">容積率：' + props.Youseki + '%</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 都市計画区域(2024)
      case 'oh-tokei2024-fill':
      case 'oh-tokei2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tokei2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tokei2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">都市計画区域</span><br>'
          if (props.CityPlnMasterPln) html.value += '<span style="font-size:14px;">都市計画マスタープラン：' + props.CityPlnMasterPln + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 区域区分(2024)
      case 'oh-senbiki2024-fill':
      case 'oh-senbiki2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('senbiki2024') === -1) {
          const areaMap = {
            1: '市街化区域',
            2: '市街化調整区域',
            3: '非線引き区域',
          }
          const areaName = areaMap[props.AreaCode] || '区域区分'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="senbiki2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + areaName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 立地適正化計画(2024)
      case 'oh-ritteki2024-fill':
      case 'oh-ritteki2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('ritteki2024') === -1) {
          const areaMap = {
            1: '都市機能誘導区域',
            2: '居住誘導区域',
          }
          const areaName = areaMap[props.AreaCode] || '立地適正化計画区域'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="ritteki2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + areaName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 都市計画道路(2024)
      case 'oh-douro2024-fill':
      case 'oh-douro2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('douro2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="douro2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">都市計画道路</span><br>'
          if (props.DouroName) html.value += '<span style="font-size:14px;">' + props.DouroName + '</span><br>'
          if (props.Width) html.value += '<span style="font-size:14px;">幅員：' + props.Width + 'm</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 公園・緑地(2024)
      case 'oh-kouen2024-fill':
      case 'oh-kouen2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kouen2024') === -1) {
          const parkMap = {
            1: '街区公園',
            2: '近隣公園',
            3: '地区公園',
            4: '総合公園',
            5: '運動公園',
            6: '広域公園',
            7: 'レクリエーション都市',
            8: '国営公園',
            9: '特殊公園',
            10: '緩衝緑地',
            11: '都市緑地',
            12: '緑道',
            13: '墓園',
          }
          const parkName = parkMap[props.ParkCode] || '公園・緑地'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kouen2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + parkName + '</span><br>'
          if (props.ParkName) html.value += '<span style="font-size:14px;">' + props.ParkName + '</span><br>'
          if (props.Area) html.value += '<span style="font-size:14px;">面積：' + props.Area + 'ha</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 防火地域(2024)
      case 'oh-bouka2024-fill':
      case 'oh-bouka2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('bouka2024') === -1) {
          const boukaMap = {
            1: '防火地域',
            2: '準防火地域',
          }
          const boukaName = boukaMap[props.BoukaCode] || '防火地域'
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="bouka2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">' + boukaName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 地区計画(2024)
      case 'oh-chikukei2024-fill':
      case 'oh-chikukei2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('chikukei2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="chikukei2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">地区計画</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 土地区画整理事業(2024)
      case 'oh-tochiku2024-fill':
      case 'oh-tochiku2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tochiku2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tochiku2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">土地区画整理事業</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.DistType) html.value += '<span style="font-size:14px;">' + props.DistType + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 特別用途地区(2024)
      case 'oh-tkbt2024-fill':
      case 'oh-tkbt2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tkbt2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tkbt2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">特別用途地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 高度利用地区(2024)
      case 'oh-koudori2024-fill':
      case 'oh-koudori2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('koudori2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="koudori2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">高度利用地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 高度地区(2024)
      case 'oh-koudoti2024-fill':
      case 'oh-koudoti2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('koudoti2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="koudoti2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">高度地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.MaxHeight) html.value += '<span style="font-size:14px;">最高高さ：' + props.MaxHeight + 'm</span><br>'
          if (props.MinHeight) html.value += '<span style="font-size:14px;">最低高さ：' + props.MinHeight + 'm</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 風致地区(2024)
      case 'oh-fuuchichiku2024-fill':
      case 'oh-fuuchichiku2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('fuuchichiku2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="fuuchichiku2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">風致地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 特定用途制限地域(2024)
      case 'oh-tokuteiyouto2024-fill':
      case 'oh-tokuteiyouto2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tokuteiyouto2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tokuteiyouto2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">特定用途制限地域</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 準都市計画区域(2024)
      case 'oh-jyuntoshi2024-fill':
      case 'oh-jyuntoshi2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('jyuntoshi2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="jyuntoshi2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">準都市計画区域</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 都市再生特別地区(2024)
      case 'oh-toshisaisei2024-fill':
      case 'oh-toshisaisei2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('toshisaisei2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="toshisaisei2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">都市再生特別地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 特別緑地保全地区(2024)
      case 'oh-tokuryoku2024-fill':
      case 'oh-tokuryoku2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tokuryoku2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tokuryoku2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">特別緑地保全地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 歴史的風土保存地区(2024)
      case 'oh-rekifuu2024-fill':
      case 'oh-rekifuu2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('rekifuu2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="rekifuu2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">歴史的風土保存地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 特定用途誘導地区(2024)
      case 'oh-tokuteiyuudou2024-fill':
      case 'oh-tokuteiyuudou2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tokuteiyuudou2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tokuteiyuudou2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">特定用途誘導地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 航空機騒音障害防止地区(2024)
      case 'oh-soubou2024-fill':
      case 'oh-soubou2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('soubou2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="soubou2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">航空機騒音障害防止地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 特定防災街区整備地区(2024)
      case 'oh-tokuteibouka2024-fill':
      case 'oh-tokuteibouka2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tokuteibouka2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tokuteibouka2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">特定防災街区整備地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 特例容積率適用地区(2024)
      case 'oh-tokureiyouseki2024-fill':
      case 'oh-tokureiyouseki2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('tokureiyouseki2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="tokureiyouseki2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">特例容積率適用地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 緑化地域(2024)
      case 'oh-ryokukachiiki2024-fill':
      case 'oh-ryokukachiiki2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('ryokukachiiki2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="ryokukachiiki2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">緑化地域</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 居住調整地域(2024)
      case 'oh-kyojyuchosei2024-fill':
      case 'oh-kyojyuchosei2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kyojyuchosei2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kyojyuchosei2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">居住調整地域</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 高層住居誘導地区(2024)
      case 'oh-kousoujyukyo2024-fill':
      case 'oh-kousoujyukyo2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('kousoujyukyo2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="kousoujyukyo2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">高層住居誘導地区</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // 復興再生拠点(2024)
      case 'oh-fukkousaiseikyoten2024-fill':
      case 'oh-fukkousaiseikyoten2024-line': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        if (html.value.indexOf('fukkousaiseikyoten2024') === -1) {
          html.value += '<div class="layer-label-div">' + getLabelByLayerId(layerId, store.state.selectedLayers) + '</div>'
          html.value += '<div class="fukkousaiseikyoten2024 popup-base">'
          html.value += '<span style="font-size:16px; font-weight:bold;">復興再生拠点</span><br>'
          if (props.DistName) html.value += '<span style="font-size:14px;">' + props.DistName + '</span><br>'
          if (props.Pref) html.value += '<span style="font-size:12px; color:#666;">' + props.Pref + (props.Cityname || '') + '</span><br>'
          html.value += '</div>'
        }
        break
      }

      // === 都市計画決定GISデータ 2024 ここまで ===

      case 'oh-homusyo-2025-ninizahyo-simbol':
      case 'oh-homusyo-2025-ninizahyo-circle': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        const zips = JSON.parse(props.ZIPファイル名)
        const lonLat = JSON.stringify([props.経度, props.緯度])
        isBreak = true
        let zipBtnsHtml = ''
        zips.forEach((zipName) => {
          zipBtnsHtml +=
            '<button lon-lat = "' +
            lonLat +
            '" style="margin-bottom: 5px; height: 30px; font-size: medium; width: 100%;" class="ninnzahyo-zip pyramid-btn">' +
            zipName +
            '</button>'
        })
        let html0 = ''
        if (html.value.indexOf('ninizahyo') === -1) {
          html.value += '<div class="layer-label-div">任意座標追加</div>'
          html0 += '<div class="ninizahyo popup-body" style="width: 200px;">'
          html0 += '編集するには右のペンアイコンをクリックしてドロー状態にしてください。'
          html0 += zipBtnsHtml
          html0 +=
            '<button style="margin-bottom: 5px; height: 30px; font-size: medium; width: 100%;" class="ninnzahyo-zip-remove pyramid-btn">削　除</button>'
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-terrain22-41-polygon': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        /**
         * GCLUSTER15 と Sinks から 概要 と 色 を返す
         * @param {number|string} gcluster15  1..15 の数値（文字でも可）
         * @param {number|string|null|undefined} sinks  0 or 1（未定義OK）
         * @returns {{summary:string, color:string, subtype?:'11a'|'11b'}}
         */
        function getTerrainInfo(gcluster15, sinks) {
          const g = Number(gcluster15)
          const s = sinks == null ? null : Number(sinks)
          switch (g) {
            case 1:
              return {
                summary: '基盤岩山地（上位）',
                color: s === 1 ? 'rgb(154,255,255)' : 'rgb(251,216,15)',
              }
            case 2:
              return { summary: '基盤岩山地（下位）', color: 'rgb(112,79,41)' }
            case 3:
              return { summary: '丘陵性山地（上位）', color: 'rgb(172,82,50)' }
            case 4:
              return { summary: '丘陵性山地（下位）', color: 'rgb(243,162,243)' }
            case 5:
              return { summary: '山地の谷', color: 'rgb(248,3,204)' }
            case 6:
              return {
                summary: '高地の長大斜面（尾根谷（凹凸）多）（火山斜面、地すべり滑落崖等）',
                color: s === 1 ? 'rgb(194,251,71)' : 'rgb(249,180,4)',
              }
            case 7:
              return {
                summary: '高地の長大斜面（尾根谷（凹凸）少）（火山斜面、地すべり土塊等）',
                color: s === 1 ? 'rgb(30,218,74)' : 'rgb(223,134,31)',
              }
            case 8:
              return {
                summary: '丘陵化した段丘（上位）',
                color: s === 1 ? 'rgb(155,141,169)' : 'rgb(125,162,143)',
              }
            case 9:
              return {
                summary: '丘陵化した段丘（下位）',
                color: s === 1 ? 'rgb(122,236,40)' : 'rgb(251,147,0)',
              }
            case 10:
              return { summary: '開析谷（火山地の谷、段丘崖等）', color: 'rgb(40,135,108)' }
            case 11: {
              const isA = s === 0 // 0→11a, 1→11b
              return {
                summary: isA
                  ? '低地の長大斜面（凸部多）（段丘、開析扇状地等）'
                  : '低地の長大斜面（凸部少）（沖積扇状地、砂州等）',
                color: s === 1 ? 'rgb(31,195,105)' : 'rgb(20,172,132)',
                subtype: isA ? '11a' : '11b',
              }
            }
            case 12:
              return { summary: '平野（尾根谷（凹凸）多）（谷底平野等）', color: 'rgb(251,154,153)' }
            case 13:
              return { summary: '平野（微高地）（自然堤防、砂州等）', color: 'rgb(153,131,83)' }
            case 14:
              return { summary: '平野（氾濫平野等）', color: 'rgb(145,150,185)' }
            case 15:
              return {
                summary: '水面高に近い低地（湿地、湖沼跡等）',
                color: s === 1 ? 'rgb(0,183,255)' : 'rgb(255,253,120)',
              }
            default:
              return { summary: '不明（該当なし）', color: 'rgb(200,200,200)' }
          }
        }

        const { summary, color, subtype } = getTerrainInfo(props.GCLUSTER15, props.Sinks)

        let html0 = ''

        if (html.value.indexOf('oh-terrain22-41') === -1) {
          html.value +=
            '<div style="background: ' +
            color +
            '" class="layer-label-div">' +
            getLabelByLayerId(layerId, store.state.selectedLayers) +
            '</div>'
          html0 +=
            '<div class="oh-terrain22-41 popup-body" style="width: 200px;">'
          html0 += summary
          html0 += '<div>'
          html.value += html0
        }
        break
      }
      case 'oh-toroku-point':
      case 'oh-toroku-point-label': {
        let features = map.queryRenderedFeatures(map.project(coordinates), { layers: [layerId] })
        if (features.length === 0) {
          features = map.queryRenderedFeatures(map.project(e.lngLat), { layers: [layerId] })
        }
        if (features.length === 0) return
        props = features[0].properties
        const header = store.state.sokuiHeader
        const row = JSON.parse(props.oh3_csv2_row)
        const rec = Object.fromEntries(header.map((k, i) => [k, i < row.length ? row[i] : null]))
        let html0 = ''
        Object.keys(rec).forEach(function (key) {
          if (key !== 'タイプ' && key !== 'パス') {
            html0 += key + '=' + rec[key] + '<br>'
          }
        })
        const imgHtml =
          rec.パス !== 'null' ? `<a href="${rec.パス}" target="_blank"><img height="200" src="${rec.パス}"></a>` : ''
        if (html.value.indexOf('zahyo') === -1) {
          html.value += `<div class="layer-label-div zahyo">座標等</div>`
          html.value += `
                        <div class="zahyo-wrap" style="display:flex; gap:12px; align-items:flex-start;">
                          <div class="zahyo-text" style="flex:1 1 auto; min-width:0;">
                            ${html0}
                          </div>
                          ${imgHtml ? `<div class="zahyo-image" style="flex:0 0 auto;">${imgHtml}</div>` : ''}
                        </div>`
        }
        break
      }
      // case 'arrows-endpoint-label-layer':
      case 'click-circle-layer':
      case 'click-circle-symbol-layer':
      case 'click-circle-line-layer':
      case 'click-circle-keiko-line-layer':
      case 'click-circle-memo-mark-layer':
      case 'click-circle-label-layer':
      case 'click-circle-polygon-symbol-area-layer':
      case 'click-circle-polygon-symbol-layer':
      case 'click-circle-layer-ex':
      case 'segment-label-layer': {
        // 標高図作成モード中は既存ドロー地物の処理をスキップ
        if (store.state.elevationDrawMode) {
          return
        }

        html.value = ''
        isBreak = true
        coordinates = store.state.coordinates

        let id = store.state.id
        let features
        if (store.state.clickCircleGeojsonText) {
          try {
            const parsed = JSON.parse(store.state.clickCircleGeojsonText)
            features = parsed.features
            if (!features) {
              console.error('No features in parsed GeoJSON')
              return
            }
          } catch (e) {
            console.error('Failed to parse clickCircleGeojsonText:', e)
            return
          }
        } else if (store.state.clickCircleGeojsonTextMyroom) {
          try {
            const parsed = JSON.parse(store.state.clickCircleGeojsonTextMyroom)
            features = parsed.features
            if (!features) {
              console.error('No features in parsed GeoJSON (myroom)')
              return
            }
            store.state.clickCircleGeojsonText = store.state.clickCircleGeojsonTextMyroom
          } catch (e) {
            console.error('Failed to parse clickCircleGeojsonTextMyroom:', e)
            return
          }
        } else {
          console.error('No GeoJSON text available')
          return
        }
        store.state.drawFeatureId = id
        // サムネイルマーカー常時表示OFFの場合、クリックで表示する
        if (!store.state.thumbnailAlwaysVisible) {
          const el = document.getElementById(`pic-marker-${id}`)
          if (el && el.style.display === 'none') {
            el.style.display = ''
          }
        }
        // ID比較は文字列で統一（サーバーからのIDは数値、ローカルは文字列の場合がある）
        const feature = features.find((feature) => String(feature.properties.id) === String(id))
        if (id === 'offlineBbox') {
          store.state.dialogForOffline2 = true
          store.state.featureForOfflineBbox = feature
          return
        }
        if (!feature) return

        // 円の場合の特別処理
        let targetFeature = feature

        // 円の中心Pointがクリックされた場合、対応するPolygonを探す
        if (feature.geometry && feature.geometry.type === 'Point' && feature.properties.isCircleCenter) {
          const polygonId = feature.properties.id.replace('-point', '')
          const circlePolygon = features.find(
            (f) => f && f.geometry && f.geometry.type === 'Polygon' && f.properties && f.properties.id === polygonId
          )
          if (circlePolygon) {
            targetFeature = circlePolygon
          }
        }

        // マップソースから最新のデータを取得してからFloatingWindowに渡す
        // 最新のGeoJSONを取得
        // 重要: src._dataは非同期更新のためstaleな可能性がある
        // store.state.clickCircleGeojsonTextはsetData()と同時に同期更新されるため、こちらを優先
        const map = store.state.map01

        // 円の場合は選択レイヤーをクリア（半径操作時の見た目の問題を回避）
        if (
          targetFeature.geometry &&
          targetFeature.geometry.type === 'Polygon' &&
          targetFeature.properties.radius !== undefined
        ) {
          clearSelectedFeature(map)
        }

        // まずstore.state.clickCircleGeojsonTextから取得（Undo後も正確）
        let freshGeojson = null
        if (store.state.clickCircleGeojsonText) {
          try {
            freshGeojson = JSON.parse(store.state.clickCircleGeojsonText)
          } catch (e) {
            console.warn('[popup] clickCircleGeojsonText parse error:', e)
          }
        }

        // フォールバック: storeにない場合はsrc._dataを使用
        if (!freshGeojson && map && map.getSource('click-circle-source')) {
          const src = map.getSource('click-circle-source')

          // MapLibre 5.13.0以降対応のGeoJSON取得
          if (src._data) {
            const rawData = src._data
            freshGeojson = rawData && rawData.geojson ? rawData.geojson : rawData
          } else if (typeof src?.getData === 'function') {
            freshGeojson = src.getData()
          } else if (src?._options && src._options.data) {
            freshGeojson = src._options.data
          }
        }

        if (freshGeojson && freshGeojson.features) {
          // ID比較は文字列で統一
          const targetIdStr = String(targetFeature.properties.id)
          const freshFeature = freshGeojson.features.find((f) => String(f.properties.id) === targetIdStr)
          if (freshFeature) {
            targetFeature = freshFeature
          }
        }

        // フリーメモポイントの場合はフリーメモを開く（FWは開かない）
        if (targetFeature.properties?.isFieldNoteMemo || targetFeature.properties?.hasMemo) {
          store.state.fieldNoteMemoPointId = targetFeature.properties.id
          store.state.isFieldNoteMemoActive = true
          closeAllPopups()
          return
        }

        // isDraw時のみFloatingWindowを開く（地物FW）
        if (store.state.isDraw) {
          // FloatingWindowを開く（円も含む全てのドロー地物）
          store.commit('setCurrentDrawFeature', targetFeature)
          store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: true })
          // 選択された地物を強調表示
          highlightSelectedFeature(map, targetFeature.properties.id)
        } else {
          // isDraw以外の時はFloatingWindowを閉じる
          store.commit('setFloatingVisible', { id: 'draw-floating-window', visible: false })
        }
        // DrawDrawerはポイントの場合のみ開く（テキストは除く）
        // PC時は常に開く、スマホ時はisDraw以外の時のみ開く（isDraw時は地物FWが開くため）
        const isPoint = targetFeature.geometry.type === 'Point' && !targetFeature.properties.isCircleCenter
        const isText = targetFeature.properties?.type === 'Text'
        if (isPoint && !isText) {
          store.state.drawFeature = targetFeature
          // PC時は常に開く、スマホ時はisDraw以外の時に開く
          if (!store.state.isSmall500 || !store.state.isDraw) {
            store.commit('setDrawDrawer', true)
          }
        } else {
          store.commit('setDrawDrawer', false)
        }
        closeAllPopups()
        return
      }
    }
    if (isBreak) {
      isBreak = false
      break
    } // 完全に抜ける
  }
  // 現在のマップのポップアップのみ閉じる（他のマップのポップアップは残す）
  closeAllPopups(mapName)

  // ポップアップ作成
  let rasterLayerIds = []
  const mapLayers = map.getStyle().layers
  mapLayers.forEach((layer) => {
    // レイヤーのtypeプロパティを取得
    const type = layer.type
    if (type === 'raster') {
      rasterLayerIds.push(layer.id)
    }
    // 本当は↓こっちが良い。昔のリンクの関係で↑を使用している。
    // if (layer.id.indexOf('rgb') !== -1) {
    //   rasterLayerIds.push(layer.id)
    // }
  })
  // ラスタレイヤのidからポップアップ表示に使用するURLを生成
  // ハイブリッドレイヤー: 高ズーム時はMVTポップアップに任せるのでラスターをスキップ
  const hybridRasterMvtSwitchZoom = {
    'oh-hyuganada-totatsu-raster': 16,
    'oh-hyuganada-tsunami-raster': 16,
    'oh-hyuganada-shindo-raster': 14,
    'oh-nantora-shindo-raster': 14,
    'oh-nantora-ekijyoka-raster': 14,
  }
  const currentZoom = map.getZoom()
  if (!store.state.isDraw) {
    rasterLayerIds.forEach((rasterLayerId) => {
      const RasterTileUrl = urlByLayerId(rasterLayerId)[0]
      const legend = urlByLayerId(rasterLayerId)[1]
      let z = urlByLayerId(rasterLayerId)[2]
      if (!RasterTileUrl || !legend) return // 凡例未定義のラスターはスキップ
      // ハイブリッドレイヤー: MVT表示ズーム以上ではラスターポップアップをスキップ
      const switchZoom = hybridRasterMvtSwitchZoom[rasterLayerId]
      if (switchZoom && currentZoom >= switchZoom) return
      const lng = e.lngLat.lng
      const lat = e.lngLat.lat
      // 土壌インベントリーは現在のマップズームを使用（低ズームでのRGB精度を保つ）
      const isDojo = rasterLayerId === 'oh-rgb-dojo-layer' || rasterLayerId === 'oh-blend-dojo-hillshade'
      if (isDojo) {
        z = Math.min(Math.floor(map.getZoom()), 15)
      }
      const getLegendFn = isDojo ? getLegendItemNearest : getLegendItem
      getLegendFn(legend, RasterTileUrl, lat, lng, z).then(function (v) {
        let res = isDojo ? v : (v ? v.title : '')
        // if (res === '') return
        if (res) {
          // 土壌インベントリーは茶色系ヘッダー、それ以外は赤
          const headerClass = isDojo ? 'layer-label-div-brown' : 'layer-label-div-red'
          html.value +=
            '<div class="' + headerClass + '">' +
            getLabelByLayerId(rasterLayerId, store.state.selectedLayers) +
            '</div>'
          switch (rasterLayerId) {
            case 'oh-rgb-kozui-keikaku-layer':
            case 'oh-rgb-kozui-saidai-layer':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 12px;">洪水によって想定される浸水深</span><br>' +
                '<span style="font-size: 24px;">' +
                res +
                '</span>' +
                '</div>'
              break
            case 'oh-rgb-tsunami-layer':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 12px;">津波によって想定される浸水深</span><br>' +
                '<span style="font-size: 24px;">' +
                res +
                '</span>' +
                '</div>'
              break
            case 'oh-rgb-shitchi-layer':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 16px;">' +
                res[0] +
                '</span><hr>' +
                '<span style="font-size: 12px;">' +
                res[1] +
                '</span>' +
                '</div>'
              break
            case 'oh-rgb-tochiriyo-raster-layer':
            case 'oh-blend-tochiriyo-hillshade':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 12px;">土地利用区分（100mメッシュ・H26）</span><br>' +
                '<span style="font-size: 24px;">' +
                res +
                '</span>' +
                '</div>'
              break
            case 'oh-rgb-dojo-layer':
            case 'oh-blend-dojo-hillshade':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 22px; padding: 10px;">' +
                '<span style="font-size: 14px; font-weight: bold;">' + (res.title || res) + '</span><br>' +
                '<span style="font-size: 12px; color: #666;">分類記号：' + (res.soilID || '') + '</span><hr style="margin: 4px 0;">' +
                '<span style="font-size: 12px;">' + (res.explain || '') + '</span>' +
                '</div>'
              break
            // --- 宮崎県防災ハイブリッドレイヤー ---
            case 'oh-hyuganada-totatsu-raster':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 12px;">津波浸水開始時間（地震発生後）</span><br>' +
                '<span style="font-size: 24px; color: red;">' +
                res +
                '</span>' +
                '</div>'
              break
            case 'oh-hyuganada-shindo-raster':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 12px;">日向灘地震 震度分布</span><br>' +
                '<span style="font-size: 24px; color: red;">' +
                res +
                '</span>' +
                '</div>'
              break
            case 'oh-nantora-shindo-raster':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 12px;">南海トラフ 震度分布</span><br>' +
                '<span style="font-size: 24px; color: red;">' +
                res +
                '</span>' +
                '</div>'
              break
            case 'oh-nantora-ekijyoka-raster':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 12px;">南海トラフ 液状化</span><br>' +
                '<span style="font-size: 24px; color: red;">' +
                res +
                '</span>' +
                '</div>'
              break
            case 'oh-hyuganada-tsunami-raster':
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 12px;">日向灘地震 津波浸水深</span><br>' +
                '<span style="font-size: 24px; color: red;">' +
                res +
                '</span>' +
                '</div>'
              break
            default:
              html.value +=
                '<div style="font-weight: normal; color: #333; line-height: 25px; padding: 10px;">' +
                '<span style="font-size: 16px;">' +
                res +
                '</span>' +
                '</div>'
          }
        }
        if (html.value) {
          try {
            createPopup(map, [lng, lat], html.value, mapName)
          } catch (e) {
          }
        }
      })
    })
  }

  // シームレス地質図ポップアップ（自然セクション + blend + 地理院タイル版）
  {
    const mapStyle = map.getStyle()
    const lng = e.lngLat.lng
    const lat = e.lngLat.lat
    const geologyResult = mapLayers.find((layer) => {
      if (layer.id === 'oh-rgb-seamless-layer' || layer.id === 'oh-blend-base-geology-layer' || layer.id === 'oh-blend-geology-hillshade' || layer.id === 'oh-blend-photo-geology' || layer.id === 'oh-blend-base-photo-3-layer') return true
      // 地理院タイル版シームレス地質図を検出（ソースURLで判定、blend://のエンコードURL含む）
      if (layer.type === 'raster' && layer.source) {
        const src = mapStyle.sources[layer.source]
        if (src && src.tiles && src.tiles.some(t => t.includes('gbank.gsj.jp/seamless/v2') || t.includes('gbank.gsj.jp%2Fseamless%2Fv2'))) return true
      }
      return false
    })
    if (geologyResult) {
      const point = lat + ',' + lng
      const url = 'https://gbank.gsj.jp/seamless/v2/api/1.2/legend.json'
      axios
        .get(url, {
          params: {
            point: point,
          },
        })
        .then(function (response) {
          const d = response.data
          if (d.symbol) {
            if (html.value.indexOf('geology-card') === -1) {
              // ラベル取得（地理院タイル版はgetLabelByLayerIdで取得できない場合がある）
              const labelId = geologyResult.id === 'oh-rgb-seamless-layer' ? 'oh-rgb-seamless-layer'
                : (geologyResult.id === 'oh-blend-photo-geology' || geologyResult.id === 'oh-blend-base-photo-3-layer') ? 'oh-blend-photo-geology'
                : 'oh-blend-geology-hillshade'
              const labelText = getLabelByLayerId(labelId, store.state.selectedLayers) || getLabelByLayerId(geologyResult.id, store.state.selectedLayers) || 'シームレス地質図'
              // 地質色をアクセントに使用
              const cr = d.r, cg = d.g, cb = d.b
              const rgb = `rgb(${cr},${cg},${cb})`
              const lum = (0.299 * cr + 0.587 * cg + 0.114 * cb) / 255
              const tc = lum > 0.55 ? '#333' : '#fff'
              html.value +=
                '<div class="layer-label-div">' + labelText + '</div>' +
                '<div class="geology-card" style="min-width:220px;">' +
                '<div style="background:' + rgb + ';padding:7px 12px;display:flex;align-items:center;justify-content:space-between;gap:8px;">' +
                '<span style="font-size:13px;font-weight:600;color:' + tc + ';">' + d.group_ja + '</span>' +
                '<span style="font-size:9px;color:' + tc + ';opacity:0.6;font-family:monospace;">' + d.symbol + '</span>' +
                '</div>' +
                '<div style="padding:10px 12px;font-size:12px;color:#333;line-height:1.5;">' +
                '<div style="margin-bottom:6px;">' +
                '<span style="color:#999;font-size:10px;letter-spacing:0.5px;">形成時代</span><br>' +
                d.formationAge_ja +
                '</div>' +
                '<div>' +
                '<span style="color:#999;font-size:10px;letter-spacing:0.5px;">岩相</span><br>' +
                d.lithology_ja +
                '</div>' +
                '</div>' +
                '</div>'
              createPopup(map, [lng, lat], html.value, mapName)
            }
          }
        })
    }
  }

  // ジオランドぎふ WMS GetFeatureInfo ポップアップ
  // 断層・活断層はWMSサーバー側でLayerNotQueryable（クエリ不可）
  const gifuWmsLayers = [
    { layerId: 'oh-wms-gifu', wmsLayer: 'gifu_detail', label: '詳細地質図' },
  ]
  const activeGifuLayers = gifuWmsLayers.filter(l => map.getLayer(l.layerId))
  if (activeGifuLayers.length > 0 && !store.state.isDraw) {
    const lng = e.lngLat.lng
    const lat = e.lngLat.lat
    // クリック座標からBBOXとピクセル位置を計算
    const zoom = map.getZoom()
    const tileSize = 256
    // クリック地点を中心に小さなBBOX（256x256相当）を生成
    const res = (40075016.686 * 2) / (tileSize * Math.pow(2, zoom)) // 1ピクセルあたりのメートル
    const halfWidth = res * tileSize / 2
    // EPSG:3857座標に変換
    const mx = lng * 20037508.34 / 180
    const my = Math.log(Math.tan((90 + lat) * Math.PI / 360)) / (Math.PI / 180) * 20037508.34 / 180
    const bboxMinX = mx - halfWidth
    const bboxMinY = my - halfWidth
    const bboxMaxX = mx + halfWidth
    const bboxMaxY = my + halfWidth
    const bbox = bboxMinX + ',' + bboxMinY + ',' + bboxMaxX + ',' + bboxMaxY
    // ピクセル位置は中心（128, 128）
    const pixelI = 128
    const pixelJ = 128

    activeGifuLayers.forEach(({ layerId, wmsLayer, label }) => {
      const params = new URLSearchParams({
        SERVICE: 'WMS',
        REQUEST: 'GetFeatureInfo',
        VERSION: '1.3.0',
        LAYERS: wmsLayer,
        QUERY_LAYERS: wmsLayer,
        CRS: 'EPSG:3857',
        BBOX: bbox,
        WIDTH: String(tileSize),
        HEIGHT: String(tileSize),
        I: String(pixelI),
        J: String(pixelJ),
        INFO_FORMAT: 'text/html',
        FEATURE_COUNT: '5',
      })
      const url = '/oh3/php/wms-proxy.php?' + params.toString()

      fetch(url)
        .then(res => res.text())
        .then(text => {
          if (!text || text.trim() === '' || text.includes('missing') || text.includes('ServiceException')) return
          // 相対パス /geoland/ を絶対URLに書き換え（href=/geoland/, src="/geoland/" 両パターン対応）
          text = text.replace(/(href|src)=(["']?)\/geoland\//g, '$1=$2https://geo-gifu.org/geoland/')
          // リンクを新しいタブで開く
          text = text.replace(/<a /g, '<a target="_blank" rel="noopener" ')
          // HTMLレスポンスをパース
          let infoHtml = ''
          // サーバーがHTML tableを返す場合
          if (text.includes('<table') || text.includes('<TABLE')) {
            infoHtml = text
          } else if (text.includes('<body')) {
            // body内容を抽出
            const bodyMatch = text.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
            if (bodyMatch && bodyMatch[1].trim()) {
              infoHtml = bodyMatch[1].trim()
            }
          } else if (text.trim().startsWith('{')) {
            // JSONレスポンス
            try {
              const json = JSON.parse(text)
              const features = json.features || []
              if (features.length === 0) return
              features.forEach(f => {
                const p = f.properties || {}
                Object.entries(p).forEach(([k, v]) => {
                  if (v !== null && v !== '' && k !== 'geometry') {
                    infoHtml += '<span style="font-size: 13px;"><b>' + k + ':</b> ' + v + '</span><br>'
                  }
                })
              })
            } catch (e) {
              console.warn('[popup] ジオランドぎふ JSON parse error', e)
              return
            }
          } else {
            // プレーンテキスト
            if (text.trim().length < 5) return // 空に近い応答は無視
            infoHtml = '<pre style="font-size: 12px; white-space: pre-wrap; margin: 0;">' + text + '</pre>'
          }

          if (!infoHtml) return
          if (html.value.indexOf('geoland-gifu-info') === -1 || !html.value.includes(wmsLayer)) {
            const displayLabel = getLabelByLayerId(layerId, store.state.selectedLayers) || ('ジオランドぎふ ' + label)
            html.value +=
              '<div class="layer-label-div">' + displayLabel + '</div>' +
              '<div class="geoland-gifu-info popup-body" style="max-width: 360px; padding: 20px;">' +
              '<style>.geoland-gifu-info table { border-collapse: collapse; width: 100%; font-size: 13px; }' +
              '.geoland-gifu-info th, .geoland-gifu-info td { border: 1px solid #ccc; padding: 4px 8px; text-align: left; }' +
              '.geoland-gifu-info th { background: #f0f0f0; font-weight: bold; }</style>' +
              infoHtml +
              '</div>'
            createPopup(map, [lng, lat], html.value, mapName)
          }
        })
        .catch(err => {
          console.warn('[popup] ジオランドぎふ GetFeatureInfo error', err)
        })
    })
  }

  // if (rasterLayerIds.length === 0) {
  //     let lng = e.lngLat.lng
  //     let lat = e.lngLat.lat
  //     if (coordinates.length > 0) {
  //         lng = coordinates[0]
  //         lat = coordinates[1]
  //     }
  //     if (html.value || store.state.mapillaryFlg) {
  //         createPopup(map, [lng, lat], html.value, mapName)
  //     }
  // } else {
  //     if (!html.value && store.state.mapillaryFlg) {
  //         let lng = e.lngLat.lng
  //         let lat = e.lngLat.lat
  //         createPopup(map, [lng,lat], html.value, mapName)
  //     }
  // }
}

// export function enableMotionPermission() {
//     if (
//         typeof DeviceOrientationEvent !== 'undefined' &&
//         typeof DeviceOrientationEvent.requestPermission === 'function'
//     ) {
//         // iOS (Safariなど)
//         DeviceOrientationEvent.requestPermission()
//             .then(permissionState => {
//                 if (permissionState === 'granted') {
//                 } else {
//                     alert('モーションセンサーが許可されていません');
//                 }
//             })
//             .catch(console.error);
//     } else {
//         // Android / PC（多くは許可不要）
//     }
// }

/**
 * 複数地物のHTMLをチップ式UIに変換する
 * @param {string} htmlContent - 累積されたポップアップHTML
 * @returns {string} チップ付きのHTML
 */
function addChipNavigation(htmlContent) {
  if (!htmlContent || htmlContent === 'dumy') return htmlContent

  // layer-label-divで始まるセクションを分割（style属性やクラス追加も考慮）
  const regex = /<div class="layer-label-div[^"]*"[^>]*>(.*?)<\/div>/g
  const matches = [...htmlContent.matchAll(regex)]

  // 複数のセクションがない場合はそのまま返す
  if (matches.length <= 1) return htmlContent

  // ユニークなラベルを収集（出現順を保持）
  const labels = []
  const labelSet = new Set()
  matches.forEach(match => {
    const label = match[1].trim()
    if (!labelSet.has(label)) {
      labelSet.add(label)
      labels.push(label)
    }
  })

  // 最初のラベル（表示するセクション）
  const firstLabel = labels[0]?.replace(/[<>'"&]/g, '')

  // 各セクションをラップする（最初以外は非表示）
  let processedHtml = htmlContent
  let sectionIndex = 0
  processedHtml = processedHtml.replace(/<div class="(layer-label-div[^"]*)"([^>]*)>(.*?)<\/div>/g, (match, className, attrs, label) => {
    const cleanLabel = label.trim()
    const dataLabel = cleanLabel.replace(/[<>'"&]/g, '') // XSS対策
    sectionIndex++
    // 最初のラベル以外は非表示にする
    const hideStyle = dataLabel !== firstLabel ? ' style="display: none;"' : ''
    return `<div class="popup-section" data-popup-label="${dataLabel}" data-section-index="${sectionIndex}"${hideStyle}><div class="${className}"${attrs}>${label}</div>`
  })

  // 各セクションの終わりを検出して閉じタグを追加
  // layer-label-divの次のlayer-label-divの前、またはHTMLの終わりまでがセクション
  const sections = processedHtml.split('<div class="popup-section"')
  let wrappedHtml = sections[0] // 最初の部分（セクション前のコンテンツ）

  for (let i = 1; i < sections.length; i++) {
    wrappedHtml += '<div class="popup-section"' + sections[i] + '</div>'
  }

  // チップUIを生成
  const chipHtml = `
    <div class="popup-chip-container">
      ${labels.map((label, index) => `
        <button class="popup-chip${index === 0 ? ' active' : ''}" data-chip-label="${label.replace(/[<>'"&]/g, '')}">
          ${label}
        </button>
      `).join('')}
    </div>
  `

  return chipHtml + wrappedHtml
}

/**
 * チップクリックのイベントリスナーを設定
 */
function setupChipListeners() {
  setTimeout(() => {
    document.querySelectorAll('.popup-chip').forEach(chip => {
      chip.addEventListener('click', (e) => {
        e.stopPropagation()
        const targetLabel = chip.dataset.chipLabel

        // アクティブ状態を更新
        document.querySelectorAll('.popup-chip').forEach(c => c.classList.remove('active'))
        chip.classList.add('active')

        // セクションの表示/非表示を切り替え
        document.querySelectorAll('.popup-section').forEach(section => {
          if (section.dataset.popupLabel === targetLabel) {
            section.style.display = 'block'
          } else {
            section.style.display = 'none'
          }
        })
      })
    })
    // 初期状態はHTML生成時に設定済み
  }, 100)
}

async function createPopup(map, coordinates, htmlContent, mapName, isPan, featureCoordinates) {
  if (isSmall && store.state.isDraw) return
  // ストリートビュー・Googleマップのリンクには地物座標を使う（ラベル端クリック対策）
  const featureCoords = featureCoordinates || coordinates
  const [lng, lat] = featureCoords

  // ストリートビューFW更新はStreet Viewボタン押下時のみ（popup-streetview-btn）

  // 周辺施設検索（トークン消費なし）
  if (store.state.nearbyPlacesFlg) {
    ;(async () => {
      store.state.nearbyPlacesLoading = true
      try {
        const { fetchNearbyPlaces, showNearbyMarkers, clearNearbyMarkers } = await import('@/js/placesService')
        clearNearbyMarkers()
        const places = await fetchNearbyPlaces(lat, lng)
        store.state.nearbyPlacesData = places
        if (places.length > 0 && map) showNearbyMarkers(places, map)
      } catch (e) {
        console.error('[Places] error:', e)
        store.state.nearbyPlacesData = []
      } finally {
        store.state.nearbyPlacesLoading = false
      }
    })()
  }

  let streetView =
    '<div class="popup-external-links">' +
    '<button class="popup-link-chip popup-streetview-btn" data-lat="' + lat + '" data-lng="' + lng + '">' +
    '<span class="popup-link-icon">📷</span>Street View</button>' +
    '<a href="https://www.google.co.jp/maps?q=' +
    lat + ',' + lng +
    '&hl=ja" target="_blank" class="popup-link-chip">' +
    '<span class="popup-link-icon">🗺</span>G Map</a>' +
    '<a href="https://earth.google.com/web/@' +
    lat + ',' + lng +
    ',1000a,1000d,0y,0h,60t,0r" target="_blank" class="popup-link-chip">' +
    '<span class="popup-link-icon">🌐</span>G Earth</a>' +
    '</div>'

  if (!isGooglemap) streetView = ''

  // 複数地物がある場合はチップ式UIを適用
  const processedContent = addChipNavigation(htmlContent)

  // 複数地物がある場合は幅を広げる（チップを1行で表示しやすくする）
  const hasMultipleFeatures = processedContent.includes('popup-chip-container')

  // ポップアップHTMLを生成
  let popupHtml = `<div class="popup-html-div">${processedContent}${streetView}</div>`
  let popupWidth = hasMultipleFeatures ? 400 : 300
  if (store.state.mapillaryFlg) {
    let height
    if (window.innerWidth < 400) {
      popupWidth = 300
      height = 200
    }
    if (htmlContent.includes('street-view') || !htmlContent) {
      popupHtml = `<div class="popup-html-div">${processedContent}<div class="mapillary-container" style="width: ${popupWidth}px;height: ${height}px; margin-top: 20px;"></div>${streetView}</div>`
    }
  }
  // 既存のポップアップを削除（このマップのみ）
  const targetMapName = mapName || 'map01'
  if (popups[targetMapName]) {
    popups[targetMapName].forEach((popup) => popup.remove())
    popups[targetMapName].length = 0
  }

  // ポップアップを作成して地図に追加
  function onPanelEnter() {
    store.state.panelHoverCount++
    store.state.isCursorOnPanel = true
  }
  function onPanelLeave() {
    store.state.panelHoverCount = Math.max(store.state.panelHoverCount - 1, 0)
    if (store.state.panelHoverCount === 0) {
      store.state.isCursorOnPanel = false
    }
  }
  const center = map.getCenter() // 中心の緯度経度
  let position = coordinates
  let isSmartPhone = false
  if (window.innerWidth < 450) isSmartPhone = true
  if (isSmartPhone) {
    position = center
  }
  if (htmlContent !== 'dumy') {
    const popup = new maplibregl.Popup({
      // closeButton: true,
      maxWidth: '380px',
      anchor: 'bottom', // 常に地物の上に表示
      // className: "my-popup"
    })
      .setLngLat(position)
      .setHTML(popupHtml)
      .addTo(map)
    // ポップアップイベント設定
    // 1) クローズボタン押下をフック（addTo前に登録）
    const onBeforeClose = (e) => {
      lavelUpdate(null, store.state.drawFeatureId)
      store.state.isLabelUpdated = false
    }
    // open 時にクローズボタンへ一度だけバインド
    popup.on('open', () => {
      const btn = popup.getElement().querySelector('.maplibregl-popup-close-button')
      if (btn) btn.addEventListener('click', onBeforeClose, { once: true })
    })
    // 2) クリック外や Esc で閉じるケースも拾うため remove() をラップ
    const origRemove = popup.remove
    popup.remove = function () {
      // まだ実行していなければ事前処理
      onBeforeClose()
      return origRemove.apply(this, arguments)
    }
    // 最後に addTo（ここで open が発火）
    popup.addTo(map)
    // 管理配列へ（このマップのポップアップとして登録）
    if (popups[targetMapName]) {
      popups[targetMapName].push(popup)
    }
    // 既に開いた状態（環境によっては同期発火）に備えて保険
    if (typeof popup.isOpen === 'function' && popup.isOpen()) {
      const btn = popup.getElement().querySelector('.maplibregl-popup-close-button')
      if (btn && !btn._oh_bound) {
        btn.addEventListener('click', onBeforeClose, { once: true })
        btn._oh_bound = true
      }
    }
    popup.on('close', () => closeAllPopups(targetMapName))
    const popupEl = popup.getElement()
    // ドロワーに負けるので後出し。
    setTimeout(() => {
      popupEl.style.zIndex = getNextZIndex()
    }, 100)
    popupEl.addEventListener('click', (e) => {
      e.currentTarget.style.zIndex = getNextZIndex()
    })
    // 次の描画タイミングで高さを取得して再配置
    if (isSmartPhone) {
      requestAnimationFrame(() => {
        const h = popup.getElement().offsetHeight
        const screen = map.getCanvas().getBoundingClientRect()
        const lngLat = map.unproject([screen.width / 2, screen.height / 2 + h / 2])
        popup.setLngLat(lngLat)
      })
    }

    // Popupのdivは複数存在する場合があるので全てに仕掛けるのが安全
    setTimeout(() => {
      document.querySelectorAll('.maplibregl-popup').forEach((div) => {
        div.addEventListener('mouseenter', onPanelEnter)
        div.addEventListener('mouseleave', onPanelLeave)
      })
    }, 500)

    // チップ式UIのイベントリスナーを設定
    setupChipListeners()

    // ポップアップルーティングボタンのイベントリスナー（汎用）
    setTimeout(() => {
      const tracker = getUsageTracker()
      document.querySelectorAll('.popup-route-btn').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation()
          if (btn.disabled) return
          tracker.trackEvent('popup', 'click', 'popup-route')
          const lng = parseFloat(btn.dataset.lng)
          const lat = parseFloat(btn.dataset.lat)
          if (!isNaN(lng) && !isNaN(lat)) {
            // ローディング状態にする
            btn.disabled = true
            btn.style.opacity = '0.5'
            btn.style.cursor = 'wait'
            try {
              await routeToPoint(map, lng, lat)
            } finally {
              // ローディング解除
              btn.disabled = false
              btn.style.opacity = '1'
              btn.style.cursor = 'pointer'
            }
          }
        })
      })
      document.querySelectorAll('.popup-route-clear-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          tracker.trackEvent('popup', 'click', 'popup-route-clear')
          clearAllPopupRoutes(map)
          clearNearestVertices(map)
        })
      })
      document.querySelectorAll('.popup-nearest-vertex-btn').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          if (btn.disabled) return
          tracker.trackEvent('popup', 'click', 'nearest-vertex')
          const lng = parseFloat(btn.dataset.lng)
          const lat = parseFloat(btn.dataset.lat)
          const layer = btn.dataset.layer || ''
          if (!isNaN(lng) && !isNaN(lat)) {
            btn.disabled = true
            btn.textContent = '検索中...'
            try {
              showNearestVertices(map, lng, lat, layer)
            } finally {
              btn.disabled = false
              btn.textContent = '最寄り筆界点'
            }
          }
        })
      })
      // Street ViewボタンでストリートビューFWを開く
      // 連打や setTimeout のレース発火で同じボタンに複数リスナーが付くのを防ぐため、
      // btn._svBound フラグで一度だけ登録する
      document.querySelectorAll('.popup-streetview-btn').forEach((btn) => {
        if (btn._svBound) return
        btn._svBound = true
        btn.addEventListener('click', (e) => {
          e.stopPropagation()
          const svLat = parseFloat(btn.dataset.lat)
          const svLng = parseFloat(btn.dataset.lng)
          if (isNaN(svLat) || isNaN(svLng)) return
          consumeToken('street_view').then(r => {
            if (!r.success && (r.error === 'POINT_INSUFFICIENT' || r.error === 'LOGIN_REQUIRED')) {
              return
            }
            store.state.streetViewCoords = { lat: svLat, lng: svLng, _t: Date.now() }
            store.dispatch('showFloatingWindow', 'street-view-window')
          })
        })
      })
    }, 100)
  }

  let container = document.querySelector('.street-view')
  let viewer = null
  if (store.state.mapillaryFlg) {
    if (!container) {
      if (window.innerWidth < 0) {
        container = document.querySelector('.mapillary-container')
      } else {
        store.state.mapillaryDialog = true
        // このマップのポップアップを閉じる
        if (popups[targetMapName]) {
          popups[targetMapName].forEach((popup) => popup.remove())
          popups[targetMapName].length = 0
        }
      }
    }
    setTimeout(() => {
      if (window.innerWidth > 0) container = document.querySelector('.mapillary-container2')
      if (container && store.state.mapillaryFlg) {
        store.state.loading2 = true
        store.state.loadingMessage = '読み込み中です。'
        async function mapillarySet() {
          const MAPILLARY_CLIENT_ID = process.env.VUE_APP_MAPILLARY_CLIENT_ID
          const deltaLat = 0.00009 // 約10m
          const deltaLng = 0.00011 // 東京近辺での約10m
          const response = await fetch(
            `https://graph.mapillary.com/images?access_token=${MAPILLARY_CLIENT_ID}&fields=id,thumb_1024_url&bbox=${lng - deltaLng},${lat - deltaLat},${lng + deltaLng},${lat + deltaLat}&limit=1`
          )
          const data = await response.json()
          if (data.data && data.data.length > 0) {
            const imageId = data.data[0].id
            const { Viewer } = await import('mapillary-js')
            viewer = new Viewer({
              accessToken: MAPILLARY_CLIENT_ID,
              container: container,
              imageId: imageId,
              component: { cover: false },
            })
            viewer.on('image', (image) => {
              setTimeout(() => {
                try {
                  const link = document.querySelector('.mapillary-attribution-image-container').href
                  const username = document.querySelector('.mapillary-attribution-username').innerHTML
                  document.querySelector('.attribution-username').innerHTML =
                    `<a href=${DOMPurify.sanitize(link)} target='_blank'>${DOMPurify.sanitize(username)}</a>`
                  document.querySelector('.attribution-date').innerHTML =
                    DOMPurify.sanitize(document.querySelector('.mapillary-attribution-date').innerHTML)
                } catch (e) {
                }
              }, 0)
              store.state.loading2 = false
            })
          } else {
            container.innerHTML =
              '<div style="text-align: center;"><span style="font-size: small">Mapillary画像が見つかりませんでした。</span></div>'
            console.warn('Mapillary画像が見つかりませんでした')
            store.state.loading2 = false
          }
        }
        mapillarySet()
      }
    }, 0)
  } else {
    if (document.querySelector('.street-view')) {
      document.querySelector('.street-view').style.height = '0px'
      document.querySelector('.street-view').style.width = '0px'
    }
  }

  // スクロールリセット（ポップアップ内のスクロール位置をリセット）
  document.querySelectorAll('.popup-html-div').forEach((element) => {
    element.scrollTop = 0
  })
  // 逆ジオコーディング（住所の取得）
  fetchReverseGeocoding(lng, lat, mapName)
  // -------------------
  const images = document.querySelectorAll('.carousel-images img')
  const carouselButtons = document.querySelector('.carousel-buttons')
  const totalImages = images.length
  if (totalImages === 1) {
    carouselButtons.classList.add('hidden')
  }
  pyramid.currentIndex = 0
}
function fetchReverseGeocoding(lng, lat, mapName) {
  axios
    .get('https://mreversegeocoder.gsi.go.jp/reverse-geocoder/LonLatToAddress', {
      params: { lon: lng, lat: lat },
    })
    .then((response) => {
      // レスポンスデータの処理
      if (response.data.results) {
        // const address = response.data.results.lv01Nm;
        // ポップアップ内の住所表示要素を更新
        const splitMuni = muni[Number(response.data.results.muniCd)].split(',')
        const address = splitMuni[1] + splitMuni[3] + response.data.results.lv01Nm
        const popupAddress = document.querySelector(`#${mapName} .popup-address`)
        if (popupAddress) {
          popupAddress.innerHTML = DOMPurify.sanitize(address)
          store.state.popupAddress = address // 状態に住所を保存
        }
      }
    })
    .catch((error) => {
      console.error('Reverse geocoding failed:', error)
    })
}

export function mouseMoveForPopup(e, map) {
  let rasterLayerIds = []
  const mapLayers = map.getStyle().layers
  mapLayers.forEach((layer) => {
    // const visibility = map.getLayoutProperty(layer.id, 'visibility');
    // レイヤーのtypeプロパティを取得
    const type = layer.type
    if (type === 'raster') {
      rasterLayerIds.push(layer.id)
    }
  })
  // ラスタレイヤのidからポップアップ表示に使用するURLを生成
  rasterLayerIds.forEach((rasterLayerId) => {
    const RasterTileUrl = urlByLayerId(rasterLayerId)[0]
    const legend = urlByLayerId(rasterLayerId)[1]
    const z = urlByLayerId(rasterLayerId)[2]
    const lng = e.lngLat.lng
    const lat = e.lngLat.lat
    if (RasterTileUrl) {
      getLegendItem(legend, RasterTileUrl, lat, lng, z).then(function (v) {
        let res = v ? v.title : ''
        if (res === '') {
          // map.getCanvas().style.cursor = ""
          map.getCanvas().classList.remove('force-pointer')
          return
        }
        // ↓。効かない。どこかで上書きされている。
        map.getCanvas().style.cursor = 'pointer'
        // ↓。効く。上書きも関係ない。注意。副作用があるかもしれない。
        map.getCanvas().classList.add('force-pointer')
      })
    }
  })
}
